package com.islamicmedia.site.pages

import androidx.compose.runtime.*
import com.islamicmedia.shared.data.cache.AdminEventRow
import com.islamicmedia.shared.data.cache.AdminMediaRow
import com.islamicmedia.shared.data.cache.UserJourneyRow
import com.islamicmedia.shared.data.cache.AnalyticsService
import com.islamicmedia.shared.data.cache.CostBreakdown
import com.islamicmedia.shared.data.cache.DashboardData
import com.islamicmedia.shared.data.cache.MediaTypeSummary
import com.islamicmedia.shared.data.cache.topSearchedMedia
import com.islamicmedia.shared.data.cache.buildDashboardData
import com.islamicmedia.shared.data.cache.UserSummary
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.domain.model.AlternativeItem
import com.islamicmedia.shared.domain.model.AlternativesResult
import com.islamicmedia.site.components.CombinedAnalysisResultContent
import com.islamicmedia.site.components.InjectSpinKeyframes
import com.islamicmedia.site.components.InjectStyles
import com.islamicmedia.site.theme.*
import com.varabyte.kobweb.core.Page
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import com.islamicmedia.shared.domain.model.PricingConfig
import com.islamicmedia.shared.domain.model.PricingPlan
import com.islamicmedia.shared.domain.model.CancelFlowConfig
import com.islamicmedia.shared.domain.model.CancelReasonOption
import com.islamicmedia.shared.domain.model.RetentionOfferConfig
import com.islamicmedia.shared.domain.model.OfferKind
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.pow
import kotlin.math.round

private val adminJson = Json { ignoreUnknownKeys = true; isLenient = true }

/** JS global — URL-encode a query-param value (emails can contain `+`). */
private external fun encodeURIComponent(value: String): String
private external fun decodeURIComponent(value: String): String

// ---------------------------------------------------------------------------
// Navigation state
// ---------------------------------------------------------------------------

private sealed class DrillDownView {
    data object Overview : DrillDownView()
    data object AllAnalyses : DrillDownView()
    data object AllMedia : DrillDownView()
    data object AllUsers : DrillDownView()
    data object FoundingMembers : DrillDownView()
    data object RegularUsers : DrillDownView()
    data object CostAnalysis : DrillDownView()
    data object UserJourneys : DrillDownView()
    data object FlaggedAnalyses : DrillDownView()
    data object Engagement : DrillDownView()
    data object RegistrationFunnel : DrillDownView()
    data object InfraCosts : DrillDownView()
    data object MrrDetail : DrillDownView()
    data class ResolveFlag(val flagId: String) : DrillDownView()
    data object AdminEmails : DrillDownView()
    data object Broadcast : DrillDownView()
    data object Newsletter : DrillDownView()
    data object NewsletterSubscribers : DrillDownView()
    data class NewsletterCampaignDetail(val campaignId: String) : DrillDownView()
    data object GrantCredits : DrillDownView()
    data object Invitations : DrillDownView()
    data object Vouchers : DrillDownView()
    data object Referrals : DrillDownView()
    data object Pricing : DrillDownView()
    data object Limits : DrillDownView()
    data object CancelFlow : DrillDownView()
    data object Ask : DrillDownView()
    data object ManageCovers : DrillDownView()
    data class EditAnalyses(val title: String? = null, val mediaType: String? = null, val year: String? = null) : DrillDownView()
    data object ReconcileDuplicates : DrillDownView()
    data object Drip : DrillDownView()
    data class DripCampaignDetail(val campaignId: String) : DrillDownView()
    data class UserDetail(val userEmail: String?, val userId: String?) : DrillDownView()
    data class TitleDetail(val title: String, val mediaType: String? = null, val year: String? = null) : DrillDownView()
    data class MediaTypeDetail(val mediaType: String) : DrillDownView()
}

// Map each view to a URL hash slug (and back) so the browser URL reflects the
// current screen — enables back/forward, refresh-stays-put, and bookmarking.
// Parameterized views encode their identity arg after a "/".
private fun viewToSlug(v: DrillDownView): String = when (v) {
    is DrillDownView.Overview -> ""
    is DrillDownView.Ask -> "ask"
    is DrillDownView.AllAnalyses -> "analyses"
    is DrillDownView.AllMedia -> "media"
    is DrillDownView.AllUsers -> "users"
    is DrillDownView.FoundingMembers -> "founding-members"
    is DrillDownView.RegularUsers -> "regular-users"
    is DrillDownView.CostAnalysis -> "cost"
    is DrillDownView.UserJourneys -> "journeys"
    is DrillDownView.FlaggedAnalyses -> "flags"
    is DrillDownView.Engagement -> "engagement"
    is DrillDownView.RegistrationFunnel -> "funnel"
    is DrillDownView.InfraCosts -> "infra-costs"
    is DrillDownView.MrrDetail -> "mrr"
    is DrillDownView.ResolveFlag -> "resolve-flag/${encodeURIComponent(v.flagId)}"
    is DrillDownView.AdminEmails -> "admin-emails"
    is DrillDownView.Broadcast -> "broadcast"
    is DrillDownView.Newsletter -> "newsletter"
    is DrillDownView.NewsletterSubscribers -> "newsletter-subscribers"
    is DrillDownView.NewsletterCampaignDetail -> "newsletter-campaign/${encodeURIComponent(v.campaignId)}"
    is DrillDownView.GrantCredits -> "grant-credits"
    is DrillDownView.Invitations -> "invitations"
    is DrillDownView.Vouchers -> "vouchers"
    is DrillDownView.Referrals -> "referrals"
    is DrillDownView.Pricing -> "pricing"
    is DrillDownView.Limits -> "limits"
    is DrillDownView.CancelFlow -> "cancel-flow"
    is DrillDownView.ManageCovers -> "covers"
    is DrillDownView.EditAnalyses -> if (v.title.isNullOrBlank()) "edit-analyses" else buildString {
        append("edit-analyses/").append(encodeURIComponent(v.title))
        if (!v.mediaType.isNullOrBlank()) {
            append("/").append(encodeURIComponent(v.mediaType))
            append("/").append(encodeURIComponent(v.year ?: ""))
        }
    }
    is DrillDownView.ReconcileDuplicates -> "reconcile"
    is DrillDownView.Drip -> "drip"
    is DrillDownView.DripCampaignDetail -> "drip-campaign/${encodeURIComponent(v.campaignId)}"
    is DrillDownView.UserDetail -> "user/${encodeURIComponent(v.userEmail ?: v.userId ?: "")}"
    is DrillDownView.TitleDetail -> buildString {
        append("title/").append(encodeURIComponent(v.title))
        if (!v.mediaType.isNullOrBlank()) {
            append("/").append(encodeURIComponent(v.mediaType))
            append("/").append(encodeURIComponent(v.year ?: ""))
        }
    }
    is DrillDownView.MediaTypeDetail -> "media-type/${encodeURIComponent(v.mediaType)}"
}

private fun slugToView(rawHash: String): DrillDownView {
    val slug = rawHash.removePrefix("#")
    val head = slug.substringBefore("/")
    val arg = slug.substringAfter("/", "").takeIf { it.isNotBlank() }?.let {
        try { decodeURIComponent(it) } catch (_: Exception) { it }
    }
    return when (head) {
        "", "overview" -> DrillDownView.Overview
        "analyses" -> DrillDownView.AllAnalyses
        "media" -> DrillDownView.AllMedia
        "users" -> DrillDownView.AllUsers
        "founding-members" -> DrillDownView.FoundingMembers
        "regular-users" -> DrillDownView.RegularUsers
        "cost" -> DrillDownView.CostAnalysis
        "journeys" -> DrillDownView.UserJourneys
        "flags" -> DrillDownView.FlaggedAnalyses
        "engagement" -> DrillDownView.Engagement
        "funnel" -> DrillDownView.RegistrationFunnel
        "infra-costs" -> DrillDownView.InfraCosts
        "mrr" -> DrillDownView.MrrDetail
        "resolve-flag" -> if (arg != null) DrillDownView.ResolveFlag(arg) else DrillDownView.FlaggedAnalyses
        "admin-emails" -> DrillDownView.AdminEmails
        "broadcast" -> DrillDownView.Broadcast
        "newsletter" -> DrillDownView.Newsletter
        "newsletter-subscribers" -> DrillDownView.NewsletterSubscribers
        "newsletter-campaign" -> if (arg != null) DrillDownView.NewsletterCampaignDetail(arg) else DrillDownView.Newsletter
        "grant-credits" -> DrillDownView.GrantCredits
        "invitations" -> DrillDownView.Invitations
        "vouchers" -> DrillDownView.Vouchers
        "referrals" -> DrillDownView.Referrals
        "ask" -> DrillDownView.Ask
        "pricing" -> DrillDownView.Pricing
        "limits" -> DrillDownView.Limits
        "cancel-flow" -> DrillDownView.CancelFlow
        "covers" -> DrillDownView.ManageCovers
        "edit-analyses" -> {
            val parts = slug.substringAfter("/", "").split("/")
            val t = parts.getOrNull(0)?.takeIf { it.isNotBlank() }?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }
            DrillDownView.EditAnalyses(
                t,
                parts.getOrNull(1)?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }?.takeIf { it.isNotBlank() },
                parts.getOrNull(2)?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }?.takeIf { it.isNotBlank() }
            )
        }
        "reconcile" -> DrillDownView.ReconcileDuplicates
        "drip" -> DrillDownView.Drip
        "drip-campaign" -> if (arg != null) DrillDownView.DripCampaignDetail(arg) else DrillDownView.Drip
        "user" -> DrillDownView.UserDetail(arg, null)
        "title" -> {
            val parts = slug.substringAfter("/", "").split("/")
            val t = parts.getOrNull(0)?.takeIf { it.isNotBlank() }?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }
            if (t == null) DrillDownView.Overview
            else DrillDownView.TitleDetail(
                t,
                parts.getOrNull(1)?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }?.takeIf { it.isNotBlank() },
                parts.getOrNull(2)?.let { try { decodeURIComponent(it) } catch (_: Exception) { it } }?.takeIf { it.isNotBlank() }
            )
        }
        "media-type" -> if (arg != null) DrillDownView.MediaTypeDetail(arg) else DrillDownView.Overview
        else -> DrillDownView.Overview
    }
}

/** Lazy per-row detail fetched on expand from /api/admin/event-result. */
@Serializable
private data class EventDetail(
    val resultJson: String? = null,
    val criteriaJson: String? = null,
    val costBreakdownJson: String? = null,
)

@Serializable
private data class DupVariant(
    val id: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String? = null,
    @SerialName("cache_key") val cacheKey: String = "",
    @SerialName("cover_url") val coverUrl: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("hit_count") val hitCount: Int = 0
)

@Serializable
private data class DupGroup(
    val key: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String = "",
    val variants: List<DupVariant> = emptyList()
)

@Serializable
private data class CoverItem(
    val id: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String? = null,
    @SerialName("cache_key") val cacheKey: String = "",
    @SerialName("cover_url") val coverUrl: String? = null,
    @SerialName("cover_source") val coverSource: String? = null,
    @SerialName("created_at") val createdAt: String? = null
)

@Serializable
private data class FlagEntry(
    val id: String? = null,
    val title: String = "",
    @SerialName("media_type") val mediaType: String? = null,
    val score: Int? = null,
    @SerialName("quick_check") val quickCheck: Boolean = false,
    val message: String? = null,
    @SerialName("criteria_json") val criteriaJson: String? = null,
    @SerialName("user_email") val userEmail: String? = null,
    val platform: String? = null,
    @SerialName("app_version") val appVersion: String? = null,
    val resolved: Boolean = false,
    @SerialName("resolved_at") val resolvedAt: String? = null,
    @SerialName("corrected_title") val correctedTitle: String? = null,
    @SerialName("created_at") val createdAt: String? = null
)

// ── Engagement: likes / dislikes / comments (GET /api/admin/engagement) ──────

@Serializable
private data class EngagementTitle(
    @SerialName("content_id") val contentId: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String? = null,
    val year: String? = null,
    val likes: Int = 0,
    val dislikes: Int = 0,
    val comments: Int = 0,
    @SerialName("last_activity_at") val lastActivityAt: String? = null,
    @SerialName("like_user_ids") val likeUserIds: List<String> = emptyList(),
    @SerialName("dislike_user_ids") val dislikeUserIds: List<String> = emptyList()
) {
    val total: Int get() = likes + dislikes + comments
    val net: Int get() = likes - dislikes
}

@Serializable
private data class EngagementComment(
    val id: String = "",
    @SerialName("content_id") val contentId: String = "",
    @SerialName("user_id") val userId: String = "",
    @SerialName("first_name") val firstName: String = "",
    val body: String = "",
    val title: String = "",
    @SerialName("media_type") val mediaType: String? = null,
    val year: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("edited_at") val editedAt: String? = null
)

@Serializable
private data class EngagementData(
    val titles: List<EngagementTitle> = emptyList(),
    val comments: List<EngagementComment> = emptyList(),
    @SerialName("total_likes") val totalLikes: Int = 0,
    @SerialName("total_dislikes") val totalDislikes: Int = 0,
    @SerialName("total_comments") val totalComments: Int = 0,
    /** Server confirmed it narrowed this payload to one title. Absent (false) on an older
     *  server build that ignored the scoping params — see [TitleEngagementCard]. */
    val scoped: Boolean = false
)

@Serializable
private data class DisambItem(
    val title: String = "",
    val year: String? = null,
    val info: String? = null,
    val mediaType: String? = null,
    /** Books only. Two works can share a title and year (Escape, 2022: James Patterson's thriller
     *  and K. R. Alexander's), so the admin needs it to pick, and the re-run needs it to search. */
    val author: String? = null
)

@Serializable
private data class DisambResponse(val items: List<DisambItem> = emptyList())

/** "Horror novel by K.R. Alexander" -> "K.R. Alexander". Null when the info line names no author. */
private fun authorFromInfo(info: String?): String? =
    info?.let { Regex("""\bby\s+([^,;()]+?)(?:\s*[,;(]|\s+\((?:\d{4})|$)""").find(it)?.groupValues?.get(1)?.trim()?.trimEnd('.') }
        ?.takeIf { it.isNotBlank() }

@Serializable
private data class ResolveResponse(
    val ok: Boolean = false,
    val score: Int? = null,
    val result: com.islamicmedia.shared.domain.model.AnalysisResult? = null,
    val oldResult: com.islamicmedia.shared.domain.model.AnalysisResult? = null
)

@Serializable
private data class RegisteredUser(
    val id: String = "",
    val email: String = "",
    val fullName: String? = null,
    val avatarUrl: String? = null,
    val provider: String? = null,
    val createdAt: String = "",
    val lastSignIn: String? = null,
    val emailConfirmed: Boolean = false,
    val isApproved: Boolean = false,
    val isRegistered: Boolean = true,
    val approvedSource: String? = null,
    /** Timestamp when the user was added to approved_users (Zapier webhook, admin, etc.) */
    val approvedAt: String? = null,
    val totalSearches: Int = 0,
    val lastPlatform: String? = null,
    val lastAppVersion: String? = null,
    // ── Billing entitlement (mirrors server AdminUser; defaults when no row) ──
    val plan: String? = null,
    val membershipStatus: String = "free",
    val fullCredits: Int = 0,
    val qcCredits: Int = 0,
    val freeFullUsed: Boolean = false,
    val freeFullRemaining: Int? = null,
    val freeQcUsed: Boolean = false,
    val unlimitedUntil: String? = null,
    val currentPeriodEnd: String? = null,
    val fullUsed: Int = 0,
    val qcUsed: Int = 0
) {
    /** Grandfathered founding/legacy member — the old $100/yr cohort (status == "legacy"). This is
     *  what the Founding Members page filters on; it is NOT the same as [isUnlimited] anymore. */
    val isLegacy: Boolean get() = membershipStatus == "legacy" && plan != "thrivecart_9"

    /** Comped membership from an admin invite (status == "gifted"). Deliberately NOT counted as
     *  legacy/founding — these people were given a 1–3 month gift, they aren't the $100/yr cohort. */
    val isGifted: Boolean get() = membershipStatus == "gifted"

    /** Has unlimited access — legacy OR an active/canceling subscription on an unlimited plan.
     *  Delegates to the shared [Entitlement] so the admin never re-derives the rule (and picks up
     *  subscription-unlimited automatically). */
    val isUnlimited: Boolean get() = entitlement.isUnlimited

    /** Registered under the new signup flow — i.e. on/after the 2026-06-05 cutover.
     *  createdAt is an ISO-8601 timestamp, so a lexical compare against the date is correct;
     *  pending (unregistered) users have a blank createdAt and are naturally excluded. */
    val isRegularSignup: Boolean get() = createdAt >= "2026-06-05"

    /** On a live paid subscription (active, past-due, or canceling-at-period-end — all still paying). */
    val isPaidMember: Boolean get() =
        membershipStatus == "active" || membershipStatus == "past_due" || membershipStatus == "canceling"

    /** Anyone whose access was PAID for, by whatever route: a live Stripe subscription, the
     *  ThriveCart $9-forever cohort, or a grandfathered $100/yr founding member. [isPaidMember]
     *  alone misses the latter two, because both carry status "legacy" rather than "active".
     *  Gifted is the one unlimited cohort deliberately excluded — those were comped, not sold. */
    val hasPaidMembership: Boolean get() = isPaidMember || (isUnlimited && !isGifted)

    /** The SAME entitlement model the user-facing app uses, so the admin table never re-derives
     *  credit rules on its own (that drift is exactly what hid the free quick check). Available
     *  counts + free-tier handling all come from here — change the rule once in [Entitlement] (or
     *  the user_entitlements row) and both surfaces reflect it. */
    private val entitlement: com.islamicmedia.shared.domain.model.Entitlement get() =
        com.islamicmedia.shared.domain.model.Entitlement(
            fullCredits = fullCredits,
            qcCredits = qcCredits,
            freeFullUsed = freeFullUsed,
            freeFullRemaining = freeFullRemaining,
            freeQcUsed = freeQcUsed,
            unlimitedUntil = unlimitedUntil,
            plan = plan,
            status = membershipStatus,
        )

    /** Effective available searches (paid credits, or the 1 free lifetime one) — from [Entitlement]. */
    val availableFullCredits: Int get() = entitlement.availableFull
    val availableQcCredits: Int get() = entitlement.availableQc

    /** True when the single available search is the free lifetime one (drives a "free" tag in the table). */
    val hasFreeFullOnly: Boolean get() = entitlement.hasFreeFullOnly
    val hasFreeQcOnly: Boolean get() = entitlement.hasFreeQcOnly

    /** Total searches the user has ever had = currently available + already used.
     *  (Used is admin-only activity data from the search ledger, not part of the entitlement.) */
    val accumulatedFullCredits: Int get() = availableFullCredits + fullUsed
    val accumulatedQcCredits: Int get() = availableQcCredits + qcUsed

    /** Short membership label for the table pill, e.g. "Pro Plan", "Annual Plan", "Free".
     *  For active subscribers the pill prefers the admin-configured name from /api/pricing;
     *  this is just the hardcoded fallback. */
    val membershipLabel: String get() = when {
        // Checked BEFORE status, because ThriveCart members are ordinary "active" subscribers now
        // (that is what makes their Manage Subscription button work) and would otherwise fall into
        // the generic "Active" below and be indistinguishable from a Stripe subscriber. Earlier
        // ThriveCart rows carry status "legacy" from the first version of the grant — both are the
        // same cohort and both should read "ThriveCart", so the plan alone decides.
        // These three checked BEFORE the isUnlimited/lapsed fallbacks below — otherwise a
        // past-due ThriveCart row (isUnlimited already false) reads as a flat "ThriveCart lapsed",
        // indistinguishable from one that's actually gone, and a canceling or paused one (still
        // isUnlimited while within its paid-through window) reads as a flat "ThriveCart", identical
        // to an ordinary live member.
        plan == "thrivecart_9" && membershipStatus == "past_due" -> "ThriveCart past due"
        // A gift on a ThriveCart row (a voucher or admin days after they stopped paying) is a gift,
        // not a live subscription; keep it out of the ThriveCart labels below.
        plan == "thrivecart_9" && isGifted -> if (isUnlimited) "Gifted" else "Gift expired"
        plan == "thrivecart_9" && membershipStatus == "canceling" -> "ThriveCart canceling"
        plan == "thrivecart_9" && membershipStatus == "paused" -> "ThriveCart paused"
        plan == "thrivecart_9" && isUnlimited -> "ThriveCart"
        plan == "thrivecart_9" -> "ThriveCart lapsed"
        // Checked BEFORE isUnlimited: someone who cancelled keeps access until the period ends, and
        // someone past due keeps it through the retry window, so isUnlimited is still true for both
        // and used to swallow them into a plain "Unlimited" — the admin could not see a cancellation
        // that Stripe had already recorded. They stop reading "Canceling" once the period actually
        // lapses and the status becomes "canceled" below.
        membershipStatus == "canceling" -> "Canceling"
        membershipStatus == "past_due"  -> "Past Due"
        membershipStatus == "active" -> when (plan) {
            "pro_29"   -> "Pro Plan"
            "annual"   -> "Annual Plan"
            else       -> "Active"
        }
        // A gift keeps status "gifted" forever, so distinguish a live one from a lapsed one —
        // otherwise an expired gift reads as a current membership.
        isGifted && isUnlimited -> "Gifted"
        isGifted       -> "Gift expired"
        isUnlimited    -> "Unlimited"   // legacy founding members
        membershipStatus == "canceled"  -> "Canceled"
        else -> "None"
    }

    /**
     * The date beside the membership pill, and the word that says what it MEANS — a renewal, a
     * cut-off, or the end of a comped window. Which underlying column holds it differs by cohort:
     * a Stripe subscriber's lives in current_period_end, a gifted or legacy member's in
     * unlimited_until (they have no billing period at all), so reading one column alone would show
     * a blank for half the users. Null when there's genuinely no date to show (free accounts).
     */
    val membershipDate: Pair<String, String>? get() {
        val subscriptionDate = currentPeriodEnd ?: unlimitedUntil
        val giftDate = unlimitedUntil ?: currentPeriodEnd
        val (word, iso) = when {
            membershipStatus == "canceling" -> "Ends" to subscriptionDate
            membershipStatus == "canceled"  -> "Ended" to subscriptionDate
            membershipStatus == "past_due"  -> "Retry until" to subscriptionDate
            isGifted                        -> (if (isUnlimited) "Gifted until" else "Expired") to giftDate
            membershipStatus == "active"    -> "Renews" to subscriptionDate
            isUnlimited                     -> "Until" to giftDate
            giftDate != null                -> "Expired" to giftDate
            else -> return null
        }
        return word to (iso ?: return null)
    }

    /** Sort rank: paid tiers first (pro_29 > annual), then legacy, then lapsed, then free. */
    val membershipRank: Int get() = when {
        membershipStatus == "active" && plan == "pro_29"   -> 0
        membershipStatus == "active" && plan == "annual"   -> 1
        membershipStatus == "active"                       -> 2
        // Same reordering as membershipLabel: a canceling or past-due member still has live access,
        // so isUnlimited would otherwise sort them among healthy members and hide the churn signal.
        membershipStatus == "past_due"                     -> 4
        membershipStatus == "canceling" || membershipStatus == "canceled" -> 5
        isUnlimited                                        -> 3
        else -> 6
    }
}

// ---------------------------------------------------------------------------
// CSV export helpers
// ---------------------------------------------------------------------------

/** Escape a single CSV field per RFC 4180 — wraps in quotes if it contains comma, quote, or newline. */
private fun csvField(raw: String?): String {
    val v = raw ?: ""
    val needsQuoting = v.contains(',') || v.contains('"') || v.contains('\n') || v.contains('\r')
    return if (needsQuoting) "\"${v.replace("\"", "\"\"")}\"" else v
}

/** Build a CSV string from a list of RegisteredUsers matching the on-screen table columns. */
private fun buildUsersCsv(users: List<RegisteredUser>): String {
    val headers = listOf(
        "Email", "Name", "Status", "Provider", "Email Confirmed",
        "Membership",
        "Full Acc", "Full Avail", "Full Used",
        "Quick Acc", "Quick Avail", "Quick Used",
        "Searches", "Last Sign In", "OS", "App Version", "Registered", "Source"
    )
    val rows = users.map { u ->
        val status = when {
            !u.isRegistered -> "Pending Signup"
            u.isApproved && u.emailConfirmed -> "Active"
            u.isApproved && !u.emailConfirmed -> "Unconfirmed"
            else -> "Not Approved"
        }
        listOf(
            u.email,
            u.fullName ?: "",
            status,
            u.provider ?: "",
            if (u.emailConfirmed) "Yes" else "No",
            u.membershipLabel,
            if (u.isUnlimited) "∞" else u.accumulatedFullCredits.toString(),
            if (u.isUnlimited) "∞" else u.availableFullCredits.toString(),
            u.fullUsed.toString(),
            if (u.isUnlimited) "∞" else u.accumulatedQcCredits.toString(),
            if (u.isUnlimited) "∞" else u.availableQcCredits.toString(),
            u.qcUsed.toString(),
            u.totalSearches.toString(),
            u.lastSignIn ?: "",
            u.lastPlatform ?: "",
            u.lastAppVersion ?: "",
            u.createdAt,
            u.approvedSource ?: ""
        ).joinToString(",") { csvField(it) }
    }
    return (listOf(headers.joinToString(",") { csvField(it) }) + rows).joinToString("\n")
}

/** Trigger a browser CSV download with today's date + active filter in the filename. */
private fun downloadUsersCsv(users: List<RegisteredUser>, filter: String) {
    val csv = buildUsersCsv(users)
    val blobParts: dynamic = js("[]")
    blobParts.push(csv)
    val blob = org.w3c.files.Blob(
        blobParts.unsafeCast<Array<Any>>(),
        org.w3c.files.BlobPropertyBag(type = "text/csv;charset=utf-8")
    )
    val url = org.w3c.dom.url.URL.createObjectURL(blob)
    val today = kotlin.js.Date().toISOString().substring(0, 10)
    val filterLabel = if (filter == "all") "all" else filter
    val anchor = kotlinx.browser.document.createElement("a").unsafeCast<org.w3c.dom.HTMLAnchorElement>()
    anchor.href = url
    anchor.setAttribute("download", "fitrah-users-${filterLabel}-${today}.csv")
    anchor.style.display = "none"
    kotlinx.browser.document.body?.appendChild(anchor)
    anchor.click()
    kotlinx.browser.document.body?.removeChild(anchor)
    org.w3c.dom.url.URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

@Page("/admin")
@Composable
fun AdminPage() {
    val analyticsService = remember { AnalyticsService() }
    val scope = rememberCoroutineScope()

    var isAuthenticated by remember { mutableStateOf<Boolean?>(null) }
    var isRefreshing by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    // Per-source dashboard data — each loads independently so sections render as they arrive
    // (null = not loaded yet → show that section's skeleton).
    var events by remember { mutableStateOf<List<AdminEventRow>?>(null) }
    var media by remember { mutableStateOf<List<AdminMediaRow>?>(null) }
    var journeys by remember { mutableStateOf<List<UserJourneyRow>?>(null) }
    var adminEmailsState by remember { mutableStateOf<List<String>?>(null) }
    var usersLoaded by remember { mutableStateOf(false) }
    val eventsLoading = events == null
    val usersLoading = !usersLoaded
    val rawData = remember(events, media, journeys, adminEmailsState) {
        buildDashboardData(events ?: emptyList(), media ?: emptyList(), journeys ?: emptyList(), adminEmailsState ?: emptyList())
    }
    var currentView by remember { mutableStateOf<DrillDownView>(slugToView(kotlinx.browser.window.location.hash)) }
    // Reflect the current screen in the URL hash so back/forward, refresh, and
    // bookmarking work instead of every screen showing /admin.
    LaunchedEffect(currentView) {
        val slug = viewToSlug(currentView)
        if (kotlinx.browser.window.location.hash.removePrefix("#") != slug) {
            kotlinx.browser.window.location.hash = slug
        }
    }
    // Keep state in sync when the user presses the browser back/forward buttons.
    LaunchedEffect(Unit) {
        kotlinx.browser.window.onhashchange = {
            currentView = slugToView(kotlinx.browser.window.location.hash)
            null
        }
    }
    var includeAdmins by remember {
        mutableStateOf(
            try { kotlinx.browser.localStorage.getItem("admin_include_admins") != "false" } catch (_: Exception) { true }
        )
    }
    // Registered users list — used for the From Good Child count on the summary card
    // and for the Good Child Signups Over Time chart. Loaded alongside dashboard data.
    var registeredUsers by remember { mutableStateOf<List<RegisteredUser>>(emptyList()) }
    val fromGoodChildCount = remember(registeredUsers) {
        registeredUsers.count { it.approvedSource == "zapier" }
    }
    // User-flagged problematic analyses — loaded alongside dashboard data.
    var flags by remember { mutableStateOf<List<FlagEntry>>(emptyList()) }
    // "bare title::media_type" → cover URL, for the title thumbnails in the dashboard lists.
    var coverByKey by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    // Refresh — fetches every source CONCURRENTLY; each updates its own state so the matching
    // dashboard section swaps from skeleton to real data the moment that source arrives.
    val refreshData: () -> Unit = {
        scope.launch {
            isRefreshing = true
            error = null
            kotlinx.coroutines.coroutineScope {
                // Main analytics — drives summary cards, charts, score distribution, all tables.
                launch {
                    try { events = analyticsService.fetchEvents() }
                    catch (e: Exception) { error = e.message ?: "Failed to load analytics"; events = emptyList() }
                }
                launch { adminEmailsState = analyticsService.fetchAdminEmails() }
                launch { media = analyticsService.fetchMedia() }
                launch { journeys = analyticsService.fetchJourneys() }
                // Registered users — From Good Child count + new-signups chart + avatars.
                launch {
                    try {
                        val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                        val headers = kotlin.js.json("Accept" to "application/json")
                        token?.let { headers["Authorization"] = "Bearer $it" }
                        val response = kotlinx.browser.window.fetch(
                            "$serverUrl/api/admin/users",
                            kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                        ).await()
                        if (response.ok) {
                            val text = response.text().await()
                            registeredUsers = adminJson.decodeFromString<List<RegisteredUser>>(text.toString())
                        }
                    } catch (_: Exception) { /* silent — card just shows 0 and chart stays empty */ }
                    usersLoaded = true
                }
                // User-flagged analyses (admin endpoint, service-role backed).
                launch {
                    try {
                        val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                        val headers = kotlin.js.json("Accept" to "application/json")
                        token?.let { headers["Authorization"] = "Bearer $it" }
                        val response = kotlinx.browser.window.fetch(
                            "$serverUrl/api/admin/flags",
                            kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                        ).await()
                        if (response.ok) {
                            val text = response.text().await()
                            flags = adminJson.decodeFromString<List<FlagEntry>>(text.toString())
                        }
                    } catch (_: Exception) { /* silent — card just shows 0 */ }
                }
                // Cover thumbnails — page through media_content (light columns).
                launch {
                    try {
                        val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                        val headers = kotlin.js.json("Accept" to "application/json")
                        token?.let { headers["Authorization"] = "Bearer $it" }
                        val map = HashMap<String, String>()
                        var off = 0; val pageSz = 200; var pages = 0
                        while (pages < 40) {
                            val resp = kotlinx.browser.window.fetch(
                                "$serverUrl/api/admin/covers?filter=all&limit=$pageSz&offset=$off",
                                kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                            ).await()
                            if (!resp.ok) break
                            val rows = adminJson.decodeFromString<List<CoverItem>>(resp.text().await().toString())
                            for (row in rows) {
                                val url = row.coverUrl
                                if (!url.isNullOrBlank()) {
                                    val key = "${row.title.trim().lowercase()}::${(row.mediaType ?: "").lowercase()}"
                                    if (!map.containsKey(key)) map[key] = url
                                }
                            }
                            pages++; off += pageSz
                            if (rows.size < pageSz) break
                        }
                        coverByKey = map
                    } catch (_: Exception) { /* thumbnails just won't render */ }
                }
            }
            isRefreshing = false
        }
    }

    // Signed in AND on the allowlist. "Signed in" alone used to be the whole gate, which was fine
    // while this page only ran on localhost — deployed, it would have rendered the full dashboard
    // shell to any account that could sign up. The server still enforces its own allowlist on every
    // /api/admin/* call (that is the real boundary); this stops a stranger seeing the shell at all.
    // FOLLOWS the auth state rather than sampling it once.
    //
    // The one-shot check this replaces ran the instant the page mounted, which is BEFORE
    // supabase-kt has finished restoring the session from storage (it logs "Loading session from
    // storage..." at that moment) and before an OAuth redirect has been exchanged. It therefore
    // read "not signed in" for a signed-in admin, showed the lock screen, and only let them in on
    // a second attempt — by which time the session had quietly finished loading. That is the
    // double-login bug: the first sign-in always worked, the page just asked before the answer
    // existed.
    //
    // Collecting sessionStatus instead means Initializing keeps isAuthenticated null (the loading
    // screen), and the gate resolves exactly once the session is genuinely known either way.
    LaunchedEffect(Unit) {
        com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.authState.collect { state ->
            when (state) {
                is com.islamicmedia.shared.data.auth.AuthState.Loading -> {
                    // Still resolving — hold on the loading screen rather than guessing "no".
                }
                is com.islamicmedia.shared.data.auth.AuthState.Authenticated ->
                    isAuthenticated = isAllowedAdminEmail(state.user.email)
                is com.islamicmedia.shared.data.auth.AuthState.NotAuthenticated ->
                    isAuthenticated = false
            }
        }
    }

    LaunchedEffect(isAuthenticated) {
        if (isAuthenticated != true) return@LaunchedEffect
        refreshData()
    }

    // Toggle off = "real, identified users only": drops admins/test accounts AND anonymous rows.
    // NOTE the gate is only the toggle — an empty allowlist used to short-circuit the whole block,
    // which would now also skip the anonymous filter.
    val data = remember(rawData, includeAdmins) {
        if (includeAdmins) {
            rawData
        } else {
            // Entries may be exact addresses OR "@domain" rules — see matchesAdminAllowlist.
            val admins = rawData.adminEmails
            fun isAdmin(email: String?) =
                com.islamicmedia.shared.data.cache.matchesAdminAllowlist(email, admins)
            // Unauthenticated searches carry no email (the server stamps ""), which is what renders
            // as "Anonymous (…)" everywhere. They're real usage but not a user, so they go with the
            // admins — flip the toggle back on to see them (and the true totals) again.
            fun isExcluded(email: String?) = email.isNullOrBlank() || isAdmin(email)

            val filteredAnalyses = rawData.allAnalyses.filter { !isExcluded(it.userEmail) }
            val filteredJourneys = rawData.userJourneys.filter { !isExcluded(it.userEmail) }

            // cacheHitRate comes from analysis_logs (system-wide, not per-user) — keep rawData's
            // real value rather than recomputing from the never-populated analytics cache_layer.
            val avgDuration = if (filteredAnalyses.isNotEmpty()) {
                filteredAnalyses.mapNotNull { it.durationSeconds }.map { it.toDouble() }.average()
                    .takeIf { !it.isNaN() } ?: 0.0
            } else 0.0

            val safe = filteredAnalyses.count { (it.score ?: 0) in 7..10 }
            val caution = filteredAnalyses.count { (it.score ?: 0) in 4..6 }
            val notSafe = filteredAnalyses.count { (it.score ?: 0) in 1..3 }

            val userGroups = filteredAnalyses.groupBy { it.userEmail?.takeIf { e -> e.isNotBlank() } ?: it.userId }
            val userSummaries = userGroups.map { (_, events) ->
                val scores = events.mapNotNull { it.score }
                UserSummary(
                    userId = events.firstOrNull()?.userId,
                    userEmail = events.firstOrNull()?.userEmail?.takeIf { it.isNotBlank() },
                    totalSearches = events.size,
                    lastSearchAt = events.maxByOrNull { it.createdAt ?: "" }?.createdAt,
                    avgScore = if (scores.isNotEmpty()) scores.average() else null
                )
            }.sortedByDescending { it.totalSearches }

            val typeGroups = filteredAnalyses.groupBy { it.mediaType ?: "unknown" }
            val mediaTypeSummaries = typeGroups.map { (type, events) ->
                val scores = events.mapNotNull { it.score }
                val islamicScores = events.mapNotNull { it.islamicScore }
                MediaTypeSummary(
                    mediaType = type,
                    totalSearches = events.size,
                    uniqueTitles = events.mapNotNull { it.title }.distinct().size,
                    avgScore = if (scores.isNotEmpty()) scores.average() else null,
                    avgIslamicScore = if (islamicScores.isNotEmpty()) islamicScores.average() else null
                )
            }.sortedByDescending { it.totalSearches }

            rawData.copy(
                totalAnalyses = filteredAnalyses.size,
                uniqueUsers = userGroups.keys.count { it != null },
                avgDuration = avgDuration,
                recentAnalyses = filteredAnalyses.sortedByDescending { it.createdAt ?: "" }.take(20),
                allAnalyses = filteredAnalyses,
                topMedia = topSearchedMedia(filteredAnalyses),  // recompute so the admin-filter toggle is respected
                userSummaries = userSummaries,
                mediaTypeSummaries = mediaTypeSummaries,
                scoreSafe = safe,
                scoreCaution = caution,
                scoreNotSafe = notSafe,
                userJourneys = filteredJourneys
            )
        }
    }

    // Lookups for the admin lists: display names + avatars by email, covers by title.
    val userNames = remember(registeredUsers) {
        registeredUsers.asSequence().filter { !it.fullName.isNullOrBlank() }
            .associate { it.email.lowercase() to it.fullName!!.trim() }
    }
    val userAvatars = remember(registeredUsers) {
        registeredUsers.asSequence().filter { !it.avatarUrl.isNullOrBlank() }
            .associate { it.email.lowercase() to it.avatarUrl!! }
    }
    CompositionLocalProvider(
        LocalUserNames provides userNames,
        LocalUserAvatars provides userAvatars,
        LocalCovers provides coverByKey,
    ) {
    // Only armed for a live session — no point watching for idleness on the lock screen, and it
    // must not fire while the user is mid-sign-in.
    if (isAuthenticated == true) AdminIdleLogout()
    // Rendered OUTSIDE the view `when` below, so it floats over every screen — the dashboard, any
    // drill-down, every management page. Someone new to this console shouldn't have to find their
    // way back to a particular page to ask a question.
    if (isAuthenticated == true) AskFab(currentView)

    when {
        isAuthenticated == null -> AdminLoadingScreen()
        isAuthenticated == false -> AccessDeniedView()
        error != null -> AdminErrorView(error!!) {
            scope.launch {
                isAuthenticated = analyticsService.isLoggedIn && isAllowedAdminEmail(currentUserEmail())
            }
        }
        else -> {
            when (val view = currentView) {
                is DrillDownView.Overview -> DashboardContent(data, includeAdmins, { includeAdmins = it; try { kotlinx.browser.localStorage.setItem("admin_include_admins", it.toString()) } catch (_: Exception) {} }, refreshData, isRefreshing, fromGoodChildCount, flags.count { !it.resolved }, registeredUsers, eventsLoading = eventsLoading, usersLoading = usersLoading) { currentView = it }
                is DrillDownView.AllAnalyses -> AllAnalysesView(data, refreshData) { currentView = it }
                is DrillDownView.AllMedia -> AllMediaView(data, refreshData) { currentView = it }
                is DrillDownView.AllUsers -> AllUsersView(data, refreshData) { currentView = it }
                is DrillDownView.FoundingMembers -> RegisteredUsersView(UserPage.FOUNDING) { currentView = it }
                is DrillDownView.RegularUsers -> RegisteredUsersView(UserPage.REGULAR) { currentView = it }
                is DrillDownView.CostAnalysis -> CostAnalysisView(data, refreshData) { currentView = it }
                is DrillDownView.UserJourneys -> UserJourneysView(data, refreshData) { currentView = it }
                is DrillDownView.FlaggedAnalyses -> FlaggedAnalysesView(flags, refreshData) { currentView = it }
                is DrillDownView.Engagement -> EngagementView(registeredUsers) { currentView = it }
                is DrillDownView.RegistrationFunnel -> RegistrationFunnelView { currentView = it }
                is DrillDownView.InfraCosts -> InfraCostsView { currentView = it }
                is DrillDownView.MrrDetail -> MrrDetailView { currentView = it }
                is DrillDownView.ResolveFlag -> {
                    val flag = flags.firstOrNull { it.id == view.flagId }
                    if (flag == null) FlaggedAnalysesView(flags, refreshData) { currentView = it }
                    else ResolveFlagView(flag, refreshData) { currentView = it }
                }
                is DrillDownView.AdminEmails -> AdminEmailsView { currentView = it }
                is DrillDownView.Broadcast -> BroadcastView { currentView = it }
                is DrillDownView.Newsletter -> NewsletterView { currentView = it }
                is DrillDownView.NewsletterSubscribers -> NewsletterSubscribersView { currentView = it }
                is DrillDownView.NewsletterCampaignDetail ->
                    NewsletterCampaignDetailView(view.campaignId) { currentView = it }
                is DrillDownView.GrantCredits -> GrantCreditsView { currentView = it }
                is DrillDownView.Invitations -> InvitationsView { currentView = it }
                is DrillDownView.Vouchers -> VouchersView { currentView = it }
                is DrillDownView.Referrals -> ReferralsView { currentView = it }
                is DrillDownView.Ask -> AskView { currentView = it }
                is DrillDownView.Pricing -> PricingView { currentView = it }
                is DrillDownView.Limits -> LimitsView { currentView = it }
                is DrillDownView.CancelFlow -> CancelFlowView { currentView = it }
                is DrillDownView.ManageCovers -> ManageCoversView { currentView = it }
                is DrillDownView.EditAnalyses -> EditAnalysesView(view.title, view.mediaType, view.year) { currentView = it }
                is DrillDownView.ReconcileDuplicates -> ReconcileDuplicatesView { currentView = it }
                is DrillDownView.Drip -> DripView { currentView = it }
                is DrillDownView.DripCampaignDetail -> DripCampaignDetailView(view.campaignId) { currentView = it }
                is DrillDownView.UserDetail -> UserDetailView(data, view, registeredUsers) { currentView = it }
                is DrillDownView.TitleDetail -> TitleDetailView(data, view, registeredUsers) { currentView = it }
                is DrillDownView.MediaTypeDetail -> MediaTypeDetailView(data, view) { currentView = it }
            }
        }
    }
    }
}

// ---------------------------------------------------------------------------
// Full-page states
// ---------------------------------------------------------------------------

@Composable
private fun AdminLoadingScreen() {
    Div(attrs = {
        style {
            width(100.percent)
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("background", GradientHeader)
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(16.px)
            }
        }) {
            Div(attrs = {
                style {
                    width(40.px)
                    height(40.px)
                    property("border", "3px solid ${Gray200}")
                    property("border-top-color", "#FFFFFF")
                    borderRadius(50.percent)
                    property("animation", "adminSpin 0.8s linear infinite")
                }
            })
            Span(attrs = {
                style {
                    color(Color.white)
                    fontSize(16.px)
                    fontWeight(500)
                }
            }) { Text("Loading analytics...") }
        }
    }
    AdminKeyframes()
}

/**
 * The only two accounts allowed into this console.
 *
 * A hardcoded pair, not a database lookup, on purpose: this list decides who can reach a dashboard
 * that administers PRODUCTION, so changing it should require a code change and a deploy rather than
 * a row someone could add. It is a convenience gate regardless — the server re-checks every
 * admin API call against its own allowlist, and that is the boundary that actually protects data.
 */
private val ADMIN_ALLOWED_EMAILS = setOf(
    "ysaber0@gmail.com",
    "emansedcorner@gmail.com",
)

private fun isAllowedAdminEmail(email: String?): Boolean =
    email?.trim()?.lowercase()?.let { it in ADMIN_ALLOWED_EMAILS } == true

/**
 * How long the console may sit idle before it signs itself out.
 *
 * This dashboard administers PRODUCTION from a URL anyone can reach, and it is the kind of tab that
 * gets left open on a laptop all day. Fifteen minutes is short enough that an unattended machine
 * isn't an open door, long enough to survive reading a report or taking a call.
 */
private const val ADMIN_IDLE_TIMEOUT_MS = 15 * 60 * 1000.0

/** Ends the session and returns to the lock screen. Reload rather than a state flip, so nothing
 *  already-fetched (user emails, revenue, journeys) is left rendered behind the sign-in card. */
private fun adminSignOut() {
    MainScope().launch {
        try {
            com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.signOut()
        } catch (_: Exception) { /* sign out locally regardless — never trap someone in the console */ }
        try { kotlinx.browser.window.location.href = "/admin" } catch (_: Exception) {}
    }
}

/**
 * Signs the admin out after [ADMIN_IDLE_TIMEOUT_MS] with no interaction.
 *
 * Activity is sampled by writing a timestamp on real input events, and a single interval checks it —
 * rather than resetting a timer on every mousemove, which would fire thousands of times a minute.
 * `visibilitychange` counts too, so returning to a backgrounded tab that idled past the limit signs
 * out immediately instead of silently extending the session.
 */
@Composable
private fun AdminIdleLogout() {
    DisposableEffect(Unit) {
        var lastActivity = kotlin.js.Date.now()
        val markActive: (org.w3c.dom.events.Event) -> Unit = { lastActivity = kotlin.js.Date.now() }
        val events = listOf("mousedown", "keydown", "scroll", "touchstart", "visibilitychange")
        events.forEach { kotlinx.browser.document.addEventListener(it, markActive) }

        val checkHandle = kotlinx.browser.window.setInterval({
            if (kotlin.js.Date.now() - lastActivity > ADMIN_IDLE_TIMEOUT_MS) {
                println("[ADMIN] idle for ${'$'}{ADMIN_IDLE_TIMEOUT_MS / 60000} min — signing out")
                adminSignOut()
            }
        }, 30_000)

        onDispose {
            events.forEach { kotlinx.browser.document.removeEventListener(it, markActive) }
            kotlinx.browser.window.clearInterval(checkHandle)
        }
    }
}

private fun currentUserEmail(): String? =
    try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.currentUser?.email }
    catch (_: Exception) { null }

@Composable
private fun AccessDeniedView() {
    val scope = rememberCoroutineScope()
    Div(attrs = {
        style {
            width(100.percent)
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("background", GradientHeader)
        }
    }) {
        Div(attrs = {
            style {
                property("background", "white")
                borderRadius(20.px)
                padding(48.px)
                property("box-shadow", CardElevatedShadow)
                property("text-align", "center")
                property("max-width", "420px")
            }
        }) {
            Div(attrs = {
                style { fontSize(48.px); property("margin-bottom", "16px") }
            }) { Text("\uD83D\uDD12") }
            H2(attrs = {
                style {
                    color(Gray900)
                    fontSize(22.px)
                    fontWeight(700)
                    property("margin", "0 0 8px")
                }
            }) { Text("Authentication Required") }
            P(attrs = {
                style {
                    color(Gray500)
                    fontSize(15.px)
                    property("line-height", "1.5")
                    property("margin", "0 0 24px")
                }
            }) { Text("This dashboard is restricted. Sign in with an authorised Google account.") }
            // Google is the ONLY option here on purpose: email/password would mean another
            // credential to manage and phish for a console that administers production, whereas
            // these two accounts already exist and carry their own 2FA.
            Div(attrs = {
                onClick {
                    // Return to /admin specifically. Supabase's default lands on the dashboard
                    // "Site URL", which would drop the admin back on the marketing page with the
                    // session applied to the wrong route.
                    scope.launch {
                        com.islamicmedia.shared.data.auth.SupabaseAuthService.instance
                            .signInWithGoogle(kotlinx.browser.window.location.origin + "/admin")
                    }
                }
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center); property("gap", "10px")
                    property("background", "white"); color(Gray900)
                    property("border", "1px solid #dadce0")
                    padding(12.px, 24.px); borderRadius(12.px)
                    fontWeight(600); fontSize(15.px); cursor("pointer")
                }
            }) {
                Img(src = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg", attrs = {
                    style { width(20.px); height(20.px) }
                })
                Text("Sign in with Google")
            }
            P(attrs = {
                style { color(Gray400); fontSize(12.px); property("margin", "16px 0 0") }
            }) { Text("Signed in with a different account? Sign out on the main site first.") }
        }
    }
}

@Composable
private fun AdminErrorView(message: String, onRetry: () -> Unit) {
    Div(attrs = {
        style {
            width(100.percent)
            property("min-height", "100vh")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("background", GradientHeader)
        }
    }) {
        Div(attrs = {
            style {
                property("background", "white")
                borderRadius(20.px)
                padding(48.px)
                property("box-shadow", CardElevatedShadow)
                property("text-align", "center")
                property("max-width", "480px")
            }
        }) {
            Div(attrs = {
                style { fontSize(48.px); property("margin-bottom", "16px") }
            }) { Text("\u26A0\uFE0F") }
            H2(attrs = {
                style { color(Gray900); fontSize(20.px); fontWeight(700); property("margin", "0 0 8px") }
            }) { Text("Error Loading Data") }
            P(attrs = {
                style { color(Gray500); fontSize(14.px); property("line-height", "1.5"); property("margin", "0 0 24px") }
            }) { Text(message) }
            Button(attrs = {
                onClick { onRetry() }
                style {
                    property("background", GradientPrimary)
                    color(Color.white)
                    property("border", "none")
                    padding(12.px, 28.px)
                    borderRadius(12.px)
                    fontWeight(600)
                    fontSize(15.px)
                    cursor("pointer")
                }
            }) { Text("Retry") }
        }
    }
}

// ---------------------------------------------------------------------------
// Page shell (shared by all views)
// ---------------------------------------------------------------------------

@Composable
private fun PageShell(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            width(100.percent)
            property("min-height", "100vh")
            property("background", GradientHeader)
            property("font-family", "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
        }
    }) {
        Div(attrs = {
            style {
                property("max-width", "1440px")
                property("margin", "0 auto")
                padding(32.px, 12.px, 0.px)
            }
        }) {
            content()
            Div(attrs = { style { height(48.px) } })
        }
    }
    AdminKeyframes()
}

// ---------------------------------------------------------------------------
// Main dashboard content (Overview)
// ---------------------------------------------------------------------------

@Composable
private fun DashboardContent(
    data: DashboardData,
    includeAdmins: Boolean,
    onToggleAdmins: (Boolean) -> Unit,
    onRefresh: () -> Unit,
    isRefreshing: Boolean = false,
    fromGoodChildCount: Int = 0,
    flaggedCount: Int = 0,
    registeredUsers: List<RegisteredUser> = emptyList(),
    eventsLoading: Boolean = false,
    usersLoading: Boolean = false,
    onNavigate: (DrillDownView) -> Unit
) {
    PageShell {
        // Header row
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                property("margin-bottom", "18px")
                property("flex-wrap", "wrap")
                gap(12.px)
            }
        }) {
            Div {
                H1(attrs = {
                    style {
                        color(Gray900)
                        fontSize(22.px)
                        fontWeight(800)
                        property("margin", "0")
                        property("letter-spacing", "-0.5px")
                    }
                }) { Text("Analytics Dashboard") }
                P(attrs = {
                    style {
                        color(Gray500)
                        fontSize(13.px)
                        property("margin", "2px 0 0")
                    }
                }) { Text("Fitrah Filter usage insights") }
            }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                // Include admins toggle
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        cursor("pointer")
                    }
                    onClick { onToggleAdmins(!includeAdmins) }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            color(Gray500)
                            fontWeight(500)
                        }
                    }) { Text("Include admins & anonymous") }
                    // Toggle switch
                    Div(attrs = {
                        style {
                            width(36.px)
                            height(20.px)
                            borderRadius(10.px)
                            backgroundColor(if (includeAdmins) Primary else Gray300)
                            property("position", "relative")
                            property("transition", TransitionMedium)
                        }
                    }) {
                        Div(attrs = {
                            style {
                                width(16.px)
                                height(16.px)
                                borderRadius(8.px)
                                backgroundColor(Color.white)
                                property("position", "absolute")
                                property("top", "2px")
                                property("left", if (includeAdmins) "18px" else "2px")
                                property("transition", TransitionMedium)
                                property("box-shadow", "0 1px 3px rgba(0,0,0,0.2)")
                            }
                        })
                    }
                }

                // Refresh button
                Span(attrs = {
                    style {
                        cursor(if (isRefreshing) "default" else "pointer")
                        fontSize(18.px)
                        color(if (isRefreshing) Primary else Gray400)
                        padding(4.px, 8.px)
                        borderRadius(6.px)
                        property("transition", TransitionMedium)
                        if (isRefreshing) {
                            property("animation", "spin 1s linear infinite")
                        }
                    }
                    if (!isRefreshing) onClick { onRefresh() }
                    attr("title", if (isRefreshing) "Refreshing..." else "Refresh all data")
                }) { Text("\u21BB") }

                // Inject spin keyframes once
                InjectSpinKeyframes()

                // Logout rather than "Back to App": this console administers PRODUCTION from a
                // publicly-reachable URL, so ending the session has to be one obvious click —
                // navigating away used to leave the session live in the browser.
                Span(attrs = {
                    onClick { adminSignOut() }
                    style {
                        color(ScoreRed)
                        fontSize(14.px)
                        fontWeight(600)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                        cursor("pointer")
                        property("transition", TransitionMedium)
                    }
                    attr("title", "Sign out of the admin console")
                }) {
                    Span { Text("\u23FB") }
                    Text("Log out")
                }
            }
        }

        // Each section shows its own skeleton until its data source arrives, then swaps to real
        // content — so the dashboard streams in section-by-section instead of all at once.
        // Most sections come from the analytics-events fetch; the signups chart from the users fetch.
        if (eventsLoading) SummaryRowSkeleton() else SummaryRow(data, fromGoodChildCount, registeredUsers.count { it.isRegularSignup }, flaggedCount, onNavigate)
        if (eventsLoading) ChartSkeleton() else AnalysesOverTimeChart(data)
        if (eventsLoading) ChartSkeleton() else CacheHitRateChart(data)
        if (usersLoading) ChartSkeleton() else NewSignupsOverTimeChart(registeredUsers)
        if (eventsLoading) ChartSkeleton() else ScoreDistributionCard(data)
        if (eventsLoading) TableSkeleton() else TopUsersTable(data.userSummaries, onNavigate = onNavigate)
        if (eventsLoading) TableSkeleton() else RecentAnalysesTable(data.recentAnalyses, onNavigate = onNavigate)
        if (eventsLoading) TableSkeleton() else TopMediaTable(data.topMedia, onNavigate = onNavigate)
    }
}

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

@Composable
private fun SummaryRow(data: DashboardData, fromGoodChildCount: Int, regularUsersCount: Int, flaggedCount: Int, onNavigate: (DrillDownView) -> Unit) {
    val overallCost = data.allAnalyses.sumOf { it.costUsd ?: 0.0 }

    // Cards are grouped into labelled sections, ordered glance-first → destructive-last: daily
    // health numbers, then who's using it, then things to review, then mutating tools. Each
    // section wraps its own responsive grid (see SummarySection) so a short group doesn't strand.
    SummarySection("Key Metrics") {
        SummaryCard("Total Analyses", data.totalAnalyses.toString(), Primary) {
            onNavigate(DrillDownView.AllAnalyses)
        }
        SummaryCard("Unique Media", data.uniqueTitles.toString(), Accent) {
            onNavigate(DrillDownView.AllMedia)
        }
        SummaryCard("Cache Hit Rate", "${(data.cacheHitRate * 100).toInt()}%", ScoreGreen)
        SummaryCard("Avg Duration", formatDuration(data.avgDuration), Gray600)
    }

    FinancesSection(overallCost, fromGoodChildCount, onNavigate)

    SummarySection("Users") {
        SummaryCard("Unique Users", data.uniqueUsers.toString(), PrimaryLight) {
            onNavigate(DrillDownView.AllUsers)
        }
        SummaryCard("Regular Users", regularUsersCount.toString(), Color("#0E7C7B")) {
            onNavigate(DrillDownView.RegularUsers)
        }
        SummaryCard("Founding Members", fromGoodChildCount.toString(), Color("#7c3aed")) {
            onNavigate(DrillDownView.FoundingMembers)
        }
        SummaryCard("Admin Emails", data.adminEmails.size.toString(), Color("#64748b")) {
            onNavigate(DrillDownView.AdminEmails)
        }
    }

    SummarySection("Review & Moderation") {
        SummaryCard("Flagged Analyses", flaggedCount.toString(), Color("#dc2626")) {
            onNavigate(DrillDownView.FlaggedAnalyses)
        }
        // Counts live on their own page — this card is a doorway, not a metric, so it shows "→"
        // rather than forcing the dashboard load to wait on the engagement query.
        SummaryCard("Likes & Comments", "→", Color("#0891b2")) {
            onNavigate(DrillDownView.Engagement)
        }
        SummaryCard("User Journeys", data.userJourneys.size.toString(), Color("#6366f1")) {
            onNavigate(DrillDownView.UserJourneys)
        }
        // Its own funnel_events pipeline, not analytics_events — so it loads on its own page
        // rather than blocking the dashboard query. "→" for the same reason as Likes & Comments.
        SummaryCard("Registration Funnel", "→", Color("#b45309")) {
            onNavigate(DrillDownView.RegistrationFunnel)
        }
    }

    SummarySection("Actions") {
        SummaryCard("Broadcast", "→", Color("#dc2626")) {
            onNavigate(DrillDownView.Broadcast)
        }
        SummaryCard("Newsletter", "→", Color("#0E7C7B")) {
            onNavigate(DrillDownView.Newsletter)
        }
        SummaryCard("Drip Campaigns", "→", Color("#7c3aed")) {
            onNavigate(DrillDownView.Drip)
        }
        // Label says "Access" because that's the current model — the credit half of this screen is
        // legacy, from when usage was counted per search. The slug stays "grant-credits" so any
        // existing /admin#grant-credits bookmark keeps working.
        SummaryCard("Grant Access", "→", Color("#0E7C7B")) {
            onNavigate(DrillDownView.GrantCredits)
        }
        SummaryCard("Invitations", "→", Color("#0f766e")) {
            onNavigate(DrillDownView.Invitations)
        }
        SummaryCard("Vouchers", "→", Color("#b45309")) {
            onNavigate(DrillDownView.Vouchers)
        }
        SummaryCard("Referrals", "→", Color("#15803d")) {
            onNavigate(DrillDownView.Referrals)
        }
        SummaryCard("Pricing", "→", Color("#0E7C7B")) {
            onNavigate(DrillDownView.Pricing)
        }
        SummaryCard("Limits", "→", Color("#b45309")) {
            onNavigate(DrillDownView.Limits)
        }
        SummaryCard("Cancel Flow", "→", Color("#475569")) {
            onNavigate(DrillDownView.CancelFlow)
        }
        SummaryCard("Manage Covers", "→", Color("#9333ea")) {
            onNavigate(DrillDownView.ManageCovers)
        }
        SummaryCard("Edit Analyses", "→", Color("#0891b2")) {
            onNavigate(DrillDownView.EditAnalyses())
        }
        SummaryCard("Reconcile Duplicates", "→", Color("#db2777")) {
            onNavigate(DrillDownView.ReconcileDuplicates)
        }
    }
}

/** A labelled dashboard section: an uppercase header above a responsive grid of cards that
 *  wraps to fit, so a short group (e.g. "Review & Moderation") doesn't look stranded. */
@Composable
private fun SummarySection(title: String, content: @Composable () -> Unit) {
    Div(attrs = { style { property("margin-bottom", "14px") } }) {
        H3(attrs = {
            style {
                fontSize(11.px)
                fontWeight(700)
                color(Gray500)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.06em")
                property("margin", "0 0 6px 2px")
            }
        }) { Text(title) }
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                // Narrower tracks than the old 190px: without the icon a card only has to fit its
                // label and number, so more fit per row and the four sections take far less height.
                property("grid-template-columns", "repeat(auto-fill, minmax(140px, 1fr))")
                gap(8.px)
            }
        }) { content() }
    }
}

/** What each Good Child founding member paid. Never went through Stripe, so it can't be measured —
 *  it's a flat per-head figure set by the founding-member deal. */
private const val FOUNDING_MEMBER_PRICE = 110.0

/**
 * Whole-dollar money for the Finances cards, with the sign OUTSIDE the currency symbol ("−$12.34",
 * not "$-12.34").
 *
 * Not [formatCost]: that one is tuned for sub-cent API spend and takes a raw substring below $0.01,
 * which mangles a negative profit (-1234.5678 → "-1234.5"). Profit is routinely negative early on,
 * so it needs a formatter that rounds properly on both sides of zero.
 */
private fun formatMoney(value: Double): String {
    val body = formatDouble(kotlin.math.abs(value), 2)
    return if (value < 0) "−$$body" else "$$body"
}

/**
 * The dirham's peg to the US dollar.
 *
 * NOT a stale constant waiting to be replaced with a rates API. The UAE has pegged the dirham to
 * the dollar at exactly this rate since 1997, and a fixed peg is not a market rate: calling a
 * currency service here would add a network dependency, a failure mode and a loading state to
 * arrive back at this same number.
 */
private const val AED_PER_USD = 3.6725

/**
 * Money for a headline figure: no trailing ".00", and the currency named.
 *
 * The cents are dropped only when they are zero. "$9 USD" is worth the space it saves on a stat
 * tile; rounding $37.42 to $37 would not be formatting, it would be losing the number.
 */
private fun formatMoneyShort(value: Double, currency: String = "USD"): String {
    val abs = kotlin.math.abs(value)
    val whole = kotlin.math.round(abs * 100) % 100L.toDouble() == 0.0
    val body = formatDouble(abs, if (whole) 0 else 2)
    val sign = if (value < 0) "−" else ""
    return if (currency == "USD") "$sign$$body USD" else "$sign$currency $body"
}

/** The same figure in dirhams, for the line under a USD headline. */
private fun formatAed(valueUsd: Double): String =
    formatMoneyShort(valueUsd * AED_PER_USD, "AED")

/** One recorded Fly/Supabase invoice. Mirrors the server's InfraCostRow. */
@Serializable
private data class InfraCostRow(
    val id: Long? = null,
    val provider: String = "",
    @SerialName("period_start") val periodStart: String = "",
    @SerialName("period_end") val periodEnd: String? = null,
    @SerialName("amount_usd") val amountUsd: Double = 0.0,
    @SerialName("invoice_ref") val invoiceRef: String? = null,
    val note: String? = null
)

@Serializable
private data class InfraCostsData(
    val rows: List<InfraCostRow> = emptyList(),
    val total: Double = 0.0,
    val byProvider: Map<String, Double> = emptyMap(),
    @SerialName("latest_period") val latestPeriod: String? = null
)

/**
 * GET /api/admin/mrr — monthly recurring revenue across BOTH Stripe accounts.
 *
 * [complete] is the field that matters: the business bills through two separate Stripe accounts,
 * so a total assembled from only one of them is not the MRR and must not be shown as though it
 * were. When it is false the card marks the figure rather than printing a confident, wrong one.
 */
@Serializable
private data class MrrSubscriber(
    val email: String? = null,
    val account: String = "",
    val monthly: Double = 0.0,
    val amount: Double = 0.0,
    val interval: String = "month",
    val plan: String? = null,
    val since: String? = null,
    val renews: String? = null,
    // Scheduled to cancel. Stripe still reports it `active` until the paid period ends, so it
    // will not bill again even though it looks live. Defaulted so an older server that doesn't
    // send this field still decodes.
    val canceling: Boolean = false,
    // "active" | "cancelling" | "canceled". Defaulted to "active" for the same backward-compat
    // reason as [canceling] above.
    val status: String = "active",
    // The discount in force, already formatted as words ("$20 off forever"). Null when none.
    val discount: String? = null
)

@Serializable
private data class MrrData(
    val main: Double = 0.0,
    val mainSubs: Int = 0,
    val mainAvailable: Boolean = false,
    val thriveCart: Double = 0.0,
    val thriveCartSubs: Int = 0,
    val thriveCartAvailable: Boolean = false,
    val total: Double = 0.0,
    val totalSubs: Int = 0,
    val complete: Boolean = false,
    // Monthly value of subscriptions already scheduled to cancel, across both accounts. NOT part
    // of [total] — Stripe reports these `active` until the paid period ends, so they have to be
    // excluded explicitly or they inflate a forward-looking number. Defaulted for the same reason
    // as [MrrSubscriber.canceling].
    val canceling: Double = 0.0,
    val cancelingSubs: Int = 0,
    val subscribers: List<MrrSubscriber> = emptyList(),
    val error: String? = null
)

/** GET /api/admin/revenue — Stripe's settled ledger. Mirrors the server's AdminRevenueResponse. */
@Serializable
private data class RevenueData(
    val gross: Double = 0.0,
    val fees: Double = 0.0,
    val net: Double = 0.0,
    val charges: Int = 0,
    val refunds: Int = 0,
    val refunded: Double = 0.0,
    val available: Boolean = false,
    val error: String? = null
)

/**
 * Money in, money out.
 *
 * Subscription revenue is read from **Stripe's balance-transactions ledger**, not inferred from
 * each subscriber's plan. That distinction is the whole point: a plan-price estimate freezes at one
 * period, so a monthly subscriber's second $29 would never appear. The ledger records every
 * settled payment separately, so the figure grows on its own with each renewal and refunds subtract
 * themselves.
 *
 * Founding members are added on top as a flat [FOUNDING_MEMBER_PRICE] per head — they came from
 * Good Child via Zapier and never paid through Stripe, so no double-count is possible.
 *
 * Fetches independently of the dashboard's bulk refresh so a slow Stripe call can't hold up the
 * Overview.
 */
@Composable
private fun FinancesSection(apiCost: Double, foundingMembers: Int, onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    var revenue by remember { mutableStateOf<RevenueData?>(null) }
    var mrr by remember { mutableStateOf<MrrData?>(null) }
    var infra by remember { mutableStateOf<InfraCostsData?>(null) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        scope.launch {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
            try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
                catch (_: Exception) { null }
                ?.let { headers["Authorization"] = "Bearer $it" }
            val init = kotlin.js.json("method" to "GET", "headers" to headers)
                .unsafeCast<org.w3c.fetch.RequestInit>()

            try {
                val resp = kotlinx.browser.window.fetch("$serverUrl/api/admin/revenue", init).await()
                val text = resp.text().await().toString()
                revenue = when {
                    // PROD serves the exported site, so an undeployed /api/* returns index.html
                    // with a 200. Treat that as "unavailable", never as $0.
                    resp.ok && text.trimStart().startsWith("<") ->
                        RevenueData(available = false, error = "Server needs deploying (no /api/admin/revenue yet)")
                    resp.ok -> adminJson.decodeFromString<RevenueData>(text)
                    else -> RevenueData(available = false, error = "Stripe lookup failed (${resp.status})")
                }
            } catch (e: Exception) {
                revenue = RevenueData(available = false, error = e.message ?: "Stripe lookup failed")
            }

            // Own try/catch for the same reason as infra below — an MRR failure (one of the two
            // Stripe accounts down) must not blank out the collected-revenue figures.
            try {
                val resp = kotlinx.browser.window.fetch("$serverUrl/api/admin/mrr", init).await()
                val text = resp.text().await().toString()
                if (resp.ok && !text.trimStart().startsWith("<")) {
                    mrr = adminJson.decodeFromString<MrrData>(text)
                } else if (resp.ok) {
                    mrr = MrrData(error = "Server needs deploying (no /api/admin/mrr yet)")
                } else {
                    mrr = MrrData(error = "MRR lookup failed (${resp.status})")
                }
            } catch (e: Exception) {
                mrr = MrrData(error = e.message ?: "MRR lookup failed")
            }

            // Separate try: a missing infra_costs table must not also blank out the Stripe half.
            try {
                val resp = kotlinx.browser.window.fetch("$serverUrl/api/admin/infra-costs", init).await()
                val text = resp.text().await().toString()
                if (resp.ok && !text.trimStart().startsWith("<")) {
                    infra = adminJson.decodeFromString<InfraCostsData>(text)
                }
            } catch (_: Exception) { /* leaves infra null → shown as unrecorded, not as $0 */ }

            loading = false
        }
    }

    val foundingRevenue = foundingMembers * FOUNDING_MEMBER_PRICE
    val r = revenue
    val stripeOk = r?.available == true
    val infraTotal = infra?.total ?: 0.0
    // Founding revenue is known regardless of Stripe, so it's always part of the total; the
    // subscription half is simply absent until Stripe answers.
    val totalSales = foundingRevenue + (if (stripeOk) r.gross else 0.0)
    // Profit nets off every real cost: Stripe's processing fee, the LLM/research API spend, and
    // the hand-entered Fly + Supabase invoices.
    val totalProfit = totalSales - (if (stripeOk) r.fees else 0.0) - apiCost - infraTotal

    val m = mrr
    SummarySection("Finances") {
        // Placed first: it is the one number that says how the business is doing right now, as
        // opposed to what it has collected historically.
        SummaryCard(
            "MRR",
            when {
                loading -> "…"
                m != null && m.complete -> formatMoneyShort(m.total)
                // A sum missing one of the two accounts is not the MRR. Showing it plain would
                // read as the real figure and quietly understate the business.
                m != null && (m.mainAvailable || m.thriveCartAvailable) -> formatMoneyShort(m.total) + "*"
                else -> "n/a"
            },
            Color("#0891b2"),
            onClick = { onNavigate(DrillDownView.MrrDetail) },
            // The same figure in the currency the business actually banks in.
            secondary = if (!loading && m != null && (m.mainAvailable || m.thriveCartAvailable))
                formatAed(m.total) else null,
            // Revenue on its way OUT. It has to be visible next to the figure it was excluded
            // from, or the headline looks healthier than it is.
            subtitle = if (!loading && m != null && m.cancelingSubs > 0)
                "${formatMoneyShort(m.canceling)}/mo cancelling" else null,
        )
        SummaryCard(
            "Total Sales",
            if (loading) "…" else formatMoney(totalSales),
            ScoreGreen
        )
        SummaryCard(
            "Total Profit",
            if (loading) "…" else formatMoney(totalProfit),
            if (totalProfit >= 0) ScoreGreen else ScoreRed
        )
        // Renamed from "Total Cost": this is per-analysis Claude + Perplexity spend only, and
        // calling it "total" next to a profit figure implied it covered infrastructure too.
        SummaryCard("API Cost", formatMoney(apiCost), Color("#b45309")) {
            onNavigate(DrillDownView.CostAnalysis)
        }
        SummaryCard(
            "Infra Cost",
            when {
                loading -> "…"
                infra != null -> formatMoney(infraTotal)
                else -> "n/a"
            },
            Color("#b45309")
        ) { onNavigate(DrillDownView.InfraCosts) }
        SummaryCard(
            "Founding Revenue",
            formatMoney(foundingRevenue),
            Color("#7c3aed")
        ) { onNavigate(DrillDownView.FoundingMembers) }
        SummaryCard(
            "Subscriptions",
            when {
                loading -> "…"
                stripeOk -> formatMoney(r.gross)
                else -> "n/a"
            },
            PrimaryLight
        )
        SummaryCard(
            "Stripe Fees",
            when {
                loading -> "…"
                stripeOk -> formatMoney(r.fees)
                else -> "n/a"
            },
            Gray600
        )
    }

    // Spell out exactly how the two headline numbers were derived. These are money figures — a
    // reader must be able to audit them without opening the source.
    Div(attrs = {
        style {
            fontSize(11.px); color(Gray500); property("margin", "-8px 0 16px 2px"); property("line-height", "1.5")
        }
    }) {
        Div {
            Text("Sales = $foundingMembers founding × ${formatMoney(FOUNDING_MEMBER_PRICE)}")
            if (stripeOk) Text(" + ${r.charges} Stripe payment${if (r.charges == 1) "" else "s"}")
            Text(" · Profit = Sales − Stripe fees − ${formatMoney(apiCost)} API − ${formatMoney(infraTotal)} infra")
        }
        // The two-account split, always shown. The whole point of this figure is that it spans
        // both Stripe accounts, so the reader needs to see both halves landed — a $0 ThriveCart
        // line is meaningful information, not noise.
        if (!loading && m != null && (m.mainAvailable || m.thriveCartAvailable)) {
            Div(attrs = { style { property("margin-top", "2px") } }) {
                Text(
                    "MRR = in-app " +
                        (if (m.mainAvailable) "${formatMoney(m.main)} (${m.mainSubs} sub${if (m.mainSubs == 1) "" else "s"})" else "unavailable") +
                        " + ThriveCart " +
                        (if (m.thriveCartAvailable) "${formatMoney(m.thriveCart)} (${m.thriveCartSubs} sub${if (m.thriveCartSubs == 1) "" else "s"})" else "unavailable") +
                        " · active subscriptions only, annual plans normalised to a month"
                )
            }
        }
        if (!loading && m != null && !m.complete) {
            Div(attrs = { style { color(Color("#b45309")); property("margin-top", "2px") } }) {
                Text(
                    if (m.mainAvailable || m.thriveCartAvailable)
                        "* MRR is PARTIAL — ${m.error ?: "one Stripe account did not answer"}. The real figure is higher."
                    else
                        "MRR unavailable — ${m.error ?: "neither Stripe account answered"}."
                )
            }
        }
        infra?.byProvider?.takeIf { it.isNotEmpty() }?.let { by ->
            Div(attrs = { style { property("margin-top", "2px") } }) {
                Text("Infra = " + by.entries.sortedByDescending { it.value }
                    .joinToString(" + ") { "${it.key} ${formatMoney(it.value)}" } +
                    " (invoiced through ${infra?.latestPeriod?.take(7) ?: "—"})")
            }
        }
        if (!loading && !stripeOk) {
            Div(attrs = { style { color(Color("#b45309")); property("margin-top", "2px") } }) {
                Text("Subscription revenue unavailable — ${r?.error ?: "Stripe unreachable"}. Founding revenue only.")
            }
        }
        if (!loading && infra == null) {
            Div(attrs = { style { color(Color("#b45309")); property("margin-top", "2px") } }) {
                Text("Infrastructure costs unrecorded — run the infra_costs migration and deploy. Profit excludes Fly + Supabase.")
            }
        }
        if (stripeOk && r.refunds > 0) {
            Div(attrs = { style { property("margin-top", "2px") } }) {
                Text("Includes ${r.refunds} refund${if (r.refunds == 1) "" else "s"} (−${formatMoney(r.refunded)}), already netted off.")
            }
        }
    }
}

/**
 * One metric tile. No icon slot by design: 50-odd cards each carrying a decorative emoji made the
 * top of the dashboard read as a sticker sheet and pushed the actual numbers below the fold. The
 * label carries the meaning; [accentColor] carries the grouping.
 */
@Composable
private fun SummaryCard(
    label: String,
    value: String,
    accentColor: CSSColorValue,
    // Neutral second line under the headline figure, for the SAME figure restated (e.g. the
    // dirham equivalent). Distinct from [subtitle], which is amber because it is a caveat: a
    // currency conversion rendered in a warning colour reads as a problem.
    secondary: String? = null,
    // Small line under the headline figure, for a caveat that belongs on the tile itself
    // rather than buried in a footnote (e.g. "$12/mo cancelling"). Null renders nothing.
    subtitle: String? = null,
    // Kept as the LAST parameter (not [subtitle]) so every existing trailing-lambda call site
    // (`SummaryCard(...) { onNavigate(...) }`) keeps binding its lambda to onClick.
    onClick: (() -> Unit)? = null,
) {
    Div(attrs = {
        if (onClick != null) {
            onClick { onClick() }
        }
        style {
            property("background", "white")
            borderRadius(12.px)
            padding(12.px, 14.px)
            property("box-shadow", CardShadow)
            property("border", CardBorder)
            property("transition", TransitionMedium)
            if (onClick != null) {
                cursor("pointer")
            }
        }
    }) {
        Span(attrs = {
            style {
                fontSize(11.px)
                fontWeight(600)
                color(Gray500)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.5px")
                // Wrap to at most two lines rather than truncating — "Registration Fu…" told the
                // reader nothing. min-height reserves both lines even for one-line labels, so a
                // row of cards keeps its numbers on a single baseline instead of stair-stepping.
                property("line-height", "1.3")
                property("min-height", "2.6em")
                property("display", "-webkit-box")
                property("-webkit-line-clamp", "2")
                property("-webkit-box-orient", "vertical")
                property("overflow", "hidden")
            }
        }) { Text(label) }
        Div(attrs = {
            style {
                fontSize(22.px)
                fontWeight(800)
                color(accentColor)
                property("letter-spacing", "-0.5px")
                property("line-height", "1.15")
                property("margin-top", "2px")
            }
        }) { Text(value) }
        if (secondary != null) {
            Div(attrs = {
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(Gray500)
                    property("margin-top", "1px")
                }
            }) { Text(secondary) }
        }
        if (subtitle != null) {
            Div(attrs = {
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(Color("#b45309"))
                    property("margin-top", "2px")
                }
            }) { Text(subtitle) }
        }
    }
}

// ---------------------------------------------------------------------------
// Analyses over time chart
// ---------------------------------------------------------------------------

private enum class DateRange(val label: String, val days: Int) {
    TODAY("Today", 0),
    THIS_WEEK("This Week", 7),
    LAST_WEEK("Last Week", -7),
    THIS_MONTH("This Month", 30),
    LAST_MONTH("Last Month", -30),
    ALL_TIME("All Time", -1),
    CUSTOM("Custom", -2)
}

/** Format a JS Date as yyyy-MM-dd for date input values */
private fun toInputDate(d: kotlin.js.Date): String {
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

/** Parse yyyy-MM-dd to JS Date (start of day) */
private fun fromInputDate(s: String): kotlin.js.Date? {
    if (s.isBlank()) return null
    return try {
        val parts = s.split("-")
        if (parts.size != 3) return null
        kotlin.js.Date(parts[0].toInt(), parts[1].toInt() - 1, parts[2].toInt())
    } catch (_: Exception) { null }
}

/** Parse ISO date string to JS Date. Returns null on failure. */
private fun parseIsoToDate(iso: String?): kotlin.js.Date? {
    if (iso.isNullOrBlank()) return null
    return try {
        val d = kotlin.js.Date(iso)
        if (d.getTime().isNaN()) null else d
    } catch (_: Exception) { null }
}

/** Format JS Date as "MMM d" (e.g. "Apr 14") using local time. */
private fun shortDateLabel(d: kotlin.js.Date): String {
    val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    return "${months[d.getMonth()]} ${d.getDate()}"
}

/** Format JS Date as "MMM d" (e.g. "Apr 14") using UTC. */
private fun shortUtcDateLabel(d: kotlin.js.Date): String {
    val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    return "${months[d.getUTCMonth()]} ${d.getUTCDate()}"
}

/** Format JS Date as "MMM YYYY" (e.g. "Apr 2026") using UTC. */
private fun shortUtcMonthLabel(d: kotlin.js.Date): String {
    val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    return "${months[d.getUTCMonth()]} ${d.getUTCFullYear()}"
}

/** Format JS Date as "HH:mm" for today view */
private fun shortTimeLabel(d: kotlin.js.Date): String {
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    return "$h:$m"
}

@Composable
private fun AnalysesOverTimeChart(data: DashboardData, onRefresh: (() -> Unit)? = null) {
    TimeSeriesChart(
        title = "Analyses Over Time",
        items = data.allAnalyses,
        timestampOf = { it.createdAt },
        unitLabel = "analyses",
        strokeColor = "rgb(14,124,123)",
        aggregate = { it.size },
        onRefresh = onRefresh
    )
}

@Composable
private fun CacheHitRateChart(data: DashboardData, onRefresh: (() -> Unit)? = null) {
    // Same cache-hit definition as the "Cache Hit Rate" stat card (see buildDashboardData):
    // the FIRST analysis of each distinct title::mediaType is a fresh computation (miss);
    // every later repeat is a cache hit. We flag each event chronologically (oldest first),
    // then bucket by time and plot hits / total per bucket as a percentage. Early in the
    // product's life almost everything is a miss (low rate); the rate climbs as the catalog fills.
    val flagged: List<Pair<AdminEventRow, Boolean>> = remember(data.allAnalyses) {
        val seen = HashSet<String>()
        data.allAnalyses
            .sortedBy { it.createdAt ?: "" } // oldest first so "first occurrence" is the earliest
            .map { row ->
                val key = "${row.title ?: ""}::${row.mediaType ?: ""}"
                val isHit = !seen.add(key) // add() returns false if already present → repeat → hit
                row to isHit
            }
    }
    TimeSeriesChart(
        title = "Cache Hit Rate Over Time",
        items = flagged,
        timestampOf = { it.first.createdAt },
        unitLabel = "hit rate",
        strokeColor = "rgb(168,85,247)", // purple — distinct from Analyses (teal) & Signups (emerald)
        // Cumulative: each point is the hit rate over ALL analyses from day 1 up to that day —
        // a smooth running trend, not a noisy per-day rate.
        aggregate = { upToNow ->
            if (upToNow.isEmpty()) 0 else (100 * upToNow.count { it.second } / upToNow.size)
        },
        yMax = 100,        // cap (rate can't exceed 100%)
        yHeadroom = 5,     // zoom: axis tops out at the peak rate + 5% so the growth is easy to read
        valueSuffix = "%",
        cumulative = true,
        monthlyForAllTime = true, // All Time → one cumulative point per month
        defaultRange = DateRange.ALL_TIME,
        onRefresh = onRefresh
    )
}

@Composable
private fun NewSignupsOverTimeChart(users: List<RegisteredUser>, onRefresh: (() -> Unit)? = null) {
    // New in-app signup flow (Supabase email + Google/Apple OAuth) — everyone who self-registered:
    // has an auth account (createdAt set) and is NOT the legacy "From Good Child" / Zapier cohort.
    // Bucket by auth account creation time, so recent buckets reflect the new flow.
    val newSignups = remember(users) {
        users.filter { it.createdAt.isNotBlank() && it.approvedSource != "zapier" }
    }
    TimeSeriesChart(
        title = "New Signups Over Time",
        items = newSignups,
        timestampOf = { it.createdAt.ifBlank { null } },
        unitLabel = "signups",
        strokeColor = "rgb(16,185,129)", // emerald — distinct from Analyses (teal)
        aggregate = { it.size },
        onRefresh = onRefresh
    )
}

@Composable
private fun <T> TimeSeriesChart(
    title: String,
    items: List<T>,
    timestampOf: (T) -> String?,
    unitLabel: String,
    strokeColor: String,
    aggregate: (List<T>) -> Int,
    yMax: Int? = null,          // hard cap for the y-axis ceiling (e.g. 100 for a percentage chart); null = no cap
    yHeadroom: Int? = null,     // when set, axis scales to (max data value + this), capped by yMax — zooms a low-range plot
    valueSuffix: String = "",   // appended to axis + header values (e.g. "%")
    cumulative: Boolean = false, // each point aggregates ALL items from day 1 up to that bucket's end (running trend), not just the bucket
    monthlyForAllTime: Boolean = false, // in the ALL_TIME range, bucket by month instead of by day
    defaultRange: DateRange = DateRange.THIS_MONTH,
    onRefresh: (() -> Unit)? = null
) {
    // Each chart keeps its own selectedRange state so users can filter the two
    // charts independently.
    var selectedRange by remember(title) { mutableStateOf(defaultRange) }

    val now = kotlin.js.Date()
    val todayStart = kotlin.js.Date(now.getFullYear(), now.getMonth(), now.getDate())

    // Custom date range state
    val defaultCustomStart = kotlin.js.Date(now.getFullYear(), now.getMonth(), now.getDate() - 14)
    var customStartStr by remember(title) { mutableStateOf(toInputDate(defaultCustomStart)) }
    var customEndStr by remember(title) { mutableStateOf(toInputDate(now)) }

    // Compute date boundaries
    val (rangeStart, rangeEnd) = when (selectedRange) {
        DateRange.TODAY -> todayStart to now
        DateRange.THIS_WEEK -> {
            val dayOfWeek = todayStart.getDay() // 0=Sun
            val startOfWeek = kotlin.js.Date(todayStart.getTime() - dayOfWeek * 86400000.0)
            startOfWeek to now
        }
        DateRange.LAST_WEEK -> {
            val dayOfWeek = todayStart.getDay()
            val endOfLastWeek = kotlin.js.Date(todayStart.getTime() - dayOfWeek * 86400000.0)
            val startOfLastWeek = kotlin.js.Date(endOfLastWeek.getTime() - 7 * 86400000.0)
            startOfLastWeek to endOfLastWeek
        }
        DateRange.THIS_MONTH -> {
            val startOfMonth = kotlin.js.Date(now.getFullYear(), now.getMonth(), 1)
            startOfMonth to now
        }
        DateRange.LAST_MONTH -> {
            val startOfLastMonth = kotlin.js.Date(now.getFullYear(), now.getMonth() - 1, 1)
            val endOfLastMonth = kotlin.js.Date(now.getFullYear(), now.getMonth(), 0)
            startOfLastMonth to endOfLastMonth
        }
        DateRange.ALL_TIME -> {
            val earliest = items.mapNotNull { parseIsoToDate(timestampOf(it)) }.minByOrNull { it.getTime() }
            (earliest ?: todayStart) to now
        }
        DateRange.CUSTOM -> {
            val s = fromInputDate(customStartStr) ?: defaultCustomStart
            val e = fromInputDate(customEndStr)?.let {
                // End of that day (23:59:59)
                kotlin.js.Date(it.getFullYear(), it.getMonth(), it.getDate(), 23, 59, 59)
            } ?: now
            s to e
        }
    }

    // Pair each item with its parsed Date, keeping only those in the selected range.
    // We keep the full item (not just the date) so the aggregate function can
    // compute things like distinct-user counts if needed.
    val filteredItems: List<Pair<kotlin.js.Date, T>> = items.mapNotNull { item ->
        parseIsoToDate(timestampOf(item))?.let { d ->
            if (d.getTime() >= rangeStart.getTime() && d.getTime() <= rangeEnd.getTime()) d to item else null
        }
    }

    val isToday = selectedRange == DateRange.TODAY
    val isMonthly = monthlyForAllTime && selectedRange == DateRange.ALL_TIME

    // Build bucketed counts
    data class ChartPoint(val label: String, val count: Int)

    // Cumulative mode: each point reflects EVERY item from the beginning of time up to the end of
    // that bucket (a running trend), not just the items inside the bucket — and ignores the range's
    // start, so e.g. the Apr 7 point is the rate over all analyses from day 1 through Apr 7. Parse
    // every item's epoch-ms once so each point is a simple "<= cutoff" filter.
    val allTimes: List<Pair<Double, T>> = remember(items, cumulative) {
        if (cumulative) items.mapNotNull { item -> parseIsoToDate(timestampOf(item))?.let { it.getTime() to item } }
        else emptyList()
    }
    fun cumulativeValue(cutoffMs: Double): Int =
        aggregate(allTimes.filter { it.first <= cutoffMs }.map { it.second })

    val points: List<ChartPoint> = if (isToday) {
        // Group by hour (local time — "today" is a local concept)
        val hourBuckets = mutableMapOf<Int, MutableList<T>>()
        filteredItems.forEach { (d, item) ->
            val h = d.getHours()
            hourBuckets.getOrPut(h) { mutableListOf() }.add(item)
        }
        val currentHour = now.getHours()
        (0..currentHour).map { h ->
            val label = "${h.toString().padStart(2, '0')}:00"
            if (cumulative) {
                val cutoff = todayStart.getTime() + (h + 1) * 3600000.0 - 1 // end of hour h, local
                ChartPoint(label, cumulativeValue(cutoff))
            } else {
                ChartPoint(label, aggregate(hourBuckets[h] ?: emptyList()))
            }
        }
    } else if (isMonthly) {
        // One point per calendar month (UTC), from the first month in range to the current month.
        val monthBuckets = mutableMapOf<String, MutableList<T>>()
        filteredItems.forEach { (d, item) ->
            val key = "${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}"
            monthBuckets.getOrPut(key) { mutableListOf() }.add(item)
        }
        val months = mutableListOf<kotlin.js.Date>()
        var y = rangeStart.getUTCFullYear(); var m = rangeStart.getUTCMonth()
        val endY = rangeEnd.getUTCFullYear(); val endM = rangeEnd.getUTCMonth()
        while (y < endY || (y == endY && m <= endM)) {
            months.add(kotlin.js.Date(kotlin.js.Date.UTC(y, m, 1)))
            m++; if (m > 11) { m = 0; y++ }
        }
        months.map { mDate ->
            if (cumulative) {
                // end of this month = start of next month (UTC), minus 1 ms
                val cutoff = kotlin.js.Date.UTC(mDate.getUTCFullYear(), mDate.getUTCMonth() + 1, 1) - 1
                ChartPoint(shortUtcMonthLabel(mDate), cumulativeValue(cutoff))
            } else {
                val key = "${mDate.getUTCFullYear()}-${(mDate.getUTCMonth() + 1).toString().padStart(2, '0')}"
                ChartPoint(shortUtcMonthLabel(mDate), aggregate(monthBuckets[key] ?: emptyList()))
            }
        }
    } else {
        // Group by UTC date — using local date would shift events across day boundaries
        // depending on the viewer's timezone (e.g. UTC Apr 20 23:50 would bucket as Apr 21
        // for anyone east of UTC). UTC gives a stable, timezone-independent bucketing
        // that matches the server's stored timestamps.
        val dayBuckets = mutableMapOf<String, MutableList<T>>()
        filteredItems.forEach { (d, item) ->
            val key = "${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}"
            dayBuckets.getOrPut(key) { mutableListOf() }.add(item)
        }
        // Generate all UTC dates in range. Anchor iteration to the UTC midnight of
        // rangeStart's UTC date so labels line up with bucket keys.
        val anchor = kotlin.js.Date(kotlin.js.Date.UTC(
            rangeStart.getUTCFullYear(),
            rangeStart.getUTCMonth(),
            rangeStart.getUTCDate()
        ))
        val totalDays = ((rangeEnd.getTime() - anchor.getTime()) / 86400000.0).toInt() + 1
        (0 until totalDays).map { i ->
            val d = kotlin.js.Date(anchor.getTime() + i * 86400000.0)
            if (cumulative) {
                val cutoff = anchor.getTime() + (i + 1) * 86400000.0 - 1 // end of UTC day i
                ChartPoint(shortUtcDateLabel(d), cumulativeValue(cutoff))
            } else {
                val key = "${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}-${d.getUTCDate().toString().padStart(2, '0')}"
                ChartPoint(shortUtcDateLabel(d), aggregate(dayBuckets[key] ?: emptyList()))
            }
        }
    }

    // Header total — aggregate across all filtered items (correct for both total
    // counts and distinct-value counts, which would be over-counted if we just
    // summed per-bucket).
    // For cumulative charts the header is the latest running value (the all-time rate up to now),
    // not an aggregate of the visible window.
    val headerTotal = if (cumulative) (points.lastOrNull()?.count ?: 0) else aggregate(filteredItems.map { it.second })

    val dataMax = (points.maxOfOrNull { it.count } ?: 1).coerceAtLeast(1)
    val maxCount = when {
        yHeadroom != null -> (dataMax + yHeadroom).let { if (yMax != null) it.coerceAtMost(yMax) else it }
        yMax != null -> yMax
        else -> dataMax
    }.coerceAtLeast(1)

    CardSection(title, onRefresh) {
        // Date range tabs
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(6.px)
                property("margin-bottom", "20px")
                property("flex-wrap", "wrap")
            }
        }) {
            DateRange.entries.forEach { range ->
                Span(attrs = {
                    style {
                        padding(5.px, 12.px)
                        borderRadius(6.px)
                        fontSize(12.px)
                        fontWeight(500)
                        cursor("pointer")
                        property("transition", TransitionMedium)
                        if (selectedRange == range) {
                            backgroundColor(Primary)
                            color(Color.white)
                        } else {
                            backgroundColor(Gray100)
                            color(Gray600)
                        }
                    }
                    onClick { selectedRange = range }
                }) { Text(range.label) }
            }
            // Total count for range
            Span(attrs = {
                style {
                    property("margin-left", "auto")
                    fontSize(13.px)
                    color(Gray500)
                    fontWeight(600)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                }
            }) { Text("$headerTotal$valueSuffix $unitLabel") }
        }

        // Custom date range pickers
        if (selectedRange == DateRange.CUSTOM) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    alignItems(AlignItems.Center)
                    property("margin-bottom", "16px")
                    property("flex-wrap", "wrap")
                }
            }) {
                Span(attrs = { style { fontSize(13.px); color(Gray500); fontWeight(500) } }) { Text("From") }
                TagElement<org.w3c.dom.HTMLElement>("input", {
                    attr("type", "date")
                    attr("value", customStartStr)
                    addEventListener("input") {
                        customStartStr = (it.target as? org.w3c.dom.HTMLInputElement)?.value ?: customStartStr
                    }
                    style {
                        padding(6.px, 10.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(13.px)
                        color(Gray700)
                        property("outline", "none")
                    }
                }) {}
                Span(attrs = { style { fontSize(13.px); color(Gray500); fontWeight(500) } }) { Text("To") }
                TagElement<org.w3c.dom.HTMLElement>("input", {
                    attr("type", "date")
                    attr("value", customEndStr)
                    addEventListener("input") {
                        customEndStr = (it.target as? org.w3c.dom.HTMLInputElement)?.value ?: customEndStr
                    }
                    style {
                        padding(6.px, 10.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(13.px)
                        color(Gray700)
                        property("outline", "none")
                    }
                }) {}
            }
        }

        if (points.isEmpty()) {
            P(attrs = { style { color(Gray400); fontSize(14.px); property("text-align", "center"); padding(20.px) } }) {
                Text("No data for this period.")
            }
        } else {
            // SVG line chart — built as a string and injected via DOM API
            // (Compose HTML's TagElement doesn't handle SVG namespaces correctly)
            val svgW = 800.0
            val svgH = 220.0
            val pl = 40.0
            val pr = 16.0
            val pt2 = 16.0
            val pb = 36.0
            val dw = svgW - pl - pr
            val dh = svgH - pt2 - pb

            val coords = points.mapIndexed { i, p ->
                val x = if (points.size == 1) pl + dw / 2.0 else pl + dw * i.toDouble() / (points.size - 1).toDouble()
                val y = pt2 + dh - (dh * p.count.toDouble() / maxCount.toDouble())
                x to y
            }

            // Build SVG markup from computed chart data (all values are numeric/label constants, no user input)
            val svgMarkup = buildString {
                append("""<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 $svgW $svgH" style="width:100%;height:auto;display:block">""")
                // Hover tooltips: each point gets a transparent full-height hit column; CSS :hover
                // reveals that column's guide line, highlight dot, and value tooltip. Pure SVG+CSS so
                // it survives the DOMParser injection with no JS wiring.
                append("""<style>.pt .tip,.pt .hl,.pt .guide{opacity:0;transition:opacity .08s}.pt:hover .tip,.pt:hover .hl,.pt:hover .guide{opacity:1}</style>""")
                val gridLines = 4
                for (i in 0..gridLines) {
                    val y = pt2 + dh - (dh * i / gridLines)
                    val value = maxCount * i / gridLines
                    append("""<line x1="$pl" y1="$y" x2="${svgW - pr}" y2="$y" stroke="#f0f0f0" stroke-width="1"/>""")
                    append("""<text x="${pl - 8}" y="${y + 3.5}" text-anchor="end" fill="#a3a3a3" font-size="11" font-family="-apple-system,BlinkMacSystemFont,sans-serif">$value$valueSuffix</text>""")
                }
                val baseline = pt2 + dh
                // Unique gradient ID so multiple charts on the same page don't collide
                val gradId = "areaGrad-" + title.filter { it.isLetterOrDigit() }
                if (coords.isNotEmpty()) {
                    append("""<defs><linearGradient id="$gradId" x1="0" y1="0" x2="0" y2="1">""")
                    append("""<stop offset="0%" stop-color="$strokeColor" stop-opacity="0.18"/>""")
                    append("""<stop offset="100%" stop-color="$strokeColor" stop-opacity="0.02"/>""")
                    append("""</linearGradient></defs>""")
                    append("""<path d="M ${coords.first().first} $baseline """)
                    coords.forEach { (x, y) -> append("L $x $y ") }
                    append("""L ${coords.last().first} $baseline Z" fill="url(#$gradId)"/>""")
                }
                if (coords.size >= 2) {
                    append("""<path d="""")
                    coords.forEachIndexed { i, (x, y) ->
                        append(if (i == 0) "M " else "L ")
                        append("$x $y ")
                    }
                    append("""" fill="none" stroke="$strokeColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>""")
                }
                // Cumulative charts read as a clean trend line — skip the per-point dots that would
                // otherwise clutter the many daily points.
                if (!cumulative) {
                    coords.forEachIndexed { i, (x, y) ->
                        if (points[i].count > 0) {
                            append("""<circle cx="$x" cy="$y" r="3.5" fill="$strokeColor" stroke="white" stroke-width="2"/>""")
                        }
                    }
                }
                val step = (points.size / 12).coerceAtLeast(1)
                coords.forEachIndexed { i, (x, _) ->
                    if (i % step == 0 || i == points.size - 1) {
                        append("""<text x="$x" y="${svgH - 6}" text-anchor="middle" fill="#a3a3a3" font-size="10" font-family="-apple-system,BlinkMacSystemFont,sans-serif">${points[i].label}</text>""")
                    }
                }
                // Interactive hover layer (drawn last so tooltips sit on top of the line).
                val spacing = if (points.size > 1) dw / (points.size - 1) else dw
                val boxW = 78.0
                val boxH = 36.0
                coords.forEachIndexed { i, (x, y) ->
                    val tx = (x - boxW / 2).coerceIn(pl, svgW - pr - boxW)
                    val ty = if (y - boxH - 10 < 0) y + 12 else y - boxH - 10
                    val valueText = "${points[i].count}$valueSuffix"
                    append("""<g class="pt">""")
                    append("""<rect x="${x - spacing / 2}" y="0" width="$spacing" height="$svgH" fill="transparent"/>""")
                    append("""<line class="guide" x1="$x" y1="$pt2" x2="$x" y2="$baseline" stroke="$strokeColor" stroke-width="1" stroke-dasharray="3 3"/>""")
                    append("""<circle class="hl" cx="$x" cy="$y" r="4" fill="$strokeColor" stroke="white" stroke-width="2"/>""")
                    append("""<g class="tip">""")
                    append("""<rect x="$tx" y="$ty" width="$boxW" height="$boxH" rx="6" fill="#1f2937"/>""")
                    append("""<text x="${tx + boxW / 2}" y="${ty + 14}" text-anchor="middle" fill="#cbd5e1" font-size="10" font-family="-apple-system,BlinkMacSystemFont,sans-serif">${points[i].label}</text>""")
                    append("""<text x="${tx + boxW / 2}" y="${ty + 28}" text-anchor="middle" fill="white" font-size="13" font-weight="600" font-family="-apple-system,BlinkMacSystemFont,sans-serif">$valueText</text>""")
                    append("</g></g>")
                }
                append("</svg>")
            }

            Div(attrs = {
                style {
                    width(100.percent)
                    property("overflow-x", "auto")
                }
            }) {
                // key(svgMarkup) forces the inner Div to be recreated whenever the markup
                // changes (e.g. on filter change). Compose HTML's `ref` block only runs on
                // mount, so without `key` the SVG content never updates on recomposition.
                ChartSvgContainer(svgMarkup)
            }
        }
    }
}

/**
 * Renders an SVG markup string into the DOM. Uses DOMParser to safely parse
 * the SVG as XML (avoids innerHTML). Keyed on the markup so Compose recreates
 * the node whenever the markup changes — necessary because `ref` blocks only
 * run on mount, not on every recomposition.
 */
@Composable
private fun ChartSvgContainer(svgMarkup: String) {
    key(svgMarkup) {
        Div(attrs = {
            ref { element ->
                val parser: dynamic = js("new DOMParser()")
                val parsed: dynamic = parser.parseFromString(svgMarkup, "image/svg+xml")
                val svgNode: dynamic = parsed.documentElement
                // Clear any previous content then append the parsed SVG node
                element.asDynamic().textContent = ""
                element.appendChild(svgNode.cloneNode(true).unsafeCast<org.w3c.dom.Node>())
                onDispose {}
            }
        }) {}
    }
}

// ---------------------------------------------------------------------------
// Score distribution
// ---------------------------------------------------------------------------

@Composable
private fun ScoreDistributionCard(data: DashboardData, onRefresh: (() -> Unit)? = null) {
    val total = data.scoreSafe + data.scoreCaution + data.scoreNotSafe
    CardSection("Score Distribution", onRefresh) {
        if (total == 0) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) {
                Text("No analyses with scores yet.")
            }
        } else {
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                    gap(16.px)
                }
            }) {
                ScoreBucket("Safe (7-10)", data.scoreSafe, total, ScoreGreenBg, ScoreGreen, "#22C55E")
                ScoreBucket("Caution (4-6)", data.scoreCaution, total, ScoreAmberBg, ScoreAmber, "#F59E0B")
                ScoreBucket("Not Safe (1-3)", data.scoreNotSafe, total, ScoreRedBg, ScoreRed, "#EF4444")
            }
        }
    }
}

@Composable
private fun ScoreBucket(
    label: String,
    count: Int,
    total: Int,
    bgColor: CSSColorValue,
    fgColor: CSSColorValue,
    barColor: String
) {
    val pct = if (total > 0) (count.toDouble() / total * 100).toInt() else 0
    Div(attrs = {
        style {
            backgroundColor(bgColor)
            borderRadius(12.px)
            padding(20.px)
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                property("margin-bottom", "8px")
            }
        }) {
            Span(attrs = {
                style { fontWeight(600); fontSize(14.px); color(fgColor) }
            }) { Text(label) }
            Span(attrs = {
                style { fontWeight(800); fontSize(22.px); color(fgColor) }
            }) { Text(count.toString()) }
        }
        Div(attrs = {
            style {
                width(100.percent)
                height(6.px)
                backgroundColor(Color("rgba(0,0,0,0.08)"))
                borderRadius(3.px)
                property("overflow", "hidden")
            }
        }) {
            Div(attrs = {
                style {
                    width(pct.percent)
                    height(100.percent)
                    property("background", barColor)
                    borderRadius(3.px)
                    property("transition", "width 0.6s ease")
                }
            })
        }
        Span(attrs = {
            style { fontSize(12.px); color(fgColor); property("margin-top", "4px"); display(DisplayStyle.Block) }
        }) { Text("$pct% of total") }
    }
}

// ---------------------------------------------------------------------------
// Top users table
// ---------------------------------------------------------------------------

@Composable
private fun TopUsersTable(users: List<UserSummary>, onRefresh: (() -> Unit)? = null, onNavigate: (DrillDownView) -> Unit) {
    CardSection("Top Users", onRefresh) {
        if (users.isEmpty()) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No user data yet.") }
        } else {
            TableWrapper {
                Thead {
                    Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                        AdminTh("User")
                        AdminTh("Searches")
                        AdminTh("Avg Score")
                        AdminTh("Last Active")
                    }
                }
                Tbody {
                    users.take(20).forEachIndexed { index, user ->
                        Tr(attrs = {
                            style {
                                if (index % 2 == 1) backgroundColor(Gray50)
                                property("border-bottom", "1px solid ${Gray200}")
                            }
                        }) {
                            Td(attrs = { style { padding(12.px, 16.px) } }) {
                                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                    RowAvatar(user.userEmail)
                                    val displayName = LocalUserNames.current[user.userEmail?.lowercase()] ?: user.userEmail ?: "Anonymous (${user.userId?.substringBefore(",")?.trim() ?: "?"})"
                                    ClickableText(displayName) {
                                        onNavigate(DrillDownView.UserDetail(user.userEmail, user.userId))
                                    }
                                }
                            }
                            Td(attrs = {
                                style { padding(12.px, 16.px); fontWeight(700); color(Primary) }
                            }) { Text(user.totalSearches.toString()) }
                            Td(attrs = { style { padding(12.px, 16.px) } }) {
                                AdminScoreBadge(user.avgScore?.toInt())
                            }
                            Td(attrs = {
                                style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) }
                            }) { Text(formatIsoDate(user.lastSearchAt)) }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Top searched media table
// ---------------------------------------------------------------------------

@Composable
private fun TopMediaTable(media: List<AdminMediaRow>, onRefresh: (() -> Unit)? = null, onNavigate: (DrillDownView) -> Unit) {
    CardSection("Top Searched Media", onRefresh) {
        if (media.isEmpty()) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No media content found.") }
        } else {
            TableWrapper {
                Thead {
                    Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                        AdminTh("Title")
                        AdminTh("Type")
                        AdminTh("Searches")
                        AdminTh("Created")
                        AdminTh("")
                    }
                }
                Tbody {
                    media.forEachIndexed { index, row ->
                        Tr(attrs = {
                            style {
                                if (index % 2 == 1) backgroundColor(Gray50)
                                property("border-bottom", "1px solid ${Gray200}")
                            }
                        }) {
                            Td(attrs = {
                                style {
                                    padding(12.px, 16.px)
                                    property("max-width", "320px")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                            }) {
                                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                    RowCover(row.title, row.mediaType)
                                    Div(attrs = { style { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap"); property("min-width", "0") } }) {
                                        ClickableText(row.displayTitle) {
                                            onNavigate(DrillDownView.TitleDetail(row.displayTitle))
                                        }
                                    }
                                }
                            }
                            Td(attrs = { style { padding(12.px, 16.px) } }) {
                                ClickableMediaTypeBadge(row.mediaType) {
                                    onNavigate(DrillDownView.MediaTypeDetail(row.mediaType))
                                }
                            }
                            Td(attrs = {
                                style { padding(12.px, 16.px); fontWeight(700); color(Primary) }
                            }) { Text(row.hitCount.toString()) }
                            Td(attrs = {
                                style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) }
                            }) { Text(formatIsoDate(row.createdAt)) }
                            Td(attrs = { style { padding(12.px, 16.px) } }) {
                                Span(attrs = {
                                    style {
                                        padding(7.px, 14.px); borderRadius(8.px)
                                        backgroundColor(PrimaryPale); color(Primary)
                                        fontSize(12.px); fontWeight(600); cursor("pointer")
                                        property("white-space", "nowrap")
                                    }
                                    onClick { onNavigate(DrillDownView.TitleDetail(row.displayTitle)) }
                                }) { Text("Details") }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Recent analyses table
// ---------------------------------------------------------------------------

/** email (lowercase) → display name, populated at the AdminPage root from the registered-users list. */
private val LocalUserNames = compositionLocalOf<Map<String, String>> { emptyMap() }
/** email (lowercase) → avatar URL (OAuth profile pic), from the registered-users list. */
private val LocalUserAvatars = compositionLocalOf<Map<String, String>> { emptyMap() }
/** "bare title::media_type" (lowercased) → cover URL, from media_content. */
private val LocalCovers = compositionLocalOf<Map<String, String>> { emptyMap() }

/** Normalized key for cover lookups — strips a trailing "(year)" so "Inception (2010)" matches. */
private fun coverLookupKey(title: String?, mediaType: String?): String {
    val bare = (title ?: "").trim().replace(Regex("""\s*\([^)]*\)\s*$"""), "").trim().lowercase()
    return "$bare::${(mediaType ?: "").lowercase()}"
}

/** Small round avatar for a user row — real photo if known, else an initial chip. */
@Composable
private fun RowAvatar(email: String?) {
    val url = email?.let { LocalUserAvatars.current[it.lowercase()] }
    if (url != null) {
        Img(src = url, alt = "", attrs = {
            attr("referrerpolicy", "no-referrer")
            style {
                width(22.px); height(22.px); borderRadius(50.percent)
                property("object-fit", "cover"); property("flex-shrink", "0")
            }
        })
    } else {
        Div(attrs = {
            style {
                width(22.px); height(22.px); borderRadius(50.percent)
                backgroundColor(PrimaryPale); color(Primary)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                fontSize(10.px); fontWeight(700); property("flex-shrink", "0")
            }
        }) { Text(email?.trim()?.firstOrNull()?.uppercase() ?: "?") }
    }
}

/** Small poster thumbnail for a title row — cover if we have one, else a neutral box. */
@Composable
private fun RowCover(title: String?, mediaType: String?) {
    val url = LocalCovers.current[coverLookupKey(title, mediaType)]?.takeIf { it.isNotBlank() }
    if (url != null) {
        Img(src = url, alt = "", attrs = {
            style {
                width(28.px); height(38.px); borderRadius(4.px)
                property("object-fit", "cover"); property("flex-shrink", "0")
                backgroundColor(Color("#f1f5f9"))
            }
        })
    } else {
        Div(attrs = {
            style {
                width(28.px); height(38.px); borderRadius(4.px)
                backgroundColor(Color("#f1f5f9")); property("flex-shrink", "0")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                fontSize(13.px); color(Gray400)
            }
        }) { Text("▦") }
    }
}

@Composable
private fun RecentAnalysesTable(events: List<AdminEventRow>, onRefresh: (() -> Unit)? = null, onNavigate: (DrillDownView) -> Unit) {
    CardSection("Recent Analyses", onRefresh) {
        if (events.isEmpty()) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No analyses recorded yet.") }
        } else {
            AnalysesTable(events, showUser = true, onNavigate = onNavigate)
        }
    }
}

// ---------------------------------------------------------------------------
// Shared analyses table (used in overview + drill-down views)
// ---------------------------------------------------------------------------

/** Display label for a search_source value (null → "Unknown"). Shared by the table badge and the
 *  Title Detail source-breakdown card. */
private fun sourceLabel(source: String?): String = when (source) {
    "autocomplete" -> "Autocomplete"
    "disambiguation" -> "Search"
    "photo_camera" -> "Camera"
    "photo_upload" -> "Upload"
    "isbn" -> "ISBN"
    "alternatives" -> "Alternatives"
    "similar_titles" -> "Similar"
    "islamic_picks" -> "Islamic Picks"
    "author_titles" -> "By Author"
    "upgrade" -> "Upgrade"
    // Kids Mode analyses: the server prefixes the kid's source with "kids_".
    "kids_search" -> "Kids: Search"
    "kids_photo" -> "Kids: Photo"
    "kids_retry" -> "Kids: Retry"
    null, "" -> "Unknown"
    else -> source
}

/** (background, foreground) for a search_source badge, grouped by family (recommendation rows share
 *  green, photo shares purple, etc.). */
private fun sourceColors(source: String?): Pair<CSSColorValue, CSSColorValue> = when (source) {
    "autocomplete" -> Color("#e0f2fe") to Color("#0369a1")   // sky
    "disambiguation" -> Color("#f1f5f9") to Color("#475569") // slate
    "photo_camera", "photo_upload" -> Color("#f3e8ff") to Color("#7e22ce") // purple
    "isbn" -> Color("#e0e7ff") to Color("#4338ca")           // indigo
    "alternatives" -> Color("#fef3c7") to Color("#b45309")   // amber
    "similar_titles", "islamic_picks", "author_titles" -> Color("#dcfce7") to Color("#15803d") // green
    "upgrade" -> Color("#fce7f3") to Color("#be185d")        // pink
    "kids_search", "kids_photo", "kids_retry" -> Color("#E6F7F6") to Color("#0E7C7B") // teal
    else -> Gray100 to Gray600
}

@Composable
private fun SourceBadge(source: String?) {
    if (source.isNullOrBlank()) {
        Span(attrs = { style { color(Gray400) } }) { Text("—") }
        return
    }
    val (bg, fg) = sourceColors(source)
    Span(attrs = {
        style {
            fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
            borderRadius(6.px); display(DisplayStyle.InlineBlock)
            property("white-space", "nowrap")
            backgroundColor(bg); color(fg)
        }
    }) { Text(sourceLabel(source)) }
}

@Composable
private fun AnalysesTable(
    events: List<AdminEventRow>,
    showUser: Boolean = false,
    showTitle: Boolean = true,
    showType: Boolean = true,
    showConcerns: Boolean = false,
    onNavigate: (DrillDownView) -> Unit
) {
    // Track which rows have expanded results
    var expandedIndex by remember { mutableStateOf<Int?>(null) }

    val colCount = listOf(showTitle, showUser, showType, true, true, true, true, true, true, true, true, showConcerns, showConcerns, true, true).count { it }

    TableWrapper {
        Thead {
            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                if (showTitle) AdminTh("Title")
                if (showUser) AdminTh("User")
                if (showType) AdminTh("Type")
                AdminTh("Mode")
                AdminTh("Platform")
                AdminTh("Source")
                AdminTh("Score")
                AdminTh("Islamic")
                AdminTh("Duration")
                AdminTh("Cache")
                AdminTh("Cost")
                if (showConcerns) AdminTh("Criteria")
                if (showConcerns) AdminTh("Age(s)")
                AdminTh("Date")
                AdminTh("")  // Show Result column
            }
        }
        Tbody {
            events.forEachIndexed { index, event ->
                val isExpanded = expandedIndex == index
                Tr(attrs = {
                    style {
                        if (index % 2 == 1) backgroundColor(Gray50)
                        property("border-bottom", if (isExpanded) "none" else "1px solid ${Gray200}")
                    }
                }) {
                    if (showTitle) {
                        Td(attrs = {
                            style {
                                padding(12.px, 16.px)
                                property("max-width", "260px")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }) {
                            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                RowCover(event.title, event.mediaType)
                                val title = event.title ?: "-"
                                Div(attrs = { style { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap"); property("min-width", "0") } }) {
                                    if (event.title != null) {
                                        ClickableText(title) { onNavigate(DrillDownView.TitleDetail(title)) }
                                    } else {
                                        Span(attrs = { style { color(Gray400) } }) { Text(title) }
                                    }
                                }
                            }
                        }
                    }
                    if (showUser) {
                        Td(attrs = {
                            style {
                                padding(12.px, 16.px)
                                property("max-width", "200px")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }) {
                            val email = event.userEmail
                            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                RowAvatar(email)
                                val display = LocalUserNames.current[email?.lowercase()] ?: email ?: "Anonymous (${event.userId?.substringBefore(",")?.trim() ?: "?"})"
                                Div(attrs = { style { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap"); property("min-width", "0") } }) {
                                    ClickableText(display) {
                                        onNavigate(DrillDownView.UserDetail(email, event.userId))
                                    }
                                }
                            }
                        }
                    }
                    if (showType) {
                        Td(attrs = { style { padding(12.px, 16.px) } }) {
                            val type = event.mediaType
                            if (type != null) {
                                ClickableMediaTypeBadge(type) {
                                    onNavigate(DrillDownView.MediaTypeDetail(type))
                                }
                            } else {
                                MediaTypeBadge("unknown")
                            }
                        }
                    }
                    // Mode (Quick vs Full)
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        val (modeText, modeBg, modeFg) = if (event.isQuickCheck)
                            Triple("Quick", Color("#e0f2fe"), Color("#0369a1"))
                        else
                            Triple("Full", PrimaryPale, Primary)
                        Span(attrs = {
                            style {
                                fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                property("white-space", "nowrap")
                                backgroundColor(modeBg); color(modeFg)
                            }
                        }) { Text(modeText) }
                    }
                    // Platform — where THIS search was run. Stamped per-row at analysis time,
                    // so it stays correct for users who switch between web and mobile.
                    Td(attrs = { style { padding(12.px, 16.px); property("white-space", "nowrap") } }) {
                        val platform = event.platform
                        if (platform == null) {
                            Span(attrs = { style { color(Gray400) } }) { Text("—") }
                        } else {
                            val (platformText, platformBg, platformFg) = when (platform) {
                                "ios" -> Triple("iOS", Color("#eef2ff"), Color("#4338ca"))
                                "android" -> Triple("Android", Color("#ecfdf5"), Color("#047857"))
                                "web" -> Triple("Web", Color("#fef3c7"), Color("#b45309"))
                                else -> Triple(platform, Gray100, Gray600)
                            }
                            Span(attrs = {
                                style {
                                    fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                    borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                    property("white-space", "nowrap")
                                    backgroundColor(platformBg); color(platformFg)
                                }
                            }) { Text(platformText) }
                            event.appVersion?.let { version ->
                                Span(attrs = {
                                    style { marginLeft(6.px); fontSize(11.px); color(Gray400) }
                                }) { Text(version) }
                            }
                        }
                    }
                    // Source — HOW this search was initiated (autocomplete/disambiguation/photo/
                    // isbn/recommendation rows/upgrade). Null on legacy + pre-release clients.
                    Td(attrs = { style { padding(12.px, 16.px); property("white-space", "nowrap") } }) {
                        SourceBadge(event.searchSource)
                    }
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        AdminScoreBadge(event.score)
                    }
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        AdminScoreBadge(event.islamicScore)
                    }
                    Td(attrs = {
                        style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) }
                    }) {
                        Text(event.durationSeconds?.let { formatDuration(it) } ?: "-")
                    }
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        CacheLayerBadge(event.cacheLayer)
                    }
                    Td(attrs = {
                        style { padding(12.px, 16.px); fontSize(13.px) }
                    }) {
                        val cost = event.costUsd
                        if (cost != null && cost > 0) {
                            Span(attrs = {
                                style { fontWeight(600); color(Color("#b45309")) }
                            }) { Text("$${formatCost(cost)}") }
                        } else if (event.cacheLayer != null) {
                            Span(attrs = { style { color(ScoreGreen); fontWeight(600) } }) { Text("$0") }
                        } else {
                            Span(attrs = { style { color(Gray400) } }) { Text("-") }
                        }
                    }
                    if (showConcerns) {
                        Td(attrs = { style { padding(12.px, 16.px) } }) {
                            CriteriaTags(event.criteriaJson)
                        }
                        Td(attrs = { style { padding(12.px, 16.px) } }) {
                            AgeTags(event.criteriaJson)
                        }
                    }
                    Td(attrs = {
                        style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) }
                    }) { Text(formatIsoDate(event.createdAt)) }
                    // Show Result button
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        if (event.id != null) {
                            Span(attrs = {
                                attr("role", "button")
                                onClick { expandedIndex = if (isExpanded) null else index }
                                style {
                                    backgroundColor(if (isExpanded) Primary else PrimaryPale)
                                    color(if (isExpanded) Color.white else Primary)
                                    fontWeight(600)
                                    fontSize(12.px)
                                    padding(4.px, 12.px)
                                    borderRadius(8.px)
                                    display(DisplayStyle.InlineBlock)
                                    cursor("pointer")
                                    property("transition", TransitionMedium)
                                    property("white-space", "nowrap")
                                }
                            }) { Text(if (isExpanded) "Hide" else "Result") }
                        } else {
                            Span(attrs = { style { color(Gray400); fontSize(12.px) } }) { Text("-") }
                        }
                    }
                }
                // Expanded result row
                if (isExpanded && event.id != null) {
                    Tr(attrs = {
                        style {
                            property("border-bottom", "1px solid ${Gray200}")
                            backgroundColor(Color("rgba(14, 124, 123, 0.03)"))
                        }
                    }) {
                        Td(attrs = {
                            attr("colspan", colCount.toString())
                            style { padding(0.px) }
                        }) {
                            ExpandedResultPanel(event)
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// All Analyses View (drilldown from Total Analyses card)
// ---------------------------------------------------------------------------

@Composable
private fun AllAnalysesView(
    data: DashboardData,
    onRefresh: (() -> Unit)? = null,
    onNavigate: (DrillDownView) -> Unit
) {
    val analyses = data.allAnalyses // already sorted by time desc

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "All Analyses" to null)

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            SummaryCard("Total", analyses.size.toString(), Primary)
            SummaryCard("Unique Titles", analyses.mapNotNull { it.title }.distinct().size.toString(), Accent)
            SummaryCard("Cache Hits", analyses.count { it.cacheLayer != null }.toString(), ScoreGreen)
            val totalCost = analyses.sumOf { it.costUsd ?: 0.0 }
            SummaryCard("Total Cost", "$${formatCost(totalCost)}", Color("#b45309"))
        }

        CardSection("All Analyses (${analyses.size})", onRefresh) {
            if (analyses.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No analyses recorded yet.") }
            } else {
                AnalysesTable(
                    events = analyses,
                    showUser = true,
                    showTitle = true,
                    showType = true,
                    showConcerns = true,
                    onNavigate = onNavigate
                )
            }
        }
    }
}

// ---------------------------------------------------------------------------
// All Media View (drilldown from Unique Media card)
// ---------------------------------------------------------------------------

@Composable
private fun AllMediaView(
    data: DashboardData,
    onRefresh: (() -> Unit)? = null,
    onNavigate: (DrillDownView) -> Unit
) {
    // Group analyses by title, sorted by number of analyses descending
    data class MediaSummaryRow(
        val title: String,
        val mediaType: String?,
        val analysisCount: Int,
        val avgScore: Double?,
        val lastAnalyzed: String?
    )

    val mediaSummaries = data.allAnalyses
        .filter { it.title != null }
        .groupBy { it.title!! }
        .map { (title, events) ->
            val scores = events.mapNotNull { it.score }
            MediaSummaryRow(
                title = title,
                mediaType = events.firstOrNull()?.mediaType,
                analysisCount = events.size,
                avgScore = if (scores.isNotEmpty()) scores.average() else null,
                lastAnalyzed = events.maxByOrNull { it.createdAt ?: "" }?.createdAt
            )
        }
        .sortedByDescending { it.analysisCount }

    // Full-catalog title search — server-side ILIKE over media_content, so it finds ANY catalog
    // title (including seeded ones no user has analyzed). Empty query falls back to the analytics list.
    var search by remember { mutableStateOf("") }
    var appliedSearch by remember { mutableStateOf("") }
    var catalogResults by remember { mutableStateOf<List<CoverItem>?>(null) }
    var searching by remember { mutableStateOf(false) }
    LaunchedEffect(appliedSearch) {
        val q = appliedSearch.trim()
        if (q.isBlank()) { catalogResults = null; return@LaunchedEffect }
        searching = true
        catalogResults = try {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
            val headers = kotlin.js.json("Accept" to "application/json")
            token?.let { headers["Authorization"] = "Bearer $it" }
            val resp = kotlinx.browser.window.fetch(
                "$serverUrl/api/admin/covers?filter=all&search=${encodeURIComponent(q)}&limit=200",
                kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            if (resp.ok) adminJson.decodeFromString<List<CoverItem>>(resp.text().await().toString()) else emptyList()
        } catch (_: Exception) { emptyList() }
        searching = false
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "All Media" to null)

        CardSection("All Media (${mediaSummaries.size})", onRefresh) {
            CatalogSearchBar(search, { search = it }, { appliedSearch = search.trim() },
                showClear = appliedSearch.isNotBlank()) { search = ""; appliedSearch = "" }
            if (searching) {
                P(attrs = { style { color(Gray400); fontSize(14.px); property("padding", "8px 0") } }) { Text("Searching catalog…") }
            } else if (catalogResults != null) {
                CatalogSearchResults(catalogResults!!, appliedSearch, onNavigate)
            } else if (mediaSummaries.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No media content found.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("Title")
                            AdminTh("Type")
                            AdminTh("Analyses")
                            AdminTh("Avg Score")
                            AdminTh("Last Analyzed")
                        }
                    }
                    Tbody {
                        mediaSummaries.forEachIndexed { index, row ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = {
                                    style {
                                        padding(12.px, 16.px)
                                        property("max-width", "320px")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                                }) {
                                    ClickableText(row.title) {
                                        onNavigate(DrillDownView.TitleDetail(row.title))
                                    }
                                }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    row.mediaType?.let { type ->
                                        ClickableMediaTypeBadge(type) {
                                            onNavigate(DrillDownView.MediaTypeDetail(type))
                                        }
                                    } ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                                }
                                Td(attrs = {
                                    style { padding(12.px, 16.px); fontWeight(700); color(Primary) }
                                }) { Text(row.analysisCount.toString()) }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    row.avgScore?.let { AdminScoreBadge(it.toInt()) }
                                        ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                                }
                                Td(attrs = {
                                    style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) }
                                }) { Text(formatIsoDate(row.lastAnalyzed)) }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CatalogSearchBar(
    value: String,
    onValue: (String) -> Unit,
    onSearch: () -> Unit,
    showClear: Boolean,
    onClear: () -> Unit
) {
    Div(attrs = { style {
        property("display", "flex"); property("gap", "8px"); property("align-items", "center")
        property("flex-wrap", "wrap"); property("margin-bottom", "16px")
    } }) {
        org.jetbrains.compose.web.dom.TextInput(value) {
            onInput { onValue(it.value) }
            onKeyDown { if (it.key == "Enter") onSearch() }
            style {
                property("flex", "1"); property("min-width", "220px")
                padding(10.px, 14.px); borderRadius(8.px)
                property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
            }
            attr("placeholder", "Search the full catalog by title…")
        }
        Span(attrs = {
            style {
                padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                color(Color.white); fontSize(14.px); fontWeight(600); cursor("pointer")
            }
            onClick { onSearch() }
        }) { Text("Search") }
        if (showClear) {
            Span(attrs = {
                style {
                    padding(10.px, 16.px); borderRadius(8.px); backgroundColor(Color("#f1f5f9"))
                    color(Gray600); fontSize(14.px); fontWeight(600); cursor("pointer")
                }
                onClick { onClear() }
            }) { Text("Clear") }
        }
    }
}

@Composable
private fun CatalogSearchResults(
    results: List<CoverItem>,
    query: String,
    onNavigate: (DrillDownView) -> Unit
) {
    P(attrs = { style { color(Gray600); fontSize(13.px); property("margin-bottom", "10px") } }) {
        Text("${results.size} catalog ${if (results.size == 1) "title" else "titles"} matching \"$query\"" +
            if (results.size >= 200) " — showing first 200, refine to narrow" else "")
    }
    if (results.isEmpty()) {
        P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No catalog titles match.") }
        return
    }
    TableWrapper {
        Thead {
            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                AdminTh("Title"); AdminTh("Type"); AdminTh("Year")
            }
        }
        Tbody {
            results.forEachIndexed { index, row ->
                Tr(attrs = { style {
                    if (index % 2 == 1) backgroundColor(Gray50)
                    property("border-bottom", "1px solid ${Gray200}")
                } }) {
                    Td(attrs = { style {
                        padding(12.px, 16.px); property("max-width", "360px")
                        property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                    } }) {
                        ClickableText(row.title) {
                            onNavigate(DrillDownView.TitleDetail(row.title, row.mediaType,
                                row.cacheKey.substringAfterLast("::").takeIf { it.isNotBlank() }))
                        }
                    }
                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                        row.mediaType?.let { type ->
                            ClickableMediaTypeBadge(type) { onNavigate(DrillDownView.MediaTypeDetail(type)) }
                        } ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                    }
                    Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) } }) {
                        Text(row.cacheKey.substringAfterLast("::").ifBlank { "—" })
                    }
                }
            }
        }
    }
}

/**
 * Renders the EXACT user-facing result for a catalog title — the same [CombinedAnalysisResultContent]
 * the public site uses — by fetching the synthesized verdict (`/api/admin/synthesized-result`: the
 * full result when one exists, else the Phase 1 quick result of a seeded title) plus live recommendations
 * (`/api/alternatives`). mediaType/year come from the click when available; otherwise resolved from
 * the catalog by title (handles old `#title/Name (2011)` links and analytics-list clicks).
 */
@Composable
private fun TitleResultPreview(
    rawTitle: String,
    mediaTypeHint: String?,
    yearHint: String?,
    registeredUsers: List<RegisteredUser> = emptyList()
) {
    var result by remember { mutableStateOf<AnalysisResult?>(null) }
    var alts by remember { mutableStateOf<AlternativesResult?>(null) }
    var loading by remember { mutableStateOf(true) }
    var err by remember { mutableStateOf<String?>(null) }
    // Real likes/dislikes/comments for this title, so the preview's rating row and Parents'
    // notes show what users actually left rather than the empty defaults.
    var engagement by remember { mutableStateOf<EngagementData?>(null) }

    LaunchedEffect(rawTitle, mediaTypeHint, yearHint) {
        loading = true; err = null; result = null; alts = null
        try {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
            val yr0 = yearHint ?: Regex("\\((\\d{4})\\)\\s*$").find(rawTitle)?.groupValues?.getOrNull(1)
            val cleanTitle = if (yearHint == null) rawTitle.replace(Regex("\\s*\\(\\d{4}\\)\\s*$"), "").trim() else rawTitle
            var mt = mediaTypeHint
            var yr = yr0
            // Resolve authoritative mediaType/year from the catalog when the click didn't carry them.
            if (mt.isNullOrBlank() || yr.isNullOrBlank()) {
                val h = kotlin.js.json("Accept" to "application/json")
                token?.let { h["Authorization"] = "Bearer $it" }
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/covers?filter=all&search=${encodeURIComponent(cleanTitle)}&limit=50",
                    kotlin.js.json("method" to "GET", "headers" to h).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    val rows = try { adminJson.decodeFromString<List<CoverItem>>(resp.text().await().toString()) } catch (_: Exception) { emptyList() }
                    val match = rows.firstOrNull { it.title.equals(cleanTitle, ignoreCase = true) && (yr0 == null || it.cacheKey.substringAfterLast("::") == yr0) }
                        ?: rows.firstOrNull { it.title.equals(cleanTitle, ignoreCase = true) }
                        ?: rows.firstOrNull()
                    if (match != null) {
                        mt = match.mediaType
                        yr = match.cacheKey.substringAfterLast("::").takeIf { it.isNotBlank() }
                    }
                }
            }
            if (mt.isNullOrBlank()) {
                err = "This title isn't in the catalog, so there's no result to preview."
                loading = false
                return@LaunchedEffect
            }
            val esc = { s: String -> s.replace("\\", "\\\\").replace("\"", "\\\"") }
            val hp = kotlin.js.json("Content-Type" to "application/json")
            token?.let { hp["Authorization"] = "Bearer $it" }
            var lastStatus: Short = 0
            suspend fun synthesized(quick: Boolean): AnalysisResult? {
                val body = buildString {
                    append("{\"title\":\"").append(esc(cleanTitle)).append("\",\"mediaType\":\"").append(esc(mt!!)).append("\"")
                    if (!yr.isNullOrBlank()) append(",\"year\":\"").append(esc(yr!!)).append("\"")
                    append(",\"quick_check\":$quick,\"criteria\":{}}")
                }
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/synthesized-result",
                    kotlin.js.json("method" to "POST", "headers" to hp, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                lastStatus = resp.status
                return if (resp.ok) try { adminJson.decodeFromString<AnalysisResult>(resp.text().await().toString()) } catch (_: Exception) { null } else null
            }
            // Every search is FULL now, so show the full result (with the concern write-ups) a user
            // gets. Fall back to Phase 1 only for titles that were seeded and never fully analysed.
            result = synthesized(quick = false) ?: synthesized(quick = true)
            if (result == null) err = "In the catalog, but no synthesized result yet (status $lastStatus)."
            val hg = kotlin.js.json("Accept" to "application/json")
            token?.let { hg["Authorization"] = "Bearer $it" }
            val altUrl = "$serverUrl/api/alternatives?title=${encodeURIComponent(cleanTitle)}&mediaType=${encodeURIComponent(mt!!)}" +
                (if (!yr.isNullOrBlank()) "&year=${encodeURIComponent(yr!!)}" else "")
            val aResp = kotlinx.browser.window.fetch(
                altUrl, kotlin.js.json("method" to "GET", "headers" to hg).unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            alts = if (aResp.ok) try { adminJson.decodeFromString<AlternativesResult>(aResp.text().await().toString()) } catch (_: Exception) { null } else null

            // Engagement, scoped to this title by the resolved (not the clicked) mediaType/year
            // so it keys off the same cache key the comment/reaction writes used.
            val engUrl = "$serverUrl/api/admin/engagement?title=${encodeURIComponent(cleanTitle)}" +
                "&mediaType=${encodeURIComponent(mt!!)}&year=${encodeURIComponent(yr ?: "")}"
            val eResp = kotlinx.browser.window.fetch(
                engUrl, kotlin.js.json("method" to "GET", "headers" to hg).unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            if (eResp.ok) {
                val eText = eResp.text().await().toString()
                // An undeployed endpoint falls through to index.html with a 200 — ignore that
                // rather than letting the JSON parser throw and blank the whole preview.
                if (!eText.trimStart().startsWith("<")) {
                    val parsed = try { adminJson.decodeFromString<EngagementData>(eText) } catch (_: Exception) { null }
                    // An older server ignores the scoping params and returns everything; only
                    // trust the payload wholesale when it says it really was scoped.
                    engagement = when {
                        parsed == null -> null
                        parsed.scoped -> parsed
                        else -> parsed.copy(
                            titles = parsed.titles.filter { it.title.trim().equals(cleanTitle.trim(), ignoreCase = true) },
                            comments = parsed.comments.filter { it.title.trim().equals(cleanTitle.trim(), ignoreCase = true) }
                        )
                    }
                }
            }
        } catch (e: Exception) {
            err = e.message ?: "Failed to load result."
        }
        loading = false
    }

    CardSection("Result Preview — exactly what users see") {
        when {
            loading -> P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading result…") }
            result != null -> {
                // The cover the USER sees (from the result), at the top.
                result!!.coverUrl?.takeIf { it.isNotBlank() }?.let { coverUrl ->
                    Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); property("margin-bottom", "20px") } }) {
                        Img(src = coverUrl, alt = rawTitle, attrs = {
                            style {
                                height(220.px); borderRadius(12.px); property("object-fit", "cover")
                                backgroundColor(Color("#f1f5f9")); property("box-shadow", CardShadow)
                            }
                        })
                    }
                }
                val engRow = engagement?.titles?.firstOrNull()
                val engComments = engagement?.comments ?: emptyList()
                val userById = registeredUsers.associateBy { it.id }
                CombinedAnalysisResultContent(
                    result = result!!,
                    alternatives = alts?.items ?: emptyList(),
                    islamicAlternatives = alts?.islamicItems ?: emptyList(),
                    // The admin payload carries user_id (the public one can't), so map it onto the
                    // public Comment shape. isMine stays false — this is a preview, not the
                    // admin's own thread, so no edit/delete affordances appear.
                    comments = engComments.map {
                        com.islamicmedia.shared.domain.model.Comment(
                            id = it.id,
                            firstName = it.firstName.ifBlank { "Anonymous" },
                            body = it.body,
                            createdAt = it.createdAt ?: "",
                            editedAt = it.editedAt,
                            isMine = false
                        )
                    },
                    likeCount = engRow?.likes ?: 0,
                    dislikeCount = engRow?.dislikes ?: 0,
                    reactionDetail = if (engRow == null || (engRow.likeUserIds.isEmpty() && engRow.dislikeUserIds.isEmpty())) null else {
                        {
                            ReactorBreakdown(
                                likeUserIds = engRow.likeUserIds,
                                dislikeUserIds = engRow.dislikeUserIds,
                                nameFor = { id -> userById[id]?.fullName?.takeIf { n -> n.isNotBlank() } },
                                emailFor = { id ->
                                    userById[id]?.let { u -> u.email.ifBlank { u.fullName ?: id } }
                                        ?: "unknown (${id.take(8)}…)"
                                }
                            )
                        }
                    }
                )
            }
            else -> P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text(err ?: "No result available.") }
        }
    }
}

// ---------------------------------------------------------------------------
// All Users View (drilldown from Unique Users card)
// ---------------------------------------------------------------------------

@Composable
private fun AllUsersView(
    data: DashboardData,
    onRefresh: (() -> Unit)? = null,
    onNavigate: (DrillDownView) -> Unit
) {
    val users = data.userSummaries // already sorted by total searches desc

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "All Users" to null)

        CardSection("All Users (${users.size})", onRefresh) {
            if (users.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No users found.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("User")
                            AdminTh("Searches")
                            AdminTh("Avg Score")
                            AdminTh("Last Active")
                        }
                    }
                    Tbody {
                        users.forEachIndexed { index, user ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    val displayName = user.userEmail ?: "Anonymous (${user.userId?.substringBefore(",")?.trim() ?: "?"})"
                                    ClickableText(displayName) {
                                        onNavigate(DrillDownView.UserDetail(user.userEmail, user.userId))
                                    }
                                }
                                Td(attrs = {
                                    style { padding(12.px, 16.px); fontWeight(700); color(Primary) }
                                }) { Text(user.totalSearches.toString()) }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    user.avgScore?.let { AdminScoreBadge(it.toInt()) }
                                        ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                                }
                                Td(attrs = {
                                    style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) }
                                }) { Text(formatIsoDate(user.lastSearchAt)) }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Flagged Analyses View
// ---------------------------------------------------------------------------

/** The exact "we fixed it" email (subject + body), assembled client-side so the preview
 *  matches what's actually sent. The admin's custom [note] is woven into the body. */
private fun composeFlagFixedEmail(title: String, note: String): Pair<String, String> {
    val subject = "We've updated the analysis you flagged: $title"
    val body = buildString {
        appendLine("Assalamu alaikum,")
        appendLine()
        appendLine("Thank you for flagging \"$title\" in Fitrah Filter. You were right — we've reviewed it and corrected the analysis. The updated result is now available in your history in the app.")
        val n = note.trim()
        if (n.isNotEmpty()) {
            appendLine()
            appendLine(n)
        }
        appendLine()
        appendLine("JazakAllah khair for helping us improve.")
        appendLine()
        appendLine("Yusuf")
        appendLine("Fitrah Filter")
    }
    return subject to body
}

@Composable
private fun FlaggedAnalysesView(
    flags: List<FlagEntry>,
    onRefresh: (() -> Unit)? = null,
    onNavigate: (DrillDownView) -> Unit
) {
    val scope = rememberCoroutineScope()
    // Which flag's "email the user" modal is open, plus its compose/send state.
    var emailFlag by remember { mutableStateOf<FlagEntry?>(null) }
    var emailSubject by remember { mutableStateOf("") }
    var emailBody by remember { mutableStateOf("") }
    var emailSending by remember { mutableStateOf(false) }
    var emailSent by remember { mutableStateOf(false) }
    var emailError by remember { mutableStateOf<String?>(null) }

    // "Mark resolved" — dismiss a flag with no correction/re-analysis. Tracks in-flight +
    // just-resolved ids locally so the row updates immediately without waiting on refetch.
    var dismissingIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var locallyResolvedIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var dismissError by remember { mutableStateOf<String?>(null) }
    fun dismissFlag(flagId: String) {
        scope.launch {
            dismissingIds = dismissingIds + flagId; dismissError = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/dismiss-flag",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to kotlin.js.JSON.stringify(kotlin.js.json("flagId" to flagId))).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    locallyResolvedIds = locallyResolvedIds + flagId; onRefresh?.invoke()
                } else {
                    dismissError = "Mark resolved failed (${resp.status}). ${resp.text().await()}".take(200)
                }
            } catch (e: Exception) {
                dismissError = "Mark resolved failed: ${e.message}"
            }
            dismissingIds = dismissingIds - flagId
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Flagged Analyses" to null)

        CardSection("Flagged Analyses (${flags.size})", onRefresh) {
            dismissError?.let { P(attrs = { style { color(Color("#dc2626")); fontSize(13.px); property("margin", "0 0 12px") } }) { Text(it) } }
            if (flags.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No flagged analyses.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("Title")
                            AdminTh("Type")
                            AdminTh("Score")
                            AdminTh("Mode")
                            AdminTh("Note")
                            AdminTh("User")
                            AdminTh("Platform")
                            AdminTh("When")
                            AdminTh("Status")
                        }
                    }
                    Tbody {
                        flags.forEachIndexed { index, flag ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(600); color(Gray900) } }) {
                                    ClickableText(flag.title.ifBlank { "(untitled)" }) {
                                        onNavigate(DrillDownView.TitleDetail(flag.title))
                                    }
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) } }) {
                                    Text(flag.mediaType ?: "-")
                                }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    flag.score?.let { AdminScoreBadge(it) }
                                        ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) } }) {
                                    Text(if (flag.quickCheck) "Quick check" else "Full")
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray700); fontSize(13.px); property("max-width", "320px") } }) {
                                    Text(flag.message?.takeIf { it.isNotBlank() } ?: "—")
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) } }) {
                                    Text(flag.userEmail ?: "Anonymous")
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray500); fontSize(13.px) } }) {
                                    Text(flag.platform ?: "-")
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) } }) {
                                    Text(formatIsoDate(flag.createdAt))
                                }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                        val isResolved = flag.resolved || flag.id in locallyResolvedIds
                                        if (isResolved) {
                                            Span(attrs = {
                                                style {
                                                    display(DisplayStyle.InlineBlock)
                                                    padding(4.px, 10.px); borderRadius(999.px)
                                                    property("background", "#DCFCE7"); color(Color("#15803d"))
                                                    fontSize(12.px); fontWeight(700)
                                                }
                                                flag.correctedTitle?.let { attr("title", "Corrected to: $it") }
                                            }) { Text("Resolved") }
                                            // A resolution can itself be wrong (Escape 2022 was re-run on the
                                            // wrong book), so a resolved flag can always be resolved again.
                                            if (flag.id != null) {
                                                Button(attrs = {
                                                    onClick { onNavigate(DrillDownView.ResolveFlag(flag.id)) }
                                                    style {
                                                        padding(6.px, 14.px); borderRadius(8.px)
                                                        property("border", "1px solid #dc2626"); property("background", "transparent")
                                                        color(Color("#dc2626")); cursor("pointer"); fontWeight(600); fontSize(13.px)
                                                    }
                                                    attr("title", "Search again, re-run the analysis with fresh research, and replace the user's history")
                                                }) { Text("Resolve again") }
                                            }
                                        } else if (flag.id != null) {
                                            val isDismissing = flag.id in dismissingIds
                                            Button(attrs = {
                                                if (isDismissing) attr("disabled", "true")
                                                onClick { dismissFlag(flag.id) }
                                                style {
                                                    padding(6.px, 14.px); borderRadius(8.px)
                                                    property("border", "1px solid #15803d"); property("background", "transparent")
                                                    color(Color("#15803d")); cursor(if (isDismissing) "default" else "pointer")
                                                    fontWeight(600); fontSize(13.px)
                                                    if (isDismissing) property("opacity", "0.6")
                                                }
                                                attr("title", "Mark resolved with no correction — for false alarms or flags already handled another way")
                                            }) { Text(if (isDismissing) "…" else "✓ Mark resolved") }
                                            Button(attrs = {
                                                onClick { onNavigate(DrillDownView.ResolveFlag(flag.id)) }
                                                style {
                                                    padding(6.px, 14.px); borderRadius(8.px); property("border", "none")
                                                    property("background", "#dc2626"); color(Color.white)
                                                    cursor("pointer"); fontWeight(600); fontSize(13.px)
                                                }
                                                attr("title", "Search the correct title, re-run analysis, and replace the user's history")
                                            }) { Text("Resolve") }
                                        }
                                        // Email the flagging user (only if we know who they are).
                                        if (!flag.userEmail.isNullOrBlank()) {
                                            Button(attrs = {
                                                onClick {
                                                    val (subj, body) = composeFlagFixedEmail(flag.title, "")
                                                    emailFlag = flag; emailSubject = subj; emailBody = body
                                                    emailSent = false; emailError = null
                                                }
                                                style {
                                                    padding(6.px, 14.px); borderRadius(8.px)
                                                    property("border", "1px solid ${Primary}"); property("background", "transparent")
                                                    color(Primary); cursor("pointer"); fontWeight(600); fontSize(13.px)
                                                }
                                            }) { Text("Email user") }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Email-the-user modal: fully editable subject + body, then send.
        emailFlag?.let { flag ->
            val recipient = flag.userEmail ?: ""
            Div(attrs = {
                onClick { if (!emailSending) emailFlag = null }
                style {
                    property("position", "fixed"); property("inset", "0")
                    property("background", "rgba(15,23,42,0.55)")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    padding(20.px); property("box-sizing", "border-box"); property("z-index", "1100")
                }
            }) {
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    style {
                        property("width", "min(94%, 600px)"); property("max-height", "90vh"); property("overflow-y", "auto")
                        backgroundColor(Color.white); borderRadius(16.px); padding(28.px); property("box-sizing", "border-box")
                        property("box-shadow", CardShadow)
                    }
                }) {
                    if (emailSent) {
                        H2(attrs = { style { margin(0.px); fontSize(18.px); fontWeight(700); color(Color("#15803d")) } }) {
                            Text("Email sent to $recipient")
                        }
                        Button(attrs = {
                            onClick { emailFlag = null }
                            style {
                                padding(10.px, 18.px); borderRadius(8.px); property("border", "none")
                                property("background", Primary.toString()); color(Color.white)
                                cursor("pointer"); fontWeight(600); fontSize(14.px); property("margin-top", "16px")
                            }
                        }) { Text("Done") }
                    } else {
                        H2(attrs = { style { margin(0.px, 0.px, 4.px); fontSize(18.px); fontWeight(700); color(Gray900) } }) {
                            Text("Email user about fix")
                        }
                        P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 16px") } }) {
                            Text("From Yusuf — Fitrah Filter <yusuf@fitrahfilter.com> → $recipient")
                        }

                        // Editable subject
                        Div(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray700); property("margin-bottom", "6px") } }) {
                            Text("Subject")
                        }
                        TextInput(emailSubject) {
                            onInput { emailSubject = it.value }
                            style {
                                width(100.percent); property("box-sizing", "border-box"); padding(10.px, 12.px)
                                borderRadius(8.px); property("border", "1.5px solid ${Gray300}")
                                fontSize(14.px); property("font-family", "inherit")
                                property("outline", "none"); property("margin-bottom", "18px")
                            }
                        }

                        // Editable body
                        Div(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray700); property("margin-bottom", "6px") } }) {
                            Text("Body")
                        }
                        TextArea(value = emailBody) {
                            onInput { emailBody = it.value }
                            attr("rows", "14")
                            style {
                                width(100.percent); property("box-sizing", "border-box"); padding(12.px)
                                borderRadius(8.px); property("border", "1.5px solid ${Gray300}")
                                fontSize(14.px); property("font-family", "inherit"); property("resize", "vertical")
                                property("line-height", "1.5"); property("outline", "none"); property("margin-bottom", "8px")
                            }
                        }

                        emailError?.let {
                            P(attrs = { style { color(Color("#dc2626")); fontSize(13.px); property("margin", "8px 0 0") } }) { Text(it) }
                        }

                        Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd); property("margin-top", "16px") } }) {
                            Button(attrs = {
                                if (emailSending) attr("disabled", "true")
                                onClick { emailFlag = null }
                                style {
                                    padding(10.px, 18.px); borderRadius(8.px)
                                    property("border", "1px solid ${Gray200}"); property("background", "transparent")
                                    color(Gray700); cursor("pointer"); fontWeight(600); fontSize(14.px)
                                }
                            }) { Text("Cancel") }
                            val sendDisabled = emailSending || emailSubject.isBlank() || emailBody.isBlank()
                            Button(attrs = {
                                if (sendDisabled) attr("disabled", "true")
                                onClick {
                                    if (emailSubject.isBlank() || emailBody.isBlank()) return@onClick
                                    emailSending = true; emailError = null
                                    scope.launch {
                                        try {
                                            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                                            val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                                            val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                                            token?.let { headers["Authorization"] = "Bearer $it" }
                                            val payload = kotlin.js.json("toEmail" to recipient, "subject" to emailSubject, "body" to emailBody)
                                            val resp = kotlinx.browser.window.fetch(
                                                "$serverUrl/api/admin/notify-flag-fixed",
                                                kotlin.js.json("method" to "POST", "headers" to headers, "body" to kotlin.js.JSON.stringify(payload)).unsafeCast<org.w3c.fetch.RequestInit>()
                                            ).await()
                                            if (resp.ok) emailSent = true
                                            else emailError = "Send failed (${resp.status}). ${resp.text().await()}"
                                        } catch (e: Exception) {
                                            emailError = e.message ?: "Send failed."
                                        } finally { emailSending = false }
                                    }
                                }
                                style {
                                    padding(10.px, 18.px); borderRadius(8.px); property("border", "none")
                                    property("background", Primary.toString()); color(Color.white)
                                    cursor(if (sendDisabled) "not-allowed" else "pointer"); fontWeight(700); fontSize(14.px)
                                    if (sendDisabled) property("opacity", "0.6")
                                }
                            }) { Text(if (emailSending) "Sending…" else "Send email") }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Engagement View — every like / dislike / comment across the catalog
// ---------------------------------------------------------------------------

/**
 * Reader + moderation view over `analysis_reactions` and `analysis_comments`. Both tables are
 * shared-per-title (not per-user), so this is the only place the whole picture is visible.
 *
 * [registeredUsers] is threaded in purely to resolve `user_id` → email/name: the comment rows
 * carry only the first name snapshotted at post time, which isn't enough to identify a repeat
 * offender. Loads its own data on mount — the dashboard's bulk refresh deliberately doesn't
 * fetch engagement, so the Overview never waits on it.
 */
@Composable
private fun EngagementView(
    registeredUsers: List<RegisteredUser>,
    onNavigate: (DrillDownView) -> Unit
) {
    val scope = rememberCoroutineScope()
    var data by remember { mutableStateOf<EngagementData?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var sortCol by remember { mutableStateOf("total") }
    var sortAsc by remember { mutableStateOf(false) }
    // contentId whose reactor list is expanded (only one at a time — the lists can be long).
    var expandedTitle by remember { mutableStateOf<String?>(null) }
    // Moderation delete: in-flight + already-gone ids so the row updates without waiting on refetch.
    var confirmDelete by remember { mutableStateOf<EngagementComment?>(null) }
    var deletingIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var deletedIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var deleteError by remember { mutableStateOf<String?>(null) }

    fun authHeaders(): dynamic {
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
            catch (_: Exception) { null }
            ?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    val load: () -> Unit = {
        scope.launch {
            loading = true; error = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/engagement",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                when {
                    // The PROD server also serves the exported Kobweb site, so an unmatched
                    // /api/* route falls through to index.html with a 200 — an endpoint that
                    // hasn't been deployed yet looks like success until you read the body.
                    resp.ok && text.trimStart().startsWith("<") ->
                        error = "The server has no /api/admin/engagement endpoint yet — it returned the website instead of JSON. Deploy the server (fly deploy --config fly.toml)."
                    resp.ok -> data = adminJson.decodeFromString<EngagementData>(text)
                    else -> error = "Failed to load (${resp.status}). ${text.take(300)}"
                }
            } catch (e: Exception) {
                error = e.message ?: "Failed to load engagement data"
            }
            loading = false
        }
    }
    LaunchedEffect(Unit) { load() }

    fun deleteComment(comment: EngagementComment) {
        scope.launch {
            deletingIds = deletingIds + comment.id; deleteError = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/comments/${encodeURIComponent(comment.id)}",
                    kotlin.js.json("method" to "DELETE", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) { deletedIds = deletedIds + comment.id; load() }
                else deleteError = "Delete failed (${resp.status}). ${resp.text().await()}".take(200)
            } catch (e: Exception) {
                deleteError = "Delete failed: ${e.message}"
            }
            deletingIds = deletingIds - comment.id
            confirmDelete = null
        }
    }

    // user_id → registered user, so a comment/reaction can be attributed to a real account.
    val userById = remember(registeredUsers) { registeredUsers.associateBy { it.id } }
    fun labelFor(userId: String): String =
        userById[userId]?.let { it.email.ifBlank { it.fullName ?: userId } } ?: "unknown (${userId.take(8)}…)"
    /** Display name on the account, when we have one — reactions carry no snapshotted name of
     *  their own (unlike comments), so this is the only way to put a face to a thumbs up/down. */
    fun nameFor(userId: String): String? = userById[userId]?.fullName?.takeIf { it.isNotBlank() }

    val q = query.trim().lowercase()
    val allComments = (data?.comments ?: emptyList()).filter { it.id !in deletedIds }
    val comments = if (q.isBlank()) allComments else allComments.filter {
        it.title.lowercase().contains(q) || it.body.lowercase().contains(q) ||
            it.firstName.lowercase().contains(q) || labelFor(it.userId).lowercase().contains(q)
    }
    val allTitles = data?.titles ?: emptyList()
    val filteredTitles = if (q.isBlank()) allTitles else allTitles.filter {
        it.title.lowercase().contains(q) || (it.mediaType ?: "").lowercase().contains(q)
    }
    val titles = remember(filteredTitles, sortCol, sortAsc) {
        val sorted = when (sortCol) {
            "title" -> filteredTitles.sortedBy { it.title.lowercase() }
            "type" -> filteredTitles.sortedBy { it.mediaType ?: "" }
            "likes" -> filteredTitles.sortedBy { it.likes }
            "dislikes" -> filteredTitles.sortedBy { it.dislikes }
            "net" -> filteredTitles.sortedBy { it.net }
            "comments" -> filteredTitles.sortedBy { it.comments }
            "last" -> filteredTitles.sortedBy { it.lastActivityAt ?: "" }
            else -> filteredTitles.sortedBy { it.total }
        }
        if (sortAsc) sorted else sorted.reversed()
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Likes, Dislikes & Comments" to null)

        error?.let { msg ->
            Div(attrs = {
                style {
                    property("background", ScoreRedBg.toString()); property("border", "1px solid ${ScoreRed}")
                    borderRadius(12.px); padding(16.px, 20.px); property("margin-bottom", "24px")
                }
            }) {
                P(attrs = { style { color(Color("#b91c1c")); fontSize(14.px); fontWeight(600); property("margin", "0 0 6px") } }) {
                    Text("Couldn't load engagement data")
                }
                P(attrs = { style { color(Gray600); fontSize(13.px); property("margin", "0"); property("word-break", "break-word") } }) {
                    Text(msg)
                }
                // The single most likely cause, and it isn't obvious from a PostgREST error string.
                if (msg.contains("does not exist") || msg.contains("42P01") || msg.contains("PGRST")) {
                    P(attrs = { style { color(Gray700); fontSize(13.px); property("margin", "10px 0 0") } }) {
                        Text("The analysis_comments / analysis_reactions tables are missing — run db/migrations/2026-07-27_analysis_comments_reactions.sql on this environment.")
                    }
                }
            }
        }

        if (loading && data == null) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading engagement data…") }
        } else {
            val d = data
            SummarySection("Engagement") {
                SummaryCard("Likes", (d?.totalLikes ?: 0).toString(), ScoreGreen)
                SummaryCard("Dislikes", (d?.totalDislikes ?: 0).toString(), ScoreRed)
                SummaryCard("Comments", allComments.size.toString(), Primary)
                SummaryCard("Titles Engaged", allTitles.size.toString(), Accent)
            }

            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap"); property("margin-bottom", "16px") } }) {
                TextInput(query) {
                    onInput { query = it.value }
                    style {
                        property("flex", "1"); property("min-width", "240px")
                        padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                    }
                    attr("placeholder", "Filter by title, comment text, or user…")
                }
                if (query.isNotBlank()) {
                    Span(attrs = {
                        onClick { query = "" }
                        style {
                            padding(10.px, 16.px); borderRadius(8.px); backgroundColor(Color("#f1f5f9"))
                            color(Gray600); fontSize(14.px); fontWeight(600); cursor("pointer")
                        }
                    }) { Text("Clear") }
                }
            }

            // ── Comments ──────────────────────────────────────────────────────
            CardSection("Comments (${comments.size})", load) {
                deleteError?.let {
                    P(attrs = { style { color(Color("#dc2626")); fontSize(13.px); property("margin", "0 0 12px") } }) { Text(it) }
                }
                if (comments.isEmpty()) {
                    P(attrs = { style { color(Gray400); fontSize(14.px) } }) {
                        Text(if (allComments.isEmpty()) "No comments yet." else "No comments match that filter.")
                    }
                } else {
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Title")
                                AdminTh("Author")
                                AdminTh("Comment")
                                AdminTh("Posted")
                                AdminTh("")
                            }
                        }
                        Tbody {
                            comments.forEachIndexed { index, comment ->
                                Tr(attrs = {
                                    style {
                                        if (index % 2 == 1) backgroundColor(Gray50)
                                        property("border-bottom", "1px solid ${Gray200}")
                                    }
                                }) {
                                    Td(attrs = { style { padding(12.px, 16.px); property("min-width", "180px") } }) {
                                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                                            RowCover(comment.title, comment.mediaType)
                                            Div {
                                                ClickableText(comment.title) {
                                                    onNavigate(DrillDownView.TitleDetail(comment.title, comment.mediaType, comment.year))
                                                }
                                                Div(attrs = { style { color(Gray400); fontSize(12.px) } }) {
                                                    Text(listOfNotNull(comment.mediaType, comment.year).joinToString(" · ").ifBlank { "—" })
                                                }
                                            }
                                        }
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); fontSize(13.px); property("min-width", "150px") } }) {
                                        Div(attrs = { style { color(Gray900); fontWeight(600) } }) {
                                            Text(comment.firstName.ifBlank { "Anonymous" })
                                        }
                                        Div(attrs = { style { color(Gray500); fontSize(12.px); property("word-break", "break-all") } }) {
                                            Text(labelFor(comment.userId))
                                        }
                                    }
                                    Td(attrs = {
                                        style {
                                            padding(12.px, 16.px); color(Gray700); fontSize(13.px)
                                            property("max-width", "460px"); property("white-space", "pre-wrap")
                                            property("word-break", "break-word"); property("line-height", "1.5")
                                        }
                                    }) { Text(comment.body) }
                                    Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px); property("white-space", "nowrap") } }) {
                                        Text(formatIsoDate(comment.createdAt))
                                        if (!comment.editedAt.isNullOrBlank()) {
                                            Div(attrs = { style { color(Accent); fontSize(11.px) } }) { Text("edited") }
                                        }
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                                        val busy = comment.id in deletingIds
                                        Button(attrs = {
                                            if (busy) attr("disabled", "true")
                                            onClick { confirmDelete = comment }
                                            style {
                                                padding(6.px, 14.px); borderRadius(8.px)
                                                property("border", "1px solid #dc2626"); property("background", "transparent")
                                                color(Color("#dc2626")); cursor(if (busy) "default" else "pointer")
                                                fontWeight(600); fontSize(13.px); property("white-space", "nowrap")
                                                if (busy) property("opacity", "0.6")
                                            }
                                            attr("title", "Remove this comment for everyone")
                                        }) { Text(if (busy) "…" else "Delete") }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // ── Reactions by title ────────────────────────────────────────────
            CardSection("Reactions by Title (${titles.size})", load) {
                if (titles.isEmpty()) {
                    P(attrs = { style { color(Gray400); fontSize(14.px) } }) {
                        Text(if (allTitles.isEmpty()) "No likes, dislikes, or comments yet." else "No titles match that filter.")
                    }
                } else {
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                SortableTh("Title", "title", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                SortableTh("Type", "type", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                SortableTh("👍 Likes", "likes", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                SortableTh("👎 Dislikes", "dislikes", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                SortableTh("Net", "net", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                SortableTh("💬 Comments", "comments", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                                AdminTh("Reacted By")
                                SortableTh("Last Activity", "last", sortCol, sortAsc) { c, a -> sortCol = c; sortAsc = a }
                            }
                        }
                        Tbody {
                            titles.forEachIndexed { index, row ->
                                val isExpanded = expandedTitle == row.contentId
                                Tr(attrs = {
                                    onClick { expandedTitle = if (isExpanded) null else row.contentId }
                                    style {
                                        if (index % 2 == 1) backgroundColor(Gray50)
                                        if (isExpanded) backgroundColor(PrimaryPale)
                                        property("border-bottom", "1px solid ${Gray200}")
                                        cursor("pointer")
                                    }
                                    attr("title", "Click for the full reactor list")
                                }) {
                                    Td(attrs = { style { padding(12.px, 16.px); property("min-width", "200px") } }) {
                                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                                            RowCover(row.title, row.mediaType)
                                            Div {
                                                Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text(row.title) }
                                                row.year?.let {
                                                    Span(attrs = { style { color(Gray400); fontSize(12.px); marginLeft(6.px) } }) { Text("($it)") }
                                                }
                                            }
                                            Span(attrs = { style { color(Gray400); fontSize(11.px) } }) {
                                                Text(if (isExpanded) "▾" else "▸")
                                            }
                                        }
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) } }) {
                                        Text(row.mediaType ?: "-")
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(if (row.likes > 0) ScoreGreen else Gray400) } }) {
                                        Text(row.likes.toString())
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(if (row.dislikes > 0) ScoreRed else Gray400) } }) {
                                        Text(row.dislikes.toString())
                                    }
                                    Td(attrs = {
                                        style {
                                            padding(12.px, 16.px); fontWeight(700)
                                            color(when { row.net > 0 -> ScoreGreen; row.net < 0 -> ScoreRed; else -> Gray400 })
                                        }
                                    }) { Text(if (row.net > 0) "+${row.net}" else row.net.toString()) }
                                    Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(if (row.comments > 0) Primary else Gray400) } }) {
                                        Text(row.comments.toString())
                                    }
                                    // Who reacted, inline. Capped so one popular title can't blow the
                                    // column out — the full list is one click away in the expanded row.
                                    Td(attrs = { style { padding(12.px, 16.px); property("min-width", "180px") } }) {
                                        val reactors = row.likeUserIds.map { true to it } + row.dislikeUserIds.map { false to it }
                                        if (reactors.isEmpty()) {
                                            Span(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("—") }
                                        } else {
                                            Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(4.px) } }) {
                                                reactors.take(REACTOR_CHIP_LIMIT).forEach { (liked, id) ->
                                                    ReactorChip(liked, nameFor(id) ?: labelFor(id), labelFor(id))
                                                }
                                                if (reactors.size > REACTOR_CHIP_LIMIT) {
                                                    Span(attrs = {
                                                        style { color(Primary); fontSize(12.px); fontWeight(600); padding(3.px, 4.px) }
                                                    }) { Text("+${reactors.size - REACTOR_CHIP_LIMIT} more") }
                                                }
                                            }
                                        }
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px); property("white-space", "nowrap") } }) {
                                        Text(formatIsoDate(row.lastActivityAt))
                                    }
                                }
                                if (isExpanded) {
                                    Tr(attrs = { style { property("border-bottom", "1px solid ${Gray200}"); backgroundColor(Color("#fbfdfd")) } }) {
                                        Td(attrs = { attr("colspan", "8"); style { padding(4.px, 16.px, 16.px) } }) {
                                            ReactorList("👍 Liked by", row.likeUserIds, ScoreGreen) { nameFor(it) to labelFor(it) }
                                            ReactorList("👎 Disliked by", row.dislikeUserIds, ScoreRed) { nameFor(it) to labelFor(it) }
                                            if (row.comments > 0) {
                                                Div(attrs = { style { property("margin-top", "10px") } }) {
                                                    ClickableText("View ${row.comments} comment${if (row.comments == 1) "" else "s"} on this title") {
                                                        query = row.title
                                                        kotlinx.browser.window.scrollTo(0.0, 0.0)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Delete confirmation — a moderation delete removes the comment for every user, so it
        // gets an explicit confirm showing exactly what's about to disappear.
        confirmDelete?.let { comment ->
            val busy = comment.id in deletingIds
            Div(attrs = {
                onClick { if (!busy) confirmDelete = null }
                style {
                    property("position", "fixed"); property("inset", "0")
                    property("background", "rgba(15,23,42,0.55)")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    padding(20.px); property("box-sizing", "border-box"); property("z-index", "1100")
                }
            }) {
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    style {
                        property("width", "min(94%, 520px)"); backgroundColor(Color.white)
                        borderRadius(16.px); padding(28.px); property("box-sizing", "border-box")
                        property("box-shadow", CardShadow)
                    }
                }) {
                    H2(attrs = { style { margin(0.px, 0.px, 6.px); fontSize(18.px); fontWeight(700); color(Gray900) } }) {
                        Text("Delete this comment?")
                    }
                    P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 16px") } }) {
                        Text("It disappears for everyone viewing \"${comment.title}\". This cannot be undone.")
                    }
                    Div(attrs = {
                        style {
                            backgroundColor(Gray50); borderRadius(10.px); padding(14.px)
                            property("border", "1px solid ${Gray200}")
                        }
                    }) {
                        Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-bottom", "6px") } }) {
                            Text("${comment.firstName.ifBlank { "Anonymous" }} · ${labelFor(comment.userId)} · ${formatIsoDate(comment.createdAt)}")
                        }
                        Div(attrs = { style { fontSize(14.px); color(Gray700); property("white-space", "pre-wrap"); property("word-break", "break-word") } }) {
                            Text(comment.body)
                        }
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); justifyContent(JustifyContent.FlexEnd); property("margin-top", "20px") } }) {
                        Button(attrs = {
                            if (busy) attr("disabled", "true")
                            onClick { confirmDelete = null }
                            style {
                                padding(10.px, 18.px); borderRadius(8.px)
                                property("border", "1px solid ${Gray200}"); property("background", "transparent")
                                color(Gray700); cursor("pointer"); fontWeight(600); fontSize(14.px)
                            }
                        }) { Text("Cancel") }
                        Button(attrs = {
                            if (busy) attr("disabled", "true")
                            onClick { deleteComment(comment) }
                            style {
                                padding(10.px, 18.px); borderRadius(8.px); property("border", "none")
                                property("background", "#dc2626"); color(Color.white)
                                cursor(if (busy) "default" else "pointer"); fontWeight(700); fontSize(14.px)
                                if (busy) property("opacity", "0.6")
                            }
                        }) { Text(if (busy) "Deleting…" else "Delete comment") }
                    }
                }
            }
        }
    }
}

/** Admin-only breakdown under the public thumbs row: one +1/-1 line per reaction, naming who
 *  cast it. Reactions carry no name of their own, so the ids are resolved against the
 *  registered-users list. */
@Composable
private fun ReactorBreakdown(
    likeUserIds: List<String>,
    dislikeUserIds: List<String>,
    nameFor: (String) -> String?,
    emailFor: (String) -> String,
) {
    Div(attrs = { style { property("margin-top", "14px") } }) {
        Div(attrs = {
            style {
                fontSize(11.px); fontWeight(700); color(Gray500)
                property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                property("margin-bottom", "8px")
            }
        }) { Text("Who reacted (admin only)") }
        (likeUserIds.map { true to it } + dislikeUserIds.map { false to it }).forEach { (liked, id) ->
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                    padding(6.px, 10.px); borderRadius(8.px)
                    backgroundColor(if (liked) ScoreGreenBg else ScoreRedBg)
                    property("border", "1px solid ${if (liked) ScoreGreen else ScoreRed}")
                    property("margin-bottom", "6px")
                }
            }) {
                Span(attrs = {
                    style {
                        fontWeight(800); fontSize(13.px); property("min-width", "26px")
                        color(if (liked) ScoreGreen else ScoreRed)
                    }
                }) { Text(if (liked) "+1" else "\u22121") }
                nameFor(id)?.let {
                    Span(attrs = { style { fontWeight(600); color(Gray900); fontSize(13.px) } }) { Text(it) }
                }
                Span(attrs = { style { color(Gray500); fontSize(12.px); property("word-break", "break-all") } }) {
                    Text(emailFor(id))
                }
            }
        }
    }
}
private const val REACTOR_CHIP_LIMIT = 3

/** A single 👍/👎 + name pill for the "Reacted By" column. [tooltip] carries the email, so a
 *  chip showing only a display name still identifies the account on hover. */
@Composable
private fun ReactorChip(liked: Boolean, name: String, tooltip: String) {
    Span(attrs = {
        attr("title", tooltip)
        style {
            property("display", "inline-flex"); alignItems(AlignItems.Center); gap(4.px)
            padding(3.px, 8.px); borderRadius(999.px)
            backgroundColor(if (liked) ScoreGreenBg else ScoreRedBg)
            property("border", "1px solid ${if (liked) ScoreGreen else ScoreRed}")
            fontSize(12.px); color(Gray700); property("white-space", "nowrap")
        }
    }) {
        Text(if (liked) "👍" else "👎")
        Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text(name) }
    }
}

/** One "liked by / disliked by" line of user chips inside an expanded engagement row.
 *  [resolve] maps a user id to (account name or null, email/fallback label). */
@Composable
private fun ReactorList(
    label: String,
    userIds: List<String>,
    accent: CSSColorValue,
    resolve: (String) -> Pair<String?, String>
) {
    if (userIds.isEmpty()) return
    Div(attrs = { style { property("margin-top", "10px") } }) {
        Div(attrs = { style { fontSize(12.px); fontWeight(700); color(accent); property("margin-bottom", "6px") } }) {
            Text("$label (${userIds.size})")
        }
        Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(6.px) } }) {
            userIds.forEach { id ->
                val (name, email) = resolve(id)
                Span(attrs = {
                    style {
                        property("display", "inline-flex"); alignItems(AlignItems.Center); gap(6.px)
                        padding(4.px, 10.px); borderRadius(999.px)
                        backgroundColor(Color.white); property("border", "1px solid ${Gray200}")
                        fontSize(12.px); color(Gray700)
                    }
                }) {
                    if (name != null) {
                        Span(attrs = { style { fontWeight(600); color(Gray900) } }) { Text(name) }
                        Span(attrs = { style { color(Gray300) } }) { Text("·") }
                    }
                    Span(attrs = { style { color(Gray500) } }) { Text(email) }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Resolve Flag View — search the correct title, re-run, replace the user's analysis
// ---------------------------------------------------------------------------

@Composable
private fun ResolveFlagView(
    flag: FlagEntry,
    onRefresh: (() -> Unit)?,
    onNavigate: (DrillDownView) -> Unit
) {
    val scope = rememberCoroutineScope()
    var query by remember { mutableStateOf(flag.title) }
    var candidates by remember { mutableStateOf<List<DisambItem>>(emptyList()) }
    var selected by remember { mutableStateOf<DisambItem?>(null) }
    var replaceMode by remember { mutableStateOf("adopt") }
    // Books: the author the re-run searches for ("Title by Author"). /api/disambiguate returns no
    // author field, only an info line like "Horror novel by K.R. Alexander", so this is prefilled
    // from that and editable. Without it Escape (2022) was re-researched as plain "Escape" and
    // blended James Patterson's book with K. R. Alexander's again.
    var authorInput by remember { mutableStateOf("") }
    var searching by remember { mutableStateOf(false) }
    var running by remember { mutableStateOf(false) }
    var accepting by remember { mutableStateOf(false) }
    var errorMsg by remember { mutableStateOf<String?>(null) }
    var done by remember { mutableStateOf(false) }
    // After Re-run: holds the fresh result JSON string + parsed object for preview.
    var freshResultJson by remember { mutableStateOf<String?>(null) }
    var freshResult by remember { mutableStateOf<com.islamicmedia.shared.domain.model.AnalysisResult?>(null) }
    var resolvedResult by remember { mutableStateOf<com.islamicmedia.shared.domain.model.AnalysisResult?>(null) }
    var oldResult by remember { mutableStateOf<com.islamicmedia.shared.domain.model.AnalysisResult?>(null) }

    fun authHeaders(): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        token?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun runSearch() {
        val q = query.trim()
        if (q.isEmpty()) return
        scope.launch {
            searching = true; errorMsg = null; candidates = emptyList(); selected = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/disambiguate",
                    kotlin.js.json(
                        "method" to "POST",
                        "headers" to authHeaders(),
                        "body" to kotlin.js.JSON.stringify(kotlin.js.json("query" to q))
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    val text = resp.text().await().toString()
                    candidates = try {
                        adminJson.decodeFromString<List<DisambItem>>(text)
                    } catch (_: Exception) {
                        try { adminJson.decodeFromString<DisambResponse>(text).items } catch (_: Exception) { emptyList() }
                    }
                    if (candidates.isEmpty()) errorMsg = "No matches found for \"$q\"."
                } else {
                    errorMsg = "Search failed (${resp.status})."
                }
            } catch (e: Exception) {
                errorMsg = e.message ?: "Search failed."
            } finally { searching = false }
        }
    }

    // Step 1: bust cache + fresh analysis → preview only, no side effects.
    fun runRerun() {
        val sel = selected ?: return
        scope.launch {
            running = true; errorMsg = null; freshResult = null; freshResultJson = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val payload = kotlin.js.json(
                    "title" to sel.title,
                    "mediaType" to (sel.mediaType ?: flag.mediaType ?: ""),
                    "year" to (sel.year ?: ""),
                    "info" to (sel.info ?: ""),
                    "author" to authorInput.trim(),
                    "quickCheck" to flag.quickCheck,
                    "criteriaJson" to (flag.criteriaJson ?: "")
                )
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/rerun-analysis",
                    kotlin.js.json(
                        "method" to "POST",
                        "headers" to authHeaders(),
                        "body" to kotlin.js.JSON.stringify(payload)
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    val text = resp.text().await().toString()
                    try {
                        val parsed = adminJson.decodeFromString<ResolveResponse>(text)
                        freshResult = parsed.result
                        // Extract the raw result JSON to pass back on accept.
                        freshResultJson = try {
                            val obj = kotlin.js.JSON.parse<dynamic>(text)
                            kotlin.js.JSON.stringify(obj.result)
                        } catch (_: Exception) { null }
                    } catch (_: Exception) { freshResult = null; freshResultJson = null }
                    if (freshResult == null) errorMsg = "Re-run succeeded but result couldn't be parsed."
                } else {
                    errorMsg = "Re-run failed (${resp.status}). ${resp.text().await()}"
                }
            } catch (e: Exception) {
                errorMsg = e.message ?: "Re-run failed."
            } finally { running = false }
        }
    }

    // Step 2: commit the pre-run result — replace user history + mark flag resolved.
    fun runAccept() {
        val sel = selected ?: return
        val rJson = freshResultJson ?: return
        scope.launch {
            accepting = true; errorMsg = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val payload = kotlin.js.json(
                    "flagId" to (flag.id ?: ""),
                    "userEmail" to (flag.userEmail ?: ""),
                    "originalTitle" to flag.title,
                    "correctTitle" to sel.title,
                    "correctMediaType" to (sel.mediaType ?: flag.mediaType ?: ""),
                    "replaceMode" to replaceMode,
                    "resultJson" to rJson
                )
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/resolve-flag",
                    kotlin.js.json(
                        "method" to "POST",
                        "headers" to authHeaders(),
                        "body" to kotlin.js.JSON.stringify(payload)
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    try {
                        val parsed = adminJson.decodeFromString<ResolveResponse>(resp.text().await().toString())
                        resolvedResult = parsed.result
                        oldResult = parsed.oldResult
                    } catch (_: Exception) { resolvedResult = freshResult; oldResult = null }
                    done = true
                    onRefresh?.invoke()
                } else {
                    errorMsg = "Accept failed (${resp.status}). ${resp.text().await()}"
                }
            } catch (e: Exception) {
                errorMsg = e.message ?: "Accept failed."
            } finally { accepting = false }
        }
    }

    PageShell {
        BreadcrumbNav(
            "Dashboard" to { onNavigate(DrillDownView.Overview) },
            "Flagged Analyses" to { onNavigate(DrillDownView.FlaggedAnalyses) },
            "Resolve" to null
        )

        CardSection("Resolve flag: ${flag.title}") {
            if (done) {
                P(attrs = { style { color(Color("#15803d")); fontSize(15.px); fontWeight(600); property("margin", "0 0 16px") } }) {
                    Text("Resolved. The corrected analysis has been written to the shared cache" +
                        (if (flag.userEmail != null) " and the user's history." else "."))
                }
                // Side-by-side: the original (flagged) analysis vs. the corrected one.
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); gap(16.px); property("flex-wrap", "wrap"); property("align-items", "flex-start")
                    }
                }) {
                    Div(attrs = { style { property("flex", "1 1 280px"); property("min-width", "0") } }) {
                        Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.4px"); property("margin-bottom", "8px") } }) {
                            Text("Before — flagged")
                        }
                        oldResult?.let { ResolvedResultPreview(it) }
                            ?: Div(attrs = {
                                style {
                                    property("border", "1px dashed ${Gray300}"); borderRadius(12.px)
                                    padding(20.px); color(Gray500); fontSize(14.px)
                                }
                            }) { Text("Original analysis not available" + (if (flag.userEmail == null) " (anonymous flag)." else " (no stored history row).")) }
                    }
                    Div(attrs = { style { property("flex", "1 1 280px"); property("min-width", "0") } }) {
                        Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Color("#15803d")); property("text-transform", "uppercase"); property("letter-spacing", "0.4px"); property("margin-bottom", "8px") } }) {
                            Text("After — corrected")
                        }
                        resolvedResult?.let { ResolvedResultPreview(it) }
                            ?: P(attrs = { style { color(Gray500); fontSize(14.px) } }) {
                                Text("(The analysis ran, but the result couldn't be parsed for preview.)")
                            }
                    }
                }
                Button(attrs = {
                    onClick { onNavigate(DrillDownView.FlaggedAnalyses) }
                    style {
                        padding(10.px, 18.px); borderRadius(8.px); property("border", "none")
                        property("background", Primary.toString()); color(Color.white)
                        cursor("pointer"); fontWeight(600); fontSize(14.px); property("margin-top", "16px")
                    }
                }) { Text("Back to Flagged Analyses") }
                return@CardSection
            }

            // Flag summary
            P(attrs = { style { color(Gray600); fontSize(14.px); property("margin", "0 0 6px") } }) {
                Text("Flagged by ${flag.userEmail ?: "an anonymous user"} • ${flag.mediaType ?: "?"} • " +
                    "${if (flag.quickCheck) "Quick check" else "Full"} • score ${flag.score ?: "-"}")
            }
            flag.message?.takeIf { it.isNotBlank() }?.let {
                P(attrs = { style { color(Gray700); fontSize(14.px); property("font-style", "italic"); property("margin", "0 0 16px") } }) {
                    Text("\"$it\"")
                }
            }

            // Step 1 — find the correct title
            P(attrs = { style { color(Gray900); fontWeight(700); fontSize(14.px); property("margin", "8px 0 8px") } }) {
                Text("1. Find the correct title")
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "16px") } }) {
                org.jetbrains.compose.web.dom.TextInput(query) {
                    onInput { query = it.value }
                    onKeyUp { if (it.key == "Enter") runSearch() }
                    style {
                        property("flex", "1"); padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid ${Gray300}"); fontSize(14.px); property("outline", "none")
                    }
                }
                Button(attrs = {
                    if (searching) attr("disabled", "true")
                    onClick { runSearch() }
                    style {
                        padding(10.px, 18.px); borderRadius(8.px); property("border", "none")
                        property("background", Primary.toString()); color(Color.white)
                        cursor("pointer"); fontWeight(600); fontSize(14.px)
                        if (searching) property("opacity", "0.6")
                    }
                }) { Text(if (searching) "Searching…" else "Search") }
            }

            // Candidate list
            if (candidates.isNotEmpty()) {
                Div(attrs = { style { property("margin-bottom", "20px"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
                    candidates.forEach { item ->
                        val isSel = selected == item
                        Div(attrs = {
                            onClick {
                                selected = item; freshResult = null; freshResultJson = null; errorMsg = null
                                authorInput = item.author ?: authorFromInfo(item.info) ?: ""
                            }
                            style {
                                padding(12.px, 14.px); borderRadius(10.px); cursor("pointer")
                                property("border", if (isSel) "2px solid ${Primary}" else "1px solid ${Gray200}")
                                property("background", if (isSel) PrimaryPale.toString() else "white")
                            }
                        }) {
                            Div(attrs = { style { fontWeight(600); color(Gray900); fontSize(14.px) } }) {
                                Text(item.title + (item.year?.let { " ($it)" } ?: ""))
                            }
                            Div(attrs = { style { color(Gray500); fontSize(13.px) } }) {
                                Text(listOfNotNull(item.mediaType, item.author?.let { "by $it" }, item.info).joinToString(" • "))
                            }
                        }
                    }
                }
            }

            // Step 2 — replace mode
            if (selected != null) {
                if ((selected!!.mediaType ?: flag.mediaType ?: "").lowercase().startsWith("book")) {
                    P(attrs = { style { color(Gray900); fontWeight(700); fontSize(14.px); property("margin", "8px 0 6px") } }) {
                        Text("Author (the re-run searches for this exact book)")
                    }
                    org.jetbrains.compose.web.dom.TextInput(authorInput) {
                        onInput { authorInput = it.value }
                        attr("placeholder", "e.g. K. R. Alexander")
                        style {
                            width(100.percent); padding(10.px, 12.px); borderRadius(8.px)
                            property("border", "1px solid ${Gray200}"); fontSize(14.px)
                            property("margin-bottom", "14px"); property("box-sizing", "border-box")
                        }
                    }
                }
                P(attrs = { style { color(Gray900); fontWeight(700); fontSize(14.px); property("margin", "8px 0 8px") } }) {
                    Text("2. How should the user's history entry look?")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); property("margin-bottom", "20px") } }) {
                    ResolveModeOption("adopt", replaceMode, "Adopt the corrected title",
                        "Their entry becomes \"${selected!!.title}\" with the new analysis.") { replaceMode = it }
                    ResolveModeOption("keep", replaceMode, "Keep their original title",
                        "Keep \"${flag.title}\" but swap in the corrected analysis content.") { replaceMode = it }
                }

                Button(attrs = {
                    if (running) attr("disabled", "true")
                    onClick { runRerun() }
                    style {
                        padding(12.px, 22.px); borderRadius(10.px); property("border", "none")
                        property("background", Primary.toString()); color(Color.white)
                        cursor("pointer"); fontWeight(700); fontSize(15.px)
                        if (running) property("opacity", "0.6")
                    }
                }) { Text(if (running) "Running fresh analysis… (up to a minute)" else "Re-run") }
            }

            // Fresh result preview — shown after Re-run, before Accept.
            if (freshResult != null) {
                Div(attrs = { style { property("margin-top", "24px") } }) {
                    Div(attrs = {
                        style {
                            fontSize(13.px); fontWeight(700); color(Primary)
                            property("text-transform", "uppercase"); property("letter-spacing", "0.4px")
                            property("margin-bottom", "10px")
                        }
                    }) { Text("Fresh result — review before accepting") }
                    ResolvedResultPreview(freshResult!!)

                    Div(attrs = { style { property("margin-top", "20px") } }) {
                        P(attrs = { style { color(Gray900); fontWeight(700); fontSize(14.px); property("margin", "0 0 10px") } }) {
                            Text("How should the user's history entry look?")
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); property("margin-bottom", "16px") } }) {
                            ResolveModeOption("adopt", replaceMode, "Adopt the corrected title",
                                "Their entry becomes \"${selected!!.title}\" with the new analysis.") { replaceMode = it }
                            ResolveModeOption("keep", replaceMode, "Keep their original title",
                                "Keep \"${flag.title}\" but swap in the corrected analysis content.") { replaceMode = it }
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); alignItems(AlignItems.Center) } }) {
                            Button(attrs = {
                                if (accepting) attr("disabled", "true")
                                onClick { runAccept() }
                                style {
                                    padding(12.px, 22.px); borderRadius(10.px); property("border", "none")
                                    property("background", "#15803d"); color(Color.white)
                                    cursor("pointer"); fontWeight(700); fontSize(15.px)
                                    if (accepting) property("opacity", "0.6")
                                }
                            }) { Text(if (accepting) "Saving…" else "Accept and replace") }
                            Span(attrs = {
                                onClick { if (!running) runRerun() }
                                style {
                                    fontSize(14.px); color(Gray500); cursor("pointer")
                                    property("text-decoration", "underline")
                                }
                            }) { Text("Re-run again") }
                        }
                    }
                }
            }

            errorMsg?.let {
                P(attrs = { style { color(Color("#dc2626")); fontSize(14.px); property("margin", "16px 0 0") } }) { Text(it) }
            }
        }
    }
}

@Composable
private fun ResolveModeOption(
    value: String,
    selected: String,
    title: String,
    subtitle: String,
    onSelect: (String) -> Unit
) {
    val isSel = value == selected
    Div(attrs = {
        onClick { onSelect(value) }
        style {
            padding(12.px, 14.px); borderRadius(10.px); cursor("pointer")
            property("border", if (isSel) "2px solid ${Primary}" else "1px solid ${Gray200}")
            property("background", if (isSel) PrimaryPale.toString() else "white")
        }
    }) {
        Div(attrs = { style { fontWeight(600); color(Gray900); fontSize(14.px) } }) { Text(title) }
        Div(attrs = { style { color(Gray500); fontSize(13.px) } }) { Text(subtitle) }
    }
}

/** Read-only summary of a freshly-run AnalysisResult, shown on the resolve success screen. */
@Composable
private fun ResolvedResultPreview(result: com.islamicmedia.shared.domain.model.AnalysisResult) {
    val score = result.score
    val verdictColor = when {
        score >= 8 -> "#15803d"
        score >= 5 -> "#b45309"
        else -> "#dc2626"
    }
    val verdictLabel = when {
        score >= 8 -> "Safe"
        score >= 5 -> "Caution"
        else -> "Not Safe"
    }
    Div(attrs = {
        style {
            property("border", "1px solid ${Gray200}"); borderRadius(12.px)
            property("background", Gray50.toString()); padding(20.px)
        }
    }) {
        // Score + verdict
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px); property("margin-bottom", "12px") } }) {
            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock); padding(4.px, 12.px); borderRadius(999.px)
                    property("background", verdictColor); color(Color.white); fontWeight(700); fontSize(14.px)
                }
            }) { Text("$score/10 · $verdictLabel") }
            if (result.quickCheck) {
                Span(attrs = { style { color(Gray500); fontSize(13.px) } }) { Text("Quick check") }
            }
            result.islamicScore?.let {
                Span(attrs = { style { color(Gray500); fontSize(13.px) } }) { Text("Islamic: $it/10") }
            }
            result.developmentalScore?.let {
                Span(attrs = { style { color(Gray500); fontSize(13.px) } }) { Text("Developmental: $it/10") }
            }
        }

        if (result.recommendation.isNotBlank()) {
            Div(attrs = { style { color(Gray900); fontSize(14.px); fontWeight(600); property("margin-bottom", "4px") } }) { Text("Recommendation") }
            P(attrs = { style { color(Gray700); fontSize(14.px); property("margin", "0 0 12px"); property("line-height", "1.5") } }) { Text(result.recommendation) }
        }
        result.summary?.takeIf { it.isNotBlank() }?.let {
            P(attrs = { style { color(Gray600); fontSize(13.px); property("margin", "0 0 12px"); property("line-height", "1.5") } }) { Text(it) }
        }

        if (result.concerns.isNotEmpty()) {
            Div(attrs = { style { color(Gray900); fontSize(13.px); fontWeight(700); property("margin-bottom", "8px") } }) { Text("Concerns (${result.concerns.size})") }
            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px); property("margin-bottom", "12px") } }) {
                result.concerns.forEach { concern ->
                    val sevColor = when (concern.severity.lowercase()) {
                        "high", "severe" -> "#dc2626"
                        "moderate", "medium" -> "#b45309"
                        else -> "#15803d"
                    }
                    Div(attrs = {
                        style {
                            property("border", "1px solid ${Gray200}"); borderRadius(8.px)
                            property("background", "white"); padding(10.px, 12.px)
                        }
                    }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); property("margin-bottom", "2px") } }) {
                            Span(attrs = { style { fontWeight(600); color(Gray900); fontSize(14.px) } }) { Text(concern.category) }
                            Span(attrs = {
                                style {
                                    fontSize(11.px); fontWeight(700); property("color", sevColor)
                                    property("text-transform", "uppercase")
                                }
                            }) { Text(concern.severity) }
                        }
                        if (concern.description.isNotBlank()) {
                            Div(attrs = { style { color(Gray600); fontSize(13.px); property("line-height", "1.45") } }) { Text(concern.description) }
                        }
                    }
                }
            }
        }

        if (result.positiveAspects.isNotEmpty()) {
            Div(attrs = { style { color(Gray900); fontSize(13.px); fontWeight(700); property("margin-bottom", "6px") } }) { Text("Positive aspects") }
            Ul(attrs = { style { property("margin", "0"); property("padding-left", "20px"); color(Gray700); fontSize(13.px) } }) {
                result.positiveAspects.forEach { Li { Text(it) } }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// User Detail View
// ---------------------------------------------------------------------------

@Composable
private fun UserDetailView(
    data: DashboardData,
    view: DrillDownView.UserDetail,
    registeredUsers: List<RegisteredUser>,
    onNavigate: (DrillDownView) -> Unit
) {
    val userEvents = data.allAnalyses.filter {
        (it.userEmail != null && it.userEmail == view.userEmail) ||
        (it.userId != null && it.userId == view.userId)
    }
    val scores = userEvents.mapNotNull { it.score }
    val islamicScores = userEvents.mapNotNull { it.islamicScore }
    val profile = registeredUsers.firstOrNull { u ->
        (view.userEmail != null && u.email.equals(view.userEmail, ignoreCase = true)) ||
        (view.userId != null && u.id.isNotBlank() && u.id == view.userId)
    }
    val displayName = profile?.fullName?.takeIf { it.isNotBlank() } ?: view.userEmail ?: "Unknown User"

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, displayName to null)

        // Profile header
        UserProfileHeader(profile, view.userEmail ?: view.userId ?: "Unknown User")

        // Summary cards
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            SummaryCard("Total Searches", userEvents.size.toString(), Primary)
            SummaryCard("Avg Score", scores.averageOrNull()?.let { formatDouble(it, 1) } ?: "-", Accent)
            SummaryCard("Avg Islamic", islamicScores.averageOrNull()?.let { formatDouble(it, 1) } ?: "-", PrimaryLight)
            val userTotalCost = userEvents.sumOf { it.costUsd ?: 0.0 }
            SummaryCard("Total Cost", "$${formatCost(userTotalCost)}", Color("#b45309"))
        }

        // Date range
        val firstSearch = userEvents.minByOrNull { it.createdAt ?: "" }?.createdAt
        val lastSearch = userEvents.maxByOrNull { it.createdAt ?: "" }?.createdAt
        if (firstSearch != null) {
            CardSection("Activity Period") {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(32.px)
                        fontSize(14.px)
                    }
                }) {
                    Div {
                        Span(attrs = { style { color(Gray500); fontWeight(600) } }) { Text("First search: ") }
                        Span(attrs = { style { color(Gray900) } }) { Text(formatIsoDate(firstSearch)) }
                    }
                    Div {
                        Span(attrs = { style { color(Gray500); fontWeight(600) } }) { Text("Last search: ") }
                        Span(attrs = { style { color(Gray900) } }) { Text(formatIsoDate(lastSearch)) }
                    }
                }
            }
        }

        // All searches
        CardSection("All Searches (${userEvents.size})") {
            if (userEvents.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No searches found.") }
            } else {
                AnalysesTable(
                    events = userEvents,
                    showUser = false,
                    showTitle = true,
                    showType = true,
                    showConcerns = true,
                    onNavigate = onNavigate
                )
            }
        }
    }
}

/** Profile header for the user-detail screen: avatar, name, email, and account metadata. */
@Composable
private fun UserProfileHeader(user: RegisteredUser?, fallbackEmail: String) {
    val email = user?.email?.takeIf { it.isNotBlank() } ?: fallbackEmail
    val name = user?.fullName?.takeIf { it.isNotBlank() }
    val planNames = rememberPlanNames()

    Div(attrs = {
        style {
            property("background", "white")
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", CardShadow)
            property("border", CardBorder)
            property("margin-bottom", "24px")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(20.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        val avatar = user?.avatarUrl?.takeIf { it.isNotBlank() }
        if (avatar != null) {
            Img(src = avatar, alt = name ?: email, attrs = {
                attr("referrerpolicy", "no-referrer")
                style {
                    width(72.px); height(72.px)
                    borderRadius(50.percent)
                    property("object-fit", "cover")
                    property("flex-shrink", "0")
                    property("border", "2px solid ${PrimaryPale}")
                }
            })
        } else {
            Div(attrs = {
                style {
                    width(72.px); height(72.px)
                    borderRadius(50.percent)
                    backgroundColor(Primary)
                    color(Color.white)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(26.px); fontWeight(700)
                    property("flex-shrink", "0")
                }
            }) { Text(profileInitials(name, email)) }
        }

        Div(attrs = { style { property("flex", "1"); property("min-width", "240px") } }) {
            H1(attrs = { style { fontSize(22.px); fontWeight(700); color(Gray900); property("margin", "0 0 4px") } }) {
                Text(name ?: email)
            }
            if (name != null) {
                P(attrs = { style { fontSize(14.px); color(Gray500); property("margin", "0 0 10px") } }) { Text(email) }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                user?.provider?.let { ProfileBadge(providerLabel(it), PrimaryPale, Primary) }
                if (user != null) {
                    val (mLabel, mFg, mBg) = membershipStyle(user.membershipStatus)
                    ProfileBadge(mLabel, mBg, mFg)
                }
                when {
                    user?.emailConfirmed == true -> ProfileBadge("✓ Email confirmed", ScoreGreenBg, ScoreGreen)
                    user != null -> ProfileBadge("Email unconfirmed", ScoreAmberBg, ScoreAmber)
                }
                user?.approvedSource?.let { ProfileBadge("via $it", AccentLight, Accent) }
            }
        }
    }

    if (user != null) {
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            ProfileField("User ID", user.id.takeIf { it.isNotBlank() } ?: "—")
            ProfileField("Joined", formatIsoDate(user.createdAt))
            ProfileField("Last sign-in", user.lastSignIn?.let { formatIsoDate(it) } ?: "—")
            ProfileField("Last platform", listOfNotNull(user.lastPlatform, user.lastAppVersion?.let { "v$it" }).joinToString(" ").ifBlank { "—" })
            ProfileField("Plan", user.plan?.let { key -> planNames[key]?.let { "$it ($key)" } ?: key } ?: "—")
            ProfileField("Credits", if (user.isUnlimited) "Unlimited" else "Full ${user.fullCredits} · QC ${user.qcCredits}")
            user.approvedAt?.let { ProfileField("Approved", formatIsoDate(it)) }
        }
    }
}

/** Small rounded pill used in the profile header. */
@Composable
private fun ProfileBadge(label: String, bg: CSSColorValue, fg: CSSColorValue) {
    Span(attrs = {
        style {
            backgroundColor(bg)
            color(fg)
            fontSize(12.px)
            fontWeight(600)
            padding(4.px, 10.px)
            borderRadius(999.px)
            property("white-space", "nowrap")
        }
    }) { Text(label) }
}

/** Label/value stacked field in a subtle card. */
@Composable
private fun ProfileField(label: String, value: String) {
    Div(attrs = {
        style {
            backgroundColor(Gray50)
            borderRadius(12.px)
            padding(14.px, 16.px)
            property("border", CardBorder)
        }
    }) {
        Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.04em"); property("margin-bottom", "4px") } }) { Text(label) }
        Div(attrs = { style { fontSize(14.px); color(Gray900); property("word-break", "break-word") } }) { Text(value) }
    }
}

private fun profileInitials(name: String?, email: String): String {
    val n = name?.trim().orEmpty()
    if (n.isNotEmpty()) {
        val parts = n.split(Regex("\\s+")).filter { it.isNotBlank() }
        return when {
            parts.size >= 2 -> "${parts[0].first()}${parts[1].first()}".uppercase()
            else -> parts[0].take(2).uppercase()
        }
    }
    return email.trim().firstOrNull()?.uppercase() ?: "?"
}

private fun providerLabel(provider: String): String = when (provider.lowercase()) {
    "google" -> "Google"
    "apple" -> "Apple"
    "email" -> "Email"
    else -> provider.replaceFirstChar { it.uppercase() }
}

private fun membershipStyle(status: String): Triple<String, CSSColorValue, CSSColorValue> = when (status.lowercase()) {
    "active" -> Triple("Active", ScoreGreen, ScoreGreenBg)
    "legacy" -> Triple("Legacy unlimited", Primary, PrimaryPale)
    "gifted" -> Triple("Gifted", ScoreGreen, ScoreGreenBg)
    "past_due" -> Triple("Past due", ScoreAmber, ScoreAmberBg)
    "canceling" -> Triple("Canceling", ScoreAmber, ScoreAmberBg)
    "canceled" -> Triple("Canceled", ScoreRed, ScoreRedBg)
    else -> Triple("Free", Gray600, Gray100)
}

// ---------------------------------------------------------------------------
// Title Detail View
// ---------------------------------------------------------------------------

@Composable
private fun TitleDetailView(
    data: DashboardData,
    view: DrillDownView.TitleDetail,
    registeredUsers: List<RegisteredUser>,
    onNavigate: (DrillDownView) -> Unit
) {
    val titleEvents = data.allAnalyses.filter { it.title == view.title }
    val scores = titleEvents.mapNotNull { it.score }
    val islamicScores = titleEvents.mapNotNull { it.islamicScore }
    val uniqueUsers = titleEvents.mapNotNull { it.userEmail }.distinct()
    val mediaType = titleEvents.firstOrNull()?.mediaType

    PageShell {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween); property("flex-wrap", "wrap"); gap(8.px) } }) {
            BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, view.title to null)
            Span(attrs = {
                style {
                    padding(8.px, 16.px); borderRadius(8.px); fontSize(13.px); fontWeight(700); cursor("pointer")
                    backgroundColor(Color("#0891b2")); color(Color.white)
                }
                onClick { onNavigate(DrillDownView.EditAnalyses(view.title, view.mediaType ?: mediaType, view.year)) }
            }) { Text("🎚️ Edit this analysis") }
        }

        // This is a READ-ONLY preview — the exact user-facing result (cover + synthesized verdict +
        // TL;DR highlights + alternatives), rendered with the same component the public site uses.
        // The cover shown is the one on the result itself (result.coverUrl) — i.e. exactly what a
        // user sees, not the admin thumbnail map. To edit anything here, use "Edit this analysis" above.
        TitleResultPreview(view.title, view.mediaType ?: mediaType, view.year, registeredUsers)

        // Summary cards
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            SummaryCard("Times Searched", titleEvents.size.toString(), Primary)
            SummaryCard("Unique Users", uniqueUsers.size.toString(), PrimaryLight)
            SummaryCard("Avg Score", scores.averageOrNull()?.let { formatDouble(it, 1) } ?: "-", Accent)
            val titleTotalCost = titleEvents.sumOf { it.costUsd ?: 0.0 }
            SummaryCard("Total Cost", "$${formatCost(titleTotalCost)}", Color("#b45309"))
        }

        // How users reached this title \u2014 counts of each search source across all its analyses.
        val bySource = titleEvents.groupingBy { it.searchSource?.ifBlank { null } }.eachCount()
            .entries.sortedByDescending { it.value }
        if (bySource.isNotEmpty()) {
            CardSection("Searched Via") {
                Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(10.px) } }) {
                    bySource.forEach { (source, count) ->
                        val (bg, fg) = sourceColors(source)
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                                backgroundColor(Gray50); borderRadius(10.px); padding(8.px, 12.px)
                                property("border", "1px solid ${Gray200}")
                            }
                        }) {
                            Span(attrs = {
                                style {
                                    fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                    borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                    property("white-space", "nowrap")
                                    backgroundColor(bg); color(fg)
                                }
                            }) { Text(sourceLabel(source)) }
                            Span(attrs = { style { fontWeight(700); fontSize(14.px); color(Gray900) } }) { Text("\u00D7$count") }
                        }
                    }
                }
            }
        }

        // Media type info
        if (mediaType != null) {
            CardSection("Media Info") {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(16.px); alignItems(AlignItems.Center) } }) {
                    Span(attrs = { style { color(Gray500); fontWeight(600); fontSize(14.px) } }) { Text("Type:") }
                    ClickableMediaTypeBadge(mediaType) {
                        onNavigate(DrillDownView.MediaTypeDetail(mediaType))
                    }
                }
            }
        }

        // Search criteria overview (custom concerns + child ages across all searches)
        val allCriteria = titleEvents.mapNotNull { event ->
            event.criteriaJson?.let { json ->
                try { adminJson.decodeFromString<SearchCriteria>(json) } catch (_: Exception) { null }
            }
        }
        val allChildAges = allCriteria.flatMap { it.childAgeRanges }.distinct().sorted()
        val allCustomConcerns = allCriteria.map { it.customConcerns.trim() }.filter { it.isNotEmpty() }.distinct()
        if (allChildAges.isNotEmpty() || allCustomConcerns.isNotEmpty()) {
            CardSection("Search Criteria Used") {
                if (allChildAges.isNotEmpty()) {
                    Div(attrs = {
                        style { property("margin-bottom", if (allCustomConcerns.isNotEmpty()) "16px" else "0") }
                    }) {
                        Span(attrs = { style { color(Gray500); fontWeight(600); fontSize(13.px); display(DisplayStyle.Block); property("margin-bottom", "8px") } }) {
                            Text("Child Age Ranges")
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(8.px) } }) {
                            allChildAges.forEach { age ->
                                Span(attrs = {
                                    style {
                                        backgroundColor(PrimaryPale)
                                        color(Primary)
                                        fontWeight(600)
                                        fontSize(12.px)
                                        padding(4.px, 12.px)
                                        borderRadius(8.px)
                                        display(DisplayStyle.InlineBlock)
                                    }
                                }) { Text(age) }
                            }
                        }
                    }
                }
                if (allCustomConcerns.isNotEmpty()) {
                    Div {
                        Span(attrs = { style { color(Gray500); fontWeight(600); fontSize(13.px); display(DisplayStyle.Block); property("margin-bottom", "8px") } }) {
                            Text("Custom Concerns")
                        }
                        allCustomConcerns.forEach { concern ->
                            Div(attrs = {
                                style {
                                    backgroundColor(ScoreAmberBg)
                                    borderRadius(8.px)
                                    padding(8.px, 12.px)
                                    fontSize(13.px)
                                    color(Gray700)
                                    property("margin-bottom", "6px")
                                    property("line-height", "1.4")
                                }
                            }) { Text(concern) }
                        }
                    }
                }
            }
        }

        // All analyses
        CardSection("All Analyses (${titleEvents.size})") {
            if (titleEvents.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No analyses found.") }
            } else {
                AnalysesTable(
                    events = titleEvents,
                    showUser = true,
                    showTitle = false,
                    showType = false,
                    showConcerns = true,
                    onNavigate = onNavigate
                )
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Media Type Detail View
// ---------------------------------------------------------------------------

@Composable
private fun MediaTypeDetailView(
    data: DashboardData,
    view: DrillDownView.MediaTypeDetail,
    onNavigate: (DrillDownView) -> Unit
) {
    val typeEvents = data.allAnalyses.filter {
        it.mediaType.equals(view.mediaType, ignoreCase = true)
    }
    val scores = typeEvents.mapNotNull { it.score }
    val islamicScores = typeEvents.mapNotNull { it.islamicScore }
    val uniqueTitles = typeEvents.mapNotNull { it.title }.distinct()
    val uniqueUsers = typeEvents.mapNotNull { it.userEmail }.distinct()
    val typeMedia = data.topMedia.filter {
        it.mediaType.equals(view.mediaType, ignoreCase = true)
    }

    PageShell {
        BreadcrumbNav(
            "Dashboard" to { onNavigate(DrillDownView.Overview) },
            view.mediaType.replaceFirstChar { it.uppercase() } to null
        )

        // Summary cards
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            SummaryCard("Total Searches", typeEvents.size.toString(), Primary)
            SummaryCard("Unique Titles", uniqueTitles.size.toString(), Accent)
            SummaryCard("Unique Users", uniqueUsers.size.toString(), PrimaryLight)
            val typeTotalCost = typeEvents.sumOf { it.costUsd ?: 0.0 }
            SummaryCard("Total Cost", "$${formatCost(typeTotalCost)}", Color("#b45309"))
        }

        // Score distribution for this type
        val safe = typeEvents.count { (it.score ?: 0) in 7..10 }
        val caution = typeEvents.count { (it.score ?: 0) in 4..6 }
        val notSafe = typeEvents.count { (it.score ?: 0) in 1..3 }
        val total = safe + caution + notSafe
        if (total > 0) {
            CardSection("Score Distribution") {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                        gap(16.px)
                    }
                }) {
                    ScoreBucket("Safe (7-10)", safe, total, ScoreGreenBg, ScoreGreen, "#22C55E")
                    ScoreBucket("Caution (4-6)", caution, total, ScoreAmberBg, ScoreAmber, "#F59E0B")
                    ScoreBucket("Not Safe (1-3)", notSafe, total, ScoreRedBg, ScoreRed, "#EF4444")
                }
            }
        }

        // Media titles in this type, grouped by analysis count
        val titleSummaries = typeEvents
            .filter { it.title != null }
            .groupBy { it.title!! }
            .map { (title, events) ->
                val s = events.mapNotNull { it.score }
                Triple(title, events.size, if (s.isNotEmpty()) s.average() else null)
            }
            .sortedByDescending { it.second }

        if (titleSummaries.isNotEmpty()) {
            CardSection("Titles (${titleSummaries.size})") {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("Title")
                            AdminTh("Analyses")
                            AdminTh("Avg Score")
                        }
                    }
                    Tbody {
                        titleSummaries.forEachIndexed { index, (title, count, avgScore) ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = {
                                    style {
                                        padding(12.px, 16.px)
                                        property("max-width", "320px")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                                }) {
                                    ClickableText(title) {
                                        onNavigate(DrillDownView.TitleDetail(title))
                                    }
                                }
                                Td(attrs = {
                                    style { padding(12.px, 16.px); fontWeight(700); color(Primary) }
                                }) { Text(count.toString()) }
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    avgScore?.let { AdminScoreBadge(it.toInt()) }
                                        ?: Span(attrs = { style { color(Gray400) } }) { Text("-") }
                                }
                            }
                        }
                    }
                }
            }
        }

        // All analyses for this type
        CardSection("All Analyses (${typeEvents.size})") {
            if (typeEvents.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No analyses found.") }
            } else {
                AnalysesTable(
                    events = typeEvents,
                    showUser = true,
                    showTitle = true,
                    showType = false,
                    showConcerns = true,
                    onNavigate = onNavigate
                )
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Breadcrumb navigation
// ---------------------------------------------------------------------------

@Composable
private fun BreadcrumbNav(vararg parts: Pair<String, (() -> Unit)?>) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            property("margin-bottom", "24px")
            property("flex-wrap", "wrap")
        }
    }) {
        parts.forEachIndexed { index, (label, onClick) ->
            if (index > 0) {
                Span(attrs = {
                    style { color(Gray400); fontSize(14.px) }
                }) { Text("\u203A") }
            }
            if (onClick != null) {
                Span(attrs = {
                    attr("role", "button")
                    this.onClick { onClick() }
                    style {
                        color(Primary)
                        fontSize(14.px)
                        fontWeight(500)
                        cursor("pointer")
                        property("text-decoration", "underline")
                        property("text-decoration-color", "rgba(14, 124, 123, 0.3)")
                        property("text-underline-offset", "3px")
                        property("transition", TransitionMedium)
                    }
                }) { Text(label) }
            } else {
                Span(attrs = {
                    style {
                        color(Gray900)
                        fontSize(14.px)
                        fontWeight(700)
                    }
                }) { Text(label) }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Reusable card wrapper & table wrapper
// ---------------------------------------------------------------------------

@Composable
private fun CardSection(title: String, onRefresh: (() -> Unit)? = null, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("background", "white")
            borderRadius(16.px)
            padding(28.px)
            property("box-shadow", CardShadow)
            property("border", CardBorder)
            property("margin-bottom", "24px")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                property("margin-bottom", "20px")
            }
        }) {
            H2(attrs = {
                style {
                    fontSize(18.px)
                    fontWeight(700)
                    color(Gray900)
                    property("margin", "0")
                }
            }) { Text(title) }
            if (onRefresh != null) {
                Span(attrs = {
                    style {
                        cursor("pointer")
                        fontSize(16.px)
                        color(Gray400)
                        padding(4.px, 8.px)
                        borderRadius(6.px)
                        property("transition", TransitionMedium)
                    }
                    onClick { onRefresh() }
                    attr("title", "Refresh")
                }) { Text("\u21BB") }
            }
        }
        content()
    }
}

@Composable
private fun TableWrapper(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("overflow-x", "auto")
            borderRadius(12.px)
            property("border", "1px solid ${Gray200}")
        }
    }) {
        Table(attrs = {
            style {
                width(100.percent)
                property("border-collapse", "collapse")
                fontSize(14.px)
            }
        }) {
            content()
        }
    }
}

// ---------------------------------------------------------------------------
// Badges and small UI helpers
// ---------------------------------------------------------------------------

@Composable
private fun AdminTh(text: String, center: Boolean = false) {
    Th(attrs = {
        style {
            padding(12.px, 16.px)
            property("text-align", if (center) "center" else "left")
            fontWeight(600)
            fontSize(12.px)
            color(Primary)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.5px")
        }
    }) { Text(text) }
}

@Composable
private fun SortableTh(
    label: String,
    column: String,
    currentSort: String,
    currentAsc: Boolean,
    onSort: (String, Boolean) -> Unit
) {
    Th(attrs = {
        onClick {
            if (currentSort == column) onSort(column, !currentAsc)
            else onSort(column, true)
        }
        style {
            padding(12.px, 16.px)
            property("text-align", "left")
            fontWeight(600)
            fontSize(12.px)
            color(Primary)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.5px")
            cursor("pointer")
            property("user-select", "none")
        }
    }) {
        Text(label)
        if (currentSort == column) {
            Span(attrs = { style { marginLeft(4.px); fontSize(10.px) } }) {
                Text(if (currentAsc) " ▲" else " ▼")
            }
        }
    }
}

/** Compact 2×3 credits grid for a user: rows Full/Quick, columns Accumulated/Available/Used.
 *  Bordered cells (gridlines via a 1px gap over a gray background). */
@Composable
private fun CreditMatrix(user: RegisteredUser) {
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "auto 34px 34px 34px")
            property("gap", "1px")
            backgroundColor(Gray200)
            property("border", "1px solid ${Gray200}")
            borderRadius(6.px)
            property("overflow", "hidden")
            property("width", "fit-content")
        }
    }) {
        CreditCell(left = false) {}             // empty top-left corner
        CreditHdr("Acc"); CreditHdr("Avail"); CreditHdr("Used")
        CreditRow("Full", user.accumulatedFullCredits, user.availableFullCredits, user.fullUsed, user.hasFreeFullOnly)
        CreditRow("Quick", user.accumulatedQcCredits, user.availableQcCredits, user.qcUsed, user.hasFreeQcOnly)
    }
}

/** One white cell in the credits grid. */
@Composable
private fun CreditCell(left: Boolean = false, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(Color.white)
            padding(4.px, 7.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(if (left) JustifyContent.FlexStart else JustifyContent.Center)
        }
    }) { content() }
}

@Composable
private fun CreditHdr(label: String) {
    CreditCell {
        Span(attrs = {
            style {
                fontSize(9.px); fontWeight(600); color(Gray400)
                property("text-transform", "uppercase"); property("letter-spacing", "0.3px")
            }
        }) { Text(label) }
    }
}

@Composable
private fun CreditRow(label: String, acc: Int, avail: Int, used: Int, freeAvail: Boolean) {
    CreditCell(left = true) {
        Span(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray600) } }) { Text(label) }
    }
    CreditNum(acc, Gray500)
    CreditNum(avail, if (freeAvail) Color("#7c3aed") else Primary, bold = true)
    CreditNum(used, Gray600)
}

@Composable
private fun CreditNum(n: Int, c: CSSColorValue, bold: Boolean = false) {
    CreditCell {
        Span(attrs = {
            style {
                fontSize(12.px); color(c)
                if (bold) fontWeight(700)
            }
        }) { Text(n.toString()) }
    }
}

@Composable
private fun AdminScoreBadge(score: Int?) {
    if (score == null) {
        Span(attrs = { style { color(Gray400) } }) { Text("-") }
        return
    }
    val (bg, fg) = when {
        score >= 7 -> ScoreGreenBg to ScoreGreen
        score >= 4 -> ScoreAmberBg to ScoreAmber
        else -> ScoreRedBg to ScoreRed
    }
    Span(attrs = {
        style {
            backgroundColor(bg)
            color(fg)
            fontWeight(700)
            fontSize(13.px)
            padding(3.px, 10.px)
            borderRadius(8.px)
            display(DisplayStyle.InlineBlock)
        }
    }) { Text(score.toString()) }
}

@Composable
private fun MediaTypeBadge(type: String) {
    Span(attrs = {
        style {
            backgroundColor(PrimaryPale)
            color(Primary)
            fontWeight(600)
            fontSize(12.px)
            padding(3.px, 10.px)
            borderRadius(8.px)
            display(DisplayStyle.InlineBlock)
            property("text-transform", "capitalize")
        }
    }) { Text(type.ifEmpty { "unknown" }) }
}

@Composable
private fun ClickableMediaTypeBadge(type: String, onClick: () -> Unit) {
    Span(attrs = {
        attr("role", "button")
        this.onClick { onClick() }
        style {
            backgroundColor(PrimaryPale)
            color(Primary)
            fontWeight(600)
            fontSize(12.px)
            padding(3.px, 10.px)
            borderRadius(8.px)
            display(DisplayStyle.InlineBlock)
            property("text-transform", "capitalize")
            cursor("pointer")
            property("transition", TransitionMedium)
        }
    }) { Text(type.ifEmpty { "unknown" }) }
}

@Composable
private fun CacheLayerBadge(layer: String?) {
    if (layer == null) {
        Span(attrs = {
            style {
                backgroundColor(Gray100)
                color(Gray500)
                fontWeight(600)
                fontSize(12.px)
                padding(3.px, 10.px)
                borderRadius(8.px)
                display(DisplayStyle.InlineBlock)
            }
        }) { Text("miss") }
        return
    }
    Span(attrs = {
        style {
            backgroundColor(ScoreGreenBg)
            color(ScoreGreen)
            fontWeight(600)
            fontSize(12.px)
            padding(3.px, 10.px)
            borderRadius(8.px)
            display(DisplayStyle.InlineBlock)
        }
    }) { Text(layer) }
}

@Composable
private fun ClickableText(text: String, onClick: () -> Unit) {
    Span(attrs = {
        attr("role", "button")
        this.onClick { onClick() }
        style {
            color(Primary)
            fontWeight(500)
            cursor("pointer")
            property("text-decoration", "underline")
            property("text-decoration-color", "rgba(14, 124, 123, 0.3)")
            property("text-underline-offset", "3px")
            property("transition", TransitionMedium)
        }
    }) { Text(text) }
}

private fun parseCriteria(criteriaJson: String?): SearchCriteria? {
    if (criteriaJson == null) return null
    return try { adminJson.decodeFromString<SearchCriteria>(criteriaJson) } catch (_: Exception) { null }
}

/** CRITERIA column — the custom concern text (child ages are shown in the Age(s) column). */
@Composable
private fun CriteriaTags(criteriaJson: String?) {
    val criteria = parseCriteria(criteriaJson)
    val custom = criteria?.customConcerns?.trim().orEmpty()
    if (criteria == null) {
        Span(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("-") }
    } else if (custom.isEmpty()) {
        Span(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("Default") }
    } else {
        Span(attrs = {
            style {
                backgroundColor(ScoreAmberBg); color(ScoreAmber); fontWeight(600); fontSize(11.px)
                padding(2.px, 8.px); borderRadius(6.px); display(DisplayStyle.InlineBlock)
                property("white-space", "nowrap"); property("max-width", "160px")
                property("overflow", "hidden"); property("text-overflow", "ellipsis")
            }
        }) { Text(custom) }
    }
}

/** Age(s) column — the child age ranges the search was run for. */
@Composable
private fun AgeTags(criteriaJson: String?) {
    val ages = parseCriteria(criteriaJson)?.childAgeRanges ?: emptyList()
    if (ages.isEmpty()) {
        Span(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("-") }
        return
    }
    Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(4.px) } }) {
        ages.forEach { age ->
            Span(attrs = {
                style {
                    backgroundColor(PrimaryPale); color(Primary); fontWeight(600); fontSize(11.px)
                    padding(2.px, 8.px); borderRadius(6.px); display(DisplayStyle.InlineBlock)
                    property("white-space", "nowrap")
                }
            }) { Text(age) }
        }
    }
}

// ---------------------------------------------------------------------------
// Expanded result panel (shown when "Show Result" is clicked)
// ---------------------------------------------------------------------------

@Composable
private fun ExpandedResultPanel(event: AdminEventRow) {
    // Lazy: the heavy result/criteria/cost JSON is NOT in the bulk dashboard load —
    // fetch it on expand by row id (kept light so the dashboard doesn't OOM).
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }
    var detail by remember(event.id) { mutableStateOf<EventDetail?>(null) }
    var loading by remember(event.id) { mutableStateOf(true) }
    var error by remember(event.id) { mutableStateOf<String?>(null) }

    LaunchedEffect(event.id) {
        val id = event.id
        if (id == null) { loading = false; error = "Missing id"; return@LaunchedEffect }
        loading = true; error = null
        try {
            val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
            val headers = kotlin.js.json("Accept" to "application/json")
            token?.let { headers["Authorization"] = "Bearer $it" }
            val response = kotlinx.browser.window.fetch(
                "$serverUrl/api/admin/event-result?id=$id",
                kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            val text = response.text().await()
            if (response.ok) detail = adminJson.decodeFromString<EventDetail>(text.toString())
            else error = "Failed to load (${response.status})"
        } catch (e: Exception) { error = "Error: ${e.message}" }
        loading = false
    }

    val result = try { detail?.resultJson?.let { adminJson.decodeFromString<AnalysisResult>(it) } } catch (_: Exception) { null }
    val criteria = try { detail?.criteriaJson?.let { adminJson.decodeFromString<SearchCriteria>(it) } } catch (_: Exception) { null }
    val costBreakdown = try { detail?.costBreakdownJson?.let { adminJson.decodeFromString<CostBreakdown>(it) } } catch (_: Exception) { null }

    Div(attrs = {
        style {
            padding(20.px, 24.px)
            property("border-top", "1px dashed ${Gray200}")
        }
    }) {
        if (loading) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading result…") }
            return@Div
        }
        if (result == null) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text(error ?: "Unable to parse result data.") }
            return@Div
        }

        // Cover + title header
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(14.px); property("margin-bottom", "16px") } }) {
            LocalCovers.current[coverLookupKey(event.title, event.mediaType)]?.takeIf { it.isNotBlank() }?.let { coverUrl ->
                Img(src = coverUrl, alt = event.title ?: "", attrs = {
                    style {
                        width(64.px); height(90.px); borderRadius(6.px); property("object-fit", "cover")
                        property("flex-shrink", "0"); backgroundColor(Color("#f1f5f9")); property("box-shadow", CardShadow)
                    }
                })
            }
            event.title?.let { t ->
                Span(attrs = { style { fontSize(18.px); fontWeight(700); color(Gray900) } }) { Text(t) }
            }
        }

        // Criteria section
        if (criteria != null && (criteria.childAgeRanges.isNotEmpty() || criteria.customConcerns.trim().isNotEmpty())) {
            Div(attrs = { style { property("margin-bottom", "16px") } }) {
                Span(attrs = { style { fontWeight(700); fontSize(13.px); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.5px") } }) {
                    Text("Search Criteria")
                }
                Div(attrs = { style { property("margin-top", "8px"); display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(8.px) } }) {
                    criteria.childAgeRanges.forEach { age ->
                        Span(attrs = {
                            style {
                                backgroundColor(PrimaryPale); color(Primary); fontWeight(600); fontSize(12.px)
                                padding(3.px, 10.px); borderRadius(8.px); display(DisplayStyle.InlineBlock)
                            }
                        }) { Text("Ages $age") }
                    }
                    if (criteria.customConcerns.trim().isNotEmpty()) {
                        Span(attrs = {
                            style {
                                backgroundColor(ScoreAmberBg); color(ScoreAmber); fontWeight(600); fontSize(12.px)
                                padding(3.px, 10.px); borderRadius(8.px); display(DisplayStyle.InlineBlock)
                            }
                        }) { Text(criteria.customConcerns.trim()) }
                    }
                }
            }
        }

        // Cost Breakdown
        if (costBreakdown != null) {
            CostBreakdownPanel(costBreakdown)
        }

        // Render using the same component as the main app
        CombinedAnalysisResultContent(result)

        // Additional notes (not in CombinedAnalysisResultContent)
        val additionalNotes = result.additionalNotes
        if (!additionalNotes.isNullOrBlank()) {
            Div(attrs = { style { marginTop(16.px) } }) {
                Span(attrs = { style { fontWeight(700); fontSize(13.px); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.5px") } }) {
                    Text("Additional Notes")
                }
                P(attrs = {
                    style {
                        color(Gray700); fontSize(13.px); property("line-height", "1.5")
                        property("margin", "8px 0 0")
                    }
                }) { Text(additionalNotes) }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

private fun formatIsoDate(iso: String?): String {
    if (iso.isNullOrBlank()) return "-"
    return try {
        val datePart = iso.substringBefore("T")
        val timePart = iso.substringAfter("T").take(5)
        "$datePart $timePart"
    } catch (_: Exception) {
        iso.take(16)
    }
}

/** "21 Oct, 15:20" in the viewer's own time zone (the database sends UTC); "—" when absent. */
private fun formatLocalDateTime(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    return try {
        val d = kotlin.js.Date(iso)
        if (d.getTime().isNaN()) iso.take(16) else d.toLocaleString("en-GB", kotlin.js.dateLocaleOptions {
            day = "numeric"; month = "short"; hour = "2-digit"; minute = "2-digit"; hour12 = false
        })
    } catch (_: Throwable) {
        iso.take(16)
    }
}

/** "in 2d 5h" / "in 3h 10m" / "now" until [iso]. */
private fun formatTimeUntil(iso: String): String {
    val ms = try { kotlin.js.Date(iso).getTime() - kotlin.js.Date().getTime() } catch (_: Throwable) { return "" }
    if (ms.isNaN()) return ""
    if (ms <= 0) return "now"
    val mins = (ms / 60_000).toLong()
    val days = mins / (60 * 24); val hours = (mins / 60) % 24; val m = mins % 60
    return when {
        days > 0 -> "in ${days}d ${hours}h"
        hours > 0 -> "in ${hours}h ${m}m"
        else -> "in ${m}m"
    }
}

private fun formatDuration(seconds: Int): String {
    val mins = seconds / 60
    val secs = seconds % 60
    return if (mins > 0) "${mins}m ${secs}s" else "${secs}s"
}

private fun formatDuration(seconds: Double): String {
    return formatDuration(seconds.toInt())
}

private fun formatDouble(value: Double, decimals: Int): String {
    val multiplier = 10.0.pow(decimals)
    val rounded = round(value * multiplier) / multiplier
    val str = rounded.toString()
    val parts = str.split(".")
    return if (parts.size == 2) {
        if (decimals == 0) parts[0] else parts[0] + "." + parts[1].padEnd(decimals, '0').take(decimals)
    } else {
        // decimals == 0 must not append a bare "." — that is where "$141." came from.
        if (decimals == 0) str else "$str." + "0".repeat(decimals)
    }
}

private fun List<Int>.averageOrNull(): Double? =
    if (isEmpty()) null else average()

private fun formatCost(cost: Double): String {
    return if (cost < 0.01) {
        val str = cost.toString()
        // Show at least 4 significant digits after decimal
        if (str.contains(".")) str.take(7) else str
    } else {
        formatDouble(cost, 2)
    }
}

private fun formatTokens(tokens: Int): String {
    return when {
        tokens >= 1_000_000 -> "${formatDouble(tokens / 1_000_000.0, 1)}M"
        tokens >= 1_000 -> "${formatDouble(tokens / 1_000.0, 1)}K"
        else -> tokens.toString()
    }
}

// ---------------------------------------------------------------------------
// Cost Breakdown Panel
// ---------------------------------------------------------------------------

@Composable
private fun CostBreakdownPanel(breakdown: CostBreakdown) {
    Div(attrs = { style { property("margin-bottom", "16px") } }) {
        Span(attrs = {
            style {
                fontWeight(700); fontSize(13.px); color(Gray500)
                property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
            }
        }) { Text("Cost Breakdown") }

        Div(attrs = {
            style {
                property("margin-top", "8px")
                backgroundColor(Color("#fffbeb")); borderRadius(12.px); padding(16.px)
                property("border", "1px solid #fde68a")
            }
        }) {
            // Total cost header
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center); property("margin-bottom", "12px")
                }
            }) {
                Span(attrs = { style { fontWeight(700); fontSize(18.px); color(Color("#92400e")) } }) {
                    Text("Total: $${formatCost(breakdown.totalCostUsd)}")
                }
                Span(attrs = { style { fontSize(12.px); color(Gray500) } }) {
                    val totalCalls = breakdown.perplexity.callCount + breakdown.haiku.callCount + breakdown.sonnet.callCount
                    Text("$totalCalls API calls")
                }
            }

            // Model breakdown cards
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(3, 1fr)")
                    gap(10.px)
                }
            }) {
                // Perplexity
                CostModelCard(
                    name = "Perplexity Sonar",
                    emoji = "\uD83D\uDD0D",
                    calls = breakdown.perplexity.callCount,
                    cost = breakdown.perplexity.costUsd,
                    inputTokens = breakdown.perplexity.inputTokens,
                    outputTokens = breakdown.perplexity.outputTokens,
                    bgColor = "#f0f9ff",
                    borderColor = "#bae6fd",
                    textColor = "#0369a1"
                )
                // Haiku
                CostModelCard(
                    name = "Claude Haiku",
                    emoji = "\uD83D\uDCDD",
                    calls = breakdown.haiku.callCount,
                    cost = breakdown.haiku.costUsd,
                    inputTokens = breakdown.haiku.inputTokens,
                    outputTokens = breakdown.haiku.outputTokens,
                    bgColor = "#f0fdf4",
                    borderColor = "#bbf7d0",
                    textColor = "#15803d"
                )
                // Sonnet
                CostModelCard(
                    name = "Claude Sonnet",
                    emoji = "\u2728",
                    calls = breakdown.sonnet.callCount,
                    cost = breakdown.sonnet.costUsd,
                    inputTokens = breakdown.sonnet.inputTokens,
                    outputTokens = breakdown.sonnet.outputTokens,
                    bgColor = "#fdf4ff",
                    borderColor = "#f0abfc",
                    textColor = "#86198f"
                )
            }

            // Phase breakdown
            if (breakdown.phases.isNotEmpty()) {
                Div(attrs = { style { property("margin-top", "12px") } }) {
                    Span(attrs = { style { fontWeight(600); fontSize(12.px); color(Gray500); property("text-transform", "uppercase") } }) {
                        Text("By Phase")
                    }
                    Div(attrs = { style { property("margin-top", "6px") } }) {
                        breakdown.phases.forEach { phase ->
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                                    padding(4.px, 0.px); fontSize(13.px)
                                    property("border-bottom", "1px solid #fde68a")
                                }
                            }) {
                                Span(attrs = { style { color(Gray700); fontWeight(500) } }) {
                                    Text(phase.phase.replace("_", " ").replaceFirstChar { it.uppercaseChar() })
                                }
                                Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px) } }) {
                                    Span(attrs = { style { color(Gray500); fontSize(12.px) } }) {
                                        Text("${phase.callCount} calls")
                                    }
                                    if (phase.totalTokens > 0) {
                                        Span(attrs = { style { color(Gray500); fontSize(12.px) } }) {
                                            Text("${formatTokens(phase.totalTokens)} tokens")
                                        }
                                    }
                                    Span(attrs = { style { fontWeight(600); color(Color("#92400e")) } }) {
                                        Text("$${formatCost(phase.costUsd)}")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CostModelCard(
    name: String,
    emoji: String,
    calls: Int,
    cost: Double,
    inputTokens: Int,
    outputTokens: Int,
    bgColor: String,
    borderColor: String,
    textColor: String
) {
    Div(attrs = {
        style {
            backgroundColor(Color(bgColor))
            property("border", "1px solid $borderColor")
            borderRadius(8.px)
            padding(10.px, 12.px)
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); alignItems(AlignItems.Center); property("margin-bottom", "6px") } }) {
            Span { Text(emoji) }
            Span(attrs = { style { fontWeight(700); fontSize(12.px); color(Color(textColor)) } }) { Text(name) }
        }
        Div(attrs = { style { fontWeight(700); fontSize(16.px); color(Color(textColor)); property("margin-bottom", "4px") } }) {
            Text("$${formatCost(cost)}")
        }
        Div(attrs = { style { fontSize(11.px); color(Gray500); property("line-height", "1.5") } }) {
            Div { Text("$calls calls") }
            if (inputTokens > 0 || outputTokens > 0) {
                Div { Text("${formatTokens(inputTokens)} in / ${formatTokens(outputTokens)} out") }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------

@Composable
private fun AdminKeyframes() {
    InjectStyles(
        "admin-keyframes",
        """
        @keyframes adminSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        @keyframes adminPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.45; }
        }
        """.trimIndent()
    )
}

// ---------------------------------------------------------------------------
// Loading skeletons — shown while the dashboard's data is still loading, so the
// layout appears immediately and each section swaps to real data as it arrives.
// ---------------------------------------------------------------------------

@Composable
private fun SkeletonBlock(width: String = "100%", height: CSSNumeric = 16.px, radius: CSSNumeric = 8.px) {
    Div(attrs = {
        style {
            property("width", width)
            height(height)
            borderRadius(radius)
            backgroundColor(Gray200)
            property("animation", "adminPulse 1.4s ease-in-out infinite")
        }
    })
}

/** A white card wrapper with a faux title bar, matching CardSection's chrome. */
@Composable
private fun SkeletonCard(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("background", "white"); borderRadius(16.px); padding(28.px)
            property("box-shadow", CardShadow); property("border", CardBorder)
            property("margin-bottom", "24px")
        }
    }) {
        SkeletonBlock("28%", 18.px)
        Div(attrs = { style { height(20.px) } })
        content()
    }
}

@Composable
private fun ChartSkeleton() = SkeletonCard { SkeletonBlock("100%", 200.px) }

@Composable
private fun TableSkeleton(rows: Int = 5) = SkeletonCard {
    repeat(rows) {
        Div(attrs = { style { display(DisplayStyle.Flex); gap(16.px); alignItems(AlignItems.Center); property("margin-bottom", "16px") } }) {
            SkeletonBlock("32px", 32.px, 6.px)
            SkeletonBlock("38%", 14.px)
            SkeletonBlock("14%", 14.px)
            SkeletonBlock("14%", 14.px)
        }
    }
}

@Composable
private fun SummaryRowSkeleton() {
    // Mirrors SummaryCard/SummarySection's compact metrics exactly — if these drift, the dashboard
    // visibly jumps when the real cards swap in.
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(140px, 1fr))")
            gap(8.px); property("margin-bottom", "14px")
        }
    }) {
        repeat(5) {
            Div(attrs = {
                style {
                    property("background", "white"); borderRadius(12.px); padding(12.px, 14.px)
                    property("box-shadow", CardShadow); property("border", CardBorder)
                }
            }) {
                // 29px ≈ the label's reserved two lines (11px × 1.3 × 2), so nothing shifts on swap.
                SkeletonBlock("55%", 29.px)
                Div(attrs = { style { height(4.px) } })
                SkeletonBlock("40%", 22.px)
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Registered Users View (fetches from /api/admin/users)
//
// One composable, two pages (selected by [UserPage]):
//   • FOUNDING — grandfathered/legacy founding members (unlimited access). Original
//     columns only; no membership/credits columns (they're all unlimited).
//   • REGULAR  — new-flow users (everyone not unlimited). Adds Membership / Credits /
//     Used columns and the Paid filter.
// ---------------------------------------------------------------------------

private enum class UserPage(val title: String) {
    FOUNDING("Founding Members"),
    REGULAR("Regular Users")
}

/** Fetch the admin-controlled plan key→name map (public /api/pricing) so user tables/detail show the
 *  configured plan name rather than a hardcoded label. Empty until loaded (callers fall back). */
@Composable
private fun rememberPlanNames(): Map<String, String> {
    var names by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    LaunchedEffect(Unit) {
        try {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val response = kotlinx.browser.window.fetch(
                "$serverUrl/api/pricing",
                kotlin.js.json("method" to "GET").unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            if (response.ok) {
                val text = response.text().await()
                names = adminJson.decodeFromString<PricingConfig>(text.toString())
                    .plans.associate { it.key to it.name }
            }
        } catch (_: Exception) {}
    }
    return names
}

@Composable
private fun RegisteredUsersView(page: UserPage, onNavigate: (DrillDownView) -> Unit) {
    var users by remember { mutableStateOf<List<RegisteredUser>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    val planNames = rememberPlanNames()

    LaunchedEffect(Unit) {
        try {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
            val headers = kotlin.js.json("Accept" to "application/json")
            token?.let { headers["Authorization"] = "Bearer $it" }
            val response = kotlinx.browser.window.fetch(
                "$serverUrl/api/admin/users",
                kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
            ).await()
            val text = response.text().await()
            if (response.ok) {
                users = adminJson.decodeFromString<List<RegisteredUser>>(text.toString())
            } else {
                error = "Failed to fetch users: ${response.status}"
            }
        } catch (e: Exception) {
            error = "Error: ${e.message}"
        }
        loading = false
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, page.title to null)

        if (loading) {
            CardSection("Loading...") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching user data...") }
            }
            return@PageShell
        }

        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
            return@PageShell
        }

        // Partition the full list into this page's cohort:
        //   FOUNDING = legacy/unlimited founding members
        //   REGULAR  = new-flow signups, on/after the 2026-06-05 cutover only.
        val pageUsers = remember(users, page) {
            when (page) {
                UserPage.FOUNDING -> users.filter { it.isLegacy }
                UserPage.REGULAR -> users.filter { it.isRegularSignup }
            }
        }
        val isRegular = page == UserPage.REGULAR

        // Summary cards
        val registered = pageUsers.count { it.isRegistered }
        val approved = pageUsers.count { it.isApproved }
        val pending = pageUsers.count { it.isApproved && !it.isRegistered }
        val unapproved = pageUsers.count { !it.isApproved && it.isRegistered }
        val confirmed = pageUsers.count { it.emailConfirmed }
        val googleUsers = pageUsers.count { it.provider == "google" }
        val fromGoodChild = pageUsers.count { it.approvedSource == "zapier" }
        val regularSignups = pageUsers.count { it.isRegularSignup }
        val paidSignups = pageUsers.count { it.isRegistered && it.isPaidMember }
        // One "Paid" bucket rather than a tab per plan: the plan-specific tabs missed every paying
        // cohort that isn't a live Stripe subscription (ThriveCart, legacy founding members), which
        // is why 3 paying ThriveCart members were sitting under "None".
        val paidCount = pageUsers.count { it.hasPaidMembership }
        val freeCount = pageUsers.count { !it.hasPaidMembership }

        // Summary cards \u2014 Founding page only (they don't apply to regular signups).
        if (!isRegular) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(160px, 1fr))")
                    gap(16.px)
                    property("margin-bottom", "20px")
                }
            }) {
                SummaryCard("Founding Members", pageUsers.size.toString(), Primary)
                SummaryCard("Approved", approved.toString(), ScoreGreen)
                SummaryCard("From Good Child", fromGoodChild.toString(), Color("#7c3aed"))
                SummaryCard("Not Approved", unapproved.toString(), ScoreRed)
            }
        }

        // Filter tabs \u2014 differ per page
        var userFilter by remember { mutableStateOf("all") }
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("margin-bottom", "20px")
                property("flex-wrap", "wrap")
            }
        }) {
            val tabs = if (isRegular) {
                listOf(
                    "all"     to "All (${pageUsers.size})",
                    "paid"    to "Paid ($paidCount)",
                    "free"    to "None ($freeCount)",
                )
            } else {
                listOf(
                    "all" to "All (${pageUsers.size})",
                    "approved" to "Approved ($approved)",
                    "goodchild" to "From Good Child ($fromGoodChild)",
                    "unapproved" to "Not Approved ($unapproved)"
                )
            }
            tabs.forEach { (key, label) ->
                Button(attrs = {
                    onClick { userFilter = key }
                    style {
                        padding(6.px, 14.px)
                        fontSize(12.px)
                        fontWeight(600)
                        borderRadius(8.px)
                        cursor("pointer")
                        property("border", "none")
                        if (userFilter == key) {
                            backgroundColor(Primary)
                            color(Color.white)
                        } else {
                            backgroundColor(Gray50)
                            color(Gray600)
                        }
                    }
                }) { Text(label) }
            }
        }

        // Add approved user form
        var newApproveEmail by remember { mutableStateOf("") }
        var actionMessage by remember { mutableStateOf<String?>(null) }
        val scope = rememberCoroutineScope()
        val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
        val authToken = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }

        fun adminHeaders(vararg extra: Pair<String, String>): dynamic {
            val h = kotlin.js.json("Content-Type" to "application/json")
            authToken?.let { h["Authorization"] = "Bearer $it" }
            extra.forEach { (k, v) -> h[k] = v }
            return h
        }

        fun adminPost(endpoint: String, body: String, onSuccess: () -> Unit) {
            scope.launch {
                try {
                    val resp = kotlinx.browser.window.fetch(
                        "$serverUrl$endpoint",
                        kotlin.js.json("method" to "POST", "headers" to adminHeaders(), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                    ).await()
                    if (resp.ok) {
                        onSuccess()
                        // Refresh users
                        val refreshResp = kotlinx.browser.window.fetch("$serverUrl/api/admin/users", kotlin.js.json("method" to "GET", "headers" to adminHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()).await()
                        val text = refreshResp.text().await()
                        if (refreshResp.ok) users = adminJson.decodeFromString<List<RegisteredUser>>(text.toString())
                    } else {
                        actionMessage = "Error: ${resp.status}"
                    }
                } catch (e: Exception) {
                    actionMessage = "Error: ${e.message}"
                }
            }
        }

        CardSection("Approve User") {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(10.px)
                    alignItems(AlignItems.Center)
                    property("flex-wrap", "wrap")
                }
            }) {
                Input(type = org.jetbrains.compose.web.attributes.InputType.Email) {
                    value(newApproveEmail)
                    onInput { newApproveEmail = it.value }
                    attr("placeholder", "Enter email to approve")
                    style {
                        padding(10.px, 14.px)
                        fontSize(14.px)
                        borderRadius(8.px)
                        property("border", "1px solid ${Gray200}")
                        property("outline", "none")
                        property("min-width", "280px")
                    }
                }
                Button(attrs = {
                    onClick {
                        if (newApproveEmail.isNotBlank()) {
                            adminPost("/api/admin/approve", """{"email":"${newApproveEmail.trim()}"}""") {
                                actionMessage = "✅ Approved: ${newApproveEmail.trim()}"
                                newApproveEmail = ""
                            }
                        }
                    }
                    style {
                        padding(10.px, 20.px)
                        fontSize(14.px)
                        fontWeight(600)
                        color(Color.white)
                        backgroundColor(ScoreGreen)
                        borderRadius(8.px)
                        property("border", "none")
                        cursor("pointer")
                    }
                }) { Text("Approve") }

                actionMessage?.let { msg ->
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            color(if (msg.startsWith("✅")) ScoreGreen else ScoreRed)
                            fontWeight(500)
                        }
                    }) { Text(msg) }
                }
            }
        }

        // Users table with sortable columns
        var sortColumn by remember { mutableStateOf("registered") }
        var sortAsc by remember { mutableStateOf(false) }

        val filteredUsers = remember(pageUsers, userFilter) {
            when (userFilter) {
                "registered" -> pageUsers.filter { it.isRegistered }
                "regular" -> pageUsers.filter { it.isRegularSignup }
                "paid"    -> pageUsers.filter { it.hasPaidMembership }
                "pro_29"  -> pageUsers.filter { it.plan == "pro_29" && it.isPaidMember }
                "annual"  -> pageUsers.filter { it.plan == "annual" && it.isPaidMember }
                "free"    -> pageUsers.filter { !it.hasPaidMembership }
                "approved" -> pageUsers.filter { it.isApproved }
                "pending" -> pageUsers.filter { it.isApproved && !it.isRegistered }
                "goodchild" -> pageUsers.filter { it.approvedSource == "zapier" }
                "unapproved" -> pageUsers.filter { !it.isApproved && it.isRegistered }
                else -> pageUsers
            }
        }

        val sortedUsers = remember(filteredUsers, sortColumn, sortAsc) {
            val sorted = when (sortColumn) {
                "email" -> filteredUsers.sortedBy { it.email.lowercase() }
                "name" -> filteredUsers.sortedBy { (it.fullName ?: "").lowercase() }
                "provider" -> filteredUsers.sortedBy { it.provider ?: "" }
                "status" -> filteredUsers.sortedBy { when { !it.isRegistered -> 0; it.isApproved -> 2; else -> 1 } }
                "confirmed" -> filteredUsers.sortedBy { if (it.emailConfirmed) 0 else 1 }
                "approved" -> filteredUsers.sortedBy { if (it.isApproved) 0 else 1 }
                "lastSignIn" -> filteredUsers.sortedBy { it.lastSignIn ?: "" }
                "registered" -> filteredUsers.sortedBy { it.createdAt }
                "searches" -> filteredUsers.sortedBy { it.totalSearches }
                "platform" -> filteredUsers.sortedBy { it.lastPlatform ?: "" }
                "appVersion" -> filteredUsers.sortedBy { it.lastAppVersion ?: "" }
                "membership" -> filteredUsers.sortedBy { it.membershipRank }
                "credits" -> filteredUsers.sortedBy { if (it.isUnlimited) Int.MAX_VALUE else it.availableFullCredits + it.qcCredits }
                "creditsUsed" -> filteredUsers.sortedBy { it.fullUsed + it.qcUsed }
                else -> filteredUsers
            }
            if (sortAsc) sorted else sorted.reversed()
        }

        CardSection("Users (${filteredUsers.size})") {
            // Export CSV button — exports whatever is currently filtered + sorted
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    property("margin-bottom", "12px")
                }
            }) {
                Button(attrs = {
                    onClick { downloadUsersCsv(sortedUsers, userFilter) }
                    style {
                        padding(10.px, 20.px)
                        fontSize(14.px)
                        fontWeight(600)
                        color(Color.white)
                        backgroundColor(Primary)
                        borderRadius(8.px)
                        property("border", "none")
                        cursor("pointer")
                    }
                }) {
                    Text("\u2B07\uFE0F  Export to CSV (${sortedUsers.size})")
                }
            }

            if (pageUsers.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No users found.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            SortableTh("Email", "email", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            SortableTh("Name", "name", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            if (!isRegular) {
                                SortableTh("Status", "status", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            }
                            SortableTh("Provider", "provider", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            if (isRegular) {
                                SortableTh("Email Confirmed", "confirmed", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                                SortableTh("Membership", "membership", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                                AdminTh("Renews / Expires")
                            }
                            SortableTh("Searches", "searches", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            SortableTh("Last Sign In", "lastSignIn", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            SortableTh("OS", "platform", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            SortableTh("Version", "appVersion", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            SortableTh("Registered", "registered", sortColumn, sortAsc) { col, asc -> sortColumn = col; sortAsc = asc }
                            AdminTh("Actions")
                        }
                    }
                    Tbody {
                        sortedUsers.forEachIndexed { index, user ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                // Email
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(500) } }) {
                                    ClickableText(user.email) {
                                        onNavigate(DrillDownView.UserDetail(user.email, user.id))
                                    }
                                }
                                // Name
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600); fontSize(13.px) } }) {
                                    Text(user.fullName ?: "-")
                                }
                                // Status (Founding page only \u2014 redundant on Regular, where Membership conveys state)
                                if (!isRegular) {
                                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                                        val (statusText, statusBg, statusColor) = when {
                                            !user.isRegistered -> Triple("Pending Signup", ScoreAmberBg, ScoreAmber)
                                            user.isApproved && user.emailConfirmed -> Triple("Active", ScoreGreenBg, ScoreGreen)
                                            user.isApproved && !user.emailConfirmed -> Triple("Unconfirmed", Color("#e0f2fe"), Color("#0369a1"))
                                            !user.isApproved -> Triple("Not Approved", ScoreRedBg, ScoreRed)
                                            else -> Triple("Unknown", Gray50, Gray500)
                                        }
                                        Div(attrs = { style { display(DisplayStyle.Flex); gap(4.px); property("flex-wrap", "wrap"); alignItems(AlignItems.Center) } }) {
                                            Span(attrs = {
                                                style {
                                                    fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                                    borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                                    backgroundColor(statusBg); color(statusColor)
                                                }
                                            }) { Text(statusText) }
                                            if (user.approvedSource == "zapier") {
                                                Span(attrs = {
                                                    style {
                                                        fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                                        borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                                        backgroundColor(Color("#f3e8ff")); color(Color("#7c3aed"))
                                                    }
                                                }) { Text("\uD83C\uDF31 Good Child") }
                                            }
                                        }
                                    }
                                }
                                // Provider
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    Span(attrs = {
                                        style {
                                            fontSize(11.px)
                                            fontWeight(700)
                                            padding(3.px, 10.px)
                                            borderRadius(6.px)
                                            display(DisplayStyle.InlineBlock)
                                            if (user.provider == "google") {
                                                backgroundColor(Color("#e0f2fe"))
                                                color(Color("#0369a1"))
                                            } else {
                                                backgroundColor(PrimaryPale)
                                                color(Primary)
                                            }
                                        }
                                    }) { Text(user.provider?.uppercase() ?: "EMAIL") }
                                }
                                if (isRegular) {
                                    // Email confirmed
                                    Td(attrs = { style { padding(12.px, 16.px); property("text-align", "center") } }) {
                                        val (cBg, cFg, cText) = if (user.emailConfirmed)
                                            Triple(ScoreGreenBg, ScoreGreen, "✓ Yes")
                                        else
                                            Triple(ScoreAmberBg, ScoreAmber, "✗ No")
                                        Span(attrs = {
                                            style {
                                                fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                                borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                                property("white-space", "nowrap")
                                                backgroundColor(cBg); color(cFg)
                                            }
                                        }) { Text(cText) }
                                    }
                                    // Membership
                                    Td(attrs = { style { padding(12.px, 16.px) } }) {
                                        val (memBg, memFg) = when {
                                            // Gifted gets its own teal so comps never look like founding members
                                            // (purple) at a glance. A LAPSED gift falls through to the gray "None".
                                            user.isGifted && user.isUnlimited -> Color("#ccfbf1") to Color("#0f766e")
                                            user.isUnlimited -> Color("#f3e8ff") to Color("#7c3aed")
                                            user.membershipStatus == "active" -> ScoreGreenBg to ScoreGreen
                                            user.membershipStatus == "past_due" -> ScoreRedBg to ScoreRed
                                            user.membershipStatus == "canceling" || user.membershipStatus == "canceled" -> ScoreAmberBg to ScoreAmber
                                            else -> Gray100 to Gray600
                                        }
                                        // Active subscribers: show admin-configured plan name (from /api/pricing),
                                        // falling back to membershipLabel. Covers both unlimited and credit plans,
                                        // so pro_29 and annual subscribers are distinguishable in the table.
                                        val memText = if (user.membershipStatus == "active")
                                            (planNames[user.plan] ?: user.membershipLabel)
                                        else user.membershipLabel
                                        Span(attrs = {
                                            style {
                                                fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                                borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                                property("white-space", "nowrap")
                                                backgroundColor(memBg); color(memFg)
                                            }
                                        }) { Text(memText) }
                                    }
                                    // Credits column removed: every membership is unlimited now, so
                                    // the matrix showed "Unlimited" for everyone who matters and a
                                    // grid of zeroes for everyone who doesn't. The underlying
                                    // credit fields still exist (CreditMatrix, the per-user profile
                                    // view) for the pre-subscription accounts that still hold them.

                                    // Renews / Expires — the word matters as much as the date: the
                                    // same column is a renewal for a subscriber, a cut-off for a
                                    // cancellation, and the end of a comped window for a gift.
                                    Td(attrs = { style { padding(12.px, 16.px); property("white-space", "nowrap") } }) {
                                        val md = user.membershipDate
                                        if (md == null) {
                                            Span(attrs = { style { color(Gray400) } }) { Text("—") }
                                        } else {
                                            val (word, iso) = md
                                            val lapsed = word == "Expired" || word == "Ended"
                                            Span(attrs = { style { fontSize(11.px); color(Gray500); property("margin-right", "6px") } }) { Text(word) }
                                            Span(attrs = {
                                                style {
                                                    fontSize(13.px); fontWeight(600)
                                                    color(if (lapsed) ScoreRed else Gray700)
                                                }
                                            }) { Text(formatIsoDate(iso)) }
                                        }
                                    }
                                }
                                // Searches
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(Primary); property("text-align", "center") } }) {
                                    Text(user.totalSearches.toString())
                                }
                                // Last Sign In
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) } }) {
                                    Text(user.lastSignIn?.let { formatIsoDate(it) } ?: "Never")
                                }
                                // OS
                                Td(attrs = { style { padding(12.px, 16.px) } }) {
                                    val platform = user.lastPlatform
                                    if (platform != null) {
                                        val (label, bg, fg) = when {
                                            platform.contains("ios", ignoreCase = true) || platform.contains("iphone", ignoreCase = true) -> Triple("iOS", Color("#f0f0f0"), Color("#333333"))
                                            platform.contains("android", ignoreCase = true) -> Triple("Android", Color("#e8f5e9"), Color("#2e7d32"))
                                            platform.contains("web", ignoreCase = true) || platform.contains("js", ignoreCase = true) -> Triple("Web", Color("#e3f2fd"), Color("#1565c0"))
                                            else -> Triple(platform, Gray100, Gray600)
                                        }
                                        Span(attrs = {
                                            style {
                                                fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                                borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                                backgroundColor(bg); color(fg)
                                            }
                                        }) { Text(label) }
                                    } else {
                                        Text("-")
                                    }
                                }
                                // Version
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray500); fontSize(12.px) } }) {
                                    Text(user.lastAppVersion ?: "-")
                                }
                                // Registered
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray400); fontSize(13.px) } }) {
                                    Text(formatIsoDate(user.createdAt))
                                }
                                // Actions
                                Td(attrs = {
                                    style {
                                        padding(8.px, 12.px)
                                        display(DisplayStyle.Flex)
                                        gap(6.px)
                                        alignItems(AlignItems.Center)
                                        property("flex-wrap", "wrap")
                                    }
                                }) {
                                    // Approve / Unapprove toggle
                                    if (user.isApproved) {
                                        Button(attrs = {
                                            onClick {
                                                adminPost("/api/admin/unapprove", """{"email":"${user.email}"}""") {
                                                    actionMessage = "Removed approval: ${user.email}"
                                                }
                                            }
                                            style {
                                                padding(4.px, 10.px)
                                                fontSize(11.px)
                                                fontWeight(600)
                                                color(ScoreAmber)
                                                backgroundColor(ScoreAmberBg)
                                                borderRadius(6.px)
                                                property("border", "none")
                                                cursor("pointer")
                                                property("white-space", "nowrap")
                                            }
                                        }) { Text("Unapprove") }
                                    } else {
                                        Button(attrs = {
                                            onClick {
                                                adminPost("/api/admin/approve", """{"email":"${user.email}"}""") {
                                                    actionMessage = "✅ Approved: ${user.email}"
                                                }
                                            }
                                            style {
                                                padding(4.px, 10.px)
                                                fontSize(11.px)
                                                fontWeight(600)
                                                color(ScoreGreen)
                                                backgroundColor(ScoreGreenBg)
                                                borderRadius(6.px)
                                                property("border", "none")
                                                cursor("pointer")
                                                property("white-space", "nowrap")
                                            }
                                        }) { Text("Approve") }
                                    }
                                    // Delete user
                                    Button(attrs = {
                                        onClick {
                                            if (kotlinx.browser.window.confirm("Delete ${user.email}? This cannot be undone.")) {
                                                adminPost("/api/admin/delete-user", """{"userId":"${user.id}","email":"${user.email}"}""") {
                                                    actionMessage = "Deleted: ${user.email}"
                                                }
                                            }
                                        }
                                        style {
                                            padding(4.px, 10.px)
                                            fontSize(11.px)
                                            fontWeight(600)
                                            color(ScoreRed)
                                            backgroundColor(ScoreRedBg)
                                            borderRadius(6.px)
                                            property("border", "none")
                                            cursor("pointer")
                                            property("white-space", "nowrap")
                                        }
                                    }) { Text("Delete") }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Cost Analysis View
// ---------------------------------------------------------------------------

private enum class CostPeriod(val label: String) {
    TODAY("Today"),
    WEEK("This Week"),
    MONTH("This Month"),
    ALL("All Time")
}

@Composable
private fun CostAnalysisView(data: DashboardData, onRefresh: (() -> Unit)? = null, onNavigate: (DrillDownView) -> Unit) {
    val analyses = data.allAnalyses
    var selectedPeriod by remember { mutableStateOf(CostPeriod.ALL) }
    var selectedUser by remember { mutableStateOf<String?>(null) }

    val uniqueUsers = remember(analyses) {
        analyses.mapNotNull { it.userEmail }.distinct().sorted()
    }

    val now = remember { kotlin.js.Date() }
    val todayPrefix = remember { "${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}" }
    val weekAgo = remember {
        val d = kotlin.js.Date(now.getTime() - 7 * 86_400_000.0)
        "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}"
    }
    val monthAgo = remember {
        val d = kotlin.js.Date(now.getTime() - 30 * 86_400_000.0)
        "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}"
    }

    val filtered = remember(analyses, selectedPeriod, selectedUser) {
        analyses.filter { event ->
            val dateOk = when (selectedPeriod) {
                CostPeriod.TODAY -> event.createdAt?.startsWith(todayPrefix) == true
                CostPeriod.WEEK -> (event.createdAt ?: "") >= weekAgo
                CostPeriod.MONTH -> (event.createdAt ?: "") >= monthAgo
                CostPeriod.ALL -> true
            }
            val userOk = selectedUser == null || event.userEmail == selectedUser
            dateOk && userOk
        }
    }

    val totalCost = filtered.sumOf { it.costUsd ?: 0.0 }
    val totalAnalyses = filtered.size
    val avgCost = if (totalAnalyses > 0) totalCost / totalAnalyses else 0.0

    val costByUser = remember(filtered) {
        filtered.groupBy { it.userEmail ?: "Anonymous" }
            .map { (email, events) -> Triple(email, events.size, events.sumOf { it.costUsd ?: 0.0 }) }
            .sortedByDescending { it.third }
    }

    val costByDay = remember(filtered) {
        filtered.groupBy { it.createdAt?.take(10) ?: "Unknown" }
            .map { (day, events) -> Triple(day, events.size, events.sumOf { it.costUsd ?: 0.0 }) }
            .sortedByDescending { it.first }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Cost Analysis" to null)

        // Filters
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
                alignItems(AlignItems.Center)
                property("flex-wrap", "wrap")
                property("margin-bottom", "24px")
            }
        }) {
            CostPeriod.entries.forEach { period ->
                Button(attrs = {
                    onClick { selectedPeriod = period }
                    style {
                        padding(8.px, 16.px)
                        fontSize(13.px)
                        fontWeight(600)
                        borderRadius(8.px)
                        cursor("pointer")
                        property("border", "none")
                        if (selectedPeriod == period) {
                            backgroundColor(Primary)
                            color(Color.white)
                        } else {
                            backgroundColor(Gray50)
                            color(Gray600)
                        }
                    }
                }) { Text(period.label) }
            }

            Span(attrs = { style { color(Gray400); fontSize(13.px); marginLeft(8.px) } }) { Text("|") }
            Select(attrs = {
                onChange { selectedUser = it.value?.ifEmpty { null } }
                style {
                    padding(8.px, 12.px)
                    fontSize(13.px)
                    borderRadius(8.px)
                    property("border", "1px solid ${Gray200}")
                    property("outline", "none")
                    backgroundColor(Color.white)
                }
            }) {
                Option("", attrs = { if (selectedUser == null) attr("selected", "") }) { Text("All Users") }
                uniqueUsers.forEach { email ->
                    Option(email, attrs = { if (selectedUser == email) attr("selected", "") }) { Text(email) }
                }
            }
        }

        // Summary cards
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(180px, 1fr))")
                gap(16.px)
                property("margin-bottom", "28px")
            }
        }) {
            SummaryCard("Total Cost", "$${formatCost(totalCost)}", Color("#b45309"))
            SummaryCard("Analyses", totalAnalyses.toString(), Primary)
            SummaryCard("Avg Cost", "$${formatCost(avgCost)}", Accent)
            SummaryCard("Period", selectedPeriod.label, Gray600)
        }

        // Cost by Day
        CardSection("Cost by Day") {
            if (costByDay.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No data for selected period.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("Date")
                            AdminTh("Analyses")
                            AdminTh("Cost")
                        }
                    }
                    Tbody {
                        costByDay.forEachIndexed { index, (day, count, cost) ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(500); color(Gray900) } }) { Text(day) }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600) } }) { Text(count.toString()) }
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(Color("#b45309")) } }) { Text("$${formatCost(cost)}") }
                            }
                        }
                        Tr(attrs = { style { property("background", PrimaryPale.toString()); fontWeight(700) } }) {
                            Td(attrs = { style { padding(12.px, 16.px); color(Primary) } }) { Text("TOTAL") }
                            Td(attrs = { style { padding(12.px, 16.px); color(Primary) } }) { Text(totalAnalyses.toString()) }
                            Td(attrs = { style { padding(12.px, 16.px); color(Color("#b45309")) } }) { Text("$${formatCost(totalCost)}") }
                        }
                    }
                }
            }
        }

        // Cost by User
        CardSection("Cost by User") {
            if (costByUser.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No data for selected period.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("User")
                            AdminTh("Analyses")
                            AdminTh("Cost")
                            AdminTh("Avg/Analysis")
                        }
                    }
                    Tbody {
                        costByUser.forEachIndexed { index, (email, count, cost) ->
                            Tr(attrs = {
                                style {
                                    if (index % 2 == 1) backgroundColor(Gray50)
                                    property("border-bottom", "1px solid ${Gray200}")
                                }
                            }) {
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(500) } }) {
                                    if (email != "Anonymous") {
                                        ClickableText(email) { onNavigate(DrillDownView.UserDetail(email, null)) }
                                    } else {
                                        Span(attrs = { style { color(Gray400) } }) { Text("Anonymous") }
                                    }
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600) } }) { Text(count.toString()) }
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(700); color(Color("#b45309")) } }) { Text("$${formatCost(cost)}") }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray600) } }) {
                                    Text("$${formatCost(if (count > 0) cost / count else 0.0)}")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// User Journeys View
// ---------------------------------------------------------------------------

@Composable
private fun UserJourneysView(data: DashboardData, onRefresh: (() -> Unit)? = null, onNavigate: (DrillDownView) -> Unit) {
    var statusFilter by remember { mutableStateOf("all") }
    var searchFilter by remember { mutableStateOf("") }

    val journeys = data.userJourneys

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "User Journeys" to null)

        // Summary cards
        val searched = journeys.count { it.status == "searched" }
        val selected = journeys.count { it.status == "selected" }
        val completed = journeys.count { it.status == "completed" }
        val failed = journeys.count { it.status == "failed" }
        val cancelled = journeys.count { it.status == "cancelled" }
        val conversionRate = if (journeys.isNotEmpty()) (completed * 100 / journeys.size) else 0
        val selectionRate = if (journeys.isNotEmpty()) ((journeys.size - searched) * 100 / journeys.size) else 0

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(140px, 1fr))")
                gap(16.px)
                property("margin-bottom", "20px")
            }
        }) {
            SummaryCard("Total", journeys.size.toString(), Primary)
            SummaryCard("Searched Only", searched.toString(), Gray500)
            SummaryCard("Selected", selected.toString(), PrimaryLight)
            SummaryCard("Completed", completed.toString(), ScoreGreen)
            SummaryCard("Failed", failed.toString(), ScoreRed)
            SummaryCard("Cancelled", cancelled.toString(), ScoreAmber)
            SummaryCard("Selection Rate", "$selectionRate%", if (selectionRate >= 70) ScoreGreen else ScoreAmber)
            SummaryCard("Completion Rate", "$conversionRate%", if (conversionRate >= 50) ScoreGreen else ScoreAmber)
        }

        // Filter tabs + search
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                property("margin-bottom", "16px")
                property("flex-wrap", "wrap")
                alignItems(AlignItems.Center)
            }
        }) {
            listOf(
                "all" to "All (${journeys.size})",
                "searched" to "Searched ($searched)",
                "selected" to "Selected ($selected)",
                "completed" to "Completed ($completed)",
                "failed" to "Failed ($failed)",
                "cancelled" to "Cancelled ($cancelled)"
            ).forEach { (value, label) ->
                Span(attrs = {
                    style {
                        padding(6.px, 14.px)
                        borderRadius(8.px)
                        fontSize(13.px)
                        fontWeight(500)
                        cursor("pointer")
                        property("transition", TransitionMedium)
                        if (statusFilter == value) {
                            backgroundColor(Primary)
                            color(Color.white)
                        } else {
                            backgroundColor(Gray100)
                            color(Gray600)
                        }
                    }
                    onClick { statusFilter = value }
                }) { Text(label) }
            }
            Div(attrs = { style { property("margin-left", "auto") } }) {
                org.jetbrains.compose.web.dom.TextInput(searchFilter) {
                    onInput { searchFilter = it.value }
                    style {
                        padding(6.px, 12.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(13.px)
                        width(200.px)
                        property("outline", "none")
                    }
                    attr("placeholder", "Search query or email...")
                }
            }
        }

        val filteredJourneys = journeys.filter { j ->
            (statusFilter == "all" || j.status == statusFilter) &&
            (searchFilter.isBlank() ||
                j.searchQuery.contains(searchFilter, ignoreCase = true) ||
                (j.userEmail ?: "").contains(searchFilter, ignoreCase = true) ||
                (j.selectedTitle ?: "").contains(searchFilter, ignoreCase = true))
        }

        CardSection("Journeys (${filteredJourneys.size})", onRefresh) {
            Div(attrs = { style { property("overflow-x", "auto") } }) {
                Table(attrs = {
                    style {
                        width(100.percent)
                        property("border-collapse", "collapse")
                        fontSize(13.px)
                    }
                }) {
                    Thead {
                        Tr {
                            listOf("Time", "User", "Search Query", "Results", "Selected", "Status", "").forEach { col ->
                                Th(attrs = {
                                    style {
                                        property("text-align", "left")
                                        padding(10.px, 12.px)
                                        color(Gray500)
                                        fontWeight(600)
                                        property("border-bottom", "2px solid $Gray200")
                                        property("white-space", "nowrap")
                                    }
                                }) { Text(col) }
                            }
                        }
                    }
                    Tbody {
                        if (filteredJourneys.isEmpty()) {
                            Tr {
                                Td(attrs = {
                                    attr("colspan", "7")
                                    style { padding(24.px); property("text-align", "center"); color(Gray400) }
                                }) { Text("No journeys found") }
                            }
                        }
                        filteredJourneys.take(200).forEach { journey ->
                            Tr(attrs = {
                                style { property("border-bottom", "1px solid $Gray100") }
                            }) {
                                Td(attrs = {
                                    style { padding(10.px, 12.px); color(Gray500); property("white-space", "nowrap") }
                                }) { Text(formatIsoDate(journey.createdAt)) }

                                Td(attrs = {
                                    style {
                                        padding(10.px, 12.px)
                                        color(if (journey.userEmail != null) Primary else Gray400)
                                        fontWeight(500)
                                        cursor("pointer")
                                        property("max-width", "180px")
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                    }
                                    if (journey.userEmail != null) {
                                        onClick { onNavigate(DrillDownView.UserDetail(journey.userEmail, journey.userId)) }
                                    }
                                }) { Text(journey.userEmail ?: "Anonymous") }

                                Td(attrs = {
                                    style {
                                        padding(10.px, 12.px); color(Gray900); fontWeight(600)
                                        property("max-width", "250px"); property("overflow", "hidden")
                                        property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                                    }
                                }) { Text(journey.searchQuery) }

                                Td(attrs = {
                                    style { padding(10.px, 12.px); color(Gray600); property("text-align", "center") }
                                }) { Text(journey.disambiguationCount.toString()) }

                                Td(attrs = {
                                    val title = journey.selectedTitle
                                    style {
                                        padding(10.px, 12.px)
                                        color(if (title != null) Primary else Gray300)
                                        property("max-width", "220px"); property("overflow", "hidden")
                                        property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                                        if (title != null) cursor("pointer")
                                    }
                                    if (title != null) {
                                        onClick { onNavigate(DrillDownView.TitleDetail(title)) }
                                    }
                                }) {
                                    val title = journey.selectedTitle
                                    if (title != null) {
                                        val suffix = buildString {
                                            journey.selectedYear?.let { append(" ($it)") }
                                            journey.selectedMediaType?.let { append(" [$it]") }
                                        }
                                        Text("$title$suffix")
                                    } else {
                                        Text("-")
                                    }
                                }

                                Td(attrs = { style { padding(10.px, 12.px) } }) {
                                    val (badgeColor, badgeBg) = when (journey.status) {
                                        "completed" -> ScoreGreen to Color("#ecfdf5")
                                        "failed" -> ScoreRed to Color("#fef2f2")
                                        "cancelled" -> ScoreAmber to Color("#fffbeb")
                                        "analyzing" -> PrimaryLight to Color("#eff6ff")
                                        "selected" -> Color("#7c3aed") to Color("#f5f3ff")
                                        else -> Gray500 to Gray100
                                    }
                                    Span(attrs = {
                                        style {
                                            padding(3.px, 10.px); borderRadius(6.px); fontSize(12.px)
                                            fontWeight(600); color(badgeColor); backgroundColor(badgeBg)
                                        }
                                    }) { Text(journey.status) }
                                }

                                // View Logs link
                                Td(attrs = {
                                    style { padding(10.px, 12.px); property("white-space", "nowrap") }
                                }) {
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Pricing (dynamic paywall) Editor
// ---------------------------------------------------------------------------

/** One labelled text field in the pricing editor. */
@Composable
private fun PricingField(label: String, value: String, placeholder: String, onChange: (String) -> Unit) {
    Div(attrs = { style { property("flex", "1"); property("min-width", "160px") } }) {
        Div(attrs = {
            style {
                fontSize(11.px); fontWeight(700); color(Gray500)
                property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                property("margin", "0 0 5px")
            }
        }) { Text(label) }
        TextInput(value) {
            onInput { onChange(it.value) }
            style {
                width(100.percent); property("box-sizing", "border-box")
                padding(9.px, 11.px); borderRadius(9.px)
                property("border", "1px solid $Gray200"); fontSize(13.px); property("outline", "none")
            }
            attr("placeholder", placeholder)
        }
    }
}

/**
 * Dynamic pricing editor. Loads the current config from /api/admin/pricing-config, lets the admin
 * compose 1–3 plans (name, Stripe key, price, optional strikethrough old price, optional badge,
 * feature lines) and publishes it back. Clients then pick it up via GET /api/pricing. Web-only —
 * there is no mobile admin.
 */
@Composable
private fun PricingView(onNavigate: (DrillDownView) -> Unit) {
    var plans by remember { mutableStateOf<List<PricingPlan>>(emptyList()) }
    // Which checkout the paywall sends buyers to. Flipping this is how a ThriveCart promotion is
    // turned on and off — it reaches every app within ~5 minutes and needs no release.
    var paywallMode by remember { mutableStateOf(PricingConfig.MODE_STRIPE) }
    var thriveCartUrl by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<String?>(null) }
    var statusError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun authHeaders(withBody: Boolean): dynamic {
        val h = if (withBody) kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                else kotlin.js.json("Accept" to "application/json")
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        token?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun fetchConfig() {
        scope.launch {
            loading = true
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/pricing-config",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                plans = if (response.ok) {
                    // Seed the editor with the built-in defaults when nothing is configured yet, so the
                    // admin starts from the plans that are actually live rather than a blank slate.
                    val cfg = adminJson.decodeFromString<PricingConfig>(text.toString())
                    paywallMode = cfg.paywallMode
                    thriveCartUrl = cfg.thriveCartUrl.orEmpty()
                    cfg.plans.ifEmpty { PricingConfig.DEFAULT.plans }
                } else {
                    status = "Couldn't load pricing (${response.status}). Showing defaults."; statusError = true
                    PricingConfig.DEFAULT.plans
                }
            } catch (e: Exception) {
                status = "Error loading pricing: ${e.message}. Showing defaults."; statusError = true
                plans = PricingConfig.DEFAULT.plans
            }
            loading = false
        }
    }

    fun updatePlan(index: Int, transform: (PricingPlan) -> PricingPlan) {
        plans = plans.mapIndexed { i, p -> if (i == index) transform(p) else p }
    }

    fun movePlan(fromIndex: Int, toIndex: Int) {
        if (toIndex !in plans.indices) return
        val mutable = plans.toMutableList()
        val item = mutable.removeAt(fromIndex)
        mutable.add(toIndex, item)
        plans = mutable
    }

    fun save() {
        val clean = plans.map { p ->
            p.copy(
                key = p.key.trim(),
                name = p.name.trim(),
                price = p.price.trim(),
                oldPrice = p.oldPrice?.trim()?.takeIf { it.isNotBlank() },
                badge = p.badge?.trim()?.takeIf { it.isNotBlank() },
                features = p.features.map { it.trim() }.filter { it.isNotBlank() },
            )
        }
        when {
            clean.isEmpty() -> { status = "Add at least one plan."; statusError = true }
            clean.any { it.name.isBlank() || it.price.isBlank() || it.key.isBlank() } -> {
                status = "Every plan needs a name, price, and Stripe key."; statusError = true
            }
            clean.any { !it.unlimited && it.fullCredits <= 0 && it.quickCredits <= 0 } -> {
                status = "A fixed plan needs at least 1 full or quick analysis — or mark it Unlimited."; statusError = true
            }
            paywallMode == PricingConfig.MODE_THRIVECART && thriveCartUrl.isBlank() -> {
                // Saving thrivecart mode with no URL would send every buyer to a dead paywall.
                status = "ThriveCart mode needs a checkout URL."; statusError = true
            }
            else -> scope.launch {
                saving = true; status = null; statusError = false
                try {
                    val body = adminJson.encodeToString(
                        PricingConfig.serializer(),
                        PricingConfig(
                            plans = clean,
                            paywallMode = paywallMode,
                            thriveCartUrl = thriveCartUrl.trim().takeIf { it.isNotBlank() },
                        )
                    )
                    val response = kotlinx.browser.window.fetch(
                        "$serverUrl/api/admin/pricing-config",
                        kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                    ).await()
                    if (response.ok) { plans = clean; status = "Saved — live on all apps within ~5 minutes."; statusError = false }
                    else { status = "Save failed (${response.status})."; statusError = true }
                } catch (e: Exception) { status = "Error saving: ${e.message}"; statusError = true }
                saving = false
            }
        }
    }

    LaunchedEffect(Unit) { fetchConfig() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Pricing" to null)

        if (loading) {
            CardSection("Loading…") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching current pricing…") }
            }
            return@PageShell
        }

        CardSection("Where the paywall sends buyers") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 12px") } }) {
                Text(
                    "Switch between selling in-app through Stripe and sending buyers to the " +
                        "ThriveCart offer. Takes effect on all apps within ~5 minutes, no release " +
                        "needed. App versions older than 3.3.7 always use Stripe."
                )
            }
            listOf(
                PricingConfig.MODE_STRIPE to ("In-app (Stripe)" to "The plans below, bought inside the app. Grants pro_29."),
                PricingConfig.MODE_THRIVECART to ("ThriveCart promotion" to "Opens the ThriveCart page instead. Grants thrivecart_9 via its webhook."),
            ).forEach { (mode, copy) ->
                val (label, help) = copy
                val selected = paywallMode == mode
                Div(attrs = {
                    onClick { paywallMode = mode }
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart); gap(10.px)
                        padding(12.px, 14.px); property("margin-bottom", "8px")
                        borderRadius(10.px); cursor("pointer")
                        property("border", if (selected) "2px solid ${Primary}" else "1px solid ${Gray200}")
                        backgroundColor(if (selected) Color("#f0fdfa") else Color.white)
                    }
                }) {
                    Span(attrs = { style { fontSize(15.px); property("line-height", "1.2") } }) {
                        Text(if (selected) "●" else "○")
                    }
                    Div {
                        Div(attrs = { style { fontWeight(700); fontSize(14.px); color(Gray900) } }) { Text(label) }
                        Div(attrs = { style { fontSize(12.px); color(Gray500) } }) { Text(help) }
                    }
                }
            }
            if (paywallMode == PricingConfig.MODE_THRIVECART) {
                Div(attrs = { style { property("margin-top", "10px") } }) {
                    Div(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray600); property("margin-bottom", "4px") } }) {
                        Text("ThriveCart checkout URL")
                    }
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                        value(thriveCartUrl)
                        onInput { thriveCartUrl = it.value }
                        attr("placeholder", "https://learn.thegoodchild.org/the-fitrah-filter/")
                        style {
                            width(100.percent); padding(10.px, 12.px); fontSize(14.px)
                            borderRadius(8.px); property("border", "1px solid ${Gray200}")
                            property("box-sizing", "border-box")
                        }
                    }
                    P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "6px 0 0") } }) {
                        Text(
                            "The buyer's signed-in email and name are added automatically so " +
                                "ThriveCart's form is pre-filled — that's what keeps the purchase " +
                                "attached to their account."
                        )
                    }
                }
            }
        }

        P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 4px") } }) {
            Text("These plans power the paywall on the website and both mobile apps. Show 1–3 plans; each grants either unlimited analyses or a fixed number of full/quick analyses. Changes go live within ~5 minutes — no app update needed. The Stripe plan key must match your Stripe setup.")
        }

        plans.forEachIndexed { index, plan ->
            val badgeSuffix = plan.badge?.takeIf { it.isNotBlank() }?.let { " · ✨ $it" } ?: ""
            CardSection("Plan ${index + 1}$badgeSuffix") {
                // Reorder arrows — only shown when there are multiple plans
                if (plans.size > 1) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); gap(6.px); justifyContent(JustifyContent.FlexEnd)
                            property("margin-bottom", "12px")
                        }
                    }) {
                        listOf("↑" to (index - 1), "↓" to (index + 1)).forEach { (arrow, targetIndex) ->
                            val enabled = targetIndex in plans.indices
                            Span(attrs = {
                                style {
                                    display(DisplayStyle.InlineBlock); width(30.px); height(30.px)
                                    borderRadius(8.px); fontSize(15.px); fontWeight(700)
                                    property("text-align", "center"); property("line-height", "30px")
                                    property("border", "1.5px solid ${if (enabled) Gray300 else Gray200}")
                                    color(if (enabled) Gray600 else Gray300)
                                    backgroundColor(Color.white)
                                    cursor(if (enabled) "pointer" else "default")
                                    property("user-select", "none")
                                    property("transition", TransitionFast)
                                }
                                if (enabled) onClick { movePlan(index, targetIndex) }
                            }) { Text(arrow) }
                        }
                    }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "12px") } }) {
                    PricingField("Plan name", plan.name, "Starter") { v -> updatePlan(index) { it.copy(name = v) } }
                    PricingField("Stripe plan key", plan.key, "basic_10") { v -> updatePlan(index) { it.copy(key = v) } }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "12px") } }) {
                    PricingField("Price", plan.price, "\$9 USD/mo") { v -> updatePlan(index) { it.copy(price = v) } }
                    PricingField("Old price — optional", plan.oldPrice ?: "", "\$12 (blank = none)") { v -> updatePlan(index) { it.copy(oldPrice = v) } }
                }
                // What the plan grants: unlimited (toggle), or a fixed number of full/quick analyses.
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                        property("margin-bottom", "12px"); cursor("pointer"); property("width", "fit-content")
                    }
                    onClick { updatePlan(index) { it.copy(unlimited = !it.unlimited) } }
                }) {
                    Div(attrs = {
                        style {
                            width(38.px); height(22.px); borderRadius(999.px)
                            property("position", "relative"); property("flex", "none")
                            property("transition", TransitionFast)
                            backgroundColor(if (plan.unlimited) Primary else Gray300)
                        }
                    }) {
                        Div(attrs = {
                            style {
                                property("position", "absolute"); property("top", "2px")
                                width(18.px); height(18.px); borderRadius(50.percent)
                                backgroundColor(Color.white); property("transition", TransitionFast)
                                if (plan.unlimited) property("right", "2px") else property("left", "2px")
                            }
                        }) {}
                    }
                    Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray700) } }) { Text("Unlimited analyses") }
                }
                if (plan.unlimited) {
                    P(attrs = { style { color(Gray500); fontSize(12.px); property("margin", "0 0 12px") } }) {
                        Text("Subscribers get unlimited full & quick analyses while their subscription is active.")
                    }
                } else {
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "12px") } }) {
                        PricingField("Full analyses / month", plan.fullCredits.toString(), "10") { v ->
                            updatePlan(index) { it.copy(fullCredits = v.filter { c -> c.isDigit() }.toIntOrNull() ?: 0) }
                        }
                        PricingField("Quick searches / month", plan.quickCredits.toString(), "5") { v ->
                            updatePlan(index) { it.copy(quickCredits = v.filter { c -> c.isDigit() }.toIntOrNull() ?: 0) }
                        }
                    }
                }
                Div(attrs = { style { property("margin-bottom", "12px") } }) {
                    PricingField("Badge — optional", plan.badge ?: "", "e.g. Best value (blank = none)") { v -> updatePlan(index) { it.copy(badge = v) } }
                }
                Div(attrs = {
                    style {
                        fontSize(11.px); fontWeight(700); color(Gray500)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                        property("margin", "0 0 5px")
                    }
                }) { Text("Features — one per line") }
                TextArea(value = plan.features.joinToString("\n")) {
                    onInput { ev -> updatePlan(index) { p -> p.copy(features = ev.value.split("\n")) } }
                    style {
                        width(100.percent); property("box-sizing", "border-box")
                        property("min-height", "72px"); padding(9.px, 11.px); borderRadius(9.px)
                        property("border", "1px solid $Gray200"); fontSize(13.px); property("outline", "none")
                        property("font-family", "inherit"); property("resize", "vertical")
                    }
                    attr("placeholder", "10 full analyses\n5 free quick searches")
                }
                if (plans.size > 1) {
                    Div(attrs = { style { property("margin-top", "12px") } }) {
                        Span(attrs = {
                            style { fontSize(13.px); color(ScoreRed); cursor("pointer"); fontWeight(600) }
                            onClick { plans = plans.filterIndexed { i, _ -> i != index } }
                        }) { Text("✕ Remove plan") }
                    }
                }
            }
        }

        if (plans.size < 3) {
            Div(attrs = { style { property("margin", "0 0 16px") } }) {
                Span(attrs = {
                    style {
                        display(DisplayStyle.InlineBlock)
                        padding(11.px, 16.px); borderRadius(11.px)
                        property("border", "1.5px dashed $Gray300")
                        color(Gray600); fontSize(14.px); fontWeight(700); cursor("pointer")
                    }
                    onClick { plans = plans + PricingPlan(key = "", name = "", price = "") }
                }) { Text("+ Add plan (up to 3)") }
            }
        }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
            Span(attrs = {
                style {
                    padding(12.px, 22.px); borderRadius(12.px)
                    property("background", GradientPrimary); color(Color.white)
                    fontSize(14.px); fontWeight(700); cursor(if (saving) "default" else "pointer")
                    if (saving) property("opacity", "0.7")
                }
                if (!saving) onClick { save() }
            }) { Text(if (saving) "Publishing…" else "Publish to all apps") }

            status?.let {
                Span(attrs = { style { fontSize(13.px); fontWeight(600); color(if (statusError) ScoreRed else PositiveText) } }) { Text(it) }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Limits: weekly analysis limit, free analyses, who hit a limit
// ---------------------------------------------------------------------------

/**
 * The Limits page. Top: the two number settings (the weekly analysis limit for monthly subscribers,
 * and the free analyses every registered account gets), both read and saved through
 * /api/admin/usage-limits. Below: who has been stopped by a limit and how much people use, from
 * GET /api/admin/limits (db/migrations/2026-10-21c_limits_dashboard.sql). A per-person limit is set on
 * the Grant Access page. Web-only; there is no mobile admin.
 */
@Composable
private fun LimitsView(onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    // ── Fair-use daily analysis cap — separate config, own load/save cycle so it never blocks
    // on (or gets blocked by) the pricing plans above. See db/migrations/2026-09-22_daily_analysis_cap.sql.
    var capEnabled by remember { mutableStateOf(false) }
    var capValue by remember { mutableStateOf("") }
    var capLoading by remember { mutableStateOf(true) }
    var capSaving by remember { mutableStateOf(false) }
    var capStatus by remember { mutableStateOf<String?>(null) }
    var capStatusError by remember { mutableStateOf(false) }
    // True when the current value couldn't be read. The editor is hidden then: its "off" would be a
    // guess, and saving it would switch a real cap off.
    var capLoadFailed by remember { mutableStateOf(false) }
    var capUpdatedAt by remember { mutableStateOf<String?>(null) }
    var capUpdatedBy by remember { mutableStateOf<String?>(null) }

    // ── Free analyses for new accounts — the same endpoint as the weekly limit but its own card and
    // own load/save cycle (a Save here posts only free_full_limit, so it never touches the weekly cap).
    // See db/migrations/2026-10-21b_free_searches_editable.sql.
    var freeLimitValue by remember { mutableStateOf("") }
    var freeLimitLoading by remember { mutableStateOf(true) }
    var freeLimitSaving by remember { mutableStateOf(false) }
    var freeLimitStatus by remember { mutableStateOf<String?>(null) }
    var freeLimitStatusError by remember { mutableStateOf(false) }
    // True when the current number couldn't be read: the box is hidden then, so a Save can't overwrite a guess.
    var freeLimitLoadFailed by remember { mutableStateOf(false) }

    fun authHeaders(withBody: Boolean): dynamic {
        val h = if (withBody) kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                else kotlin.js.json("Accept" to "application/json")
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        token?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun fetchCapConfig() {
        scope.launch {
            capLoading = true
            capLoadFailed = false
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/usage-limits",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val cap = (obj?.get("dailyAnalysisCap") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    capEnabled = cap != null
                    capValue = cap?.toString() ?: ""
                    capUpdatedAt = (obj?.get("updatedAt") as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                    capUpdatedBy = (obj?.get("updatedByEmail") as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    capStatus = "Couldn't load the weekly limit (${response.status})${detail?.let { ": $it" } ?: ""}."
                    capStatusError = true; capLoadFailed = true
                }
            } catch (e: Exception) {
                capStatus = "Error loading the weekly limit: ${e.message}"; capStatusError = true; capLoadFailed = true
            }
            capLoading = false
        }
    }

    fun saveCapConfig() {
        val value: Int? = if (!capEnabled) null else {
            val n = capValue.trim().toIntOrNull()
            if (n == null || n !in 1..200) {
                capStatus = "Enter a whole number from 1 to 200, or turn the limit off."
                capStatusError = true
                return
            }
            n
        }
        scope.launch {
            capSaving = true; capStatus = null; capStatusError = false
            try {
                val body = """{"dailyAnalysisCap":${value?.toString() ?: "null"}}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/usage-limits",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    capUpdatedAt = (obj?.get("updatedAt") as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                    capUpdatedBy = (obj?.get("updatedByEmail") as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                    capStatus = "Saved. Live for all users now."; capStatusError = false
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    capStatus = "Save failed (${response.status})${detail?.let { ": $it" } ?: ""}."; capStatusError = true
                }
            } catch (e: Exception) { capStatus = "Error saving: ${e.message}"; capStatusError = true }
            capSaving = false
        }
    }

    fun fetchFreeLimit() {
        scope.launch {
            freeLimitLoading = true
            freeLimitLoadFailed = false
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/usage-limits",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val n = (obj?.get("free_full_limit") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    freeLimitValue = (n ?: 3).toString()
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    freeLimitStatus = "Couldn't load the free analyses number (${response.status})${detail?.let { ": $it" } ?: ""}."
                    freeLimitStatusError = true; freeLimitLoadFailed = true
                }
            } catch (e: Exception) {
                freeLimitStatus = "Error loading the free analyses number: ${e.message}"; freeLimitStatusError = true; freeLimitLoadFailed = true
            }
            freeLimitLoading = false
        }
    }

    fun saveFreeLimit() {
        val n = freeLimitValue.trim().toIntOrNull()
        if (n == null || n !in 0..50) {
            freeLimitStatus = "Enter a whole number from 0 to 50."
            freeLimitStatusError = true
            return
        }
        scope.launch {
            freeLimitSaving = true; freeLimitStatus = null; freeLimitStatusError = false
            try {
                val body = """{"free_full_limit":$n}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/usage-limits",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val saved = (obj?.get("free_full_limit") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    val updated = (obj?.get("accounts_updated") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    if (saved != null) freeLimitValue = saved.toString()
                    freeLimitStatus = "Saved. $updated ${if (updated == 1) "account" else "accounts"} updated."; freeLimitStatusError = false
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    freeLimitStatus = "Save failed (${response.status})${detail?.let { ": $it" } ?: ""}."; freeLimitStatusError = true
                }
            } catch (e: Exception) { freeLimitStatus = "Error saving: ${e.message}"; freeLimitStatusError = true }
            freeLimitSaving = false
        }
    }

    // ── Who hit a limit, and how much people use ───────────────────────────────────────────────
    // One GET of /api/admin/limits (admin_limits_overview, db/migrations/2026-10-21c_limits_dashboard.sql).
    // Separate from the two settings above: a failure here (migration not run yet) never hides them.
    var days by remember { mutableStateOf(30) }
    var overview by remember { mutableStateOf<LimitsOverview?>(null) }
    var overviewLoading by remember { mutableStateOf(true) }
    var overviewError by remember { mutableStateOf<String?>(null) }
    // Admin and test accounts are hidden from every list unless this is on.
    var showAdmins by remember { mutableStateOf(false) }
    // Bumped by every request, so a slow answer for 90 days never overwrites a newer one for 7.
    var requestId by remember { mutableStateOf(0) }

    fun fetchOverview() {
        requestId += 1
        val mine = requestId
        scope.launch {
            overviewLoading = true; overviewError = null
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/limits?days=$days",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (mine != requestId) return@launch
                if (response.ok) {
                    overview = limitsJson.decodeFromString<LimitsOverview>(text)
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    // Old numbers are dropped, not left up: they would read as current.
                    overview = null
                    overviewError = "Couldn't load the limit numbers (${response.status})${detail?.let { ": $it" } ?: ""}."
                }
            } catch (e: Exception) {
                if (mine == requestId) { overview = null; overviewError = "Error loading the limit numbers: ${e.message}" }
            }
            if (mine == requestId) overviewLoading = false
        }
    }

    // ── Requests for more searches ─────────────────────────────────────────────────────────────
    // What people wrote in the weekly-limit pop-up's "Request additional searches" form
    // (GET /api/admin/limit-requests, db/migrations/2026-10-21e_limit_requests.sql). Its own load, so a
    // failure here (migration not run yet) never hides the numbers below, and the other way round.
    var requests by remember { mutableStateOf<List<LimitRequestRow>?>(null) }
    var requestsLoading by remember { mutableStateOf(true) }
    var requestsError by remember { mutableStateOf<String?>(null) }
    var showHandled by remember { mutableStateOf(false) }
    var requestsLoadId by remember { mutableStateOf(0) }
    // The request a "Mark handled" / "Reopen" is saving right now (one at a time), and why one failed.
    var busyRequestId by remember { mutableStateOf<Long?>(null) }
    var requestActionError by remember { mutableStateOf<String?>(null) }

    fun fetchRequests() {
        requestsLoadId += 1
        val mine = requestsLoadId
        val status = if (showHandled) "all" else "open"
        scope.launch {
            requestsLoading = true; requestsError = null
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/limit-requests?status=$status",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (mine != requestsLoadId) return@launch
                if (response.ok) {
                    requests = limitsJson.decodeFromString<List<LimitRequestRow>>(text)
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    // An old list is dropped, not left up: it would read as current.
                    requests = null
                    requestsError = "Couldn't load the requests (${response.status})${detail?.let { ": $it" } ?: ""}."
                }
            } catch (e: Exception) {
                if (mine == requestsLoadId) { requests = null; requestsError = "Error loading the requests: ${e.message}" }
            }
            if (mine == requestsLoadId) requestsLoading = false
        }
    }

    fun setRequestStatus(id: Long, status: String) {
        if (busyRequestId != null) return
        scope.launch {
            busyRequestId = id; requestActionError = null
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/limit-requests/$id/status",
                    kotlin.js.json(
                        "method" to "POST", "headers" to authHeaders(true), "body" to """{"status":"$status"}"""
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val updated = limitsJson.decodeFromString<LimitRequestRow>(text)
                    requests = requests?.let { list ->
                        // The open-only list no longer shows a request once it is handled.
                        if (!showHandled && updated.status != "open") list.filter { it.id != updated.id }
                        else list.map { if (it.id == updated.id) updated else it }
                    }
                } else {
                    val detail = runCatching {
                        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                            ?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    }.getOrNull()
                    requestActionError = "Couldn't update the request (${response.status})${detail?.let { ": $it" } ?: ""}."
                }
            } catch (e: Exception) { requestActionError = "Error updating the request: ${e.message}" }
            busyRequestId = null
        }
    }

    LaunchedEffect(Unit) { fetchCapConfig() }
    LaunchedEffect(Unit) { fetchFreeLimit() }
    LaunchedEffect(days) { fetchOverview() }
    LaunchedEffect(showHandled) { fetchRequests() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Limits" to null)

        CardSection("Fair use: weekly analysis limit") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 14px"); property("line-height", "1.5") } }) {
                Text(
                    "This limit applies to monthly subscribers only (in-app and ThriveCart monthly). " +
                        "Founding members, annual plans, gifted members, free accounts and Library " +
                        "(search pack) accounts are not limited. Every analysis, quick or full, counts " +
                        "toward one number per account, over a rolling 7 days (each search frees up " +
                        "exactly 7 days after it was made). Off means no limit for anyone, except an " +
                        "account given its own number on the Grant Access page: a limit set there applies " +
                        "to that person whatever their plan, always takes precedence over this one (even " +
                        "when this is off), and \"No limit for this user\" exempts them. A change here " +
                        "applies to all monthly subscribers immediately. New app builds show a friendly " +
                        "dialog when someone hits the limit; older builds already in the stores show a " +
                        "plain message instead. Switching it on counts searches from the previous 7 days " +
                        "straight away."
                )
            }
            if (capLoading) {
                P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
            } else if (capLoadFailed) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                    capStatus?.let {
                        Span(attrs = { style { fontSize(13.px); fontWeight(600); color(ScoreRed) } }) { Text(it) }
                    }
                    Span(attrs = {
                        style { fontSize(13.px); fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                        onClick { capStatus = null; capStatusError = false; fetchCapConfig() }
                    }) { Text("Retry") }
                }
            } else {
                EditorToggle(label = "Weekly limit ${if (capEnabled) "on" else "off"}", checked = capEnabled) { capEnabled = !capEnabled }
                if (capEnabled) {
                    Div(attrs = { style { property("margin-top", "12px"); property("max-width", "220px") } }) {
                        PricingField("Analyses per monthly subscriber / week (1-200)", capValue, "e.g. 5") { v ->
                            capValue = v.filter { c -> c.isDigit() }
                        }
                    }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap"); property("margin-top", "14px") } }) {
                    Span(attrs = {
                        style {
                            padding(10.px, 20.px); borderRadius(10.px)
                            property("background", GradientPrimary); color(Color.white)
                            fontSize(13.px); fontWeight(700); cursor(if (capSaving) "default" else "pointer")
                            if (capSaving) property("opacity", "0.7")
                        }
                        if (!capSaving) onClick { saveCapConfig() }
                    }) { Text(if (capSaving) "Saving…" else "Save") }
                    capStatus?.let {
                        Span(attrs = { style { fontSize(13.px); fontWeight(600); color(if (capStatusError) ScoreRed else PositiveText) } }) { Text(it) }
                    }
                }
                if (capUpdatedAt != null || capUpdatedBy != null) {
                    P(attrs = { style { color(Gray400); fontSize(11.px); property("margin", "10px 0 0") } }) {
                        Text("Last updated ${capUpdatedAt?.take(16)?.replace("T", " ") ?: "unknown time"}${capUpdatedBy?.let { " by $it" } ?: ""}")
                    }
                }
            }
        }

        CardSection("Free analyses for new accounts") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 14px"); property("line-height", "1.5") } }) {
                Text(
                    "How many full analyses a registered, non-paying account gets for free before the " +
                        "paywall. It's a lifetime number, not per month. Changing it applies to every " +
                        "account straight away: raising it gives people who already used theirs the " +
                        "difference; lowering it can send some people straight to the paywall. 0 means " +
                        "no free analyses. Quick checks are never free."
                )
            }
            if (freeLimitLoading) {
                P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
            } else if (freeLimitLoadFailed) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                    freeLimitStatus?.let {
                        Span(attrs = { style { fontSize(13.px); fontWeight(600); color(ScoreRed) } }) { Text(it) }
                    }
                    Span(attrs = {
                        style { fontSize(13.px); fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                        onClick { freeLimitStatus = null; freeLimitStatusError = false; fetchFreeLimit() }
                    }) { Text("Retry") }
                }
            } else {
                Div(attrs = { style { property("max-width", "220px") } }) {
                    PricingField("Free analyses per account (0-50)", freeLimitValue, "e.g. 3") { v ->
                        freeLimitValue = v.filter { c -> c.isDigit() }
                    }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap"); property("margin-top", "14px") } }) {
                    Span(attrs = {
                        style {
                            padding(10.px, 20.px); borderRadius(10.px)
                            property("background", GradientPrimary); color(Color.white)
                            fontSize(13.px); fontWeight(700); cursor(if (freeLimitSaving) "default" else "pointer")
                            if (freeLimitSaving) property("opacity", "0.7")
                        }
                        if (!freeLimitSaving) onClick { saveFreeLimit() }
                    }) { Text(if (freeLimitSaving) "Saving…" else "Save") }
                    freeLimitStatus?.let {
                        Span(attrs = { style { fontSize(13.px); fontWeight(600); color(if (freeLimitStatusError) ScoreRed else PositiveText) } }) { Text(it) }
                    }
                }
            }
        }

        CardSection("Usage and limit hits", onRefresh = { fetchOverview() }) {
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px); property("flex-wrap", "wrap") } }) {
                Span(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.04em") } }) {
                    Text("Look back")
                }
                listOf(7, 30, 90).forEach { d ->
                    LimitsDayChip("$d days", d == days) { if (d != days) days = d }
                }
                Div(attrs = { style { property("margin-left", "auto") } }) {
                    EditorToggle(label = "Show admin and test accounts", checked = showAdmins) { showAdmins = !showAdmins }
                }
            }
            P(attrs = { style { color(Gray500); fontSize(12.px); property("margin", "12px 0 0"); property("line-height", "1.5") } }) {
                Text(
                    "Who was stopped by a limit, and how much people use. \"Who hit a limit\" and the free-analyses " +
                        "list look back over the days chosen above; the weekly numbers always cover the last 7 days. " +
                        "Hits are recorded from 21 Oct 2026."
                )
            }

            val ov = overview
            if (overviewLoading && ov == null) {
                P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "14px 0 0") } }) { Text("Loading…") }
            } else if (overviewError != null) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap"); property("margin-top", "14px") } }) {
                    Span(attrs = { style { fontSize(13.px); fontWeight(600); color(ScoreRed) } }) { Text(overviewError!!) }
                    Span(attrs = {
                        style { fontSize(13.px); fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                        onClick { fetchOverview() }
                    }) { Text("Retry") }
                }
            } else if (ov != null) {
                if (overviewLoading) {
                    P(attrs = { style { color(Gray400); fontSize(12.px); property("margin", "10px 0 0") } }) { Text("Refreshing…") }
                }
                // The tiles follow the lists: with admin and test accounts hidden, a tile counting them would
                // disagree with the table under it. The hits list is capped at 500 rows by the database, so
                // when it is full the database's own totals are used instead.
                val visibleHits = ov.hits.filter { showAdmins || !it.isAdmin }
                val hitsCapped = ov.hits.size >= 500
                fun hitPeople(kind: String) = visibleHits.filter { it.kind == kind }.map { it.email }.toSet().size
                val weeklyPeople = if (hitsCapped) ov.summary.weeklyHitPeople else hitPeople("weekly_cap")
                val weeklyTimes = if (hitsCapped) ov.summary.weeklyHitTotal else visibleHits.filter { it.kind == "weekly_cap" }.sumOf { it.times }
                val outOfFreePeople = if (hitsCapped) ov.summary.outOfFreePeople else hitPeople("out_of_free")
                val packEmptyPeople = if (hitsCapped) ov.summary.packEmptyPeople else hitPeople("pack_empty")
                val visibleCustom = ov.customLimits.filter { showAdmins || !it.isAdmin }
                val visibleLibrary = ov.library.filter { showAdmins || !it.isAdmin }

                Div(attrs = { style { property("margin-top", "18px") } }) {
                    SummarySection("Last $days days") {
                        // Not date-filtered: every request still open, whenever it came in.
                        requests?.let { list ->
                            val open = list.count { it.status == "open" }
                            SummaryCard("Open requests", open.toString(), if (open > 0) Color("#b45309") else Gray600)
                        }
                        SummaryCard(
                            "Hit the weekly limit", weeklyPeople.toString(), Color("#b45309"),
                            secondary = "$weeklyTimes ${if (weeklyTimes == 1) "time" else "times"} in total"
                        )
                        SummaryCard("Ran out of free analyses", outOfFreePeople.toString(), ScoreRed)
                        SummaryCard("Library out of searches", packEmptyPeople.toString(), Color("#7e22ce"))
                        SummaryCard("Monthly subscribers", ov.summary.monthlySubscribers.toString(), Primary)
                        SummaryCard("Custom limits", visibleCustom.size.toString(), Gray600)
                        SummaryCard("Library accounts", visibleLibrary.size.toString(), Gray600)
                    }
                }
            }
        }

        // ── Requests for more searches ──────────────────────────────────────────────────────────
        CardSection("Requests for more searches", onRefresh = { fetchRequests() }) {
            LimitsNote(
                "Sent from the weekly-limit pop-up (\"Request additional searches\"), newest first, and also " +
                    "emailed to support@fitrahfilter.com with the person's address as the reply address. Nothing is " +
                    "granted automatically: to give more searches, set a weekly limit for that person on Grant " +
                    "Access, answer them by email, then press Mark handled. One request per person per day."
            )
            Div(attrs = { style { property("margin-bottom", "14px") } }) {
                EditorToggle(label = "Show handled", checked = showHandled) { showHandled = !showHandled }
            }
            val list = requests
            if (requestsLoading && list == null) {
                P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
            } else if (requestsError != null) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                    Span(attrs = { style { fontSize(13.px); fontWeight(600); color(ScoreRed) } }) { Text(requestsError!!) }
                    Span(attrs = {
                        style { fontSize(13.px); fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                        onClick { fetchRequests() }
                    }) { Text("Retry") }
                }
            } else if (list != null) {
                if (requestsLoading) {
                    P(attrs = { style { color(Gray400); fontSize(12.px); property("margin", "0 0 10px") } }) { Text("Refreshing…") }
                }
                requestActionError?.let {
                    P(attrs = { style { color(ScoreRed); fontSize(13.px); fontWeight(600); property("margin", "0 0 10px") } }) { Text(it) }
                }
                if (list.isEmpty()) {
                    LimitsNote(if (showHandled) "No requests yet." else "No open requests.")
                } else {
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("When"); AdminTh("Person"); AdminTh("Reason"); AdminTh("Used / limit")
                                AdminTh("Can search again"); AdminTh("Status")
                            }
                        }
                        Tbody {
                            list.forEachIndexed { index, r ->
                                LimitsRow(index) {
                                    LimitsTd(formatLocalDateTime(r.createdAt), textColor = Gray500)
                                    LimitsPersonTd(r.email, false)
                                    // The full text, wrapped, with the person's own line breaks kept.
                                    Td(attrs = {
                                        style {
                                            padding(12.px, 16.px); fontSize(13.px); color(Gray900)
                                            property("min-width", "260px"); property("max-width", "460px")
                                            property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere")
                                            property("line-height", "1.5")
                                        }
                                    }) { Text(r.reason) }
                                    LimitsTd(
                                        when {
                                            r.used != null && r.capLimit != null -> "${r.used} / ${r.capLimit}"
                                            r.used != null -> r.used.toString()
                                            else -> "—"
                                        },
                                        bold = r.used != null, textColor = if (r.used != null) Primary else Gray500
                                    )
                                    LimitsTd(
                                        r.resetsAt?.let { "${formatLocalDateTime(it)} (${formatTimeUntil(it)})" } ?: "—",
                                        textColor = Gray500
                                    )
                                    LimitRequestStatusTd(r, busy = busyRequestId == r.id) {
                                        setRequestStatus(r.id, if (r.status == "handled") "open" else "handled")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        val ov = overview
        if (ov != null) {
            val visibleHits = ov.hits.filter { showAdmins || !it.isAdmin }
            val visibleUsage = ov.weeklyUsage.filter { showAdmins || !it.isAdmin }
            val visibleFreeRecent = ov.freeUsage.allUsedRecent.filter { showAdmins || !it.isAdmin }
            val visibleCustom = ov.customLimits.filter { showAdmins || !it.isAdmin }
            val visibleLibrary = ov.library.filter { showAdmins || !it.isAdmin }
            val weeklyLimit = ov.settings.weeklyLimit

            // ── Who hit a limit ─────────────────────────────────────────────────────────────────
            CardSection("Who hit a limit") {
                if (visibleHits.isEmpty()) {
                    LimitsNote("No one has hit a limit in the last ${ov.days} days. (Hits are recorded from 21 Oct 2026.)")
                } else {
                    LimitsNote("One row per person and kind of limit, newest first. \"Times\" is how many separate searches were refused in the last ${ov.days} days.")
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Person"); AdminTh("Limit hit"); AdminTh("Times"); AdminTh("Last"); AdminTh("Plan"); AdminTh("Platform")
                            }
                        }
                        Tbody {
                            visibleHits.forEachIndexed { index, h ->
                                LimitsRow(index) {
                                    LimitsPersonTd(h.email, h.isAdmin)
                                    Td(attrs = { style { padding(12.px, 16.px) } }) { LimitsKindPill(h.kind) }
                                    LimitsTd(h.times.toString(), bold = true, textColor = Primary)
                                    LimitsTd(formatIsoDate(h.lastAt), textColor = Gray500)
                                    LimitsTd(limitsPlanLabel(h.planKind))
                                    LimitsTd(limitsPlatformLabel(h.lastPlatform))
                                }
                            }
                        }
                    }
                }
            }

            // ── Monthly subscribers this week ───────────────────────────────────────────────────
            CardSection("Monthly subscribers this week") {
                LimitsNote(
                    "How many searches each monthly subscriber ran in the last 7 days (quick and full count together). " +
                        "The weekly limit is " + (if (weeklyLimit != null) "$weeklyLimit searches per ${ov.settings.windowDays} days" else "off") + " right now. " +
                        "Use this to pick the weekly number: anyone above it would have been stopped. " +
                        "For anyone at or over their limit: when they reached it, when they can search again, and when they were last refused (your local time)."
                )
                val dist = ov.weeklyDistribution
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap"); property("margin-bottom", "16px") } }) {
                    LimitsCountChip("Monthly subscribers", dist.monthlySubscribers, Primary)
                    LimitsCountChip("0 searches", dist.ran0, Gray600)
                    LimitsCountChip("1–4 searches", dist.ran1to4, Gray600)
                    LimitsCountChip("5–9 searches", dist.ran5to9, Gray600)
                    LimitsCountChip("10–19 searches", dist.ran10to19, Color("#b45309"))
                    LimitsCountChip("20+ searches", dist.ran20plus, ScoreRed)
                }
                if (visibleUsage.isEmpty()) {
                    LimitsNote("No one has searched this week.")
                } else {
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Person"); AdminTh("Plan"); AdminTh("Searches this week"); AdminTh("Limit")
                                AdminTh("Reached limit"); AdminTh("Can search again"); AdminTh("Last refused")
                            }
                        }
                        Tbody {
                            visibleUsage.forEachIndexed { index, u ->
                                val over = u.limit != null && u.limit > 0 && u.used >= u.limit
                                LimitsRow(index) {
                                    LimitsPersonTd(u.email, u.isAdmin)
                                    LimitsTd(limitsPlanLabel(u.planKind))
                                    LimitsTd(u.used.toString(), bold = true, textColor = if (over) ScoreRed else Primary)
                                    LimitsTd(u.limit?.toString() ?: "—", textColor = Gray500)
                                    LimitsTd(formatLocalDateTime(u.limitReachedAt), textColor = if (over) ScoreRed else Gray500)
                                    LimitsTd(
                                        u.resetsAt?.let { "${formatLocalDateTime(it)} (${formatTimeUntil(it)})" } ?: "—",
                                        bold = u.resetsAt != null, textColor = if (u.resetsAt != null) Primary else Gray500
                                    )
                                    LimitsTd(formatLocalDateTime(u.lastRefusedAt), textColor = Gray500)
                                }
                            }
                        }
                    }
                }
            }

            // ── Free analyses ───────────────────────────────────────────────────────────────────
            CardSection("Free analyses") {
                val freeLimit = ov.freeUsage.limit
                if (freeLimit <= 0) {
                    LimitsNote("Free analyses are set to 0, so nobody gets any.")
                } else {
                    LimitsNote("Everyone not subscribed, by how many of their $freeLimit free analyses they have used. Quick checks are never free.")
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap"); property("margin-bottom", "16px") } }) {
                        ov.freeUsage.byUsed.sortedBy { it.used }.forEach { b ->
                            val all = b.used >= freeLimit
                            LimitsCountChip(if (all) "Used all $freeLimit" else "Used ${b.used}", b.accounts, if (all) ScoreRed else Gray600)
                        }
                    }
                }
                Div(attrs = { style { fontSize(14.px); fontWeight(700); color(Gray900); property("margin", "4px 0 4px") } }) {
                    Text("Used all their free analyses recently (not subscribed)")
                }
                if (visibleFreeRecent.isEmpty()) {
                    LimitsNote("No one in the last ${ov.days} days.")
                } else {
                    LimitsNote("A good list to follow up with.")
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Person"); AdminTh("Last free analysis")
                            }
                        }
                        Tbody {
                            visibleFreeRecent.forEachIndexed { index, r ->
                                LimitsRow(index) {
                                    LimitsPersonTd(r.email, r.isAdmin)
                                    LimitsTd(formatIsoDate(r.lastFreeAt), textColor = Gray500)
                                }
                            }
                        }
                    }
                }
            }

            // ── Custom limits ───────────────────────────────────────────────────────────────────
            CardSection("Custom limits (set on Grant Access)") {
                if (visibleCustom.isEmpty()) {
                    LimitsNote("No account has its own limit.")
                } else {
                    LimitsNote("Accounts given their own weekly number on the Grant Access page. It replaces the site-wide rule for that person.")
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Person"); AdminTh("Limit"); AdminTh("Plan"); AdminTh("Used this week")
                            }
                        }
                        Tbody {
                            visibleCustom.forEachIndexed { index, c ->
                                val atLimit = c.customLimit > 0 && c.usedInWindow >= c.customLimit
                                LimitsRow(index) {
                                    LimitsPersonTd(c.email, c.isAdmin)
                                    LimitsTd(if (c.customLimit == 0) "No limit" else "${c.customLimit} per week", bold = true)
                                    LimitsTd(limitsPlanLabel(c.planKind))
                                    LimitsTd(c.usedInWindow.toString(), bold = true, textColor = if (atLimit) ScoreRed else Primary)
                                }
                            }
                        }
                    }
                }
            }

            // ── Library accounts ────────────────────────────────────────────────────────────────
            CardSection("Library accounts") {
                if (visibleLibrary.isEmpty()) {
                    LimitsNote("No account holds a search pack.")
                } else {
                    LimitsNote("Accounts with a search pack. A row in red has no searches left.")
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Person"); AdminTh("Used / Total"); AdminTh("Remaining"); AdminTh("Expires")
                            }
                        }
                        Tbody {
                            visibleLibrary.forEachIndexed { index, l ->
                                val empty = l.remaining <= 0
                                LimitsRow(index, highlight = empty) {
                                    LimitsPersonTd(l.email, l.isAdmin)
                                    LimitsTd("${(l.total - l.remaining).coerceAtLeast(0)} / ${l.total}")
                                    LimitsTd(l.remaining.toString(), bold = true, textColor = if (empty) ScoreRed else PositiveText)
                                    LimitsTd(l.expiresAt?.let { formatIsoDate(it).substringBefore(" ") } ?: "Never", textColor = Gray500)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** GET /api/admin/limits, as the Limits page reads it. Every field has a default so a missing one never blanks the page. */
@Serializable
private data class LimitsOverview(
    @SerialName("generated_at") val generatedAt: String? = null,
    val days: Int = 30,
    val settings: LimitsSettings = LimitsSettings(),
    val summary: LimitsSummary = LimitsSummary(),
    val hits: List<LimitHitRow> = emptyList(),
    @SerialName("weekly_usage") val weeklyUsage: List<LimitWeeklyRow> = emptyList(),
    @SerialName("weekly_distribution") val weeklyDistribution: LimitDistribution = LimitDistribution(),
    @SerialName("free_usage") val freeUsage: LimitFreeUsage = LimitFreeUsage(),
    @SerialName("custom_limits") val customLimits: List<LimitCustomRow> = emptyList(),
    val library: List<LimitLibraryRow> = emptyList(),
)

@Serializable
private data class LimitsSettings(
    /** null = the weekly limit is off. */
    @SerialName("weekly_limit") val weeklyLimit: Int? = null,
    @SerialName("window_days") val windowDays: Int = 7,
    @SerialName("free_full_limit") val freeFullLimit: Int = 3,
)

@Serializable
private data class LimitsSummary(
    @SerialName("weekly_hit_people") val weeklyHitPeople: Int = 0,
    @SerialName("weekly_hit_total") val weeklyHitTotal: Int = 0,
    @SerialName("out_of_free_people") val outOfFreePeople: Int = 0,
    @SerialName("pack_empty_people") val packEmptyPeople: Int = 0,
    @SerialName("monthly_subscribers") val monthlySubscribers: Int = 0,
    @SerialName("free_all_used") val freeAllUsed: Int = 0,
    @SerialName("custom_limit_accounts") val customLimitAccounts: Int = 0,
    @SerialName("library_accounts") val libraryAccounts: Int = 0,
)

/** One person and one kind of limit: kind is weekly_cap, out_of_free or pack_empty. */
@Serializable
private data class LimitHitRow(
    val email: String = "",
    val kind: String = "",
    val times: Int = 0,
    @SerialName("first_at") val firstAt: String? = null,
    @SerialName("last_at") val lastAt: String? = null,
    @SerialName("plan_kind") val planKind: String? = null,
    @SerialName("last_platform") val lastPlatform: String? = null,
    @SerialName("is_admin") val isAdmin: Boolean = false,
)

@Serializable
private data class LimitWeeklyRow(
    val email: String = "",
    @SerialName("plan_kind") val planKind: String? = null,
    val used: Int = 0,
    /** null = this account has no weekly limit. */
    val limit: Int? = null,
    @SerialName("is_admin") val isAdmin: Boolean = false,
    /** When they used their last allowed search this window; null while under the limit (2026-10-21d). */
    @SerialName("limit_reached_at") val limitReachedAt: String? = null,
    /** When they can search again (enough of this window's searches have aged out); null while under the limit. */
    @SerialName("resets_at") val resetsAt: String? = null,
    /** Their latest refusal by the weekly limit, if any. */
    @SerialName("last_refused_at") val lastRefusedAt: String? = null,
)

@Serializable
private data class LimitDistribution(
    @SerialName("monthly_subscribers") val monthlySubscribers: Int = 0,
    @SerialName("ran_0") val ran0: Int = 0,
    @SerialName("ran_1_4") val ran1to4: Int = 0,
    @SerialName("ran_5_9") val ran5to9: Int = 0,
    @SerialName("ran_10_19") val ran10to19: Int = 0,
    @SerialName("ran_20_plus") val ran20plus: Int = 0,
)

@Serializable
private data class LimitFreeBucket(val used: Int = 0, val accounts: Int = 0)

@Serializable
private data class LimitFreeRecent(
    val email: String = "",
    @SerialName("last_free_at") val lastFreeAt: String? = null,
    @SerialName("is_admin") val isAdmin: Boolean = false,
)

@Serializable
private data class LimitFreeUsage(
    val limit: Int = 3,
    /** Non-subscribers by free analyses used; the last bucket is "all used". */
    @SerialName("by_used") val byUsed: List<LimitFreeBucket> = emptyList(),
    @SerialName("all_used_recent") val allUsedRecent: List<LimitFreeRecent> = emptyList(),
)

@Serializable
private data class LimitCustomRow(
    val email: String = "",
    /** 0 = no limit for this account; otherwise its own weekly number. */
    @SerialName("override") val customLimit: Int = 0,
    @SerialName("plan_kind") val planKind: String? = null,
    @SerialName("used_in_window") val usedInWindow: Int = 0,
    @SerialName("is_admin") val isAdmin: Boolean = false,
)

@Serializable
private data class LimitLibraryRow(
    val email: String = "",
    val total: Int = 0,
    val remaining: Int = 0,
    @SerialName("expires_at") val expiresAt: String? = null,
    @SerialName("is_admin") val isAdmin: Boolean = false,
)

/** One row of GET /api/admin/limit-requests: what someone wrote when the weekly limit stopped them. */
@Serializable
private data class LimitRequestRow(
    val id: Long = 0,
    val email: String = "",
    val reason: String = "",
    /** Their searches in the last 7 days, and their limit, when they asked. */
    val used: Int? = null,
    @SerialName("cap_limit") val capLimit: Int? = null,
    @SerialName("resets_at") val resetsAt: String? = null,
    val platform: String? = null,
    @SerialName("app_version") val appVersion: String? = null,
    /** False when the email to support did not go out; the request is still here. */
    val emailed: Boolean = false,
    /** "open" or "handled". */
    val status: String = "open",
    @SerialName("handled_at") val handledAt: String? = null,
    @SerialName("handled_by") val handledBy: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
)

/** A request's status pill and its "Mark handled" / "Reopen" button; [onToggle] flips it. */
@Composable
private fun LimitRequestStatusTd(r: LimitRequestRow, busy: Boolean, onToggle: () -> Unit) {
    val handled = r.status == "handled"
    Td(attrs = { style { padding(12.px, 16.px); property("white-space", "nowrap") } }) {
        Span(attrs = {
            style {
                display(DisplayStyle.InlineBlock); padding(3.px, 10.px); borderRadius(999.px)
                fontSize(12.px); fontWeight(700)
                if (handled) { backgroundColor(PositiveBg); color(PositiveText) } else { backgroundColor(ScoreAmberBg); color(Color("#b45309")) }
            }
        }) { Text(if (handled) "Handled" else "Open") }
        Div(attrs = { style { property("margin-top", "8px") } }) {
            Span(attrs = {
                attr("role", "button")
                if (!busy) onClick { onToggle() }
                style {
                    display(DisplayStyle.InlineBlock); padding(5.px, 12.px); borderRadius(8.px)
                    fontSize(12.px); fontWeight(700); cursor(if (busy) "default" else "pointer")
                    if (handled) {
                        color(Gray600); backgroundColor(Color.white); property("border", "1px solid ${Gray300}")
                    } else {
                        color(Color.white); property("background", GradientPrimary)
                    }
                    if (busy) property("opacity", "0.6")
                }
            }) { Text(if (busy) "Saving…" else if (handled) "Reopen" else "Mark handled") }
        }
        if (handled && (r.handledBy != null || r.handledAt != null)) {
            Div(attrs = { style { property("margin-top", "6px"); fontSize(11.px); color(Gray400) } }) {
                Text("${formatLocalDateTime(r.handledAt)}${r.handledBy?.let { " by $it" } ?: ""}")
            }
        }
        if (!handled && !r.emailed) {
            Div(attrs = { style { property("margin-top", "6px"); fontSize(11.px); color(ScoreRed); property("white-space", "normal") } }) {
                Text("The email to support did not go out.")
            }
        }
    }
}

/** Lenient on purpose: a null or missing number must fall back to its default, not fail the whole page. */
private val limitsJson = Json { ignoreUnknownKeys = true; isLenient = true; coerceInputValues = true }

private fun limitsPlanLabel(kind: String?): String = when (kind) {
    "monthly" -> "Monthly"
    "annual" -> "Annual"
    "founding" -> "Founding"
    "gifted" -> "Gifted"
    "library" -> "Library"
    "other_paid" -> "Paid (other)"
    "free" -> "Free"
    else -> kind?.takeIf { it.isNotBlank() } ?: "—"
}

private fun limitsKindLabel(kind: String): String = when (kind) {
    "weekly_cap" -> "Weekly limit"
    "out_of_free" -> "Out of free analyses"
    "pack_empty" -> "Library out of searches"
    else -> kind
}

private fun limitsPlatformLabel(platform: String?): String = when (platform?.lowercase()) {
    "ios" -> "iOS"
    "android" -> "Android"
    "web" -> "Web"
    "bulk_scan" -> "Bulk scan"
    null, "" -> "—"
    else -> platform
}

/** A short grey explanation above a table. */
@Composable
private fun LimitsNote(text: String) {
    P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 12px"); property("line-height", "1.5") } }) { Text(text) }
}

/** One table row with the zebra striping the other admin tables use; [highlight] paints it red. */
@Composable
private fun LimitsRow(index: Int, highlight: Boolean = false, cells: @Composable () -> Unit) {
    Tr(attrs = {
        style {
            if (highlight) backgroundColor(ScoreRedBg) else if (index % 2 == 1) backgroundColor(Gray50)
            property("border-bottom", "1px solid ${Gray200}")
        }
    }) { cells() }
}

@Composable
private fun LimitsTd(text: String, bold: Boolean = false, textColor: CSSColorValue = Gray700) {
    Td(attrs = {
        style {
            padding(12.px, 16.px); fontSize(13.px); color(textColor)
            if (bold) fontWeight(700)
        }
    }) { Text(text) }
}

/** The email, a grey "admin" tag for the team's own accounts, and a Copy action for pasting into Grant Access. */
@Composable
private fun LimitsPersonTd(email: String, isAdmin: Boolean) {
    Td(attrs = { style { padding(12.px, 16.px) } }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); property("flex-wrap", "wrap") } }) {
            Span(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray900); property("word-break", "break-all") } }) { Text(email) }
            if (isAdmin) {
                Span(attrs = {
                    style {
                        fontSize(10.px); fontWeight(700); color(Gray500); padding(1.px, 6.px); borderRadius(999.px)
                        backgroundColor(Gray100); property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                    }
                }) { Text("admin") }
            }
            LimitsCopyButton(email)
        }
    }
}

@Composable
private fun LimitsCopyButton(email: String) {
    var copied by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    Span(attrs = {
        attr("role", "button")
        attr("title", "Copy this email")
        onClick {
            scope.launch {
                try {
                    kotlinx.browser.window.navigator.clipboard.writeText(email).await()
                    copied = true
                    delay(1500)
                    copied = false
                } catch (_: Throwable) {
                    // Blocked by the browser (not a secure page, or no permission): say so rather than pretend.
                    copied = false
                }
            }
        }
        style {
            fontSize(11.px); fontWeight(600); color(Primary); cursor("pointer")
            property("text-decoration", "underline"); property("white-space", "nowrap")
        }
    }) { Text(if (copied) "Copied" else "Copy") }
}

@Composable
private fun LimitsKindPill(kind: String) {
    val (bg, fg) = when (kind) {
        "weekly_cap" -> ScoreAmberBg to Color("#b45309")
        "out_of_free" -> ScoreRedBg to Color("#b91c1c")
        else -> Color("#f3e8ff") to Color("#7e22ce")
    }
    Span(attrs = {
        style {
            display(DisplayStyle.InlineBlock); padding(3.px, 10.px); borderRadius(999.px)
            backgroundColor(bg); color(fg); fontSize(12.px); fontWeight(700); property("white-space", "nowrap")
        }
    }) { Text(limitsKindLabel(kind)) }
}

/** A small figure with a caption, for the "how many searches did people run" rows. */
@Composable
private fun LimitsCountChip(label: String, value: Int, accent: CSSColorValue) {
    Div(attrs = {
        style {
            padding(8.px, 14.px); borderRadius(10.px); property("min-width", "92px")
            property("border", "1px solid ${Gray200}"); backgroundColor(Gray50)
        }
    }) {
        Div(attrs = { style { fontSize(20.px); fontWeight(800); color(accent); property("line-height", "1.15") } }) { Text(value.toString()) }
        Div(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-top", "2px") } }) { Text(label) }
    }
}

@Composable
private fun LimitsDayChip(label: String, selected: Boolean, onSelect: () -> Unit) {
    Span(attrs = {
        attr("role", "button")
        onClick { onSelect() }
        style {
            padding(6.px, 14.px); borderRadius(999.px); fontSize(13.px); fontWeight(600); cursor("pointer")
            if (selected) {
                backgroundColor(Primary); color(Color.white)
            } else {
                backgroundColor(Color.white); color(Gray700); property("border", "1px solid ${Gray200}")
            }
        }
    }) { Text(label) }
}

// ---------------------------------------------------------------------------
// Cancel Flow Editor
// ---------------------------------------------------------------------------

/**
 * Editor for the in-app cancellation flow: the survey reasons and the save offers shown while
 * someone is cancelling. Backed by /api/admin/cancel-flow (GET returns the stored config, or the
 * built-in defaults if nobody has edited it yet; POST validates + upserts the whole object).
 * Shares [PricingView]'s fetch/save/reorder plumbing, just with different fields.
 */
@Composable
private fun CancelFlowView(onNavigate: (DrillDownView) -> Unit) {
    var reasons by remember { mutableStateOf<List<CancelReasonOption>>(emptyList()) }
    var offers by remember { mutableStateOf<List<RetentionOfferConfig>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<String?>(null) }
    var statusError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun authHeaders(withBody: Boolean): dynamic {
        val h = if (withBody) kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                else kotlin.js.json("Accept" to "application/json")
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        token?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun fetchConfig() {
        scope.launch {
            loading = true
            status = null; statusError = false
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/cancel-flow",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val cfg = adminJson.decodeFromString<CancelFlowConfig>(text.toString())
                    reasons = cfg.reasons
                    offers = cfg.offers
                } else {
                    status = "Couldn't load the cancel flow (${response.status}). Showing defaults."; statusError = true
                    reasons = CancelFlowConfig.DEFAULT_REASONS
                    offers = CancelFlowConfig.DEFAULT_OFFERS
                }
            } catch (e: Exception) {
                status = "Error loading the cancel flow: ${e.message}. Showing defaults."; statusError = true
                reasons = CancelFlowConfig.DEFAULT_REASONS
                offers = CancelFlowConfig.DEFAULT_OFFERS
            }
            loading = false
        }
    }

    fun updateReason(index: Int, transform: (CancelReasonOption) -> CancelReasonOption) {
        reasons = reasons.mapIndexed { i, r -> if (i == index) transform(r) else r }
    }
    fun moveReason(fromIndex: Int, toIndex: Int) {
        if (toIndex !in reasons.indices) return
        val mutable = reasons.toMutableList()
        val item = mutable.removeAt(fromIndex)
        mutable.add(toIndex, item)
        reasons = mutable
    }
    fun updateOffer(index: Int, transform: (RetentionOfferConfig) -> RetentionOfferConfig) {
        offers = offers.mapIndexed { i, o -> if (i == index) transform(o) else o }
    }
    fun moveOffer(fromIndex: Int, toIndex: Int) {
        if (toIndex !in offers.indices) return
        val mutable = offers.toMutableList()
        val item = mutable.removeAt(fromIndex)
        mutable.add(toIndex, item)
        offers = mutable
    }

    fun save() {
        val cleanReasons = reasons.map { it.copy(key = it.key.trim(), label = it.label.trim()) }
        val cleanOffers = offers.map {
            it.copy(
                key = it.key.trim(), headline = it.headline.trim(), body = it.body.trim(),
                acceptLabel = it.acceptLabel.trim(), confirmation = it.confirmation.trim(),
            )
        }
        val reasonKeys = cleanReasons.map { it.key }
        val offerKeys = cleanOffers.map { it.key }
        when {
            // Mirrors the server's own checks (AdminRoutes.kt POST /api/admin/cancel-flow) so a
            // rejected save shows a reason here instead of a bare "Save failed (400)".
            cleanReasons.isEmpty() -> { status = "Add at least one reason. The server rejects an empty survey."; statusError = true }
            cleanReasons.any { it.key.isBlank() || it.label.isBlank() } -> { status = "Every reason needs a key and a label."; statusError = true }
            reasonKeys.size != reasonKeys.distinct().size -> { status = "Reason keys must be unique."; statusError = true }
            cleanOffers.any { it.key.isBlank() } -> { status = "Every offer needs a key."; statusError = true }
            offerKeys.size != offerKeys.distinct().size -> { status = "Offer keys must be unique."; statusError = true }
            cleanOffers.any { !it.isKnownKind } -> { status = "Every offer needs a kind: discount, pause, or free time."; statusError = true }
            else -> scope.launch {
                saving = true; status = null; statusError = false
                try {
                    val body = adminJson.encodeToString(
                        CancelFlowConfig.serializer(),
                        CancelFlowConfig(reasons = cleanReasons, offers = cleanOffers)
                    )
                    val response = kotlinx.browser.window.fetch(
                        "$serverUrl/api/admin/cancel-flow",
                        kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                    ).await()
                    if (response.ok) {
                        reasons = cleanReasons; offers = cleanOffers
                        status = "Saved. Live in the app now."; statusError = false
                    } else {
                        status = "Save failed (${response.status})."; statusError = true
                    }
                } catch (e: Exception) { status = "Error saving: ${e.message}"; statusError = true }
                saving = false
            }
        }
    }

    LaunchedEffect(Unit) { fetchConfig() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Cancel Flow" to null)

        if (loading) {
            CardSection("Loading…") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching the current cancel flow…") }
            }
            return@PageShell
        }

        CardSection("How this flow works") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 10px") } }) {
                Text(
                    "Someone cancelling picks one of the survey answers below, then sees the save " +
                        "offers in escalation order: that answer's lead offer first, if it has one, " +
                        "otherwise the order the offers are listed in below."
                )
            }
            P(attrs = {
                style {
                    fontSize(13.px); color(Color("#b45309")); backgroundColor(Color("#fffbeb"))
                    padding(9.px, 12.px); borderRadius(8.px); property("margin", "0 0 8px")
                }
            }) {
                Text(
                    "Discount and Pause offers are withheld from annual subscribers no matter how " +
                        "they're configured here. A percent-off coupon applies to the NEXT invoice, " +
                        "so half off on an annual plan means half off a whole year, and a pause skips " +
                        "the next cycle, which can be up to ten months away. Only Free time reads " +
                        "correctly on any billing interval."
                )
            }
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) {
                Text(
                    "Offers may be empty. That is valid, it just means the flow goes survey, " +
                        "confirm, goodbye with no save attempt shown in between."
                )
            }
        }

        CardSection("Survey answers") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 14px") } }) {
                Text("What someone sees when asked why they're cancelling. The order below is the order they see them in.")
            }
            val offerKeys = offers.map { it.key }.filter { it.isNotBlank() }
            reasons.forEachIndexed { index, reason ->
                ReasonRow(
                    index = index,
                    total = reasons.size,
                    reason = reason,
                    offerKeys = offerKeys,
                    onEdit = { transform -> updateReason(index, transform) },
                    onMove = { to -> moveReason(index, to) },
                    onRemove = { reasons = reasons.filterIndexed { i, _ -> i != index } },
                )
            }
            Span(attrs = {
                style { fontSize(13.px); fontWeight(700); color(Primary); cursor("pointer") }
                onClick { reasons = reasons + CancelReasonOption(key = "", label = "") }
            }) { Text("+ Add reason") }
        }

        CardSection("Save offers") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 14px") } }) {
                Text("The order of the cards below IS the escalation order. Whoever declines every offer ends on the last one.")
            }
            offers.forEachIndexed { index, offer ->
                OfferCard(
                    index = index,
                    total = offers.size,
                    offer = offer,
                    onEdit = { transform -> updateOffer(index, transform) },
                    onMove = { to -> moveOffer(index, to) },
                    onRemove = { offers = offers.filterIndexed { i, _ -> i != index } },
                )
            }
            Span(attrs = {
                style { fontSize(13.px); fontWeight(700); color(Primary); cursor("pointer") }
                onClick {
                    offers = offers + RetentionOfferConfig(
                        key = "", kind = OfferKind.DISCOUNT, headline = "", body = "",
                        acceptLabel = "", confirmation = "",
                    )
                }
            }) { Text("+ Add offer") }
        }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
            Span(attrs = {
                style {
                    padding(12.px, 22.px); borderRadius(12.px)
                    property("background", GradientPrimary); color(Color.white)
                    fontSize(14.px); fontWeight(700); cursor(if (saving) "default" else "pointer")
                    if (saving) property("opacity", "0.7")
                }
                if (!saving) onClick { save() }
            }) { Text(if (saving) "Saving…" else "Save cancel flow") }

            Span(attrs = {
                style {
                    padding(11.px, 20.px); borderRadius(12.px)
                    property("border", "1.5px solid $Gray300")
                    color(Gray600); fontSize(14.px); fontWeight(700)
                    cursor(if (saving) "default" else "pointer")
                    if (saving) property("opacity", "0.6")
                }
                // "Reset to defaults" is just a reload: GET already falls back to the built-in
                // defaults when nothing has been saved, so there's no separate defaults endpoint
                // to call, and nothing to delete on the server.
                if (!saving) onClick { fetchConfig() }
            }) { Text("Reload from server") }

            status?.let {
                Span(attrs = { style { fontSize(13.px); fontWeight(600); color(if (statusError) ScoreRed else PositiveText) } }) { Text(it) }
            }
        }
    }
}

/** Reorder arrows shared by the reason rows and offer cards below, matching [PricingView]'s. */
@Composable
private fun ReorderArrows(index: Int, size: Int, onMove: (Int) -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); gap(6.px); justifyContent(JustifyContent.FlexEnd)
            property("margin-bottom", "12px")
        }
    }) {
        listOf("↑" to (index - 1), "↓" to (index + 1)).forEach { (arrow, targetIndex) ->
            val enabled = targetIndex in 0 until size
            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock); width(30.px); height(30.px)
                    borderRadius(8.px); fontSize(15.px); fontWeight(700)
                    property("text-align", "center"); property("line-height", "30px")
                    property("border", "1.5px solid ${if (enabled) Gray300 else Gray200}")
                    color(if (enabled) Gray600 else Gray300)
                    backgroundColor(Color.white)
                    cursor(if (enabled) "pointer" else "default")
                    property("user-select", "none")
                    property("transition", TransitionFast)
                }
                if (enabled) onClick { onMove(targetIndex) }
            }) { Text(arrow) }
        }
    }
}

/** A labelled toggle switch, styled identically to [PricingView]'s "Unlimited analyses" switch. */
@Composable
private fun EditorToggle(label: String, checked: Boolean, onToggle: () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
            cursor("pointer"); property("width", "fit-content")
        }
        onClick { onToggle() }
    }) {
        Div(attrs = {
            style {
                width(38.px); height(22.px); borderRadius(999.px)
                property("position", "relative"); property("flex", "none")
                property("transition", TransitionFast)
                backgroundColor(if (checked) Primary else Gray300)
            }
        }) {
            Div(attrs = {
                style {
                    property("position", "absolute"); property("top", "2px")
                    width(18.px); height(18.px); borderRadius(50.percent)
                    backgroundColor(Color.white); property("transition", TransitionFast)
                    if (checked) property("right", "2px") else property("left", "2px")
                }
            }) {}
        }
        Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray700) } }) { Text(label) }
    }
}

/** One survey-answer row: label, key (with a rename warning), enabled, allow-note, lead offer. */
@Composable
private fun ReasonRow(
    index: Int,
    total: Int,
    reason: CancelReasonOption,
    offerKeys: List<String>,
    // Named onEdit rather than onChange: inside the Select below, a parameter called onChange
    // would be shadowed by AttrsScope's own onChange (the DOM event hook), and every call meant
    // for this callback would silently resolve to that instead.
    onEdit: ((CancelReasonOption) -> CancelReasonOption) -> Unit,
    onMove: (Int) -> Unit,
    onRemove: () -> Unit,
) {
    Div(attrs = {
        style {
            property("border", "1px solid $Gray200"); borderRadius(12.px)
            padding(16.px); property("margin-bottom", "12px")
        }
    }) {
        if (total > 1) ReorderArrows(index, total, onMove)

        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "4px") } }) {
            PricingField("Label", reason.label, "e.g. It's too expensive") { v -> onEdit { it.copy(label = v) } }
            PricingField("Key (wire format)", reason.key, "too_expensive") { v -> onEdit { it.copy(key = v) } }
        }
        // The key is the analytics dimension (cancellation_flows.reason), not just an editor id —
        // renaming it splits the metric in two and strands every past row under the old key.
        P(attrs = {
            style {
                fontSize(12.px); color(Color("#b45309")); backgroundColor(Color("#fffbeb"))
                padding(7.px, 11.px); borderRadius(8.px); property("margin", "0 0 12px")
            }
        }) { Text("⚠ Changing the key splits this answer's analytics history in two and orphans the past rows under the old key.") }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(20.px); property("flex-wrap", "wrap"); alignItems(AlignItems.Center); property("margin-bottom", "12px") } }) {
            EditorToggle("Enabled", reason.enabled) { onEdit { it.copy(enabled = !it.enabled) } }
            EditorToggle("Allow free-text note", reason.allowNote) { onEdit { it.copy(allowNote = !it.allowNote) } }
        }

        Div(attrs = { style { property("max-width", "320px") } }) {
            Label(attrs = {
                style {
                    fontSize(11.px); fontWeight(700); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                    property("margin-bottom", "5px"); display(DisplayStyle.Block)
                }
            }) { Text("Lead offer") }
            Select(attrs = {
                onChange { ev -> val v = ev.value; onEdit { it.copy(leadOffer = if (v.isNullOrBlank()) null else v) } }
                style {
                    width(100.percent); padding(9.px, 11.px); fontSize(13.px); borderRadius(9.px)
                    property("border", "1px solid $Gray200"); property("outline", "none")
                }
            }) {
                Option("", attrs = { if (reason.leadOffer.isNullOrBlank()) attr("selected", "") }) { Text("No preference (standard order)") }
                offerKeys.forEach { key ->
                    Option(key, attrs = { if (reason.leadOffer == key) attr("selected", "") }) { Text(key) }
                }
            }
        }

        if (total > 1) {
            Div(attrs = { style { property("margin-top", "12px") } }) {
                Span(attrs = {
                    style { fontSize(13.px); color(ScoreRed); cursor("pointer"); fontWeight(600) }
                    onClick { onRemove() }
                }) { Text("✕ Remove reason") }
            }
        }
    }
}

/** One save-offer card: everything the escalation step needs, plus kind-dependent parameters. */
@Composable
private fun OfferCard(
    index: Int,
    total: Int,
    offer: RetentionOfferConfig,
    // See ReasonRow's onEdit for why this isn't named onChange (it would be shadowed by the Kind
    // Select's own AttrsScope.onChange below).
    onEdit: ((RetentionOfferConfig) -> RetentionOfferConfig) -> Unit,
    onMove: (Int) -> Unit,
    onRemove: () -> Unit,
) {
    val disabledSuffix = if (!offer.enabled) " · disabled" else ""
    CardSection("Offer ${index + 1}$disabledSuffix") {
        if (total > 1) ReorderArrows(index, total, onMove)

        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "4px") } }) {
            PricingField("Key", offer.key, "discount_50") { v -> onEdit { it.copy(key = v) } }
            Div(attrs = { style { property("flex", "1"); property("min-width", "160px") } }) {
                Label(attrs = {
                    style {
                        fontSize(11.px); fontWeight(700); color(Gray500)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                        property("margin-bottom", "5px"); display(DisplayStyle.Block)
                    }
                }) { Text("Kind") }
                Select(attrs = {
                    onChange { ev -> val v = ev.value ?: OfferKind.DISCOUNT; onEdit { it.copy(kind = v) } }
                    style {
                        width(100.percent); padding(9.px, 11.px); fontSize(13.px); borderRadius(9.px)
                        property("border", "1px solid $Gray200"); property("outline", "none")
                    }
                }) {
                    Option(OfferKind.DISCOUNT, attrs = { if (offer.kind == OfferKind.DISCOUNT) attr("selected", "") }) { Text("Discount") }
                    Option(OfferKind.PAUSE, attrs = { if (offer.kind == OfferKind.PAUSE) attr("selected", "") }) { Text("Pause") }
                    Option(OfferKind.FREE, attrs = { if (offer.kind == OfferKind.FREE) attr("selected", "") }) { Text("Free time") }
                }
            }
        }
        // The key is what the once-per-year eligibility check matches on (the retention_offers
        // ledger), not just a label — renaming it makes the server treat it as a brand-new offer,
        // so it re-grants to everyone who already took the old one.
        P(attrs = {
            style {
                fontSize(12.px); color(Color("#b45309")); backgroundColor(Color("#fffbeb"))
                padding(7.px, 11.px); borderRadius(8.px); property("margin", "0 0 12px")
            }
        }) { Text("⚠ Changing the key re-grants this offer to everyone who already took it, because eligibility is matched on the key.") }

        EditorToggle("Enabled", offer.enabled) { onEdit { it.copy(enabled = !it.enabled) } }

        Div(attrs = { style { property("margin", "12px 0") } }) {
            PricingField("Headline", offer.headline, "Stay for half the price") { v -> onEdit { it.copy(headline = v) } }
        }
        Div(attrs = {
            style {
                fontSize(11.px); fontWeight(700); color(Gray500)
                property("text-transform", "uppercase"); property("letter-spacing", "0.04em")
                property("margin", "0 0 5px")
            }
        }) { Text("Body") }
        TextArea(value = offer.body) {
            onInput { ev -> onEdit { it.copy(body = ev.value) } }
            style {
                width(100.percent); property("box-sizing", "border-box")
                property("min-height", "72px"); padding(9.px, 11.px); borderRadius(9.px)
                property("border", "1px solid $Gray200"); fontSize(13.px); property("outline", "none")
                property("font-family", "inherit"); property("resize", "vertical")
            }
            attr("placeholder", "What this offer promises, in the admin's own words.")
        }
        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin", "12px 0") } }) {
            PricingField("Accept button label", offer.acceptLabel, "Yes, give me 50% off") { v -> onEdit { it.copy(acceptLabel = v) } }
            PricingField("Confirmation text", offer.confirmation, "Done. Your next payment is half price.") { v -> onEdit { it.copy(confirmation = v) } }
        }

        // Same wording the offer actually does, per kind (see OfferKind's KDoc in Cancellation.kt),
        // so the number an admin types reads correctly instead of just being labelled "Months".
        val monthsLabel = when (offer.kind) {
            OfferKind.DISCOUNT -> "Invoices covered (1 = once only)"
            OfferKind.PAUSE -> "Months to skip (access lapses)"
            OfferKind.FREE -> "Months free (access continues)"
            else -> "Months"
        }
        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "8px") } }) {
            if (offer.kind == OfferKind.DISCOUNT) {
                PricingField("Percent off", offer.percentOff.toString(), "50") { v ->
                    onEdit { it.copy(percentOff = v.filter { c -> c.isDigit() }.toIntOrNull() ?: 0) }
                }
            }
            PricingField(monthsLabel, offer.months.toString(), "1") { v ->
                onEdit { it.copy(months = v.filter { c -> c.isDigit() }.toIntOrNull() ?: 1) }
            }
        }

        if (offer.monthlyOnly) {
            P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "0 0 12px") } }) {
                Text("This kind only reads correctly on a monthly subscription. The server withholds it from annual subscribers automatically, no matter how it's configured here.")
            }
        }

        Div(attrs = { style { property("margin-top", "4px") } }) {
            Span(attrs = {
                style { fontSize(13.px); color(ScoreRed); cursor("pointer"); fontWeight(600) }
                onClick { onRemove() }
            }) { Text("✕ Remove offer") }
        }
    }
}

// ---------------------------------------------------------------------------
// Admin Emails Management View
// ---------------------------------------------------------------------------

@Composable
private fun AdminEmailsView(onNavigate: (DrillDownView) -> Unit) {
    var emails by remember { mutableStateOf<List<String>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var newEmail by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchEmails() {
        scope.launch {
            loading = true
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/admin-emails",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    emails = adminJson.decodeFromString<List<String>>(text.toString())
                } else {
                    error = "Failed to fetch: ${response.status}"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            loading = false
        }
    }

    fun addEmail(email: String) {
        if (email.isBlank() || !email.contains("@")) return
        scope.launch {
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/admin-emails",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to """{"email":"$email"}""").unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                newEmail = ""
                fetchEmails()
            } catch (_: Exception) {}
        }
    }

    fun removeEmail(email: String) {
        scope.launch {
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/admin-emails",
                    kotlin.js.json("method" to "DELETE", "headers" to headers, "body" to """{"email":"$email"}""").unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                fetchEmails()
            } catch (_: Exception) {}
        }
    }

    LaunchedEffect(Unit) { fetchEmails() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Admin Emails" to null)

        CardSection("Add Admin Email") {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    alignItems(AlignItems.Center)
                }
            }) {
                org.jetbrains.compose.web.dom.TextInput(newEmail) {
                    onInput { newEmail = it.value }
                    style {
                        property("flex", "1")
                        padding(10.px, 14.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(14.px)
                        property("outline", "none")
                    }
                    attr("placeholder", "admin@example.com  —  or  @grr.la  for a whole domain")
                    onKeyDown { if (it.key == "Enter") addEmail(newEmail) }
                }
                Span(attrs = {
                    style {
                        padding(10.px, 20.px)
                        borderRadius(8.px)
                        backgroundColor(Primary)
                        color(Color.white)
                        fontSize(14.px)
                        fontWeight(600)
                        cursor("pointer")
                        property("transition", TransitionMedium)
                    }
                    onClick { addEmail(newEmail) }
                }) { Text("Add") }
            }
            P(attrs = {
                style { color(Gray400); fontSize(12.px); property("margin", "8px 0 0") }
            }) { Text("Admin emails — along with anonymous (signed-out) activity — are excluded " +
                "from analytics when \"Include admins & anonymous\" is toggled off.") }
            P(attrs = {
                style { color(Gray400); fontSize(12.px); property("margin", "4px 0 0") }
            }) {
                Text("Start an entry with \"@\" to cover an entire domain — e.g. ")
                Span(attrs = { style { fontWeight(600); color(Gray500) } }) { Text("@grr.la") }
                Text(" excludes every address at that domain, existing and future. Domain rules " +
                    "affect analytics only; they never grant access to this dashboard.")
            }
        }

        if (loading) {
            CardSection("Loading...") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching admin emails...") }
            }
            return@PageShell
        }

        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
        }

        CardSection("Admin Emails (${emails.size})") {
            if (emails.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No admin emails configured.") }
            } else {
                Table(attrs = {
                    style {
                        width(100.percent)
                        property("border-collapse", "collapse")
                        fontSize(14.px)
                    }
                }) {
                    Thead {
                        Tr {
                            Th(attrs = {
                                style {
                                    property("text-align", "left"); padding(10.px, 12.px)
                                    color(Gray500); fontWeight(600); property("border-bottom", "2px solid $Gray200")
                                }
                            }) { Text("Email") }
                            Th(attrs = {
                                style {
                                    property("text-align", "right"); padding(10.px, 12.px)
                                    color(Gray500); fontWeight(600); property("border-bottom", "2px solid $Gray200")
                                    width(80.px)
                                }
                            }) { Text("Action") }
                        }
                    }
                    Tbody {
                        emails.forEach { email ->
                            Tr(attrs = {
                                style { property("border-bottom", "1px solid $Gray100") }
                            }) {
                                Td(attrs = {
                                    style { padding(12.px); color(Gray900); fontWeight(500) }
                                }) { Text(email) }
                                Td(attrs = {
                                    style { padding(12.px); property("text-align", "right") }
                                }) {
                                    Span(attrs = {
                                        style {
                                            padding(4.px, 12.px)
                                            borderRadius(6.px)
                                            fontSize(12.px)
                                            fontWeight(600)
                                            color(ScoreRed)
                                            backgroundColor(Color("#fef2f2"))
                                            cursor("pointer")
                                            property("transition", TransitionMedium)
                                        }
                                        onClick { removeEmail(email) }
                                    }) { Text("Remove") }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Manage Covers View — list titles missing (or any) cover art and hand-upload one
// ---------------------------------------------------------------------------

// One image result from the "GI search" picker (Serper.dev Google Images).
@kotlinx.serialization.Serializable
private data class CoverImageResult(
    val thumb: String = "",
    val full: String = "",
    val width: Int = 0,
    val height: Int = 0,
    val source: String = ""
)

// Response wrapper — author is resolved (books only) and folded into the query.
@kotlinx.serialization.Serializable
private data class CoverImageSearchResponse(
    val author: String? = null,
    val results: List<CoverImageResult> = emptyList()
)

@Composable
private fun ManageCoversView(onNavigate: (DrillDownView) -> Unit) {
    var items by remember { mutableStateOf<List<CoverItem>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var filter by remember { mutableStateOf("missing") }   // "missing" | "all"
    var search by remember { mutableStateOf("") }
    var appliedSearch by remember { mutableStateOf("") }    // last value actually fetched
    var error by remember { mutableStateOf<String?>(null) }
    var uploadingId by remember { mutableStateOf<String?>(null) }
    var status by remember { mutableStateOf<String?>(null) }
    var offset by remember { mutableStateOf(0) }
    val pageSize = 60
    val scope = rememberCoroutineScope()

    // "GI search" cover picker modal state.
    var giItem by remember { mutableStateOf<CoverItem?>(null) }   // open for this card; null = closed
    var giResults by remember { mutableStateOf<List<CoverImageResult>>(emptyList()) }
    var giAuthor by remember { mutableStateOf<String?>(null) }    // resolved book author (books only)
    var giLoading by remember { mutableStateOf(false) }
    var giError by remember { mutableStateOf<String?>(null) }
    var settingUrl by remember { mutableStateOf<String?>(null) }  // image URL currently being applied
    var failedUrls by remember { mutableStateOf<Set<String>>(emptySet()) }
    var backfilling by remember { mutableStateOf(false) }         // Serper auto-find batch in flight
    var ytBackfilling by remember { mutableStateOf(false) }       // YouTube avatar re-resolve batch in flight
    var ytOffset by remember { mutableStateOf(0) }                // resume cursor for the YouTube re-resolve pass

    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchCovers() {
        scope.launch {
            loading = true
            error = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val q = if (appliedSearch.isNotBlank()) "&search=${encodeURIComponent(appliedSearch)}" else ""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/covers?filter=$filter&limit=$pageSize&offset=$offset$q",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    items = adminJson.decodeFromString<List<CoverItem>>(text.toString())
                } else {
                    error = "Failed to fetch: ${response.status}"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            loading = false
        }
    }

    fun postCover(id: String, dataUrl: String) {
        scope.launch {
            uploadingId = id
            status = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = kotlin.js.JSON.stringify(kotlin.js.json("id" to id, "imageBase64" to dataUrl))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/upload-cover",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text.toString()) as? kotlinx.serialization.json.JsonObject
                    val url = (obj?.get("coverUrl") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    if (url != null) {
                        items = items.map { if (it.id == id) it.copy(coverUrl = url) else it }
                        status = "Cover uploaded ✓"
                    } else {
                        status = "Upload returned no URL"
                    }
                } else {
                    status = "Upload failed (${response.status}): ${text.toString().take(160)}"
                }
            } catch (e: Exception) {
                status = "Error: ${e.message}"
            }
            uploadingId = null
        }
    }

    fun uploadFile(id: String, file: org.w3c.files.File) {
        val reader = org.w3c.files.FileReader()
        reader.onload = {
            postCover(id, reader.result.toString())  // "data:image/...;base64,xxxx"
        }
        reader.onerror = { status = "Could not read file" }
        reader.readAsDataURL(file)
    }

    // Open the picker for a card and run a Google image search for its cover.
    fun openGiSearch(item: CoverItem) {
        giItem = item
        giResults = emptyList()
        giAuthor = null
        giError = null
        failedUrls = emptySet()
        scope.launch {
            giLoading = true
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val year = item.cacheKey.substringAfterLast("::").trim()
                val params = StringBuilder("title=${encodeURIComponent(item.title)}")
                item.mediaType?.let { params.append("&mediaType=${encodeURIComponent(it)}") }
                if (year.isNotBlank()) params.append("&year=${encodeURIComponent(year)}")
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/search-cover-images?$params",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val resp = adminJson.decodeFromString<CoverImageSearchResponse>(text.toString())
                    giResults = resp.results
                    giAuthor = resp.author
                    if (giResults.isEmpty()) giError = "No image results found. Try the manual upload."
                } else {
                    giError = "Search failed (${response.status}): ${text.toString().take(160)}"
                }
            } catch (e: Exception) {
                giError = "Error: ${e.message}"
            }
            giLoading = false
        }
    }

    // Apply a chosen image URL as the card's cover (server downloads + self-hosts it).
    fun setCoverFromUrl(id: String, imageUrl: String) {
        scope.launch {
            settingUrl = imageUrl
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = kotlin.js.JSON.stringify(kotlin.js.json("id" to id, "imageUrl" to imageUrl))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/set-cover-from-url",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text.toString()) as? kotlinx.serialization.json.JsonObject
                    val url = (obj?.get("coverUrl") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    if (url != null) {
                        items = items.map { if (it.id == id) it.copy(coverUrl = url) else it }
                        status = "Cover set ✓"
                        giItem = null   // close the picker
                    } else {
                        giError = "Server returned no URL"
                    }
                } else {
                    // Likely a hotlink-blocked image — let the admin pick another.
                    failedUrls = failedUrls + imageUrl
                    giError = "Couldn't use that image (${response.status}) — try another."
                }
            } catch (e: Exception) {
                failedUrls = failedUrls + imageUrl
                giError = "Error: ${e.message}"
            }
            settingUrl = null
        }
    }

    LaunchedEffect(filter, appliedSearch, offset) { fetchCovers() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Manage Covers" to null)

        CardSection("Filter") {
            Div(attrs = {
                style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") }
            }) {
                listOf("missing" to "Missing only", "review" to "Auto-picked (review)", "all" to "All titles").forEach { (key, label) ->
                    Span(attrs = {
                        style {
                            padding(8.px, 16.px)
                            borderRadius(8.px)
                            fontSize(14.px)
                            fontWeight(600)
                            cursor("pointer")
                            property("transition", TransitionMedium)
                            if (filter == key) {
                                backgroundColor(Primary); color(Color.white)
                            } else {
                                backgroundColor(Color("#f1f5f9")); color(Gray600)
                            }
                        }
                        onClick { if (filter != key) { offset = 0; filter = key } }
                    }) { Text(label) }
                }
                org.jetbrains.compose.web.dom.TextInput(search) {
                    onInput { search = it.value }
                    style {
                        property("flex", "1"); property("min-width", "200px")
                        padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                    }
                    attr("placeholder", "Search title…")
                    onKeyDown { if (it.key == "Enter") { offset = 0; appliedSearch = search.trim() } }
                }
                Span(attrs = {
                    style {
                        padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                        color(Color.white); fontSize(14.px); fontWeight(600); cursor("pointer")
                    }
                    onClick { offset = 0; appliedSearch = search.trim() }
                }) { Text("Search") }
                // Auto-find covers for titles still missing one: catalog first, then Serper
                // (Google Images). Runs one batch and jumps to the review filter to show results.
                Span(attrs = {
                    style {
                        padding(10.px, 20.px); borderRadius(8.px)
                        backgroundColor(if (backfilling) Gray300 else Color("#7c3aed"))
                        color(Color.white); fontSize(14.px); fontWeight(600)
                        cursor(if (backfilling) "default" else "pointer")
                    }
                    onClick {
                        if (backfilling) return@onClick
                        backfilling = true
                        status = "Searching for covers… (can take ~30s)"
                        scope.launch {
                            try {
                                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                                val headers = kotlin.js.json("Content-Type" to "application/json")
                                token?.let { headers["Authorization"] = "Bearer $it" }
                                val resp = kotlinx.browser.window.fetch(
                                    "$serverUrl/api/admin/backfill-covers-serper?limit=10",
                                    kotlin.js.json("method" to "POST", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                                ).await()
                                val text = resp.text().await().toString()
                                if (resp.ok) {
                                    val obj = try { Json.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject } catch (_: Exception) { null }
                                    fun n(k: String) = (obj?.get(k) as? kotlinx.serialization.json.JsonPrimitive)?.content ?: "?"
                                    status = "✓ Scanned ${n("scanned")} — ${n("serperAuto")} auto-picked, ${n("catalog")} from catalog, ${n("missed")} none found. Showing the auto-picked ones to review."
                                    offset = 0; filter = "review"
                                } else status = "Auto-find failed (${resp.status}): ${text.take(160)}"
                            } catch (e: Throwable) { status = "Auto-find failed: ${e.message}" }
                            finally { backfilling = false }
                        }
                    }
                }) { Text(if (backfilling) "Searching…" else "🪄 Auto-find missing") }

                // Re-resolve YouTube channel covers via the YouTube Data API (official avatar).
                // Processes 50 channels per click (≈5,000 quota units); click again to continue from
                // where it stopped (offset resets to 0 after a full pass). Channels YouTube can't find
                // keep their current cover — no downgrade.
                Span(attrs = {
                    style {
                        padding(10.px, 20.px); borderRadius(8.px)
                        backgroundColor(if (ytBackfilling) Gray300 else Color("#dc2626"))
                        color(Color.white); fontSize(14.px); fontWeight(600)
                        cursor(if (ytBackfilling) "default" else "pointer")
                    }
                    onClick {
                        if (ytBackfilling) return@onClick
                        ytBackfilling = true
                        val start = ytOffset
                        status = "Re-resolving YouTube covers from #$start…"
                        scope.launch {
                            try {
                                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                                val headers = kotlin.js.json("Content-Type" to "application/json")
                                token?.let { headers["Authorization"] = "Bearer $it" }
                                val resp = kotlinx.browser.window.fetch(
                                    "$serverUrl/api/admin/backfill-covers?reresolve=true&types=YOUTUBE&limit=50&offset=$start",
                                    kotlin.js.json("method" to "POST", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                                ).await()
                                val text = resp.text().await().toString()
                                if (resp.ok) {
                                    val obj = try { Json.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject } catch (_: Exception) { null }
                                    fun n(k: String) = (obj?.get(k) as? kotlinx.serialization.json.JsonPrimitive)?.content ?: "?"
                                    val more = n("moreRemaining") == "true"
                                    val next = n("nextOffset").toIntOrNull() ?: start
                                    ytOffset = if (more) next else 0
                                    status = "YouTube covers #$start–$next: ${n("updated")} updated, ${n("unchanged")} unchanged, ${n("missed")} kept. " +
                                        if (more) "Click again for the next 50 (mind the ~10k/day quota)." else "✓ Full pass complete — offset reset to 0."
                                } else status = "YouTube re-resolve failed (${resp.status}): ${text.take(160)}"
                            } catch (e: Throwable) { status = "YouTube re-resolve failed: ${e.message}" }
                            finally { ytBackfilling = false }
                        }
                    }
                }) { Text(if (ytBackfilling) "Re-resolving…" else "▶ Re-resolve YouTube covers (from #$ytOffset)") }
            }
        }

        status?.let {
            CardSection("Status") {
                P(attrs = { style { color(if (it.contains("✓")) ScoreGreen else ScoreRed); fontSize(14.px); property("margin", "0") } }) { Text(it) }
            }
        }

        if (loading) {
            CardSection("Loading…") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching titles…") }
            }
            return@PageShell
        }

        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
        }

        CardSection(if (filter == "missing") "Titles missing a cover (${items.size})" else "Titles (${items.size})") {
            if (items.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) {
                    Text(if (filter == "missing") "No titles are missing a cover. 🎉" else "No titles found.")
                }
            } else {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fill, minmax(220px, 1fr))")
                        gap(16.px)
                    }
                }) {
                    items.forEach { item ->
                        Div(attrs = {
                            style {
                                property("border", "1px solid $Gray200")
                                borderRadius(12.px)
                                padding(12.px)
                                display(DisplayStyle.Flex)
                                property("flex-direction", "column")
                                gap(8.px)
                                backgroundColor(Color.white)
                            }
                        }) {
                            // Thumbnail or placeholder
                            val cover = item.coverUrl?.takeIf { it.isNotBlank() }
                            if (cover != null) {
                                Img(src = cover, alt = item.title, attrs = {
                                    style {
                                        width(100.percent); height(220.px)
                                        property("object-fit", "cover")
                                        borderRadius(8.px)
                                        backgroundColor(Color("#f1f5f9"))
                                    }
                                })
                            } else {
                                Div(attrs = {
                                    style {
                                        width(100.percent); height(220.px)
                                        borderRadius(8.px)
                                        backgroundColor(Color("#f1f5f9"))
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                                        color(Gray400); fontSize(36.px)
                                    }
                                }) { Text("🖼️") }
                            }
                            P(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900); property("margin", "0") } }) { Text(item.title) }
                            // cache_key is "title::type::year" — show the year so otherwise
                            // identical-looking duplicate rows (same title+type) are distinguishable.
                            val year = item.cacheKey.substringAfterLast("::").trim().ifBlank { "no year" }
                            val typeLabel = item.mediaType?.replace("_", " ") ?: "—"
                            P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "0") } }) { Text("$typeLabel · $year") }
                            // Auto-picked (Serper/Google Images) covers are lower-confidence — flag for review.
                            if (item.coverSource == "serper_auto") {
                                Span(attrs = {
                                    style {
                                        property("align-self", "flex-start")
                                        padding(2.px, 8.px); borderRadius(999.px)
                                        backgroundColor(Color("#fffbeb")); color(Color("#b45309"))
                                        fontSize(11.px); fontWeight(700)
                                        property("border", "1px solid #fcd34d")
                                    }
                                }) { Text("⚑ Auto-picked — review") }
                            }
                            item.createdAt?.takeIf { it.isNotBlank() }?.let { c ->
                                P(attrs = { style { fontSize(11.px); color(Gray400); property("margin", "0") } }) { Text("added ${c.take(10)}") }
                            }
                            if (uploadingId == item.id) {
                                P(attrs = { style { fontSize(12.px); color(Primary); property("margin", "0") } }) { Text("Uploading…") }
                            } else {
                                // GI search — Google image picker for this title.
                                Span(attrs = {
                                    style {
                                        padding(7.px, 12.px); borderRadius(8.px)
                                        backgroundColor(Color("#eef2ff")); color(Primary)
                                        fontSize(12.px); fontWeight(600); cursor("pointer")
                                        property("text-align", "center")
                                        property("border", "1px solid #c7d2fe")
                                    }
                                    onClick { openGiSearch(item) }
                                }) { Text("🔍 GI search") }
                                // Approve an auto-picked cover → clears the review flag (serper_auto → manual).
                                if (item.coverSource == "serper_auto") {
                                    Span(attrs = {
                                        style {
                                            padding(7.px, 12.px); borderRadius(8.px)
                                            backgroundColor(Color("#dcfce7")); color(Color("#15803d"))
                                            fontSize(12.px); fontWeight(600); cursor("pointer")
                                            property("text-align", "center")
                                            property("border", "1px solid #86efac")
                                        }
                                        onClick {
                                            val capturedId = item.id
                                            scope.launch {
                                                try {
                                                    val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                                                    val headers = kotlin.js.json("Content-Type" to "application/json")
                                                    token?.let { headers["Authorization"] = "Bearer $it" }
                                                    val resp = kotlinx.browser.window.fetch(
                                                        "$serverUrl/api/admin/approve-cover",
                                                        kotlin.js.json("method" to "POST", "headers" to headers, "body" to "{\"id\":\"$capturedId\"}").unsafeCast<org.w3c.fetch.RequestInit>()
                                                    ).await()
                                                    if (resp.ok) {
                                                        items = if (filter == "review") items.filter { it.id != capturedId }
                                                                else items.map { if (it.id == capturedId) it.copy(coverSource = "manual") else it }
                                                        status = "Cover approved."
                                                    } else status = "Approve failed (${resp.status})."
                                                } catch (e: Throwable) { status = "Approve failed: ${e.message}" }
                                            }
                                        }
                                    }) { Text("✓ Approve") }
                                }
                                val inputId = "cover-file-${item.id}"
                                Input(type = org.jetbrains.compose.web.attributes.InputType.File, attrs = {
                                    id(inputId)
                                    attr("accept", "image/*")
                                    style { fontSize(12.px) }
                                    onChange {
                                        val input = kotlinx.browser.document.getElementById(inputId) as? org.w3c.dom.HTMLInputElement
                                        val file = input?.files?.item(0)
                                        if (file != null) uploadFile(item.id, file)
                                        input?.value = ""
                                    }
                                })
                            }
                        }
                    }
                }
            }
        }

        // Pager — server caps each page at `pageSize`; a full page means there may
        // be more. Prev/Next step the offset, which retriggers the fetch.
        val hasPrev = offset > 0
        val hasNext = items.size == pageSize
        if (hasPrev || hasNext) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center); property("margin-top", "20px")
                }
            }) {
                Span(attrs = {
                    style {
                        padding(8.px, 18.px); borderRadius(8.px); fontSize(14.px); fontWeight(600)
                        if (hasPrev) {
                            backgroundColor(Primary); color(Color.white); cursor("pointer")
                        } else {
                            backgroundColor(Color("#f1f5f9")); color(Gray400); cursor("default")
                        }
                    }
                    if (hasPrev) onClick { offset = (offset - pageSize).coerceAtLeast(0) }
                }) { Text("← Prev") }
                Span(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                    Text("${offset + 1}–${offset + items.size}")
                }
                Span(attrs = {
                    style {
                        padding(8.px, 18.px); borderRadius(8.px); fontSize(14.px); fontWeight(600)
                        if (hasNext) {
                            backgroundColor(Primary); color(Color.white); cursor("pointer")
                        } else {
                            backgroundColor(Color("#f1f5f9")); color(Gray400); cursor("default")
                        }
                    }
                    if (hasNext) onClick { offset += pageSize }
                }) { Text("Next →") }
            }
        }

        // ── GI search picker modal ──
        giItem?.let { current ->
            Div(attrs = {
                style {
                    position(Position.Fixed)
                    property("inset", "0")
                    backgroundColor(rgba(0, 0, 0, 0.5))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    property("z-index", "1000"); padding(16.px)
                }
                onClick { if (settingUrl == null) giItem = null }
            }) {
                Div(attrs = {
                    style {
                        backgroundColor(Color.white); borderRadius(16.px); padding(20.px)
                        property("max-width", "1040px"); width(92.percent)
                        property("max-height", "86vh"); property("overflow-y", "auto")
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.25)")
                    }
                    onClick { it.stopPropagation() }
                }) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center); property("margin-bottom", "12px")
                        }
                    }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); property("flex-direction", "column"); gap(2.px) } }) {
                            P(attrs = { style { fontSize(16.px); fontWeight(700); color(Gray900); property("margin", "0") } }) {
                                Text("Cover results — ${current.title}")
                            }
                            giAuthor?.takeIf { it.isNotBlank() }?.let { a ->
                                P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0") } }) { Text("by $a") }
                            }
                        }
                        Span(attrs = {
                            style { fontSize(22.px); color(Gray500); cursor("pointer"); property("line-height", "1") }
                            onClick { if (settingUrl == null) giItem = null }
                        }) { Text("×") }
                    }

                    giError?.let {
                        P(attrs = { style { fontSize(13.px); color(ScoreRed); property("margin", "0 0 10px") } }) { Text(it) }
                    }

                    if (giLoading) {
                        P(attrs = { style { fontSize(14.px); color(Gray500) } }) { Text("Searching Google Images…") }
                    } else if (giResults.isNotEmpty()) {
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(280px, 1fr))")
                                gap(12.px)
                            }
                        }) {
                            giResults.forEach { res ->
                                val failed = failedUrls.contains(res.full)
                                val setting = settingUrl == res.full
                                Div(attrs = {
                                    style {
                                        property("border", "1px solid $Gray200"); borderRadius(10.px)
                                        property("overflow", "hidden"); position(Position.Relative)
                                        backgroundColor(Color("#f8fafc"))
                                        if (failed) { opacity(0.4); cursor("not-allowed") }
                                        else cursor(if (settingUrl == null) "pointer" else "default")
                                    }
                                    if (!failed && settingUrl == null) onClick { setCoverFromUrl(current.id, res.full) }
                                }) {
                                    Img(src = res.thumb, alt = res.source, attrs = {
                                        style {
                                            width(100.percent); height(340.px)
                                            property("object-fit", "contain")
                                            property("display", "block")
                                        }
                                    })
                                    P(attrs = { style { fontSize(10.px); color(Gray400); property("margin", "0"); padding(4.px, 6.px); property("text-overflow", "ellipsis"); property("overflow", "hidden"); property("white-space", "nowrap") } }) {
                                        Text(if (failed) "couldn't use this one" else "${res.source} · ${res.width}×${res.height}")
                                    }
                                    if (setting) {
                                        Div(attrs = {
                                            style {
                                                position(Position.Absolute); property("inset", "0")
                                                backgroundColor(rgba(255, 255, 255, 0.8))
                                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                                                color(Primary); fontSize(13.px); fontWeight(600)
                                            }
                                        }) { Text("Setting…") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Edit Analyses View — search titles, adjust concern severities (R/Y/G) + overall
// score. Saves to media_phase1; users see it on their next check of the title.
// ---------------------------------------------------------------------------

@kotlinx.serialization.Serializable
private data class AdminQuranRef(val english: String = "", val arabic: String = "", val reference: String = "")

@kotlinx.serialization.Serializable
private data class AdminHadithRef(val english: String = "", val arabic: String = "", val reference: String = "")

@kotlinx.serialization.Serializable
private data class AdminTldrPoint(val text: String = "", val severity: String = "medium", val category: String? = null)

@kotlinx.serialization.Serializable
private data class AdminConcern(
    val category: String = "",
    val severity: String = "none",   // none | low | medium | high
    val found: Boolean = false,
    val rationale: String = "",
    val description: String = "",
    val islamicPerspective: String = "",
    val evidence: String = "",
    val quranReference: AdminQuranRef? = null,
    val hadithReference: AdminHadithRef? = null
)

@kotlinx.serialization.Serializable
private data class AdminAnalysis(
    val title: String = "",
    val author: String? = null,
    val score: Int = 0,
    val islamicScore: Int = 0,
    val summary: String = "",
    val positiveAspects: List<String> = emptyList(),
    val tldr: List<AdminTldrPoint> = emptyList(),
    val recommendation: String = "",
    val ageGuidance: String = "",
    val additionalNotes: String = "",
    val hasPhase2: Boolean = false,
    val concerns: List<AdminConcern> = emptyList(),
    /** True when this came from the legacy pre-V2 media_analysis fallback — read-only, no
     *  editable media_phase1 row exists yet for this title. */
    val legacy: Boolean = false
)

// Overall 1-10 score → verdict color (matches ScoreCard: >=7 green, 4-6 amber, else red).
private fun verdictColor(score: Int): CSSColorValue =
    if (score >= 7) ScoreGreen else if (score >= 4) ScoreAmber else ScoreRed

private fun severityColor(severity: String): CSSColorValue =
    when (severity) { "high" -> ScoreRed; "medium" -> ScoreAmber; else -> ScoreGreen }

// Read-only rendering of a full analysis — used for both the legacy "current" panel and the
// freshly-reanalyzed "fresh" panel in the side-by-side comparison. No inputs, no save button.
@Composable
private fun ReadOnlyAnalysisPanel(
    a: AdminAnalysis,
    // Rendered after each flagged concern's content — lets a caller attach per-concern controls
    // (e.g. a "Regenerate" button + its old-vs-new comparison) without this panel knowing about
    // that state. Null/no-op for genuinely read-only panels (e.g. the legacy "current" column).
    concernActions: (@Composable (AdminConcern) -> Unit)? = null
) {
    Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(16.px); property("margin-bottom", "10px") } }) {
        Span(attrs = { style { fontSize(20.px); fontWeight(700); color(verdictColor(a.score)) } }) { Text("${a.score}/10") }
        Span(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Islamic score ${a.islamicScore}/10") }
    }
    if (a.summary.isNotBlank()) {
        P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0 0 10px") } }) { Text(a.summary) }
    }
    if (a.positiveAspects.isNotEmpty()) {
        P(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600); property("margin", "0 0 4px") } }) { Text("POSITIVE ASPECTS") }
        a.positiveAspects.forEach { pt -> P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0 0 2px") } }) { Text("• $pt") } }
    }
    val flagged = a.concerns.filter { it.severity != "none" }
    if (flagged.isNotEmpty()) {
        P(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600); property("margin", "12px 0 4px") } }) { Text("CONCERNS") }
        flagged.forEach { c ->
            Div(attrs = { style { property("margin-bottom", "10px"); property("padding", "2px 0 2px 10px"); property("border-left", "3px solid ${severityColor(c.severity)}") } }) {
                Span(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray900) } }) { Text("${c.category} — ${c.severity}") }
                if (c.description.isNotBlank()) P(attrs = { style { fontSize(12.px); color(Gray700); property("margin", "2px 0") } }) { Text(c.description) }
                if (c.islamicPerspective.isNotBlank()) P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "2px 0") } }) { Text("Islamic perspective: ${c.islamicPerspective}") }
                if (c.evidence.isNotBlank()) P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "2px 0") } }) { Text("Evidence: ${c.evidence}") }
                concernActions?.invoke(c)
            }
        }
    }
    if (a.recommendation.isNotBlank()) {
        P(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600); property("margin", "12px 0 4px") } }) { Text("RECOMMENDATION") }
        P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0") } }) { Text(a.recommendation) }
    }
    if (a.ageGuidance.isNotBlank()) {
        P(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600); property("margin", "10px 0 4px") } }) { Text("AGE GUIDANCE") }
        P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0") } }) { Text(a.ageGuidance) }
    }
    if (a.additionalNotes.isNotBlank()) {
        P(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600); property("margin", "10px 0 4px") } }) { Text("ADDITIONAL NOTES") }
        P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0") } }) { Text(a.additionalNotes) }
    }
}

// Inline "Regenerate this concern" affordance — a button that kicks off a fresh (no-cache) redo of
// ONE concern, then an old-vs-new comparison with Confirm/Discard once it comes back. Used both in
// the editable Concerns list and the fresh-reanalyze preview panel; the caller decides what
// Confirm actually applies the result to.
@Composable
private fun RegenerateConcernButton(
    regenerating: Boolean, error: String?, old: AdminConcern?, fresh: AdminConcern?,
    onStart: () -> Unit, onConfirm: () -> Unit, onDiscard: () -> Unit
) {
    if (old == null) {
        Span(attrs = {
            style { fontSize(11.px); fontWeight(600); color(Primary); cursor("pointer"); property("margin-top", "4px"); display(DisplayStyle.InlineBlock) }
            onClick { onStart() }
        }) { Text("↻ Regenerate (fresh, no cache)") }
        return
    }
    Div(attrs = { style { property("margin", "8px 0"); padding(10.px, 12.px); borderRadius(8.px); backgroundColor(Color("#f8fafc")); property("border", "1px solid $Gray200") } }) {
        if (regenerating) {
            P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "0") } }) { Text("Regenerating this concern fresh (no cache)… 30–90s") }
        } else {
            error?.let { P(attrs = { style { fontSize(12.px); color(ScoreRed); property("margin", "0 0 8px") } }) { Text(it) } }
            fresh?.let { f ->
                Div(attrs = { style { display(DisplayStyle.Grid); property("grid-template-columns", "1fr 1fr"); gap(12.px) } }) {
                    Div {
                        Span(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray500) } }) { Text("OLD — ${old.severity}") }
                        P(attrs = { style { fontSize(12.px); color(Gray700); property("margin", "2px 0") } }) { Text(old.description.ifBlank { old.rationale }.ifBlank { "(nothing)" }) }
                    }
                    Div {
                        Span(attrs = { style { fontSize(11.px); fontWeight(700); color(severityColor(f.severity)) } }) { Text("NEW — ${f.severity}") }
                        P(attrs = { style { fontSize(12.px); color(Gray700); property("margin", "2px 0") } }) { Text(f.description.ifBlank { f.rationale }.ifBlank { "(nothing)" }) }
                    }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-top", "8px") } }) {
                    Span(attrs = {
                        style { padding(6.px, 14.px); borderRadius(7.px); fontSize(12.px); fontWeight(700); cursor("pointer"); backgroundColor(Primary); color(Color.white) }
                        onClick { onConfirm() }
                    }) { Text("Use this") }
                    Span(attrs = {
                        style { padding(6.px, 14.px); borderRadius(7.px); fontSize(12.px); fontWeight(600); cursor("pointer"); backgroundColor(Color.white); color(Gray500); property("border", "1px solid $Gray200") }
                        onClick { onDiscard() }
                    }) { Text("Discard") }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// LGBTQ+ re-score (2026-09-28): re-judge the LGBTQ+ concern of every title that has one with the
// three-level rubric (not really LGBTQ+ / background / part of the story), against the research
// already saved. List (free) -> score in batches of 10 (paid, writes nothing) -> apply (no model
// calls). See AdminRoutes /api/admin/concern-rescore/*.
// ---------------------------------------------------------------------------

@Serializable
private data class RescoreCandidate(
    val phase1Id: String = "", val contentId: String = "", val title: String = "",
    val mediaType: String = "", val severity: String = "", val score: Int = 0
)

@Serializable
private data class RescoreProposal(
    val phase1Id: String = "", val contentId: String = "", val title: String = "",
    val oldSeverity: String = "", val newSeverity: String = "",
    val oldScore: Int = 0, val newScore: Int = 0,
    val rationale: String = "", val instances: List<String> = emptyList(),
    val concern: JsonObject? = null,   // echoed back to apply untouched
    val error: String? = null
)

@Serializable
private data class RescoreApply(val changes: List<RescoreProposal>)

/** The level a stored LGBTQ+ severity shows as (low is read as amber, like the server does). */
private fun lgbtqLevel(severity: String): String = when (severity) {
    "high" -> "Red"
    "medium", "low" -> "Amber"
    else -> "Not LGBTQ+"
}

@Composable
private fun LgbtqRescoreCard() {
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }
    var candidates by remember { mutableStateOf<List<RescoreCandidate>?>(null) }
    var proposals by remember { mutableStateOf<List<RescoreProposal>>(emptyList()) }
    var busy by remember { mutableStateOf(false) }
    var stopRequested by remember { mutableStateOf(false) }
    var applied by remember { mutableStateOf(false) }
    var rewritten by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<String?>(null) }

    suspend fun call(method: String, path: String, body: String? = null): Pair<Boolean, String> {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        token?.let { h["Authorization"] = "Bearer $it" }
        val init = kotlin.js.json("method" to method, "headers" to h)
        body?.let { init["body"] = it }
        val resp = kotlinx.browser.window.fetch("$serverUrl$path", init.unsafeCast<org.w3c.fetch.RequestInit>()).await()
        return resp.ok to resp.text().await().toString()
    }

    val changed = proposals.filter { it.concern != null && it.error == null && it.newSeverity != it.oldSeverity }
    val errors = proposals.count { it.error != null }

    CardSection("LGBTQ+ re-score") {
        P(attrs = { style { fontSize(13.px); color(Gray600); property("margin", "0 0 10px") } }) {
            Text("Re-judges the LGBTQ+ concern of every title that has one: any direct mention of a same-sex relationship or same-sex feelings is red, a symbol only (a Pride flag, no people) is amber, and things that aren't really LGBTQ+ (a disguise, a tomboy) get no card. Uses each title's saved research. Scoring costs about 1¢ a title and changes nothing until you press Apply.")
        }
        Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap"); alignItems(AlignItems.Center) } }) {
            @Composable
            fun button(label: String, color: CSSColorValue, enabled: Boolean, action: () -> Unit) {
                Span(attrs = {
                    style {
                        padding(8.px, 16.px); borderRadius(8.px); fontSize(14.px); fontWeight(600)
                        backgroundColor(if (enabled) color else Gray300); color(Color.white)
                        cursor(if (enabled) "pointer" else "default")
                    }
                    onClick { if (enabled) action() }
                }) { Text(label) }
            }
            button("Find titles", Primary, !busy) {
                busy = true; status = "Looking…"; proposals = emptyList(); applied = false; rewritten = false
                scope.launch {
                    try {
                        val (ok, text) = call("GET", "/api/admin/concern-rescore/candidates")
                        if (ok) {
                            val list = adminJson.decodeFromString<List<RescoreCandidate>>(text)
                            candidates = list
                            val red = list.count { lgbtqLevel(it.severity) == "Red" }
                            status = "✓ ${list.size} titles have an LGBTQ+ finding today (${list.size - red} amber, $red red)."
                        } else status = "Failed: ${text.take(200)}"
                    } catch (e: Throwable) { status = "Failed: ${e.message}" }
                    busy = false
                }
            }
            val list = candidates.orEmpty()
            val todo = list.filter { c -> proposals.none { it.contentId == c.contentId && it.error == null } }
            if (list.isNotEmpty() && !applied) {
                button(
                    if (proposals.isEmpty()) "Score all ${list.size} (≈$${(list.size * 0.012).toString().take(4)})" else "Score remaining ${todo.size}",
                    Color("#7c3aed"), !busy && todo.isNotEmpty()
                ) {
                    busy = true; stopRequested = false
                    scope.launch {
                        try {
                            for (batch in todo.chunked(10)) {
                                if (stopRequested) break
                                status = "Scoring… ${proposals.count { it.error == null }}/${list.size} done"
                                val body = """{"contentIds":[${batch.joinToString(",") { "\"${it.contentId}\"" }}]}"""
                                val (ok, text) = call("POST", "/api/admin/concern-rescore/score", body)
                                if (!ok) { status = "Scoring stopped: ${text.take(200)}"; break }
                                val got = adminJson.decodeFromString<List<RescoreProposal>>(text)
                                proposals = proposals.filterNot { p -> got.any { it.contentId == p.contentId } } + got
                            }
                            if (status?.startsWith("Scoring stopped") != true) {
                                status = "✓ Scored ${proposals.count { it.error == null }}/${list.size}. Review the changes below, then Apply."
                            }
                        } catch (e: Throwable) { status = "Scoring stopped: ${e.message}" }
                        busy = false
                    }
                }
                if (busy && proposals.isNotEmpty()) button("Stop", ScoreRed, !stopRequested) { stopRequested = true }
            }
            if (changed.isNotEmpty() && !busy && !applied) {
                button("Apply ${changed.size} changes", ScoreGreen, true) {
                    busy = true; status = "Applying…"
                    scope.launch {
                        try {
                            var written = 0
                            for (batch in changed.chunked(50)) {
                                val (ok, text) = call("POST", "/api/admin/concern-rescore/apply", adminJson.encodeToString(RescoreApply.serializer(), RescoreApply(batch)))
                                if (!ok) { status = "Apply failed after $written: ${text.take(200)}"; break }
                                val obj = adminJson.parseToJsonElement(text) as? JsonObject
                                written += (obj?.get("written") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                            }
                            if (status == "Applying…") {
                                applied = true
                                status = "✓ Applied $written of ${changed.size}. Users see the new colours on their next check. Now rewrite the text written under the old judgement."
                            }
                        } catch (e: Throwable) { status = "Apply failed: ${e.message}" }
                        busy = false
                    }
                }
            }
            // After Apply: the highlights, verdict text, developmental notes and LGBTQ+ card were
            // written under the old judgement ("generally suitable" beside a red verdict). A few model
            // calls per saved version of each title, 3 titles per request.
            if (applied && !rewritten) {
                button("Rewrite text for ${changed.size} titles (≈3¢ per saved version)", Color("#7c3aed"), !busy) {
                    busy = true
                    scope.launch {
                        var done = 0
                        var versions = 0
                        try {
                            for (batch in changed.chunked(3)) {
                                status = "Rewriting… $done/${changed.size} titles"
                                val body = """{"contentIds":[${batch.joinToString(",") { "\"${it.contentId}\"" }}]}"""
                                val (ok, text) = call("POST", "/api/admin/concern-rescore/rewrite", body)
                                if (!ok) { status = "Rewrite stopped after $done: ${text.take(200)}"; break }
                                val obj = adminJson.parseToJsonElement(text) as? JsonObject
                                versions += (obj?.get("versions") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                                done += batch.size
                            }
                            if (done == changed.size) {
                                rewritten = true
                                status = "✓ Rewrote the text of $done titles ($versions saved versions). Users see it on their next check."
                            }
                        } catch (e: Throwable) { status = "Rewrite stopped after $done: ${e.message}" }
                        busy = false
                    }
                }
            }
        }
        status?.let {
            P(attrs = { style { fontSize(13.px); color(if (it.startsWith("✓") || it.startsWith("Scoring…") || it.startsWith("Looking") || it.startsWith("Applying") || it.startsWith("Rewriting")) Gray700 else ScoreRed); property("margin", "10px 0 0") } }) { Text(it) }
        }
        if (proposals.isNotEmpty()) {
            val scored = proposals.filter { it.error == null }
            fun count(level: String) = scored.count { lgbtqLevel(it.newSeverity) == level }
            P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "8px 0") } }) {
                Text("New levels: ${count("Not LGBTQ+")} not LGBTQ+, ${count("Amber")} amber, ${count("Red")} red" +
                    (if (errors > 0) " · $errors failed (score again to retry)" else "") +
                    " · ${changed.size} change.")
            }
            // Changes first, biggest verdict swing on top; unchanged rows are listed after, dimmed.
            val rows = proposals.sortedWith(compareBy<RescoreProposal>({ it.error != null }, { it.newSeverity == it.oldSeverity }, { -(it.newScore - it.oldScore) }))
            Div(attrs = { style { property("max-height", "520px"); property("overflow-y", "auto"); property("border", "1px solid $Gray200"); borderRadius(8.px) } }) {
                rows.forEach { p ->
                    val same = p.error == null && p.newSeverity == p.oldSeverity
                    Div(attrs = {
                        style {
                            padding(8.px, 12.px); property("border-bottom", "1px solid $Gray200")
                            if (same) property("opacity", "0.5")
                        }
                    }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                            Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(p.title) }
                            if (p.error != null) {
                                Span(attrs = { style { fontSize(12.px); color(ScoreRed) } }) { Text(p.error) }
                            } else {
                                Span(attrs = { style { fontSize(12.px); color(Gray600) } }) {
                                    Text("${lgbtqLevel(p.oldSeverity)} → ${lgbtqLevel(p.newSeverity)}")
                                }
                                Span(attrs = { style { fontSize(12.px); fontWeight(700); color(verdictColor(p.oldScore)) } }) { Text("${p.oldScore}") }
                                Span(attrs = { style { fontSize(12.px); color(Gray500) } }) { Text("→") }
                                Span(attrs = { style { fontSize(12.px); fontWeight(700); color(verdictColor(p.newScore)) } }) { Text("${p.newScore}") }
                            }
                        }
                        if (p.error == null && p.rationale.isNotBlank()) {
                            P(attrs = { style { fontSize(12.px); color(Gray600); property("margin", "2px 0 0") } }) { Text(p.rationale) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EditAnalysesView(
    initialTitle: String? = null, initialMediaType: String? = null, initialYear: String? = null,
    onNavigate: (DrillDownView) -> Unit
) {
    var search by remember { mutableStateOf(initialTitle ?: "") }
    var results by remember { mutableStateOf<List<CoverItem>>(emptyList()) }
    var searching by remember { mutableStateOf(false) }
    var listError by remember { mutableStateOf<String?>(null) }

    // Arrived here via "Edit this analysis" from Title Detail — auto-select the matching
    // title once its search result comes back instead of making the admin pick it again.
    var autoSelectPending by remember { mutableStateOf(!initialTitle.isNullOrBlank()) }

    var selected by remember { mutableStateOf<CoverItem?>(null) }
    var analysis by remember { mutableStateOf<AdminAnalysis?>(null) }
    var loadingAnalysis by remember { mutableStateOf(false) }
    var analysisError by remember { mutableStateOf<String?>(null) }
    // True on a 404 (no phase-1 row AND no legacy row) — distinct from analysisError, a genuine
    // network/server failure. `analysis?.legacy == true` is the other read-only case.
    var neverAnalyzed by remember { mutableStateOf(false) }

    // "No-cache" reanalyze preview (legacy/never-analyzed titles only). freshResultRaw is the raw
    // response text so Accept can echo it back verbatim without re-encoding a complex nested object.
    var freshResultRaw by remember { mutableStateOf<String?>(null) }
    var freshResult by remember { mutableStateOf<AdminAnalysis?>(null) }
    var reanalyzing by remember { mutableStateOf(false) }
    var reanalyzeError by remember { mutableStateOf<String?>(null) }
    var accepting by remember { mutableStateOf(false) }

    // Per-concern "regenerate" preview (fresh, no-cache) — old vs new, staged until confirmed.
    // regenSurface distinguishes where Confirm applies the result: "editable" (working[] map, still
    // needs Save changes) or "fresh" (the reanalyze preview, still needs Use this new analysis).
    var regenSurface by remember { mutableStateOf<String?>(null) }
    var regenCategory by remember { mutableStateOf<String?>(null) }
    var regenOld by remember { mutableStateOf<AdminConcern?>(null) }
    var regenNew by remember { mutableStateOf<AdminConcern?>(null) }
    var regenerating by remember { mutableStateOf(false) }
    var regenError by remember { mutableStateOf<String?>(null) }

    // Editable working state for the selected title.
    var titleEdit by remember { mutableStateOf("") }
    var authorEdit by remember { mutableStateOf("") }
    var originalTitle by remember { mutableStateOf("") }
    var originalAuthor by remember { mutableStateOf("") }
    var score by remember { mutableStateOf(5) }
    var islamicScore by remember { mutableStateOf(5) }
    var summary by remember { mutableStateOf("") }
    var positiveAspectsText by remember { mutableStateOf("") }   // one per line
    var recommendation by remember { mutableStateOf("") }
    var ageGuidance by remember { mutableStateOf("") }
    var additionalNotes by remember { mutableStateOf("") }
    var tldrWorking by remember { mutableStateOf<List<AdminTldrPoint>>(emptyList()) }
    var hasPhase2 by remember { mutableStateOf(false) }
    var working by remember { mutableStateOf<Map<String, AdminConcern>>(emptyMap()) }   // category -> full editable state
    var original by remember { mutableStateOf<Map<String, AdminConcern>>(emptyMap()) }
    var expanded by remember { mutableStateOf<Set<String>>(emptySet()) }   // categories with details open
    var saving by remember { mutableStateOf(false) }
    var status by remember { mutableStateOf<String?>(null) }

    fun updateConcern(category: String, edit: (AdminConcern) -> AdminConcern) {
        val base = working[category] ?: AdminConcern(category = category)
        working = working + (category to edit(base))
    }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(json: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val h = if (json) kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        else kotlin.js.json("Accept" to "application/json")
        token?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun runSearch() {
        scope.launch {
            searching = true; listError = null
            try {
                val q = if (search.trim().isNotBlank()) "&search=${encodeURIComponent(search.trim())}" else ""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/covers?filter=all&limit=40&offset=0$q",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) results = adminJson.decodeFromString<List<CoverItem>>(text.toString())
                else listError = "Search failed: ${response.status}"
            } catch (e: Exception) { listError = "Error: ${e.message}" }
            searching = false
        }
    }

    // Autocomplete: search the full catalog as the admin types, debounced so we don't
    // fire a request per keystroke. Blank query just clears suggestions.
    LaunchedEffect(search) {
        if (search.trim().isBlank()) { results = emptyList(); return@LaunchedEffect }
        delay(250)
        runSearch()
    }

    fun loadAnalysis(item: CoverItem) {
        selected = item; analysis = null; analysisError = null; status = null
        neverAnalyzed = false; freshResult = null; freshResultRaw = null; reanalyzeError = null
        regenSurface = null; regenCategory = null; regenOld = null; regenNew = null; regenError = null
        scope.launch {
            loadingAnalysis = true
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/analysis?contentId=${encodeURIComponent(item.id)}",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val a = adminJson.decodeFromString<AdminAnalysis>(text.toString())
                    analysis = a
                    titleEdit = a.title.ifBlank { item.title }
                    originalTitle = titleEdit
                    authorEdit = a.author ?: ""
                    originalAuthor = authorEdit
                    score = a.score.coerceIn(1, 10)
                    islamicScore = a.islamicScore.coerceIn(1, 10)
                    summary = a.summary
                    positiveAspectsText = a.positiveAspects.joinToString("\n")
                    recommendation = a.recommendation
                    ageGuidance = a.ageGuidance
                    additionalNotes = a.additionalNotes
                    tldrWorking = a.tldr
                    hasPhase2 = a.hasPhase2
                    val byCat = a.concerns.associateBy { it.category }
                    working = byCat; original = byCat
                    expanded = emptySet()
                } else if (response.status.toInt() == 404) {
                    neverAnalyzed = true
                } else {
                    analysisError = "Failed to load: ${response.status}"
                }
            } catch (e: Exception) { analysisError = "Error: ${e.message}" }
            loadingAnalysis = false
        }
    }

    // "No-cache" reanalyze: runs a brand-new full analysis server-side (ignores every cache layer,
    // persists nothing) and returns the result for preview. Only reachable for legacy/never-analyzed
    // titles — takes 1-2 minutes and costs the same as a real user's full analysis.
    fun reanalyze() {
        val item = selected ?: return
        scope.launch {
            reanalyzing = true; reanalyzeError = null
            try {
                val year = item.cacheKey.substringAfterLast("::").trim().ifBlank { null }
                val body = kotlin.js.JSON.stringify(kotlin.js.json(
                    "contentId" to item.id,
                    "title" to item.title,
                    "mediaType" to (item.mediaType ?: ""),
                    "year" to year,
                    "author" to analysis?.author
                ))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/reanalyze",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    freshResultRaw = text
                    freshResult = adminJson.decodeFromString<AdminAnalysis>(text)
                } else {
                    reanalyzeError = "Reanalyze failed (${response.status}): ${text.take(200)}"
                }
            } catch (e: Exception) { reanalyzeError = "Error: ${e.message}" }
            reanalyzing = false
        }
    }

    // Persist the previewed fresh result as the title's real verdict, then reload — the screen
    // flips into the normal editable form since a real media_phase1 row now exists.
    fun acceptReanalysis() {
        val item = selected ?: return
        val raw = freshResultRaw ?: return
        scope.launch {
            accepting = true; reanalyzeError = null
            try {
                val body = """{"contentId":"${item.id}","analysis":$raw}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/accept-reanalysis",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    freshResult = null; freshResultRaw = null
                    loadAnalysis(item)
                } else {
                    val t = response.text().await().toString()
                    reanalyzeError = "Accept failed (${response.status}): ${t.take(200)}"
                }
            } catch (e: Exception) { reanalyzeError = "Error: ${e.message}" }
            accepting = false
        }
    }

    // Redo ONE concern with a fresh (no-cache) pass. surface = "editable" (working[] map — still
    // needs Save changes afterward) or "fresh" (the reanalyze preview — still needs Use this new
    // analysis afterward). Only stages the result; nothing is applied until Confirm.
    fun regenerateConcern(surface: String, old: AdminConcern) {
        val item = selected ?: return
        scope.launch {
            regenSurface = surface; regenCategory = old.category; regenOld = old; regenNew = null
            regenerating = true; regenError = null
            try {
                val year = item.cacheKey.substringAfterLast("::").trim().ifBlank { null }
                val body = kotlin.js.JSON.stringify(kotlin.js.json(
                    "contentId" to item.id,
                    "title" to item.title,
                    "mediaType" to (item.mediaType ?: ""),
                    "year" to year,
                    "author" to analysis?.author,
                    "category" to old.category
                ))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/regenerate-concern",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    regenNew = adminJson.decodeFromString<AdminConcern>(text)
                } else {
                    regenError = "Regenerate failed (${response.status}): ${text.take(200)}"
                }
            } catch (e: Exception) { regenError = "Error: ${e.message}" }
            regenerating = false
        }
    }

    fun clearRegenerate() {
        regenSurface = null; regenCategory = null; regenOld = null; regenNew = null; regenError = null
    }

    // Apply the staged regenerated concern to whichever surface it came from. Neither branch
    // touches the server — "editable" still needs Save changes, "fresh" still needs Use this new
    // analysis, exactly like every other pending edit on those two screens.
    fun confirmRegenerate() {
        val cat = regenCategory ?: return
        val fresh = regenNew ?: return
        when (regenSurface) {
            "editable" -> updateConcern(cat) { fresh }
            "fresh" -> freshResult?.let { fr ->
                val updated = fr.copy(concerns = fr.concerns.map { if (it.category == cat) fresh else it })
                freshResult = updated
                freshResultRaw = adminJson.encodeToString(AdminAnalysis.serializer(), updated)
            }
        }
        clearRegenerate()
    }

    fun save() {
        val item = selected ?: return
        scope.launch {
            saving = true; status = null
            try {
                // Only send concerns with an actual diff against the loaded baseline. "none"
                // severity is a removal directive (the server drops the concern entirely).
                val changes = working.values.mapNotNull { cur ->
                    val orig = original[cur.category] ?: AdminConcern(category = cur.category)
                    val sevChanged = cur.severity != orig.severity
                    val fieldsChanged = cur.rationale != orig.rationale || cur.description != orig.description ||
                        cur.islamicPerspective != orig.islamicPerspective || cur.evidence != orig.evidence ||
                        cur.quranReference != orig.quranReference || cur.hadithReference != orig.hadithReference
                    if (!sevChanged && !fieldsChanged) return@mapNotNull null
                    kotlin.js.json(
                        "category" to cur.category,
                        "severity" to (if (sevChanged) cur.severity else null),
                        "rationale" to (if (cur.rationale != orig.rationale) cur.rationale else null),
                        "description" to (if (cur.description != orig.description) cur.description else null),
                        "islamicPerspective" to (if (cur.islamicPerspective != orig.islamicPerspective) cur.islamicPerspective else null),
                        "evidence" to (if (cur.evidence != orig.evidence) cur.evidence else null),
                        "quranReference" to (if (cur.quranReference != orig.quranReference) cur.quranReference?.let {
                            kotlin.js.json("english" to it.english, "arabic" to it.arabic, "reference" to it.reference)
                        } else null),
                        "hadithReference" to (if (cur.hadithReference != orig.hadithReference) cur.hadithReference?.let {
                            kotlin.js.json("english" to it.english, "arabic" to it.arabic, "reference" to it.reference)
                        } else null)
                    )
                }.toTypedArray()
                val positiveAspects = positiveAspectsText.split("\n").map { it.trim() }.filter { it.isNotBlank() }.toTypedArray()
                val tldr = tldrWorking.filter { it.text.isNotBlank() }
                    .map { kotlin.js.json("text" to it.text, "severity" to it.severity, "category" to it.category) }
                    .toTypedArray()
                val trimmedTitle = titleEdit.trim()
                val body = kotlin.js.JSON.stringify(kotlin.js.json(
                    "contentId" to item.id,
                    "title" to (if (trimmedTitle.isNotBlank() && trimmedTitle != originalTitle) trimmedTitle else null),
                    "author" to (if (authorEdit != originalAuthor) authorEdit else null),
                    "score" to score, "islamicScore" to islamicScore,
                    "summary" to summary, "positiveAspects" to positiveAspects, "tldr" to tldr,
                    "recommendation" to recommendation, "ageGuidance" to ageGuidance, "additionalNotes" to additionalNotes,
                    "changes" to changes
                ))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/edit-analysis",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    status = "Saved ✓ — users see it on their next check"
                    original = working   // new baseline
                    originalTitle = trimmedTitle; originalAuthor = authorEdit
                } else {
                    val t = response.text().await()
                    status = "Save failed (${response.status}): ${t.toString().take(160)}"
                }
            } catch (e: Exception) { status = "Error: ${e.message}" }
            saving = false
        }
    }

    LaunchedEffect(results) {
        if (autoSelectPending && results.isNotEmpty()) {
            autoSelectPending = false
            // Prefer the exact (title, mediaType, year) cache-key match — the catalog can hold
            // duplicate rows for the same title, and picking the wrong one silently shows a
            // different row's (possibly stale/empty) data instead of the one just viewed.
            val wantKey = if (!initialMediaType.isNullOrBlank())
                "${initialTitle}::${initialMediaType}::${initialYear ?: ""}".lowercase() else null
            val match = (wantKey?.let { key -> results.firstOrNull { it.cacheKey.lowercase() == key } })
                ?: results.firstOrNull { it.title.equals(initialTitle, ignoreCase = true) }
                ?: results.first()
            loadAnalysis(match)
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Edit Analyses" to null)

        CardSection("Find a title") {
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                org.jetbrains.compose.web.dom.TextInput(search) {
                    onInput { search = it.value }
                    style {
                        property("flex", "1"); property("min-width", "240px")
                        padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                    }
                    attr("placeholder", "Search title…")
                    onKeyDown { if (it.key == "Enter") runSearch() }
                }
                Span(attrs = {
                    style {
                        padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Primary)
                        color(Color.white); fontSize(14.px); fontWeight(600); cursor("pointer")
                    }
                    onClick { runSearch() }
                }) { Text("Search") }
            }
            listError?.let { P(attrs = { style { color(ScoreRed); fontSize(13.px) } }) { Text(it) } }
            if (searching) P(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("Searching…") }
            if (results.isNotEmpty()) {
                Div(attrs = { style { display(DisplayStyle.Flex); property("flex-direction", "column"); gap(4.px); property("margin-top", "10px") } }) {
                    results.forEach { item ->
                        val year = item.cacheKey.substringAfterLast("::").trim().ifBlank { "—" }
                        val typeLabel = item.mediaType?.replace("_", " ") ?: "—"
                        val isSel = selected?.id == item.id
                        Div(attrs = {
                            style {
                                padding(8.px, 12.px); borderRadius(8.px); cursor("pointer")
                                backgroundColor(if (isSel) Color("#ecfeff") else Color("#f8fafc"))
                                property("border", if (isSel) "1px solid #0891b2" else "1px solid $Gray200")
                            }
                            onClick { loadAnalysis(item) }
                        }) {
                            Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(item.title) }
                            Span(attrs = { style { fontSize(12.px); color(Gray500); property("margin-left", "8px") } }) { Text("$typeLabel · $year") }
                        }
                    }
                }
            }
        }

        selected?.let { item ->
            CardSection("Editing — ${item.title}") {
                if (loadingAnalysis) {
                    P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading analysis…") }
                    return@CardSection
                }
                analysisError?.let {
                    P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
                    return@CardSection
                }

                if (neverAnalyzed || analysis?.legacy == true) {
                    if (neverAnalyzed) {
                        P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0 0 14px") } }) {
                            Text("This title has never been analyzed — there's nothing to show yet.")
                        }
                    } else {
                        P(attrs = {
                            style {
                                fontSize(13.px); color(Color("#b45309")); backgroundColor(Color("#fffbeb"))
                                padding(8.px, 12.px); borderRadius(8.px); property("margin", "0 0 14px")
                            }
                        }) { Text("This is a LEGACY analysis (pre-V2 pipeline) — read-only. Run a fresh analysis below to replace it.") }
                        analysis?.let { ReadOnlyAnalysisPanel(it) }
                    }

                    Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); property("margin-top", "8px") } }) {
                        Span(attrs = {
                            style {
                                padding(10.px, 22.px); borderRadius(8.px); fontSize(14.px); fontWeight(700)
                                cursor(if (reanalyzing) "default" else "pointer")
                                backgroundColor(if (reanalyzing) Gray400 else Primary); color(Color.white)
                            }
                            if (!reanalyzing) onClick { reanalyze() }
                        }) { Text(if (reanalyzing) "Running fresh analysis… (1–2 min)" else "Run fresh analysis (no cache)") }
                    }
                    reanalyzeError?.let {
                        P(attrs = { style { color(ScoreRed); fontSize(13.px); property("margin-top", "8px") } }) { Text(it) }
                    }

                    freshResult?.let { fresh ->
                        Div(attrs = { style { display(DisplayStyle.Grid); property("grid-template-columns", "1fr 1fr"); gap(24.px); property("margin-top", "20px") } }) {
                            Div {
                                P(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray500); property("margin", "0 0 8px") } }) {
                                    Text(if (neverAnalyzed) "CURRENT — never analyzed" else "CURRENT (legacy)")
                                }
                                if (!neverAnalyzed) analysis?.let { ReadOnlyAnalysisPanel(it) }
                            }
                            Div {
                                P(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray500); property("margin", "0 0 8px") } }) { Text("FRESH (unsaved)") }
                                ReadOnlyAnalysisPanel(fresh, concernActions = { c ->
                                    RegenerateConcernButton(
                                        regenerating = regenerating && regenSurface == "fresh" && regenCategory == c.category,
                                        error = if (regenSurface == "fresh" && regenCategory == c.category) regenError else null,
                                        old = if (regenSurface == "fresh" && regenCategory == c.category) regenOld else null,
                                        fresh = if (regenSurface == "fresh" && regenCategory == c.category) regenNew else null,
                                        onStart = { regenerateConcern("fresh", c) },
                                        onConfirm = { confirmRegenerate() },
                                        onDiscard = { clearRegenerate() }
                                    )
                                })
                            }
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("margin-top", "16px"); property("flex-wrap", "wrap") } }) {
                            Span(attrs = {
                                style {
                                    padding(9.px, 20.px); borderRadius(8.px); fontSize(13.px); fontWeight(700)
                                    cursor(if (accepting) "default" else "pointer")
                                    backgroundColor(if (accepting) Gray400 else Primary); color(Color.white)
                                }
                                if (!accepting) onClick { acceptReanalysis() }
                            }) { Text(if (accepting) "Saving…" else "Use this new analysis") }
                            Span(attrs = {
                                style {
                                    padding(9.px, 20.px); borderRadius(8.px); fontSize(13.px); fontWeight(700)
                                    cursor(if (reanalyzing) "default" else "pointer")
                                    backgroundColor(Color.white); color(Primary); property("border", "1px solid ${Primary.toString()}")
                                }
                                if (!reanalyzing) onClick { reanalyze() }
                            }) { Text("Re-analyze again") }
                            Span(attrs = {
                                style {
                                    padding(9.px, 20.px); borderRadius(8.px); fontSize(13.px); fontWeight(600); cursor("pointer")
                                    backgroundColor(Color.white); color(Gray500); property("border", "1px solid $Gray200")
                                }
                                onClick { freshResult = null; freshResultRaw = null }
                            }) { Text("Keep current") }
                        }
                    }
                    return@CardSection
                }

                if (analysis == null) return@CardSection

                // Title / Author
                Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "18px") } }) {
                    Div(attrs = { style { property("flex", "2"); property("min-width", "220px") } }) {
                        Span(attrs = { style { fontSize(12.px); color(Gray500); fontWeight(600) } }) { Text("Title") }
                        org.jetbrains.compose.web.dom.TextInput(titleEdit) {
                            onInput { ev -> titleEdit = ev.value }
                            style {
                                width(100.percent); property("box-sizing", "border-box"); property("margin-top", "4px")
                                padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(14.px)
                            }
                        }
                    }
                    Div(attrs = { style { property("flex", "1"); property("min-width", "180px") } }) {
                        Span(attrs = { style { fontSize(12.px); color(Gray500); fontWeight(600) } }) { Text("Author") }
                        org.jetbrains.compose.web.dom.TextInput(authorEdit) {
                            onInput { ev -> authorEdit = ev.value }
                            style {
                                width(100.percent); property("box-sizing", "border-box"); property("margin-top", "4px")
                                padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(14.px)
                            }
                            attr("placeholder", "e.g. Roald Dahl (books only)")
                        }
                    }
                }

                // Overall scores
                Div(attrs = { style { display(DisplayStyle.Flex); gap(28.px); property("flex-wrap", "wrap"); property("margin-bottom", "18px") } }) {
                    @Composable
                    fun stepper(label: String, value: Int, colorHint: Boolean, set: (Int) -> Unit) {
                        Div(attrs = { style { display(DisplayStyle.Flex); property("flex-direction", "column"); gap(6.px) } }) {
                            Span(attrs = { style { fontSize(12.px); color(Gray500); fontWeight(600) } }) { Text(label) }
                            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                                Span(attrs = {
                                    style { padding(4.px, 12.px); borderRadius(8.px); backgroundColor(Color("#f1f5f9")); cursor("pointer"); fontSize(18.px); fontWeight(700); color(Gray600) }
                                    onClick { set((value - 1).coerceAtLeast(1)) }
                                }) { Text("−") }
                                Span(attrs = {
                                    style {
                                        fontSize(20.px); fontWeight(700); property("min-width", "32px"); property("text-align", "center")
                                        color(if (colorHint) verdictColor(value) else Gray900)
                                    }
                                }) { Text("$value") }
                                Span(attrs = {
                                    style { padding(4.px, 12.px); borderRadius(8.px); backgroundColor(Color("#f1f5f9")); cursor("pointer"); fontSize(18.px); fontWeight(700); color(Gray600) }
                                    onClick { set((value + 1).coerceAtMost(10)) }
                                }) { Text("+") }
                            }
                        }
                    }
                    stepper("Overall score (verdict color)", score, true) { score = it }
                    stepper("Islamic score", islamicScore, false) { islamicScore = it }
                }

                @Composable
                fun sectionLabel(text: String) {
                    P(attrs = { style { fontSize(12.px); color(Gray500); fontWeight(600); property("margin", "16px 0 6px") } }) { Text(text) }
                }
                @Composable
                fun editTextArea(value: String, placeholder: String, minHeight: Int = 60, onChange: (String) -> Unit) {
                    TextArea(value = value) {
                        onInput { onChange(it.value) }
                        style {
                            width(100.percent); property("box-sizing", "border-box")
                            property("min-height", "${minHeight}px"); padding(9.px, 11.px); borderRadius(9.px)
                            property("border", "1px solid $Gray200"); fontSize(13.px); property("outline", "none")
                            property("font-family", "inherit"); property("resize", "vertical")
                        }
                        attr("placeholder", placeholder)
                    }
                }

                sectionLabel("SUMMARY")
                editTextArea(summary, "One-line summary shown across the app…") { summary = it }

                sectionLabel("POSITIVE ASPECTS — one per line")
                editTextArea(positiveAspectsText, "Strong male role models\nEncourages honesty") { positiveAspectsText = it }

                sectionLabel("HIGHLIGHTS (TL;DR)")
                tldrWorking.forEachIndexed { idx, point ->
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("margin-bottom", "6px") } }) {
                        org.jetbrains.compose.web.dom.TextInput(point.text) {
                            onInput { ev -> tldrWorking = tldrWorking.toMutableList().also { it[idx] = point.copy(text = ev.value) } }
                            style { property("flex", "1"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px) }
                            attr("placeholder", "e.g. Uses profanity twice in chapter 3")
                        }
                        org.jetbrains.compose.web.dom.Select(attrs = {
                            style { padding(6.px, 8.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(12.px) }
                            onChange { ev -> tldrWorking = tldrWorking.toMutableList().also { it[idx] = point.copy(severity = ev.value ?: "medium") } }
                        }) {
                            listOf("high", "medium", "positive").forEach { s ->
                                Option(s, attrs = { if (point.severity == s) attr("selected", "true") }) { Text(s) }
                            }
                        }
                        Span(attrs = {
                            style { padding(6.px, 10.px); borderRadius(7.px); fontSize(12.px); fontWeight(600); cursor("pointer"); color(ScoreRed); property("border", "1px solid #fecaca") }
                            onClick { tldrWorking = tldrWorking.filterIndexed { i, _ -> i != idx } }
                        }) { Text("✕") }
                    }
                }
                Span(attrs = {
                    style { fontSize(12.px); fontWeight(600); color(Primary); cursor("pointer") }
                    onClick { tldrWorking = tldrWorking + AdminTldrPoint(severity = "medium") }
                }) { Text("+ Add highlight") }

                sectionLabel("RECOMMENDATION")
                editTextArea(recommendation, "Age-tailored recommendation shown at the top of the result…") { recommendation = it }
                sectionLabel("AGE GUIDANCE")
                editTextArea(ageGuidance, "Guidance for parents by age…", minHeight = 44) { ageGuidance = it }
                sectionLabel("ADDITIONAL NOTES")
                editTextArea(additionalNotes, "Anything else worth flagging…", minHeight = 44) { additionalNotes = it }
                if (!hasPhase2) {
                    P(attrs = { style { fontSize(12.px); color(Gray400); property("margin", "4px 0 0") } }) {
                        Text("This title has no full-analysis enrichment yet — recommendation/age guidance/notes/per-concern details won't be saved until a user runs a full analysis.")
                    }
                }

                // Per-concern editing
                sectionLabel("CONCERNS")
                analysis!!.concerns.forEach { c0 ->
                    val current = working[c0.category] ?: c0
                    val isOpen = c0.category in expanded
                    Div(attrs = { style { padding(8.px, 0.px); property("border-bottom", "1px solid #f1f5f9") } }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween); gap(12.px) } }) {
                            Div(attrs = {
                                style { display(DisplayStyle.Flex); property("flex-direction", "column"); cursor("pointer") }
                                onClick { expanded = if (isOpen) expanded - c0.category else expanded + c0.category }
                            }) {
                                Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text("${if (isOpen) "▾" else "▸"} ${c0.category}") }
                                if (current.severity == "none") Span(attrs = { style { fontSize(11.px); color(Gray400) } }) { Text("not flagged") }
                            }
                            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                                listOf(Triple("Red", "high", ScoreRed), Triple("Yellow", "medium", ScoreAmber), Triple("Green", "low", ScoreGreen)).forEach { (label, sev, col) ->
                                    val active = current.severity == sev
                                    Span(attrs = {
                                        style {
                                            padding(6.px, 14.px); borderRadius(8.px); fontSize(12.px); fontWeight(600); cursor("pointer")
                                            if (active) { backgroundColor(col); color(Color.white); property("border", "1px solid ${col.toString()}") }
                                            else { backgroundColor(Color.white); color(Gray500); property("border", "1px solid $Gray200") }
                                        }
                                        onClick { updateConcern(c0.category) { it.copy(severity = sev) } }
                                    }) { Text(label) }
                                }
                                // Remove the concern entirely (only meaningful when currently flagged).
                                if (current.severity != "none") {
                                    Span(attrs = {
                                        style {
                                            property("margin-left", "4px")
                                            padding(6.px, 12.px); borderRadius(8.px); fontSize(12.px); fontWeight(600); cursor("pointer")
                                            backgroundColor(Color.white); color(ScoreRed); property("border", "1px solid #fecaca")
                                        }
                                        attr("title", "Remove this concern from the analysis")
                                        onClick { updateConcern(c0.category) { it.copy(severity = "none") } }
                                    }) { Text("✕ Remove") }
                                }
                            }
                        }
                        RegenerateConcernButton(
                            regenerating = regenerating && regenSurface == "editable" && regenCategory == c0.category,
                            error = if (regenSurface == "editable" && regenCategory == c0.category) regenError else null,
                            old = if (regenSurface == "editable" && regenCategory == c0.category) regenOld else null,
                            fresh = if (regenSurface == "editable" && regenCategory == c0.category) regenNew else null,
                            onStart = { regenerateConcern("editable", current) },
                            onConfirm = { confirmRegenerate() },
                            onDiscard = { clearRegenerate() }
                        )
                        if (isOpen) {
                            Div(attrs = { style { property("margin", "10px 0 4px 12px"); display(DisplayStyle.Flex); property("flex-direction", "column"); gap(8.px) } }) {
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Internal rationale") }
                                    editTextArea(current.rationale, "Why this severity was chosen…", minHeight = 44) { v -> updateConcern(c0.category) { it.copy(rationale = v) } }
                                }
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Description shown to users") }
                                    editTextArea(current.description, "What the user sees for this concern…", minHeight = 60) { v -> updateConcern(c0.category) { it.copy(description = v) } }
                                }
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Islamic perspective") }
                                    editTextArea(current.islamicPerspective, "Islamic framing for this concern…", minHeight = 44) { v -> updateConcern(c0.category) { it.copy(islamicPerspective = v) } }
                                }
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Evidence") }
                                    editTextArea(current.evidence, "Specific example / scene / quote…", minHeight = 44) { v -> updateConcern(c0.category) { it.copy(evidence = v) } }
                                }
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Quran reference (Arabic is re-verified server-side — just set English + reference)") }
                                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-top", "4px") } }) {
                                        org.jetbrains.compose.web.dom.TextInput(current.quranReference?.english ?: "") {
                                            onInput { ev -> updateConcern(c0.category) { it.copy(quranReference = (it.quranReference ?: AdminQuranRef()).copy(english = ev.value)) } }
                                            style { property("flex", "2"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px) }
                                            attr("placeholder", "English translation")
                                        }
                                        org.jetbrains.compose.web.dom.TextInput(current.quranReference?.reference ?: "") {
                                            onInput { ev -> updateConcern(c0.category) { it.copy(quranReference = (it.quranReference ?: AdminQuranRef()).copy(reference = ev.value)) } }
                                            style { property("flex", "1"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px) }
                                            attr("placeholder", "e.g. Al-Isra 17:32")
                                        }
                                    }
                                }
                                Div {
                                    Span(attrs = { style { fontSize(11.px); color(Gray500); fontWeight(600) } }) { Text("Hadith reference") }
                                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-top", "4px") } }) {
                                        org.jetbrains.compose.web.dom.TextInput(current.hadithReference?.english ?: "") {
                                            onInput { ev -> updateConcern(c0.category) { it.copy(hadithReference = (it.hadithReference ?: AdminHadithRef()).copy(english = ev.value)) } }
                                            style { property("flex", "2"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px) }
                                            attr("placeholder", "English translation")
                                        }
                                        org.jetbrains.compose.web.dom.TextInput(current.hadithReference?.arabic ?: "") {
                                            onInput { ev -> updateConcern(c0.category) { it.copy(hadithReference = (it.hadithReference ?: AdminHadithRef()).copy(arabic = ev.value)) } }
                                            style { property("flex", "2"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px); property("direction", "rtl") }
                                            attr("placeholder", "Arabic")
                                        }
                                        org.jetbrains.compose.web.dom.TextInput(current.hadithReference?.reference ?: "") {
                                            onInput { ev -> updateConcern(c0.category) { it.copy(hadithReference = (it.hadithReference ?: AdminHadithRef()).copy(reference = ev.value)) } }
                                            style { property("flex", "1"); padding(7.px, 10.px); borderRadius(7.px); property("border", "1px solid $Gray200"); fontSize(13.px) }
                                            attr("placeholder", "e.g. Sahih al-Bukhari 1234")
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(16.px); property("margin-top", "18px") } }) {
                    Span(attrs = {
                        style {
                            padding(10.px, 24.px); borderRadius(8.px); fontSize(14.px); fontWeight(700); cursor(if (saving) "default" else "pointer")
                            backgroundColor(if (saving) Gray400 else Primary); color(Color.white)
                        }
                        if (!saving) onClick { save() }
                    }) { Text(if (saving) "Saving…" else "Save changes") }
                    status?.let { P(attrs = { style { fontSize(13.px); color(if (it.contains("✓")) ScoreGreen else ScoreRed); property("margin", "0") } }) { Text(it) } }
                }
            }
        }

        LgbtqRescoreCard()
    }
}

// ---------------------------------------------------------------------------
// Reconcile Duplicates View — merge media_content rows that are the same title+type
// but split across slightly different years (e.g. "MovieA (2018)" vs "(2019)").
// ---------------------------------------------------------------------------

// Groups the admin marked "Not Duplicates" are remembered per-browser (localStorage)
// so they stop reappearing in the list. Local-only: a cache clear forgets them,
// which is acceptable for a single admin.
private fun loadNotDuplicates(): Set<String> = try {
    val raw = kotlinx.browser.localStorage.getItem("not_duplicate_groups") ?: "[]"
    adminJson.decodeFromString<List<String>>(raw).toSet()
} catch (_: Exception) { emptySet() }

private fun saveNotDuplicates(keys: Set<String>) {
    try { kotlinx.browser.localStorage.setItem("not_duplicate_groups", kotlin.js.JSON.stringify(keys.toTypedArray())) } catch (_: Exception) {}
}

@Composable
private fun ReconcileDuplicatesView(onNavigate: (DrillDownView) -> Unit) {
    var groups by remember { mutableStateOf<List<DupGroup>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var status by remember { mutableStateOf<String?>(null) }
    var busyKey by remember { mutableStateOf<String?>(null) }
    // group.key -> chosen keeper variant id
    val selected = remember { mutableStateMapOf<String, String>() }
    var dismissed by remember { mutableStateOf(loadNotDuplicates()) }   // group keys marked "Not Duplicates"
    val scope = rememberCoroutineScope()

    // Manual search-and-merge: find titles, check the duplicates, pick a keeper, merge.
    var searchQuery by remember { mutableStateOf("") }
    var searchResults by remember { mutableStateOf<List<CoverItem>>(emptyList()) }
    var searching by remember { mutableStateOf(false) }
    var searchError by remember { mutableStateOf<String?>(null) }
    var selectedIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var keeperId by remember { mutableStateOf<String?>(null) }
    var mergingManual by remember { mutableStateOf(false) }

    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    // Default keeper: a variant with a cover, else most hits, else newest.
    fun defaultKeeper(g: DupGroup): String {
        val v = g.variants.firstOrNull { !it.coverUrl.isNullOrBlank() }
            ?: g.variants.maxByOrNull { it.hitCount }
            ?: g.variants.firstOrNull()
        return v?.id ?: ""
    }

    fun fetchGroups() {
        scope.launch {
            loading = true
            error = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/duplicate-titles",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    groups = adminJson.decodeFromString<List<DupGroup>>(text.toString())
                    groups.forEach { g -> if (selected[g.key] == null) selected[g.key] = defaultKeeper(g) }
                } else {
                    error = "Failed to fetch: ${response.status}"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            loading = false
        }
    }

    fun merge(group: DupGroup) {
        val keepId = selected[group.key] ?: return
        val mergeIds = group.variants.map { it.id }.filter { it != keepId }
        if (mergeIds.isEmpty()) return
        scope.launch {
            busyKey = group.key
            status = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = kotlin.js.JSON.stringify(kotlin.js.json("keepId" to keepId, "mergeIds" to mergeIds.toTypedArray()))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/merge-titles",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text.toString()) as? kotlinx.serialization.json.JsonObject
                    val n = (obj?.get("merged") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    groups = groups.filter { it.key != group.key }
                    status = "Merged $n duplicate(s) into \"${group.title}\" ✓"
                } else {
                    status = "Merge failed (${response.status}): ${text.toString().take(160)}"
                }
            } catch (e: Exception) {
                status = "Error: ${e.message}"
            }
            busyKey = null
        }
    }

    fun runTitleSearch() {
        scope.launch {
            searching = true; searchError = null
            selectedIds = emptySet(); keeperId = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val q = if (searchQuery.trim().isNotBlank()) "&search=${encodeURIComponent(searchQuery.trim())}" else ""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/covers?filter=all&limit=60&offset=0$q",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) searchResults = adminJson.decodeFromString<List<CoverItem>>(text.toString())
                else searchError = "Search failed: ${response.status}"
            } catch (e: Exception) { searchError = "Error: ${e.message}" }
            searching = false
        }
    }

    fun mergeSelected() {
        val selOrdered = searchResults.filter { it.id in selectedIds }
        val keeper = keeperId?.takeIf { selectedIds.contains(it) }
            ?: selOrdered.firstOrNull { !it.coverUrl.isNullOrBlank() }?.id
            ?: selOrdered.firstOrNull()?.id ?: return
        val losers = selectedIds.filter { it != keeper }
        if (losers.isEmpty()) return
        val keeperTitle = searchResults.firstOrNull { it.id == keeper }?.title ?: "kept title"
        scope.launch {
            mergingManual = true; status = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = kotlin.js.JSON.stringify(kotlin.js.json("keepId" to keeper, "mergeIds" to losers.toTypedArray()))
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/merge-titles",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text.toString()) as? kotlinx.serialization.json.JsonObject
                    val n = (obj?.get("merged") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    val mergedSet = losers.toSet()
                    searchResults = searchResults.filter { it.id !in mergedSet }
                    selectedIds = emptySet(); keeperId = null
                    status = "Merged $n into \"$keeperTitle\" ✓"
                    fetchGroups()
                } else {
                    status = "Merge failed (${response.status}): ${text.toString().take(160)}"
                }
            } catch (e: Exception) { status = "Error: ${e.message}" }
            mergingManual = false
        }
    }

    LaunchedEffect(Unit) { fetchGroups() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Reconcile Duplicates" to null)

        CardSection("About") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) {
                Text("These titles exist as separate rows that differ only by year. Pick the one to keep — the others are deleted and any user history pointing at them is redirected to the kept one.")
            }
        }

        CardSection("Find duplicates manually") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 10px") } }) {
                Text("Search any title, check the rows that are the same thing, choose which to keep, then merge. Use this for duplicates the auto-detector misses.")
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                org.jetbrains.compose.web.dom.TextInput(searchQuery) {
                    onInput { searchQuery = it.value }
                    style {
                        property("flex", "1"); property("min-width", "240px"); padding(10.px, 14.px)
                        borderRadius(8.px); property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                    }
                    attr("placeholder", "Search titles…")
                    onKeyDown { if (it.key == "Enter") runTitleSearch() }
                }
                Span(attrs = {
                    style { padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Primary); color(Color.white); fontSize(14.px); fontWeight(600); cursor("pointer") }
                    onClick { runTitleSearch() }
                }) { Text("Search") }
            }
            if (searching) P(attrs = { style { color(Gray400); fontSize(13.px) } }) { Text("Searching…") }
            searchError?.let { P(attrs = { style { color(ScoreRed); fontSize(13.px) } }) { Text(it) } }

            if (searchResults.isNotEmpty()) {
                val selectedOrdered = searchResults.filter { it.id in selectedIds }
                val effKeeper = keeperId?.takeIf { selectedIds.contains(it) }
                    ?: selectedOrdered.firstOrNull { !it.coverUrl.isNullOrBlank() }?.id
                    ?: selectedOrdered.firstOrNull()?.id
                Div(attrs = { style { display(DisplayStyle.Flex); property("flex-direction", "column"); gap(4.px); property("margin-top", "12px") } }) {
                    searchResults.forEach { item ->
                        val checked = item.id in selectedIds
                        val isKeeper = checked && item.id == effKeeper
                        val year = item.cacheKey.substringAfterLast("::").trim().ifBlank { "—" }
                        val typeLabel = item.mediaType?.replace("_", " ") ?: "—"
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                                padding(8.px, 10.px); borderRadius(8.px)
                                backgroundColor(if (checked) Color("#fdf2f8") else Color.white)
                                property("border", if (isKeeper) "2px solid #db2777" else if (checked) "1px solid #f9a8d4" else "1px solid $Gray200")
                            }
                        }) {
                            org.jetbrains.compose.web.dom.CheckboxInput(checked) {
                                onChange {
                                    selectedIds = if (item.id in selectedIds) selectedIds - item.id else selectedIds + item.id
                                    if (keeperId != null && keeperId !in selectedIds) keeperId = null
                                }
                            }
                            val cover = item.coverUrl?.takeIf { it.isNotBlank() }
                            if (cover != null) {
                                Img(src = cover, alt = item.title, attrs = {
                                    style { width(40.px); height(56.px); property("object-fit", "cover"); borderRadius(4.px); backgroundColor(Color("#f1f5f9")) }
                                })
                            } else {
                                Div(attrs = {
                                    style {
                                        width(40.px); height(56.px); borderRadius(4.px); backgroundColor(Color("#f1f5f9"))
                                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); fontSize(18.px)
                                    }
                                }) { Text("🖼️") }
                            }
                            Div(attrs = { style { display(DisplayStyle.Flex); property("flex-direction", "column"); property("flex", "1"); property("min-width", "0") } }) {
                                Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(item.title) }
                                Span(attrs = { style { fontSize(12.px); color(Gray500) } }) { Text("$typeLabel · $year") }
                            }
                            if (checked) {
                                Span(attrs = {
                                    style {
                                        padding(5.px, 12.px); borderRadius(6.px); fontSize(12.px); fontWeight(600); cursor("pointer")
                                        if (isKeeper) { backgroundColor(Color("#db2777")); color(Color.white) }
                                        else { backgroundColor(Color.white); color(Gray600); property("border", "1px solid $Gray200") }
                                    }
                                    onClick { keeperId = item.id }
                                }) { Text(if (isKeeper) "✓ Keep" else "Keep") }
                            }
                        }
                    }
                }
                val canMerge = selectedIds.size >= 2 && effKeeper != null && !mergingManual
                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(14.px); property("margin-top", "12px") } }) {
                    Span(attrs = {
                        style {
                            padding(10.px, 22.px); borderRadius(8.px); fontSize(14.px); fontWeight(700)
                            if (canMerge) { backgroundColor(Color("#db2777")); color(Color.white); cursor("pointer") }
                            else { backgroundColor(Color("#f1f5f9")); color(Gray400); cursor("default") }
                        }
                        if (canMerge) onClick { mergeSelected() }
                    }) { Text(if (mergingManual) "Merging…" else "Merge ${(selectedIds.size - 1).coerceAtLeast(0)} into keeper") }
                    if (selectedIds.size == 1) Span(attrs = { style { fontSize(12.px); color(Gray400) } }) { Text("select at least 2") }
                }
            }
        }

        status?.let {
            CardSection("Status") {
                P(attrs = { style { color(if (it.contains("✓")) ScoreGreen else ScoreRed); fontSize(14.px); property("margin", "0") } }) { Text(it) }
            }
        }

        if (loading) {
            CardSection("Loading…") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Scanning for duplicates…") }
            }
            return@PageShell
        }

        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
        }

        val visible = groups.filter { it.key !in dismissed }
        CardSection("Duplicate groups (${visible.size})") {
            if (dismissed.isNotEmpty()) {
                P(attrs = { style { color(Gray500); fontSize(12.px); property("margin", "0 0 12px") } }) {
                    Text("${dismissed.size} marked Not Duplicates · ")
                    Span(attrs = {
                        style { color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                        onClick { dismissed = emptySet(); saveNotDuplicates(emptySet()) }
                    }) { Text("reset") }
                }
            }
            if (visible.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) {
                    Text(if (groups.isEmpty()) "No duplicate titles found. 🎉" else "No remaining duplicate groups.")
                }
            } else {
                visible.forEach { group ->
                    Div(attrs = {
                        style {
                            property("border", "1px solid $Gray200"); borderRadius(12.px)
                            padding(16.px); property("margin-bottom", "16px")
                        }
                    }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); property("margin-bottom", "12px") } }) {
                            P(attrs = { style { fontSize(15.px); fontWeight(700); color(Gray900); property("margin", "0") } }) {
                                Text("${group.title} · ${group.mediaType.replace("_", " ")}")
                            }
                            val keepId = selected[group.key]
                            val canMerge = keepId != null && group.variants.count { it.id != keepId } > 0 && busyKey != group.key
                            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px) } }) {
                                // Leave these as separate titles — remembered so the group won't re-list.
                                Span(attrs = {
                                    style {
                                        padding(8.px, 16.px); borderRadius(8.px); fontSize(13.px); fontWeight(600)
                                        backgroundColor(Color.white); color(Gray600); property("border", "1px solid $Gray200"); cursor("pointer")
                                    }
                                    onClick { dismissed = dismissed + group.key; saveNotDuplicates(dismissed) }
                                }) { Text("Not Duplicates") }
                                Span(attrs = {
                                    style {
                                        padding(8.px, 18.px); borderRadius(8.px); fontSize(13.px); fontWeight(600)
                                        if (canMerge) { backgroundColor(Color("#db2777")); color(Color.white); cursor("pointer") }
                                        else { backgroundColor(Color("#f1f5f9")); color(Gray400); cursor("default") }
                                    }
                                    if (canMerge) onClick { merge(group) }
                                }) { Text(if (busyKey == group.key) "Merging…" else "Merge") }
                            }
                        }
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(180px, 1fr))")
                                gap(12.px)
                            }
                        }) {
                            group.variants.forEach { v ->
                                val isKeeper = selected[group.key] == v.id
                                val year = v.cacheKey.substringAfterLast("::").trim().ifBlank { "no year" }
                                Div(attrs = {
                                    style {
                                        property("border", if (isKeeper) "2px solid #db2777" else "1px solid $Gray200")
                                        borderRadius(10.px); padding(10.px); cursor("pointer")
                                        display(DisplayStyle.Flex); property("flex-direction", "column"); gap(6.px)
                                        backgroundColor(if (isKeeper) Color("#fdf2f8") else Color.white)
                                    }
                                    onClick { selected[group.key] = v.id }
                                }) {
                                    val cover = v.coverUrl?.takeIf { it.isNotBlank() }
                                    if (cover != null) {
                                        Img(src = cover, alt = v.title, attrs = {
                                            style { width(100.percent); height(180.px); property("object-fit", "cover"); borderRadius(6.px); backgroundColor(Color("#f1f5f9")) }
                                        })
                                    } else {
                                        Div(attrs = {
                                            style {
                                                width(100.percent); height(180.px); borderRadius(6.px); backgroundColor(Color("#f1f5f9"))
                                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); color(Gray400); fontSize(30.px)
                                            }
                                        }) { Text("🖼️") }
                                    }
                                    P(attrs = { style { fontSize(13.px); fontWeight(700); color(if (isKeeper) Color("#db2777") else Gray700); property("margin", "0") } }) {
                                        Text(if (isKeeper) "✓ Keep · $year" else year)
                                    }
                                    P(attrs = { style { fontSize(11.px); color(Gray500); property("margin", "0") } }) {
                                        Text("${v.hitCount} hits${v.createdAt?.takeIf { it.isNotBlank() }?.let { " · ${it.take(10)}" } ?: ""}")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Grant Credits View — manual entitlement adjustments (comps, refunds, support)
// ---------------------------------------------------------------------------

@Composable
private fun GrantCreditsView(onNavigate: (DrillDownView) -> Unit) {
    var email by remember { mutableStateOf("") }
    var fullDelta by remember { mutableStateOf("") }
    var qcDelta by remember { mutableStateOf("") }
    var daysDelta by remember { mutableStateOf("") }
    var current by remember { mutableStateOf<String?>(null) }
    var looking by remember { mutableStateOf(false) }
    var applying by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<String?>(null) }
    var resultIsError by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    // ── Daily analysis limit — per-user override, loaded alongside the entitlement lookup below.
    // See db/migrations/2026-09-22_daily_analysis_cap.sql.
    var dailyCapLoading by remember { mutableStateOf(false) }
    var dailyCapOverride by remember { mutableStateOf<Int?>(null) }   // raw column: null=default, 0=no limit, N=custom
    var dailyCapGlobal by remember { mutableStateOf<Int?>(null) }
    var dailyCapEffective by remember { mutableStateOf<Int?>(null) }
    var dailyCapUsed by remember { mutableStateOf(0) }
    var dailyCapResetsAt by remember { mutableStateOf<String?>(null) }
    var dailyCapChoice by remember { mutableStateOf("default") }      // "default" | "unlimited" | "custom" — the radio being edited
    var dailyCapCustomText by remember { mutableStateOf("") }
    var dailyCapSaving by remember { mutableStateOf(false) }
    var dailyCapStatus by remember { mutableStateOf<String?>(null) }
    var dailyCapStatusError by remember { mutableStateOf(false) }

    // ── Search packs (organisations) — see db/migrations/2026-10-07_weekly_cap_and_search_packs.sql.
    // Not sold anywhere in the app: an organisation contacts us, we invoice them, then grant here.
    var packsLoading by remember { mutableStateOf(false) }
    var packRemaining by remember { mutableStateOf(0) }
    var packRows by remember { mutableStateOf<List<kotlinx.serialization.json.JsonObject>>(emptyList()) }
    var packStatus by remember { mutableStateOf<String?>(null) }
    var packStatusError by remember { mutableStateOf(false) }
    var packSaving by remember { mutableStateOf(false) }
    var packSearchesText by remember { mutableStateOf("") }
    var packMonthsText by remember { mutableStateOf("") }   // blank = never expires
    var packAmountText by remember { mutableStateOf("") }
    var packInvoiceText by remember { mutableStateOf("") }
    var packNoteText by remember { mutableStateOf("") }

    // ── KIDZONE (Kids Mode) — OFF for every account until switched on here. The server enforces it on
    // every kids path. See db/migrations/2026-10-11_kids_mode_allowlist.sql.
    var kidsLoading by remember { mutableStateOf(false) }
    // The address the state below was read for. Set ONLY by a read that succeeded, so a failed read can
    // never show as "off", and an answer for an address no longer in the box can never be acted on.
    var kidsLoadedFor by remember { mutableStateOf<String?>(null) }
    var kidsEnabled by remember { mutableStateOf(false) }
    var kidsHasAccount by remember { mutableStateOf(false) }
    var kidsSaving by remember { mutableStateOf(false) }
    var kidsStatus by remember { mutableStateOf<String?>(null) }
    var kidsStatusError by remember { mutableStateOf(false) }
    var kidsAccounts by remember { mutableStateOf<List<String>?>(null) }   // null = not read yet, or the read failed
    var kidsAccountsError by remember { mutableStateOf<String?>(null) }
    val kidsRequest = remember { IntArray(1) }                              // only the latest lookup may write

    // ── Bulk book scan — OFF for every account until switched on here. The server enforces it on
    // every bulk path. See db/migrations/2026-10-17_bulk_scan_allowlist.sql.
    var bulkLoading by remember { mutableStateOf(false) }
    // The address the state below was read for. Set ONLY by a read that succeeded, so a failed read can
    // never show as "off", and an answer for an address no longer in the box can never be acted on.
    var bulkLoadedFor by remember { mutableStateOf<String?>(null) }
    var bulkEnabled by remember { mutableStateOf(false) }
    var bulkHasAccount by remember { mutableStateOf(false) }
    var bulkSaving by remember { mutableStateOf(false) }
    var bulkStatus by remember { mutableStateOf<String?>(null) }
    var bulkStatusError by remember { mutableStateOf(false) }
    var bulkAccounts by remember { mutableStateOf<List<String>?>(null) }   // null = not read yet, or the read failed
    var bulkAccountsError by remember { mutableStateOf<String?>(null) }
    val bulkRequest = remember { IntArray(1) }                              // only the latest lookup may write

    // Render the server's user_entitlements row JSON into a short human summary.
    fun summarize(rowJson: String): String {
        return try {
            val obj = adminJson.parseToJsonElement(rowJson) as? kotlinx.serialization.json.JsonObject
            if (obj == null || obj["error"] != null) {
                "No entitlement row yet"
            } else {
                fun s(k: String) = (obj[k] as? kotlinx.serialization.json.JsonPrimitive)?.content
                val full = s("full_credits") ?: "0"
                val qc = s("qc_credits") ?: "0"
                val plan = s("plan") ?: "—"
                val status = s("status") ?: "free"
                val unlimited = s("unlimited_until")
                buildString {
                    append("$full full · $qc quick")
                    append("  ·  plan: $plan  ·  status: $status")
                    if (!unlimited.isNullOrBlank()) append("  ·  unlimited until $unlimited")
                }
            }
        } catch (_: Exception) {
            rowJson
        }
    }

    /** Fills the per-user fields from a GET/POST /api/admin/user-daily-cap body (same shape). */
    fun applyDailyCapJson(obj: kotlinx.serialization.json.JsonObject?) {
        fun i(k: String) = (obj?.get(k) as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
        fun s(k: String) = (obj?.get(k) as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
        dailyCapOverride = i("override")
        dailyCapGlobal = i("globalCap")
        dailyCapEffective = i("effectiveLimit")
        dailyCapUsed = i("usedInWindow") ?: i("usedLast24h") ?: 0
        dailyCapResetsAt = s("resetsAt")
        dailyCapChoice = when {
            dailyCapOverride == null -> "default"
            dailyCapOverride == 0 -> "unlimited"
            else -> "custom"
        }
        dailyCapCustomText = dailyCapOverride?.takeIf { it > 0 }?.toString() ?: ""
    }

    /** [afterSave]: called to reload after a successful save, so end on "Saved." rather than
     *  clearing it. */
    fun fetchDailyCap(afterSave: Boolean = false) {
        val addr = email.trim()
        if (addr.isBlank()) return
        scope.launch {
            dailyCapLoading = true
            dailyCapStatus = null; dailyCapStatusError = false
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val q = encodeURIComponent(addr)
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-daily-cap?email=$q",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    applyDailyCapJson(adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)
                    if (afterSave) dailyCapStatus = "Saved."
                } else {
                    dailyCapStatus = if (afterSave) "Saved, but couldn't reload it (${response.status})."
                        else "Couldn't load the weekly limit (${response.status})."
                    dailyCapStatusError = !afterSave
                }
            } catch (e: Exception) {
                dailyCapStatus = "Error loading the weekly limit: ${e.message}"
                dailyCapStatusError = true
            }
            dailyCapLoading = false
        }
    }

    fun saveDailyCap() {
        val addr = email.trim()
        if (addr.isBlank()) return
        val overrideValue: Int? = when (dailyCapChoice) {
            "default" -> null
            "unlimited" -> 0
            else -> {
                val n = dailyCapCustomText.trim().toIntOrNull()
                if (n == null || n !in 1..200) {
                    dailyCapStatus = "Custom limit must be a whole number from 1 to 200."
                    dailyCapStatusError = true
                    return
                }
                n
            }
        }
        scope.launch {
            dailyCapSaving = true
            dailyCapStatus = null; dailyCapStatusError = false
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = """{"email":"${addr.replace("\"", "\\\"")}","override":${overrideValue?.toString() ?: "null"}}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-daily-cap",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    // The POST answers in the GET's shape: use it directly. Re-fetching instead
                    // cleared this "Saved." under the loading state, so a save looked like nothing.
                    val obj = runCatching { adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject }.getOrNull()
                    if (obj != null && (obj.containsKey("usedInWindow") || obj.containsKey("usedLast24h"))) {
                        applyDailyCapJson(obj)
                        dailyCapStatus = "Saved."
                        dailyCapStatusError = false
                    } else {
                        fetchDailyCap(afterSave = true)
                    }
                } else {
                    dailyCapStatus = "Save failed (${response.status})."
                    dailyCapStatusError = true
                }
            } catch (e: Exception) {
                dailyCapStatus = "Error saving: ${e.message}"
                dailyCapStatusError = true
            }
            dailyCapSaving = false
        }
    }

    fun packHeaders(json: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (json) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun fetchPacks() {
        val addr = email.trim()
        if (addr.isBlank()) return
        scope.launch {
            packsLoading = true
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-packs?email=${encodeURIComponent(addr)}",
                    kotlin.js.json("method" to "GET", "headers" to packHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    packRemaining = (obj?.get("remaining") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    packRows = (obj?.get("packs") as? kotlinx.serialization.json.JsonArray)
                        ?.mapNotNull { it as? kotlinx.serialization.json.JsonObject } ?: emptyList()
                } else {
                    packRows = emptyList(); packRemaining = 0
                    packStatus = "Couldn't load packs (${response.status}). Has the 2026-10-07 migration been run?"
                    packStatusError = true
                }
            } catch (e: Exception) {
                packStatus = "Error loading packs: ${e.message}"; packStatusError = true
            }
            packsLoading = false
        }
    }

    fun grantPack() {
        val addr = email.trim()
        val searches = packSearchesText.trim().toIntOrNull()
        val months = packMonthsText.trim().toIntOrNull()   // null = never expires
        if (addr.isBlank() || searches == null || searches !in 1..100000) {
            packStatus = "Enter the account email above and a number of searches from 1 to 100000."; packStatusError = true; return
        }
        if (packMonthsText.isNotBlank() && (months == null || months !in 1..36)) {
            packStatus = "Expires after must be 1 to 36 months, or blank for never."; packStatusError = true; return
        }
        val dollars = packAmountText.trim().takeIf { it.isNotEmpty() }?.toDoubleOrNull()
        if (packAmountText.isNotBlank() && (dollars == null || dollars < 0)) {
            packStatus = "Amount paid must be a number of dollars, or blank."; packStatusError = true; return
        }
        scope.launch {
            packSaving = true; packStatus = null; packStatusError = false
            try {
                val body = buildString {
                    append("{")
                    append("\"email\":${kotlinx.serialization.json.JsonPrimitive(addr)},")
                    append("\"searches\":$searches,")
                    if (months != null) append("\"months\":$months,")
                    if (dollars != null) append("\"amountCents\":${kotlin.math.round(dollars * 100).toInt()},")
                    append("\"invoiceRef\":${kotlinx.serialization.json.JsonPrimitive(packInvoiceText.trim())},")
                    append("\"note\":${kotlinx.serialization.json.JsonPrimitive(packNoteText.trim())}")
                    append("}")
                }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/grant-pack",
                    kotlin.js.json("method" to "POST", "headers" to packHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    packStatus = "Granted $searches searches."
                    packSearchesText = ""; packAmountText = ""; packInvoiceText = ""; packNoteText = ""
                    fetchPacks()
                } else {
                    packStatus = "Grant failed (${response.status}): ${response.text().await()}"; packStatusError = true
                }
            } catch (e: Exception) {
                packStatus = "Error: ${e.message}"; packStatusError = true
            }
            packSaving = false
        }
    }

    /** zero = refund (also stamps the pack as refunded); otherwise set the remaining count and/or move the expiry. */
    fun adjustPack(packId: String, zero: Boolean = false, remaining: Int? = null, extendDays: Int? = null) {
        scope.launch {
            packSaving = true; packStatus = null; packStatusError = false
            try {
                val body = buildString {
                    append("{\"packId\":\"$packId\",\"zero\":$zero")
                    if (remaining != null) append(",\"remaining\":$remaining")
                    if (extendDays != null) append(",\"extendDays\":$extendDays")
                    append("}")
                }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/adjust-pack",
                    kotlin.js.json("method" to "POST", "headers" to packHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) { packStatus = "Saved."; fetchPacks() }
                else { packStatus = "Save failed (${response.status}): ${response.text().await()}"; packStatusError = true }
            } catch (e: Exception) {
                packStatus = "Error: ${e.message}"; packStatusError = true
            }
            packSaving = false
        }
    }

    fun kidsErrorText(text: String): String = try {
        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content ?: text
    } catch (_: Exception) { text }

    /** Everyone KIDZONE is on for. A failed read shows as an error, never as an empty list. */
    fun fetchKidsAccounts() {
        scope.launch {
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/kids-mode-accounts",
                    kotlin.js.json("method" to "GET", "headers" to packHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val emails = (adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)?.get("emails") as? kotlinx.serialization.json.JsonArray
                    if (emails == null) {
                        kidsAccounts = null; kidsAccountsError = "Unexpected answer from the server."
                    } else {
                        kidsAccounts = emails.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                        kidsAccountsError = null
                    }
                } else {
                    kidsAccounts = null
                    kidsAccountsError = "Couldn't load the list (${response.status}): ${kidsErrorText(text)}"
                }
            } catch (e: Exception) {
                kidsAccounts = null
                kidsAccountsError = "Error loading the list: ${e.message}"
            }
        }
    }

    /** Reads KIDZONE for the address in the box. [savedNote] is shown after a successful read (a save that had to re-read). */
    fun fetchKids(savedNote: String? = null) {
        val addr = email.trim().lowercase()
        if (addr.isBlank()) return
        val myRequest = ++kidsRequest[0]
        scope.launch {
            kidsLoading = true
            kidsLoadedFor = null
            kidsStatus = null; kidsStatusError = false
            var enabled = false
            var hasAccount = false
            var error: String? = null
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-kids-mode?email=${encodeURIComponent(addr)}",
                    kotlin.js.json("method" to "GET", "headers" to packHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val read = (obj?.get("enabled") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull()
                    if (read == null) error = "Unexpected answer from the server."
                    else {
                        enabled = read
                        hasAccount = (obj?.get("hasAccount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: false
                    }
                } else {
                    error = "Couldn't read KIDZONE status (${response.status}): ${kidsErrorText(text)}"
                }
            } catch (e: Exception) {
                error = "Error reading KIDZONE status: ${e.message}"
            }
            if (myRequest != kidsRequest[0]) return@launch   // a newer lookup took over
            if (error == null) {
                kidsEnabled = enabled; kidsHasAccount = hasAccount
                kidsLoadedFor = addr
                kidsStatus = savedNote
            } else {
                kidsStatus = error; kidsStatusError = true
            }
            kidsLoading = false
        }
    }

    fun setKids(on: Boolean) {
        val addr = email.trim().lowercase()
        // Only ever acts on a state that was read for the address in the box.
        if (addr.isBlank() || kidsLoadedFor != addr) return
        if (on && !kotlinx.browser.window.confirm(
                "Switch KIDZONE ON for $addr?\n\nThey will get a KIDZONE item in their menu. " +
                    "They still need to be an active subscriber to use it."
            )) return
        scope.launch {
            kidsSaving = true; kidsStatus = null; kidsStatusError = false
            try {
                val body = """{"email":${kotlinx.serialization.json.JsonPrimitive(addr)},"enabled":$on}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-kids-mode",
                    kotlin.js.json("method" to "POST", "headers" to packHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    // The POST answers with what the database now says; when it couldn't re-read, read again.
                    val obj = runCatching { adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject }.getOrNull()
                    val now = (obj?.get("enabled") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull()
                    val note = "Saved. KIDZONE is ${if (on) "ON" else "OFF"} for $addr."
                    if (now != null && email.trim().lowercase() == addr) {
                        kidsEnabled = now
                        kidsHasAccount = (obj?.get("hasAccount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: kidsHasAccount
                        kidsStatus = if (now == on) note else "Saved, but the database now says KIDZONE is ${if (now) "ON" else "OFF"}."
                        kidsStatusError = now != on
                    } else {
                        fetchKids(savedNote = note)
                    }
                    fetchKidsAccounts()
                } else {
                    kidsStatus = "Save failed (${response.status}): ${kidsErrorText(text)}"; kidsStatusError = true
                }
            } catch (e: Exception) {
                kidsStatus = "Error: ${e.message}"; kidsStatusError = true
            }
            kidsSaving = false
        }
    }

    fun bulkErrorText(text: String): String = try {
        ((adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content ?: text
    } catch (_: Exception) { text }

    /** Everyone Bulk scan is on for. A failed read shows as an error, never as an empty list. */
    fun fetchBulkAccounts() {
        scope.launch {
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/bulk-scan-accounts",
                    kotlin.js.json("method" to "GET", "headers" to packHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val emails = (adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject)?.get("emails") as? kotlinx.serialization.json.JsonArray
                    if (emails == null) {
                        bulkAccounts = null; bulkAccountsError = "Unexpected answer from the server."
                    } else {
                        bulkAccounts = emails.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                        bulkAccountsError = null
                    }
                } else {
                    bulkAccounts = null
                    bulkAccountsError = "Couldn't load the list (${response.status}): ${bulkErrorText(text)}"
                }
            } catch (e: Exception) {
                bulkAccounts = null
                bulkAccountsError = "Error loading the list: ${e.message}"
            }
        }
    }

    /** Reads Bulk scan for the address in the box. [savedNote] is shown after a successful read (a save that had to re-read). */
    fun fetchBulk(savedNote: String? = null) {
        val addr = email.trim().lowercase()
        if (addr.isBlank()) return
        val myRequest = ++bulkRequest[0]
        scope.launch {
            bulkLoading = true
            bulkLoadedFor = null
            bulkStatus = null; bulkStatusError = false
            var enabled = false
            var hasAccount = false
            var error: String? = null
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-bulk-scan?email=${encodeURIComponent(addr)}",
                    kotlin.js.json("method" to "GET", "headers" to packHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val read = (obj?.get("enabled") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull()
                    if (read == null) error = "Unexpected answer from the server."
                    else {
                        enabled = read
                        hasAccount = (obj?.get("hasAccount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: false
                    }
                } else {
                    error = "Couldn't read Bulk scan status (${response.status}): ${bulkErrorText(text)}"
                }
            } catch (e: Exception) {
                error = "Error reading Bulk scan status: ${e.message}"
            }
            if (myRequest != bulkRequest[0]) return@launch   // a newer lookup took over
            if (error == null) {
                bulkEnabled = enabled; bulkHasAccount = hasAccount
                bulkLoadedFor = addr
                bulkStatus = savedNote
            } else {
                bulkStatus = error; bulkStatusError = true
            }
            bulkLoading = false
        }
    }

    fun setBulk(on: Boolean) {
        val addr = email.trim().lowercase()
        // Only ever acts on a state that was read for the address in the box.
        if (addr.isBlank() || bulkLoadedFor != addr) return
        if (on && !kotlinx.browser.window.confirm(
                "Switch Bulk scan ON for $addr?\n\nThey will get a Bulk scan item in their menu. " +
                    "They still need to be an active subscriber to use it."
            )) return
        scope.launch {
            bulkSaving = true; bulkStatus = null; bulkStatusError = false
            try {
                val body = """{"email":${kotlinx.serialization.json.JsonPrimitive(addr)},"enabled":$on}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/user-bulk-scan",
                    kotlin.js.json("method" to "POST", "headers" to packHeaders(true), "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    // The POST answers with what the database now says; when it couldn't re-read, read again.
                    val obj = runCatching { adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject }.getOrNull()
                    val now = (obj?.get("enabled") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull()
                    val note = "Saved. Bulk scan is ${if (on) "ON" else "OFF"} for $addr."
                    if (now != null && email.trim().lowercase() == addr) {
                        bulkEnabled = now
                        bulkHasAccount = (obj?.get("hasAccount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: bulkHasAccount
                        bulkStatus = if (now == on) note else "Saved, but the database now says Bulk scan is ${if (now) "ON" else "OFF"}."
                        bulkStatusError = now != on
                    } else {
                        fetchBulk(savedNote = note)
                    }
                    fetchBulkAccounts()
                } else {
                    bulkStatus = "Save failed (${response.status}): ${bulkErrorText(text)}"; bulkStatusError = true
                }
            } catch (e: Exception) {
                bulkStatus = "Error: ${e.message}"; bulkStatusError = true
            }
            bulkSaving = false
        }
    }

    fun lookup() {
        if (email.isBlank()) return
        scope.launch {
            looking = true
            current = null
            result = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val q = encodeURIComponent(email.trim())
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/entitlement?email=$q",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                current = if (response.ok) summarize(text) else "Lookup failed: ${response.status} - $text"
            } catch (e: Exception) {
                current = "Error: ${e.message}"
            }
            looking = false
        }
        fetchDailyCap()
        packStatus = null; packStatusError = false
        fetchPacks()
        fetchKids()
        fetchBulk()
    }

    LaunchedEffect(Unit) { fetchKidsAccounts(); fetchBulkAccounts() }

    /** Grant (or with a negative value, claw back) N days of unlimited access. The
     *  extend-don't-overwrite and don't-clobber-an-active-subscriber rules live in the DB
     *  function, not here — see 2026-08-19_admin_grant_access_days.sql. */
    fun applyDays() {
        if (email.isBlank()) return
        val days = daysDelta.trim().toIntOrNull()
        if (days == null || days == 0) {
            result = "Days must be a non-zero whole number (negatives take access back)"
            resultIsError = true
            return
        }
        if (kotlin.math.abs(days) > 400) {
            result = "Days must be within +/-400"
            resultIsError = true
            return
        }
        scope.launch {
            applying = true
            result = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = """{"email":"${email.trim().replace("\"", "\\\"")}","days":$days}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/grant-access",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val summary = summarize(text)
                    current = summary
                    result = "Applied (${if (days >= 0) "+" else ""}$days days). Now: $summary"
                    resultIsError = false
                    daysDelta = ""
                } else {
                    result = "Failed: ${response.status} - $text"
                    resultIsError = true
                }
            } catch (e: Exception) {
                result = "Error: ${e.message}"
                resultIsError = true
            }
            applying = false
        }
    }

    fun applyGrant() {
        if (email.isBlank()) return
        val full = fullDelta.trim().ifBlank { "0" }.toIntOrNull()
        val qc = qcDelta.trim().ifBlank { "0" }.toIntOrNull()
        if (full == null || qc == null) {
            result = "Full and Quick must be whole numbers (negatives allowed)"
            resultIsError = true
            return
        }
        if (full == 0 && qc == 0) {
            result = "Nothing to apply — enter a non-zero amount"
            resultIsError = true
            return
        }
        scope.launch {
            applying = true
            result = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json(
                    "Content-Type" to "application/json",
                    "Accept" to "application/json"
                )
                token?.let { headers["Authorization"] = "Bearer $it" }
                val body = """{"email":"${email.trim().replace("\"", "\\\"")}","full":$full,"qc":$qc}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/grant-credits",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val summary = summarize(text)
                    current = summary
                    result = "Applied (${if (full >= 0) "+" else ""}$full full, ${if (qc >= 0) "+" else ""}$qc quick). Now: $summary"
                    resultIsError = false
                    fullDelta = ""
                    qcDelta = ""
                } else {
                    result = "Failed: ${response.status} - $text"
                    resultIsError = true
                }
            } catch (e: Exception) {
                result = "Error: ${e.message}"
                resultIsError = true
            }
            applying = false
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Grant Access" to null)

        CardSection("Grant a user access or credits") {
            // Email + lookup
            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "6px")
                }
            }) { Text("User Email") }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "16px") } }) {
                org.jetbrains.compose.web.dom.TextInput(email) {
                    onInput { email = it.value }
                    style {
                        property("flex", "1")
                        padding(10.px, 14.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(14.px)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                    }
                    attr("placeholder", "user@example.com")
                    attr("type", "email")
                }
                val canLookup = email.isNotBlank() && !looking
                Span(attrs = {
                    style {
                        display(DisplayStyle.InlineBlock)
                        padding(10.px, 18.px)
                        borderRadius(8.px)
                        backgroundColor(if (canLookup) Gray100 else Gray50)
                        color(if (canLookup) Gray700 else Gray400)
                        fontSize(13.px); fontWeight(600)
                        property("white-space", "nowrap")
                        if (canLookup) cursor("pointer")
                        property("transition", TransitionMedium)
                    }
                    if (canLookup) onClick { lookup() }
                }) { Text(if (looking) "Looking…" else "Look up") }
            }

            // Current balance readout
            current?.let { summary ->
                Div(attrs = {
                    style {
                        padding(12.px, 16.px)
                        borderRadius(10.px)
                        backgroundColor(Gray50)
                        property("border", "1px solid $Gray200")
                        property("margin-bottom", "20px")
                    }
                }) {
                    Div(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "4px") } }) { Text("Current") }
                    Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text(summary) }
                }

                // ── Daily analysis limit — per-user override ──────────────────
                Div(attrs = {
                    style {
                        padding(16.px); borderRadius(10.px); backgroundColor(Color.white)
                        property("border", "1px solid $Gray200"); property("margin-top", "16px")
                    }
                }) {
                    Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray700); property("margin-bottom", "4px") } }) {
                        Text("Weekly analysis limit")
                    }
                    Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "12px"); property("line-height", "1.5") } }) {
                        Text("Sets a limit for this one account, whatever its plan, and it applies even while the site-wide limit is off. The site-wide limit (Limits page) covers monthly subscribers only, so founding, annual, gifted, free and Library accounts have no limit unless you set one here. \"No limit for this user\" exempts the account even if it is a monthly subscriber.")
                    }
                    if (dailyCapLoading) {
                        P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                    } else {
                        Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "12px") } }) {
                            Text("Used ${dailyCapUsed} in the last 7 days. Effective limit: ${dailyCapEffective?.toString() ?: "No limit"}.")
                            dailyCapResetsAt?.let { resets ->
                                Text(" Resets ${resets.take(16).replace("T", " ")}.")
                            }
                        }
                        val globalLabel = dailyCapGlobal?.let { "$it / week" } ?: "Off"
                        listOf(
                            "default" to "Use default ($globalLabel, monthly subscribers only)",
                            "unlimited" to "No limit for this user",
                            "custom" to "Custom:",
                        ).forEach { (choiceKey, label) ->
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                                    property("margin-bottom", "8px"); cursor("pointer")
                                }
                                onClick { dailyCapChoice = choiceKey }
                            }) {
                                Span(attrs = { style { fontSize(14.px); color(Gray600) } }) { Text(if (dailyCapChoice == choiceKey) "●" else "○") }
                                Span(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text(label) }
                                if (choiceKey == "custom" && dailyCapChoice == "custom") {
                                    org.jetbrains.compose.web.dom.TextInput(dailyCapCustomText) {
                                        onInput { dailyCapCustomText = it.value }
                                        style {
                                            width(70.px); padding(6.px, 10.px); borderRadius(6.px)
                                            property("border", "1px solid $Gray200"); fontSize(13.px)
                                            property("outline", "none"); property("box-sizing", "border-box")
                                        }
                                        attr("placeholder", "1-200")
                                        attr("type", "number")
                                    }
                                }
                            }
                        }
                        Span(attrs = {
                            style {
                                display(DisplayStyle.InlineBlock); padding(9.px, 18.px); borderRadius(8.px)
                                backgroundColor(if (dailyCapSaving) Gray300 else Primary); color(Color.white)
                                fontSize(13.px); fontWeight(600)
                                if (!dailyCapSaving) cursor("pointer")
                                property("margin-top", "4px")
                            }
                            if (!dailyCapSaving) onClick { saveDailyCap() }
                        }) { Text(if (dailyCapSaving) "Saving…" else "Save weekly limit") }
                        dailyCapStatus?.let {
                            Div(attrs = { style { fontSize(12.px); fontWeight(600); color(if (dailyCapStatusError) ScoreRed else PositiveText); property("margin-top", "8px") } }) { Text(it) }
                        }
                    }
                }
            }

            // ── Search packs (organisations) ─────────────────────────────────────
            // Not advertised anywhere: schools and libraries contact us, we invoice them (outside the
            // app), then grant the pack here. Each search an account runs spends one; a subscriber
            // over the weekly limit uses the pack; a pack-only account has no weekly limit.
            Div(attrs = {
                style {
                    padding(16.px); borderRadius(10.px); backgroundColor(Color.white)
                    property("border", "1px solid $Gray200"); property("margin-top", "16px")
                }
            }) {
                Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray700); property("margin-bottom", "4px") } }) { Text("Search packs") }
                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "12px"); property("line-height", "1.5") } }) {
                    Text("Prepaid searches for a school, library or other organisation. Not shown anywhere in the app. Look the account up above first.")
                }
                if (packsLoading) {
                    P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                } else {
                    Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "10px") } }) {
                        Text(if (email.isBlank()) "Enter an email above." else "Searches left now: $packRemaining")
                    }
                    packRows.forEach { row ->
                        fun f(k: String) = (row[k] as? kotlinx.serialization.json.JsonPrimitive)?.takeIf { it !is kotlinx.serialization.json.JsonNull }?.content
                        val id = f("id") ?: return@forEach
                        val refunded = f("refunded_at") != null
                        Div(attrs = {
                            style {
                                padding(10.px); borderRadius(8.px); backgroundColor(Gray100)
                                property("margin-bottom", "8px"); fontSize(12.px); color(Gray700); property("line-height", "1.5")
                            }
                        }) {
                            Div(attrs = { style { fontWeight(600) } }) {
                                Text("${f("searches_remaining")} of ${f("searches_total")} left" + if (refunded) " (zeroed)" else "")
                            }
                            Div {
                                Text("Granted ${f("purchased_at")?.take(10)} · " + (f("expires_at")?.let { "expires ${it.take(10)}" } ?: "never expires") +
                                    (f("amount_cents")?.toIntOrNull()?.let { " · paid ${it / 100.0} ${f("currency")?.uppercase() ?: ""}" } ?: "") +
                                    (f("invoice_ref")?.let { " · invoice $it" } ?: "") +
                                    (f("granted_by")?.let { " · by $it" } ?: ""))
                            }
                            f("note")?.let { Div { Text(it) } }
                            if (!refunded) {
                                val hasExpiry = f("expires_at") != null
                                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); property("margin-top", "6px") } }) {
                                    Span(attrs = {
                                        style { fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                                        onClick {
                                            val answer = kotlinx.browser.window.prompt("Set searches remaining on this pack to:", f("searches_remaining") ?: "")
                                            val n = answer?.trim()?.toIntOrNull()
                                            if (n != null && n >= 0) adjustPack(id, remaining = n)
                                        }
                                    }) { Text("Set remaining") }
                                    if (hasExpiry) Span(attrs = {
                                        style { fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                                        onClick { adjustPack(id, extendDays = 30) }
                                    }) { Text("Extend 30 days") }
                                    Span(attrs = {
                                        style { fontWeight(600); color(ScoreRed); cursor("pointer"); property("text-decoration", "underline") }
                                        onClick {
                                            if (kotlinx.browser.window.confirm("Zero this pack (a refund)? Its remaining searches are removed for good.")) adjustPack(id, zero = true)
                                        }
                                    }) { Text("Zero it (refund)") }
                                }
                            }
                        }
                    }
                    Div(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray700); property("margin", "12px 0 6px") } }) { Text("Grant a new pack") }
                    listOf(
                        Triple("Searches (1-100000)", packSearchesText, 0),
                        Triple("Expires after (months, 1-36; blank = never)", packMonthsText, 1),
                        Triple("Amount paid in USD (optional)", packAmountText, 2),
                        Triple("Invoice reference (optional)", packInvoiceText, 3),
                        Triple("Note (optional)", packNoteText, 4),
                    ).forEach { (label, value, idx) ->
                        Div(attrs = { style { property("margin-bottom", "8px"); property("max-width", "340px") } }) {
                            PricingField(label, value, "") { v ->
                                when (idx) {
                                    0 -> packSearchesText = v.filter { c -> c.isDigit() }
                                    1 -> packMonthsText = v.filter { c -> c.isDigit() }
                                    2 -> packAmountText = v.filter { c -> c.isDigit() || c == '.' }
                                    3 -> packInvoiceText = v
                                    else -> packNoteText = v
                                }
                            }
                        }
                    }
                    Span(attrs = {
                        style {
                            display(DisplayStyle.InlineBlock); padding(9.px, 18.px); borderRadius(8.px)
                            backgroundColor(if (packSaving) Gray300 else Primary); color(Color.white)
                            fontSize(13.px); fontWeight(600)
                            if (!packSaving) cursor("pointer")
                            property("margin-top", "4px")
                        }
                        if (!packSaving) onClick { grantPack() }
                    }) { Text(if (packSaving) "Saving…" else "Grant pack") }
                    packStatus?.let {
                        Div(attrs = { style { fontSize(12.px); fontWeight(600); color(if (packStatusError) ScoreRed else PositiveText); property("margin-top", "8px") } }) { Text(it) }
                    }
                }
            }

            // ── KIDZONE (Kids Mode) ──────────────────────────────────────────────
            // Off for every account until switched on here. The server refuses every kids request from
            // an account that is not on this list, so this is the only way in.
            Div(attrs = {
                style {
                    padding(16.px); borderRadius(10.px); backgroundColor(Color.white)
                    property("border", "1px solid $Gray200"); property("margin-top", "16px")
                }
            }) {
                Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray700); property("margin-bottom", "4px") } }) { Text("KIDZONE (Kids Mode)") }
                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "12px"); property("line-height", "1.5") } }) {
                    Text("Off for every account unless switched on here. The account must be an active subscriber. On the web it takes effect straight away; phones need the next app release. Switching it off does not pull a device that is already inside KIDZONE out of it: that device stays locked until the PIN (or the emailed reset code) is entered.")
                }
                val kidsAddr = email.trim().lowercase()
                if (kidsLoading) {
                    P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                } else if (kidsAddr.isNotBlank() && kidsLoadedFor == kidsAddr) {
                    Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "10px") } }) {
                        Text(if (kidsEnabled) "KIDZONE is ON for this account." else "KIDZONE is off for this account.")
                    }
                    if (!kidsEnabled && !kidsHasAccount) {
                        Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "10px"); property("line-height", "1.5") } }) {
                            Text("There is no account with this email, so it can't be switched on. They must sign up first.")
                        }
                    } else {
                        Span(attrs = {
                            style {
                                display(DisplayStyle.InlineBlock); padding(9.px, 18.px); borderRadius(8.px)
                                backgroundColor(if (kidsSaving) Gray300 else if (kidsEnabled) ScoreRed else Primary); color(Color.white)
                                fontSize(13.px); fontWeight(600)
                                if (!kidsSaving) cursor("pointer")
                            }
                            if (!kidsSaving) onClick { setKids(!kidsEnabled) }
                        }) { Text(if (kidsSaving) "Saving…" else if (kidsEnabled) "Switch KIDZONE off" else "Switch KIDZONE on") }
                    }
                } else {
                    // Nothing read for this address (not looked up yet, or the read failed): never a guess.
                    Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text("Look the account up above first.") }
                }
                kidsStatus?.let {
                    Div(attrs = { style { fontSize(12.px); fontWeight(600); color(if (kidsStatusError) ScoreRed else PositiveText); property("margin-top", "8px") } }) { Text(it) }
                }

                Div(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray700); property("margin", "16px 0 6px") } }) { Text("Accounts with KIDZONE on") }
                val kidsList = kidsAccounts
                when {
                    kidsAccountsError != null -> Div(attrs = { style { fontSize(12.px); fontWeight(600); color(ScoreRed) } }) { Text(kidsAccountsError ?: "") }
                    kidsList == null -> P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                    kidsList.isEmpty() -> Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text("None. KIDZONE is off for everyone.") }
                    else -> kidsList.forEach { account ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center)
                                padding(8.px, 10.px); borderRadius(8.px); backgroundColor(Gray100)
                                property("margin-bottom", "6px"); fontSize(12.px); color(Gray700)
                            }
                        }) {
                            Span(attrs = { style { property("flex", "1"); property("word-break", "break-all") } }) { Text(account) }
                            Span(attrs = {
                                style { fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                                onClick { email = account; lookup() }
                            }) { Text("Look up") }
                        }
                    }
                }
            }

            // ── Bulk book scan ──────────────────────────────────────────────
            // Off for every account until switched on here. The server refuses every bulk request from
            // an account that is not on this list, so this is the only way in.
            Div(attrs = {
                style {
                    padding(16.px); borderRadius(10.px); backgroundColor(Color.white)
                    property("border", "1px solid $Gray200"); property("margin-top", "16px")
                }
            }) {
                Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Gray700); property("margin-bottom", "4px") } }) { Text("Bulk book scan") }
                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "12px"); property("line-height", "1.5") } }) {
                    Text("Off for every account unless switched on here. The account must ALSO hold a search pack (library account): scanning a stack costs money and is charged against the pack. Takes effect straight away on the web; phones need the next app release.")
                }
                val bulkAddr = email.trim().lowercase()
                if (bulkLoading) {
                    P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                } else if (bulkAddr.isNotBlank() && bulkLoadedFor == bulkAddr) {
                    Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "10px") } }) {
                        Text(if (bulkEnabled) "Bulk book scan is ON for this account." else "Bulk book scan is off for this account.")
                    }
                    if (!bulkEnabled && !bulkHasAccount) {
                        Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "10px"); property("line-height", "1.5") } }) {
                            Text("There is no account with this email, so it can't be switched on. They must sign up first.")
                        }
                    } else {
                        Span(attrs = {
                            style {
                                display(DisplayStyle.InlineBlock); padding(9.px, 18.px); borderRadius(8.px)
                                backgroundColor(if (bulkSaving) Gray300 else if (bulkEnabled) ScoreRed else Primary); color(Color.white)
                                fontSize(13.px); fontWeight(600)
                                if (!bulkSaving) cursor("pointer")
                            }
                            if (!bulkSaving) onClick { setBulk(!bulkEnabled) }
                        }) { Text(if (bulkSaving) "Saving…" else if (bulkEnabled) "Switch bulk scan off" else "Switch bulk scan on") }
                    }
                } else {
                    // Nothing read for this address (not looked up yet, or the read failed): never a guess.
                    Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text("Look the account up above first.") }
                }
                bulkStatus?.let {
                    Div(attrs = { style { fontSize(12.px); fontWeight(600); color(if (bulkStatusError) ScoreRed else PositiveText); property("margin-top", "8px") } }) { Text(it) }
                }

                Div(attrs = { style { fontSize(12.px); fontWeight(700); color(Gray700); property("margin", "16px 0 6px") } }) { Text("Accounts with bulk scan on") }
                val bulkList = bulkAccounts
                when {
                    bulkAccountsError != null -> Div(attrs = { style { fontSize(12.px); fontWeight(600); color(ScoreRed) } }) { Text(bulkAccountsError ?: "") }
                    bulkList == null -> P(attrs = { style { color(Gray400); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
                    bulkList.isEmpty() -> Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text("None. Bulk scan is off for everyone.") }
                    else -> bulkList.forEach { account ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); gap(14.px); alignItems(AlignItems.Center)
                                padding(8.px, 10.px); borderRadius(8.px); backgroundColor(Gray100)
                                property("margin-bottom", "6px"); fontSize(12.px); color(Gray700)
                            }
                        }) {
                            Span(attrs = { style { property("flex", "1"); property("word-break", "break-all") } }) { Text(account) }
                            Span(attrs = {
                                style { fontWeight(600); color(Primary); cursor("pointer"); property("text-decoration", "underline") }
                                onClick { email = account; lookup() }
                            }) { Text("Look up") }
                        }
                    }
                }
            }

            // ── Grant unlimited access (days) — the current model ────────────────
            Div(attrs = {
                style {
                    padding(16.px); borderRadius(10.px); backgroundColor(PrimaryPale)
                    property("border", "1px solid ${Primary}33"); property("margin-bottom", "24px")
                }
            }) {
                Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Primary); property("margin-bottom", "4px") } }) {
                    Text("Grant unlimited access")
                }
                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-bottom", "12px"); property("line-height", "1.5") } }) {
                    Text("Adds days of unlimited use. Extends whatever they already have rather than replacing it, so this can never shorten a paid member's access — and it won't disturb an active Stripe subscription. Negative takes days back.")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center) } }) {
                    org.jetbrains.compose.web.dom.TextInput(daysDelta) {
                        onInput { daysDelta = it.value }
                        style {
                            width(140.px); padding(10.px, 14.px); borderRadius(8.px)
                            property("border", "1px solid $Gray200"); fontSize(14.px)
                            property("outline", "none"); property("box-sizing", "border-box")
                        }
                        attr("placeholder", "e.g. 14 or -7")
                        attr("type", "number")
                    }
                    Span(attrs = { style { fontSize(13.px); color(Gray600) } }) { Text("days") }
                    val canDays = email.isNotBlank() && daysDelta.isNotBlank() && !applying
                    Span(attrs = {
                        style {
                            display(DisplayStyle.InlineBlock); padding(10.px, 20.px); borderRadius(8.px)
                            backgroundColor(if (canDays) Primary else Gray300); color(Color.white)
                            fontSize(13.px); fontWeight(600)
                            if (canDays) cursor("pointer")
                        }
                        if (canDays) onClick { applyDays() }
                    }) { Text(if (applying) "Applying…" else "Grant access") }
                }
                // Quick presets — the common comps, so a support gesture is one click.
                Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); property("margin-top", "10px"); property("flex-wrap", "wrap") } }) {
                    listOf(7, 14, 30, 90).forEach { d ->
                        Span(attrs = {
                            style {
                                padding(4.px, 10.px); borderRadius(20.px); fontSize(11.px); fontWeight(600)
                                backgroundColor(Color.white); color(Primary); cursor("pointer")
                                property("border", "1px solid ${Primary}33"); property("user-select", "none")
                            }
                            onClick { daysDelta = d.toString() }
                        }) { Text("$d days") }
                    }
                }
            }

            // ── Credit balance — legacy, from when usage was counted per search ──
            Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray400); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "8px") } }) {
                Text("Credit balance (legacy)")
            }

            // Full + Quick delta inputs (negatives allowed to subtract)
            Div(attrs = { style { display(DisplayStyle.Flex); gap(16.px); property("margin-bottom", "20px") } }) {
                Div(attrs = { style { property("flex", "1") } }) {
                    Span(attrs = {
                        style {
                            fontSize(12.px); fontWeight(600); color(Gray500)
                            property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                            display(DisplayStyle.Block); property("margin-bottom", "6px")
                        }
                    }) { Text("Full searches (±)") }
                    org.jetbrains.compose.web.dom.TextInput(fullDelta) {
                        onInput { fullDelta = it.value }
                        style {
                            width(100.percent)
                            padding(10.px, 14.px)
                            borderRadius(8.px)
                            property("border", "1px solid $Gray200")
                            fontSize(14.px)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                        attr("placeholder", "e.g. 10 or -1")
                        attr("type", "number")
                    }
                }
                Div(attrs = { style { property("flex", "1") } }) {
                    Span(attrs = {
                        style {
                            fontSize(12.px); fontWeight(600); color(Gray500)
                            property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                            display(DisplayStyle.Block); property("margin-bottom", "6px")
                        }
                    }) { Text("Quick checks (±)") }
                    org.jetbrains.compose.web.dom.TextInput(qcDelta) {
                        onInput { qcDelta = it.value }
                        style {
                            width(100.percent)
                            padding(10.px, 14.px)
                            borderRadius(8.px)
                            property("border", "1px solid $Gray200")
                            fontSize(14.px)
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                        attr("placeholder", "e.g. 5 or -1")
                        attr("type", "number")
                    }
                }
            }

            // Apply button
            val canApply = email.isNotBlank() && !applying
            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock)
                    padding(12.px, 24.px)
                    borderRadius(10.px)
                    backgroundColor(if (canApply) Primary else Gray300)
                    color(Color.white)
                    fontSize(14.px); fontWeight(600)
                    if (canApply) cursor("pointer")
                    property("transition", TransitionMedium)
                }
                if (canApply) onClick { applyGrant() }
            }) { Text(if (applying) "Applying…" else "Apply adjustment") }

            // Result message
            result?.let {
                Div(attrs = {
                    style {
                        padding(12.px, 16.px)
                        borderRadius(10.px)
                        backgroundColor(if (resultIsError) Color("#fef2f2") else Color("#ecfdf5"))
                        property("border", "1px solid ${if (resultIsError) Color("#fecaca") else Color("#bbf7d0")}")
                        property("margin-top", "16px")
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            color(if (resultIsError) Color("#991b1b") else Color("#065f46"))
                            fontWeight(500)
                        }
                    }) { Text(it) }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Invitations View — gift 1/2/3 months of unlimited access
// ---------------------------------------------------------------------------

/** One row of /api/admin/invitations (the invitation_list view — never carries a token). */
private data class InviteRow(
    val id: String,
    val durationMonths: Int,
    val mode: String,
    val status: String,              // pending | redeemed | revoked | expired
    val intendedEmail: String?,
    val redeemedByEmail: String?,
    val redeemedAt: String?,
    val expiresAt: String?,
    val createdAt: String?,
    /** The shareable link. Only present while the invite is still live — the token is wiped on
     *  redeem/revoke, and invites created before the 2026-07-14b migration never stored one. */
    val url: String?,
)

/**
 * Create and manage gifted memberships.
 *
 * Phase 1 is LINK-ONLY on purpose: the server accepts mode="email" and locks redemption to the
 * addressee, but we don't send the mail yet (deliverability on the fitrahfilter.com sender needs
 * checking first — a test send landed in spam). So: mint a link, paste it into a chat.
 *
 * The raw token comes back exactly once, in the create response. It is never stored in readable
 * form and can never be shown again — if you lose the link, revoke it and mint another.
 */
@Composable
private fun InvitationsView(onNavigate: (DrillDownView) -> Unit) {
    var months by remember { mutableStateOf(1) }
    var mode by remember { mutableStateOf("link") }        // "link" | "email"
    var inviteEmail by remember { mutableStateOf("") }
    var creating by remember { mutableStateOf(false) }
    var newLink by remember { mutableStateOf<String?>(null) }
    var emailSent by remember { mutableStateOf<Boolean?>(null) }
    var sentTo by remember { mutableStateOf<String?>(null) }
    var copied by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    var rows by remember { mutableStateOf<List<InviteRow>>(emptyList()) }
    var loadingList by remember { mutableStateOf(true) }
    var busyId by remember { mutableStateOf<String?>(null) }
    var copiedId by remember { mutableStateOf<String?>(null) }

    // Early clawback of an ALREADY-CLAIMED gift (distinct from revoking an unclaimed link).
    var revokeEmail by remember { mutableStateOf("") }
    var revoking by remember { mutableStateOf(false) }
    var revokeResult by remember { mutableStateOf<String?>(null) }
    var revokeIsError by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(withBody: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (withBody) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun refresh() {
        scope.launch {
            loadingList = true
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/invitations",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                rows = if (!response.ok) emptyList() else {
                    val arr = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonArray
                    arr?.mapNotNull { el ->
                        val o = el as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        fun s(k: String) = (o[k] as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                        InviteRow(
                            id = s("id") ?: return@mapNotNull null,
                            durationMonths = s("duration_months")?.toIntOrNull() ?: 0,
                            mode = s("mode") ?: "link",
                            status = s("status") ?: "pending",
                            intendedEmail = s("intended_email"),
                            redeemedByEmail = s("redeemed_by_email"),
                            redeemedAt = s("redeemed_at"),
                            expiresAt = s("expires_at"),
                            createdAt = s("created_at"),
                            url = s("url"),
                        )
                    } ?: emptyList()
                }
            } catch (e: Exception) {
                rows = emptyList()
            }
            loadingList = false
        }
    }

    LaunchedEffect(Unit) { refresh() }

    fun create() {
        val addr = inviteEmail.trim()
        if (mode == "email" && (addr.isBlank() || !addr.contains("@"))) {
            error = "Enter the recipient's email address."
            return
        }
        scope.launch {
            creating = true
            error = null
            newLink = null
            emailSent = null
            sentTo = null
            copied = false
            try {
                val body = if (mode == "email")
                    """{"durationMonths":$months,"mode":"email","email":"${addr.replace("\"", "\\\"")}"}"""
                else """{"durationMonths":$months,"mode":"link"}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/invitations",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val o = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    newLink = (o?.get("url") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    if (mode == "email") {
                        emailSent = (o?.get("emailSent") as? kotlinx.serialization.json.JsonPrimitive)?.content == "true"
                        sentTo = addr
                        inviteEmail = ""
                    }
                    refresh()
                } else {
                    error = "Failed: ${response.status} - $text"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            creating = false
        }
    }

    fun revokeInvite(id: String) {
        scope.launch {
            busyId = id
            try {
                kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/invitations/revoke",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to """{"id":"$id"}""")
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                refresh()
            } catch (_: Exception) {
            }
            busyId = null
        }
    }

    fun revokeMembership() {
        if (revokeEmail.isBlank()) return
        scope.launch {
            revoking = true
            revokeResult = null
            try {
                val body = """{"email":"${revokeEmail.trim().replace("\"", "\\\"")}"}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/revoke-membership",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                val o = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                val ok = (o?.get("ok") as? kotlinx.serialization.json.JsonPrimitive)?.content == "true"
                revokeIsError = !ok
                revokeResult = if (ok) "Membership revoked — ${revokeEmail.trim()} is back on the free tier."
                else (o?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content
                    ?: "Failed: ${response.status}"
                if (ok) revokeEmail = ""
            } catch (e: Exception) {
                revokeResult = "Error: ${e.message}"
                revokeIsError = true
            }
            revoking = false
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Invitations" to null)

        CardSection("Gift a membership") {
            Div(attrs = { style { fontSize(13.px); color(Gray600); property("line-height", "1.5"); property("margin-bottom", "18px") } }) {
                Text("Grants unlimited access for the chosen period. The recipient signs up, claims it, and the membership lands on the account they actually sign in with. Single-use; expires in 30 days if unclaimed.")
            }

            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "8px")
                }
            }) { Text("Duration") }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "20px") } }) {
                listOf(1, 2, 3).forEach { m ->
                    val selected = months == m
                    Span(attrs = {
                        onClick { months = m }
                        style {
                            display(DisplayStyle.InlineBlock)
                            padding(10.px, 22.px); borderRadius(8.px)
                            backgroundColor(if (selected) Primary else Gray50)
                            color(if (selected) Color.white else Gray700)
                            property("border", "1px solid ${if (selected) Primary else Gray200}")
                            fontSize(14.px); fontWeight(600); cursor("pointer")
                            property("transition", TransitionMedium)
                        }
                    }) { Text(if (m == 1) "1 month" else "$m months") }
                }
            }

            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "8px")
                }
            }) { Text("How to send it") }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "16px") } }) {
                listOf("link" to "Shareable link", "email" to "Email it").forEach { (key, label) ->
                    val selected = mode == key
                    Span(attrs = {
                        onClick { mode = key; error = null }
                        style {
                            display(DisplayStyle.InlineBlock)
                            padding(10.px, 20.px); borderRadius(8.px)
                            backgroundColor(if (selected) Primary else Gray50)
                            color(if (selected) Color.white else Gray700)
                            property("border", "1px solid ${if (selected) Primary else Gray200}")
                            fontSize(14.px); fontWeight(600); cursor("pointer")
                            property("transition", TransitionMedium)
                        }
                    }) { Text(label) }
                }
            }

            // Email mode locks redemption to this address — forwarding the mail doesn't transfer
            // the gift. Say so, because it's a surprising rule if you hit it by accident.
            if (mode == "email") {
                Div(attrs = { style { property("margin-bottom", "16px") } }) {
                    org.jetbrains.compose.web.dom.TextInput(inviteEmail) {
                        onInput { inviteEmail = it.value }
                        style {
                            width(100.percent); padding(10.px, 14.px); borderRadius(8.px)
                            property("border", "1px solid $Gray200"); fontSize(14.px)
                            property("outline", "none"); property("box-sizing", "border-box")
                        }
                        attr("placeholder", "recipient@example.com")
                        attr("type", "email")
                    }
                    Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "6px"); property("line-height", "1.5") } }) {
                        Text("Only this address can claim it — if they sign in with a different email (a hidden Apple ID, say), it won't work.")
                    }
                }
            }

            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock)
                    padding(12.px, 24.px); borderRadius(10.px)
                    backgroundColor(if (creating) Gray300 else Primary)
                    color(Color.white); fontSize(14.px); fontWeight(600)
                    if (!creating) cursor("pointer")
                    property("transition", TransitionMedium)
                }
                if (!creating) onClick { create() }
            }) {
                Text(
                    when {
                        creating && mode == "email" -> "Sending…"
                        creating -> "Creating…"
                        mode == "email" -> "Send invitation"
                        else -> "Create invite link"
                    }
                )
            }

            // Email outcome. A failed send is NOT a failed invite — the link exists either way, so
            // say that plainly and show it, rather than leaving a valid gift stranded.
            emailSent?.let { sent ->
                Div(attrs = {
                    style {
                        padding(12.px, 16.px); borderRadius(10.px)
                        backgroundColor(if (sent) Color("#ecfdf5") else Color("#fffbeb"))
                        property("border", "1px solid ${if (sent) Color("#bbf7d0") else Color("#fde68a")}")
                        property("margin-top", "16px")
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px); fontWeight(500)
                            color(if (sent) Color("#065f46") else Color("#92400e"))
                        }
                    }) {
                        Text(
                            if (sent) "Sent to ${sentTo ?: "recipient"} ✓  (the link is below too, just in case)"
                            else "The invitation was created, but the email didn't send. Copy the link below and send it yourself."
                        )
                    }
                }
            }

            // The token is shown ONCE — it exists nowhere else in readable form.
            newLink?.let { link ->
                Div(attrs = {
                    style {
                        padding(16.px); borderRadius(10.px)
                        backgroundColor(Color("#ecfdf5"))
                        property("border", "1px solid ${Color("#bbf7d0")}")
                        property("margin-top", "18px")
                    }
                }) {
                    Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Color("#065f46")); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "8px") } }) {
                        Text("Share this link — it won't be shown again")
                    }
                    Div(attrs = {
                        style {
                            fontSize(13.px); color(Gray900); backgroundColor(Color.white)
                            padding(10.px, 12.px); borderRadius(8.px)
                            property("border", "1px solid ${Color("#bbf7d0")}")
                            property("word-break", "break-all"); property("margin-bottom", "10px")
                            property("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
                        }
                    }) { Text(link) }
                    Span(attrs = {
                        onClick {
                            kotlinx.browser.window.navigator.clipboard.writeText(link)
                            copied = true
                        }
                        style {
                            display(DisplayStyle.InlineBlock)
                            padding(8.px, 16.px); borderRadius(8.px)
                            backgroundColor(Color("#065f46")); color(Color.white)
                            fontSize(13.px); fontWeight(600); cursor("pointer")
                        }
                    }) { Text(if (copied) "Copied ✓" else "Copy link") }
                }
            }

            error?.let {
                Div(attrs = {
                    style {
                        padding(12.px, 16.px); borderRadius(10.px)
                        backgroundColor(Color("#fef2f2"))
                        property("border", "1px solid ${Color("#fecaca")}")
                        property("margin-top", "16px")
                    }
                }) {
                    Span(attrs = { style { fontSize(13.px); color(Color("#991b1b")); fontWeight(500) } }) { Text(it) }
                }
            }
        }

        CardSection("All invitations") {
            when {
                loadingList -> Div(attrs = { style { fontSize(13.px); color(Gray500); padding(8.px) } }) { Text("Loading…") }
                rows.isEmpty() -> Div(attrs = { style { fontSize(13.px); color(Gray500); padding(8.px) } }) { Text("No invitations yet.") }
                else -> Table(attrs = { style { width(100.percent); property("border-collapse", "collapse") } }) {
                    Thead {
                        Tr {
                            listOf("Status", "Gift", "For", "Claimed by", "Expires", "Link", "").forEach { h ->
                                Th(attrs = {
                                    style {
                                        property("text-align", "left"); padding(10.px, 12.px)
                                        fontSize(11.px); fontWeight(700); color(Gray500)
                                        property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                                        property("border-bottom", "1px solid $Gray200")
                                    }
                                }) { Text(h) }
                            }
                        }
                    }
                    Tbody {
                        rows.forEach { row ->
                            Tr {
                                Td(attrs = { style { padding(12.px); property("border-bottom", "1px solid $Gray100") } }) {
                                    val (bg, fg, label) = when (row.status) {
                                        "pending" -> Triple(Color("#ecfdf5"), Color("#065f46"), "Pending")
                                        "redeemed" -> Triple(Color("#ccfbf1"), Color("#0f766e"), "Claimed")
                                        "revoked" -> Triple(Color("#fef2f2"), Color("#991b1b"), "Revoked")
                                        else -> Triple(Gray100, Gray600, "Expired")
                                    }
                                    Span(attrs = {
                                        style {
                                            fontSize(11.px); fontWeight(700); padding(3.px, 10.px)
                                            borderRadius(6.px); display(DisplayStyle.InlineBlock)
                                            backgroundColor(bg); color(fg)
                                        }
                                    }) { Text(label) }
                                }
                                Td(attrs = { style { padding(12.px); fontSize(13.px); color(Gray900); property("border-bottom", "1px solid $Gray100") } }) {
                                    Text(if (row.durationMonths == 1) "1 month" else "${row.durationMonths} months")
                                }
                                Td(attrs = { style { padding(12.px); fontSize(13.px); color(Gray600); property("border-bottom", "1px solid $Gray100") } }) {
                                    Text(row.intendedEmail ?: "Anyone with the link")
                                }
                                Td(attrs = { style { padding(12.px); fontSize(13.px); color(Gray600); property("border-bottom", "1px solid $Gray100") } }) {
                                    Text(row.redeemedByEmail ?: "—")
                                }
                                Td(attrs = { style { padding(12.px); fontSize(13.px); color(Gray500); property("border-bottom", "1px solid $Gray100") } }) {
                                    Text(row.expiresAt?.substringBefore("T") ?: "—")
                                }
                                // Copy link. Present only while the invite is live — the token is wiped
                                // on redeem/revoke, and invites minted before the token column existed
                                // never had one stored, so those show a dash.
                                Td(attrs = { style { padding(12.px); property("border-bottom", "1px solid $Gray100") } }) {
                                    val url = row.url
                                    if (url != null) {
                                        Span(attrs = {
                                            onClick {
                                                kotlinx.browser.window.navigator.clipboard.writeText(url)
                                                copiedId = row.id
                                            }
                                            style {
                                                display(DisplayStyle.InlineBlock)
                                                padding(5.px, 12.px); borderRadius(6.px)
                                                backgroundColor(if (copiedId == row.id) Color("#ccfbf1") else Gray100)
                                                color(if (copiedId == row.id) Color("#0f766e") else Gray700)
                                                fontSize(12.px); fontWeight(600); cursor("pointer")
                                                property("white-space", "nowrap")
                                                property("transition", TransitionMedium)
                                            }
                                        }) { Text(if (copiedId == row.id) "Copied ✓" else "Copy link") }
                                    } else {
                                        Span(attrs = { style { fontSize(13.px); color(Gray400) } }) { Text("—") }
                                    }
                                }
                                Td(attrs = { style { padding(12.px); property("border-bottom", "1px solid $Gray100") } }) {
                                    // Only an unclaimed invite can be revoked here. Clawing back a gift
                                    // that's already been granted is the separate action below.
                                    if (row.status == "pending") {
                                        Span(attrs = {
                                            onClick { revokeInvite(row.id) }
                                            style {
                                                fontSize(12.px); fontWeight(600); color(Color("#991b1b"))
                                                cursor("pointer")
                                            }
                                        }) { Text(if (busyId == row.id) "…" else "Revoke") }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        CardSection("End a gifted membership early") {
            Div(attrs = { style { fontSize(13.px); color(Gray600); property("line-height", "1.5"); property("margin-bottom", "16px") } }) {
                Text("Immediately cancels a gift that's already been claimed, dropping the user back to the free tier. Paying subscribers are protected — this refuses to touch anyone whose membership isn't a gift.")
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px) } }) {
                org.jetbrains.compose.web.dom.TextInput(revokeEmail) {
                    onInput { revokeEmail = it.value }
                    style {
                        property("flex", "1"); padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid $Gray200"); fontSize(14.px)
                        property("outline", "none"); property("box-sizing", "border-box")
                    }
                    attr("placeholder", "user@example.com")
                    attr("type", "email")
                }
                val canRevoke = revokeEmail.isNotBlank() && !revoking
                Span(attrs = {
                    style {
                        display(DisplayStyle.InlineBlock)
                        padding(10.px, 18.px); borderRadius(8.px)
                        backgroundColor(if (canRevoke) Color("#991b1b") else Gray300)
                        color(Color.white); fontSize(13.px); fontWeight(600)
                        property("white-space", "nowrap")
                        if (canRevoke) cursor("pointer")
                    }
                    if (canRevoke) onClick { revokeMembership() }
                }) { Text(if (revoking) "Revoking…" else "Revoke membership") }
            }
            revokeResult?.let {
                Div(attrs = {
                    style {
                        padding(12.px, 16.px); borderRadius(10.px)
                        backgroundColor(if (revokeIsError) Color("#fef2f2") else Color("#ecfdf5"))
                        property("border", "1px solid ${if (revokeIsError) Color("#fecaca") else Color("#bbf7d0")}")
                        property("margin-top", "14px")
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px); fontWeight(500)
                            color(if (revokeIsError) Color("#991b1b") else Color("#065f46"))
                        }
                    }) { Text(it) }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Broadcast Notification View
// ---------------------------------------------------------------------------

@Composable
private fun BroadcastView(onNavigate: (DrillDownView) -> Unit) {
    var title by remember { mutableStateOf("") }
    var messageBody by remember { mutableStateOf("") }
    var deviceCount by remember { mutableStateOf<Int?>(null) }
    var sending by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<String?>(null) }
    var resultIsError by remember { mutableStateOf(false) }
    var showConfirm by remember { mutableStateOf(false) }
    // Recipient mode: "all" or "user"
    var recipientMode by remember { mutableStateOf("all") }
    var targetEmail by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchDeviceCount() {
        scope.launch {
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/device-count",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    val text = response.text().await().toString()
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    deviceCount = (obj?.get("count") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                }
            } catch (_: Exception) {}
        }
    }

    fun sendBroadcast() {
        if (title.isBlank() || messageBody.isBlank()) return
        if (recipientMode == "user" && targetEmail.isBlank()) return
        scope.launch {
            sending = true
            result = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json(
                    "Content-Type" to "application/json",
                    "Accept" to "application/json"
                )
                token?.let { headers["Authorization"] = "Bearer $it" }
                val emailField = if (recipientMode == "user") ",\"userEmail\":\"${targetEmail.trim().replace("\"", "\\\"")}\"" else ""
                val body = """{"title":"${title.replace("\"", "\\\"")}","body":"${messageBody.replace("\"", "\\\"")}"$emailField}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/broadcast",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val total = (obj?.get("totalDevices") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    val success = (obj?.get("successCount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    val failed = (obj?.get("failureCount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    result = "Sent! Delivered to $success of $total devices" + if (failed > 0) " ($failed failed)" else ""
                    resultIsError = false
                    title = ""
                    messageBody = ""
                    fetchDeviceCount()
                } else {
                    result = "Failed: ${response.status} - $text"
                    resultIsError = true
                }
            } catch (e: Exception) {
                result = "Error: ${e.message}"
                resultIsError = true
            }
            sending = false
        }
    }

    LaunchedEffect(Unit) { fetchDeviceCount() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Broadcast Notification" to null)

        CardSection("Send push notification") {
            // Recipient mode selector
            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "8px")
                }
            }) { Text("Send To") }
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    property("margin-bottom", "16px")
                }
            }) {
                listOf("all" to "\uD83C\uDF0D All Users", "user" to "\uD83D\uDC64 Specific User").forEach { (mode, label) ->
                    Span(attrs = {
                        style {
                            padding(10.px, 18.px)
                            borderRadius(8.px)
                            fontSize(13.px)
                            fontWeight(600)
                            cursor("pointer")
                            property("transition", TransitionMedium)
                            if (recipientMode == mode) {
                                backgroundColor(Primary)
                                color(Color.white)
                            } else {
                                backgroundColor(Gray100)
                                color(Gray600)
                            }
                        }
                        onClick { recipientMode = mode }
                    }) { Text(label) }
                }
            }

            // Email input (shown only for "user" mode)
            if (recipientMode == "user") {
                Span(attrs = {
                    style {
                        fontSize(12.px); fontWeight(600); color(Gray500)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                        display(DisplayStyle.Block); property("margin-bottom", "6px")
                    }
                }) { Text("User Email") }
                org.jetbrains.compose.web.dom.TextInput(targetEmail) {
                    onInput { targetEmail = it.value }
                    style {
                        width(100.percent)
                        padding(10.px, 14.px)
                        borderRadius(8.px)
                        property("border", "1px solid $Gray200")
                        fontSize(14.px)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        property("margin-bottom", "16px")
                    }
                    attr("placeholder", "user@example.com")
                    attr("type", "email")
                }
            }

            // Info banner
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    padding(12.px, 16.px)
                    borderRadius(10.px)
                    backgroundColor(if (recipientMode == "all") Color("#fef2f2") else Color("#eff6ff"))
                    property("border", "1px solid ${if (recipientMode == "all") Color("#fecaca") else Color("#bfdbfe")}")
                    property("margin-bottom", "20px")
                }
            }) {
                Span(attrs = { style { fontSize(16.px) } }) { Text("\uD83D\uDCE2") }
                Span(attrs = {
                    style {
                        fontSize(13.px)
                        color(if (recipientMode == "all") Color("#991b1b") else Color("#1e40af"))
                        fontWeight(500)
                    }
                }) {
                    Text(
                        when {
                            recipientMode == "user" && targetEmail.isNotBlank() -> "Will send to all devices registered for ${targetEmail.trim()}"
                            recipientMode == "user" -> "Enter the recipient's email above"
                            deviceCount != null -> "This will notify $deviceCount registered device(s) across all users"
                            else -> "Loading device count..."
                        }
                    )
                }
            }

            // Title input
            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "6px")
                }
            }) { Text("Title") }
            org.jetbrains.compose.web.dom.TextInput(title) {
                onInput { title = it.value }
                style {
                    width(100.percent)
                    padding(10.px, 14.px)
                    borderRadius(8.px)
                    property("border", "1px solid $Gray200")
                    fontSize(14.px)
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("margin-bottom", "16px")
                }
                attr("placeholder", "e.g. New feature available!")
                attr("maxlength", "100")
            }

            // Body input (textarea)
            Span(attrs = {
                style {
                    fontSize(12.px); fontWeight(600); color(Gray500)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    display(DisplayStyle.Block); property("margin-bottom", "6px")
                }
            }) { Text("Message") }
            TextArea(value = messageBody, attrs = {
                onInput { messageBody = it.value }
                style {
                    width(100.percent)
                    padding(10.px, 14.px)
                    borderRadius(8.px)
                    property("border", "1px solid $Gray200")
                    fontSize(14.px)
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    property("margin-bottom", "8px")
                    property("min-height", "100px")
                    property("font-family", "inherit")
                    property("resize", "vertical")
                }
                attr("placeholder", "What do you want to tell your users?")
                attr("maxlength", "500")
            })

            // Character counter
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    property("margin-bottom", "20px")
                }
            }) {
                Span(attrs = { style { fontSize(11.px); color(Gray400) } }) {
                    Text("${title.length}/100 title, ${messageBody.length}/500 message")
                }
            }

            // Send button
            val recipientValid = recipientMode == "all" || targetEmail.isNotBlank()
            val canSend = title.isNotBlank() && messageBody.isNotBlank() && recipientValid && !sending
            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock)
                    padding(12.px, 24.px)
                    borderRadius(10.px)
                    backgroundColor(if (canSend) Color("#dc2626") else Gray300)
                    color(Color.white)
                    fontSize(14.px)
                    fontWeight(600)
                    if (canSend) cursor("pointer")
                    property("transition", TransitionMedium)
                }
                if (canSend) onClick { showConfirm = true }
            }) {
                Text(
                    when {
                        sending -> "Sending..."
                        recipientMode == "user" -> "\uD83D\uDCE2 Send to ${targetEmail.ifBlank { "User" }}"
                        else -> "\uD83D\uDCE2 Send to All Users"
                    }
                )
            }

            // Result message
            result?.let {
                Div(attrs = {
                    style {
                        padding(12.px, 16.px)
                        borderRadius(10.px)
                        backgroundColor(if (resultIsError) Color("#fef2f2") else Color("#ecfdf5"))
                        property("border", "1px solid ${if (resultIsError) Color("#fecaca") else Color("#bbf7d0")}")
                        property("margin-top", "16px")
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(13.px)
                            color(if (resultIsError) Color("#991b1b") else Color("#065f46"))
                            fontWeight(500)
                        }
                    }) { Text(it) }
                }
            }
        }

        // Confirmation dialog
        if (showConfirm) {
            Div(attrs = {
                style {
                    property("position", "fixed")
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                    property("bottom", "0")
                    backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
                onClick { showConfirm = false }
            }) {
                Div(attrs = {
                    style {
                        backgroundColor(Color.white)
                        borderRadius(16.px)
                        padding(28.px)
                        property("max-width", "460px")
                        width(90.percent)
                        property("box-shadow", "0 20px 50px rgba(0,0,0,0.3)")
                    }
                    onClick { event -> event.stopPropagation() }
                }) {
                    H2(attrs = {
                        style {
                            fontSize(18.px); fontWeight(700); color(Gray900); property("margin", "0 0 12px")
                        }
                    }) {
                        Text(
                            if (recipientMode == "user") "Send to ${targetEmail.trim()}?"
                            else "Send to ${deviceCount ?: "all"} device(s)?"
                        )
                    }
                    P(attrs = {
                        style {
                            fontSize(14.px); color(Gray600); lineHeight(20.px.unsafeCast<CSSNumeric>()); property("margin", "0 0 16px")
                        }
                    }) { Text("This will send the notification immediately. This action cannot be undone.") }

                    // Preview
                    Div(attrs = {
                        style {
                            padding(12.px, 16.px)
                            borderRadius(10.px)
                            backgroundColor(Gray50)
                            property("border", "1px solid $Gray200")
                            property("margin-bottom", "20px")
                        }
                    }) {
                        Div(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray900); property("margin-bottom", "4px") } }) { Text(title) }
                        Div(attrs = { style { fontSize(12.px); color(Gray600); lineHeight(18.px.unsafeCast<CSSNumeric>()) } }) { Text(messageBody) }
                    }

                    // Buttons
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            gap(8.px)
                            justifyContent(JustifyContent.FlexEnd)
                        }
                    }) {
                        Span(attrs = {
                            style {
                                padding(10.px, 20.px)
                                borderRadius(8.px)
                                backgroundColor(Gray100)
                                color(Gray700)
                                fontSize(14.px)
                                fontWeight(600)
                                cursor("pointer")
                            }
                            onClick { showConfirm = false }
                        }) { Text("Cancel") }
                        Span(attrs = {
                            style {
                                padding(10.px, 20.px)
                                borderRadius(8.px)
                                backgroundColor(Color("#dc2626"))
                                color(Color.white)
                                fontSize(14.px)
                                fontWeight(600)
                                cursor("pointer")
                            }
                            onClick {
                                showConfirm = false
                                sendBroadcast()
                            }
                        }) { Text("Send Now") }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Newsletter (GET /api/admin/newsletter/list, /preview-audience, /suggest-titles,
//             POST /api/admin/newsletter/send)
// ---------------------------------------------------------------------------

@Serializable
private data class NewsletterCampaign(
    val id: String = "",
    val subject: String = "",
    val status: String = "",
    @SerialName("sent_count") val sentCount: Int = 0,
    @SerialName("failed_count") val failedCount: Int = 0,
    @SerialName("created_at") val createdAt: String = "",
    @SerialName("sent_at") val sentAt: String? = null
)

@Serializable
private data class TitleSuggestion(
    val title: String = "",
    @SerialName("media_type") val mediaType: String = "",
    @SerialName("hit_count") val hitCount: Int = 0
)

@Serializable
private data class NewsletterPlaceholder(
    val key: String = "",
    val label: String = "",
    val group: String = "",
    val description: String = "",
    val sample: String = ""
)

@Serializable
private data class TitleSuggestions(
    val newThisWeek: List<TitleSuggestion> = emptyList(),
    val evergreen: List<TitleSuggestion> = emptyList()
)

/** Four fixed presets rather than a general filter builder — mirrors how RegisteredUsersView
 *  already exposes single-select tabs instead of a query builder. Matches what the growth plan's
 *  §2.5 audience list actually needs; a real filter builder can come later if it's ever needed. */
private enum class AudiencePreset(val label: String, val queryString: String, val bodyJsonFields: String) {
    ALL("All registered users", "", ""),
    FREE_USED_CREDIT(
        "Free tier, used their credit",
        "?filterMembershipStatus=free&filterFreeFullUsed=true",
        ""","filterMembershipStatus":"free","filterFreeFullUsed":true"""
    ),
    PAID("Paid subscribers", "?filterMembershipStatus=active", ""","filterMembershipStatus":"active""""),
    RECENT("Signed up in last 30 days", "", "")  // date computed at call time, see thirtyDaysAgoIso()
}

private fun thirtyDaysAgoIso(): String =
    kotlin.js.Date(kotlin.js.Date.now() - 30.0 * 24 * 60 * 60 * 1000).toISOString()

@Composable
private fun NewsletterView(onNavigate: (DrillDownView) -> Unit) {
    var campaigns by remember { mutableStateOf<List<NewsletterCampaign>>(emptyList()) }
    var loadingCampaigns by remember { mutableStateOf(true) }
    var showCompose by remember { mutableStateOf(false) }

    var subject by remember { mutableStateOf("") }
    var bodyPlain by remember { mutableStateOf("") }
    var sendMode by remember { mutableStateOf("audience") }  // "audience" | "test"
    var testEmail by remember { mutableStateOf("") }
    var audiencePreset by remember { mutableStateOf(AudiencePreset.ALL) }
    var audienceCount by remember { mutableStateOf<Int?>(null) }
    var suggestions by remember { mutableStateOf<TitleSuggestions?>(null) }
    var loadingSuggestions by remember { mutableStateOf(false) }
    var previewHtml by remember { mutableStateOf<String?>(null) }
    // Set when an edit came FROM the iframe. Without it the flow loops: typing in the preview
    // updates bodyPlain, which refires the preview fetch, which replaces the iframe's srcdoc,
    // which blows away the caret mid-sentence. When this is true we skip the refetch — the iframe
    // already shows the change, because the user just typed it there.
    var editFromPreview by remember { mutableStateOf(false) }
    /** Bumped on blur to force a clean re-render even when the text itself didn't change. */
    var previewRefreshKey by remember { mutableStateOf(0) }
    var placeholders by remember { mutableStateOf<List<NewsletterPlaceholder>>(emptyList()) }
    var imageStatus by remember { mutableStateOf<String?>(null) }
    // The paragraph the caret was last in (the preview reports it on blur); -1 = append at the end.
    var imageAfterPara by remember { mutableStateOf(-1) }
    var sending by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<String?>(null) }
    var resultIsError by remember { mutableStateOf(false) }
    var showConfirm by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authToken(): String? =
        try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }

    fun audienceQueryString(): String =
        if (audiencePreset == AudiencePreset.RECENT) "?filterCreatedAfter=${thirtyDaysAgoIso()}" else audiencePreset.queryString

    fun audienceBodyFields(): String =
        if (audiencePreset == AudiencePreset.RECENT) ""","filterCreatedAfter":"${thirtyDaysAgoIso()}"""" else audiencePreset.bodyJsonFields

    fun fetchCampaigns() {
        scope.launch {
            loadingCampaigns = true
            try {
                val headers = kotlin.js.json("Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/list",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    campaigns = adminJson.decodeFromString<List<NewsletterCampaign>>(response.text().await().toString())
                }
            } catch (_: Exception) {}
            loadingCampaigns = false
        }
    }

    fun fetchAudienceCount() {
        scope.launch {
            try {
                val headers = kotlin.js.json("Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/preview-audience${audienceQueryString()}",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(response.text().await().toString()) as? kotlinx.serialization.json.JsonObject
                    audienceCount = (obj?.get("count") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                }
            } catch (_: Exception) {}
        }
    }

    fun fetchSuggestions() {
        scope.launch {
            loadingSuggestions = true
            try {
                val headers = kotlin.js.json("Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/suggest-titles",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) {
                    suggestions = adminJson.decodeFromString<TitleSuggestions>(response.text().await().toString())
                }
            } catch (_: Exception) {}
            loadingSuggestions = false
        }
    }

    /** Ask the server to render the campaign through the REAL email template. Deliberately a
     *  server round-trip rather than reproducing the template in Kotlin/JS: a second copy would
     *  drift from what actually gets sent, which defeats the whole point of a preview. */
    fun fetchPreview() {
        scope.launch {
            try {
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "text/html")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val payload = """{"bodyPlain":"${bodyPlain.replace("\\", "\\\\").replace("\"", "\\\"").replace("\n", "\\n")}"}"""
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/preview",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to payload).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (response.ok) previewHtml = response.text().await().toString()
            } catch (_: Exception) {}
        }
    }

    fun fetchPlaceholders() {
        scope.launch {
            try {
                val headers = kotlin.js.json("Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val r = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/placeholders",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (r.ok) placeholders = adminJson.decodeFromString<List<NewsletterPlaceholder>>(r.text().await().toString())
            } catch (_: Exception) {}
        }
    }

    /** Ask the preview iframe to drop [tag] in at the caret. postMessage is the only channel we
     *  have into it (sandbox=allow-scripts means no same-origin DOM access), and it keeps the
     *  insertion at the cursor instead of appending blindly to the end. */
    fun insertPlaceholder(tag: String) {
        val frame = kotlinx.browser.document.getElementById("ff-preview-frame")
        frame?.unsafeCast<org.w3c.dom.HTMLIFrameElement>()?.contentWindow?.postMessage("ff-insert:" + tag, "*")
    }

    fun postImage(dataUrl: String) {
        scope.launch {
            imageStatus = "Uploading image…"
            try {
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/image",
                    kotlin.js.json("method" to "POST", "headers" to headers,
                        "body" to kotlin.js.JSON.stringify(kotlin.js.json("imageBase64" to dataUrl))).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val obj = adminJson.parseToJsonElement(response.text().await().toString()) as? JsonObject
                val url = (obj?.get("url") as? kotlinx.serialization.json.JsonPrimitive)?.content
                if (response.ok && url != null) {
                    val frame = kotlinx.browser.document.getElementById("ff-preview-frame")
                    frame?.unsafeCast<org.w3c.dom.HTMLIFrameElement>()?.contentWindow?.postMessage("ff-insert-image:$imageAfterPara|$url", "*")
                    imageStatus = null
                } else {
                    imageStatus = (obj?.get("error") as? kotlinx.serialization.json.JsonPrimitive)?.content ?: "Upload failed (HTTP ${response.status})"
                }
            } catch (e: Exception) { imageStatus = "Upload failed: ${e.message}" }
        }
    }

    /** Shrink to the email's 2x width (1072px) as a JPEG in the browser first — a 12MB phone photo
     *  becomes a few hundred KB, so the upload is quick and the server never decodes a huge image.
     *  White background, since JPEG has no transparency. Falls back to the original on any error. */
    fun downscaleThenUpload(original: String) {
        val img = kotlinx.browser.document.createElement("img").unsafeCast<org.w3c.dom.HTMLImageElement>()
        img.onload = {
            val scale = if (img.naturalWidth > 1072) 1072.0 / img.naturalWidth else 1.0
            val w = (img.naturalWidth * scale).toInt().coerceAtLeast(1)
            val h = (img.naturalHeight * scale).toInt().coerceAtLeast(1)
            val canvas = kotlinx.browser.document.createElement("canvas").unsafeCast<org.w3c.dom.HTMLCanvasElement>()
            canvas.width = w; canvas.height = h
            val ctx = canvas.getContext("2d").unsafeCast<org.w3c.dom.CanvasRenderingContext2D>()
            ctx.fillStyle = "#ffffff"; ctx.fillRect(0.0, 0.0, w.toDouble(), h.toDouble())
            ctx.drawImage(img, 0.0, 0.0, w.toDouble(), h.toDouble())
            postImage(try { canvas.toDataURL("image/jpeg", 0.85) } catch (_: Throwable) { original })
            null
        }
        img.onerror = { _, _, _, _, _ -> postImage(original); null }
        img.src = original
    }

    /** "Add image": upload to the server (resized, public URL back), then have the preview drop it
     *  in at the caret as its own paragraph — the iframe re-renders it through the real template. */
    fun uploadImage(file: org.w3c.files.File) {
        val reader = org.w3c.files.FileReader()
        reader.onload = { downscaleThenUpload(reader.result.toString()) }
        reader.onerror = { imageStatus = "Could not read that file" }
        reader.readAsDataURL(file)
    }

    fun insertSuggestions(list: List<TitleSuggestion>) {
        if (list.isEmpty()) return
        val snippet = list.take(3).joinToString("\n") { "• ${it.title}" }
        bodyPlain = if (bodyPlain.isBlank()) "This week's picks:\n$snippet"
                    else "$bodyPlain\n\nThis week's picks:\n$snippet"
    }

    fun sendCampaign() {
        if (subject.isBlank() || bodyPlain.isBlank()) return
        if (sendMode == "test" && testEmail.isBlank()) return
        scope.launch {
            sending = true
            result = null
            try {
                val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
                authToken()?.let { headers["Authorization"] = "Bearer $it" }
                val targetingFields = if (sendMode == "test")
                    ""","testEmail":"${testEmail.trim().replace("\\", "\\\\").replace("\"", "\\\"")}""""
                else audienceBodyFields()
                val body = """{"subject":"${subject.replace("\\", "\\\\").replace("\"", "\\\"")}",""" +
                    """"bodyPlain":"${bodyPlain.replace("\\", "\\\\").replace("\"", "\\\"").replace("\n", "\\n")}"""" +
                    targetingFields + "}"
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/send",
                    kotlin.js.json("method" to "POST", "headers" to headers, "body" to body).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
                    val count = (obj?.get("recipientCount") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull() ?: 0
                    val matchedReal = (obj?.get("testMatchedRealUser") as? kotlinx.serialization.json.JsonPrimitive)?.content == "true"
                    result = if (sendMode == "test") {
                        // Say which data the merge tags used — otherwise a test full of fallbacks
                        // looks like the placeholders are broken rather than "this address has no
                        // account behind it".
                        if (matchedReal) "Test sent to ${testEmail.trim()} — personalised with their real account data"
                        else "Test sent to ${testEmail.trim()} — no account found for that address, so placeholders show their fallbacks"
                    } else "Sending to $count recipient(s) — refresh in a moment to see progress"
                    resultIsError = false
                    subject = ""
                    bodyPlain = ""
                    testEmail = ""
                    showCompose = false
                    fetchCampaigns()
                } else {
                    result = "Failed: ${response.status} - $text"
                    resultIsError = true
                }
            } catch (e: Exception) {
                result = "Error: ${e.message}"
                resultIsError = true
            }
            sending = false
        }
    }

    LaunchedEffect(Unit) { fetchCampaigns() }
    LaunchedEffect(showCompose, audiencePreset, sendMode) { if (showCompose && sendMode == "audience") fetchAudienceCount() }
    // Debounced so typing doesn't fire a render per keystroke. Skipped when the change came from
    // inside the preview itself — see [editFromPreview].
    LaunchedEffect(showCompose, bodyPlain, previewRefreshKey) {
        if (!showCompose) return@LaunchedEffect
        if (editFromPreview) { editFromPreview = false; return@LaunchedEffect }
        delay(500)
        fetchPreview()
    }

    // The preview iframe posts the edited text out; sandboxed to allow-scripts only, so it has an
    // opaque origin and postMessage is the ONLY channel it has back to this page.
    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val me = ev.unsafeCast<org.w3c.dom.MessageEvent>()
            val s = me.data?.toString()
            when {
                // Blur: re-render through the server so the preview stops being raw
                // contenteditable output and goes back to being the real email. Bumping the
                // refresh key forces that even when the text itself didn't change.
                s != null && s.startsWith("ff-caret:") ->
                    imageAfterPara = s.removePrefix("ff-caret:").toIntOrNull() ?: -1
                s != null && s.startsWith("ff-body-final:") -> {
                    editFromPreview = false
                    bodyPlain = s.removePrefix("ff-body-final:")
                    previewRefreshKey++
                }
                // Mid-typing: keep the text, leave the iframe alone so the caret survives.
                s != null && s.startsWith("ff-body:") -> {
                    editFromPreview = true
                    bodyPlain = s.removePrefix("ff-body:")
                }
            }
        }
        kotlinx.browser.window.addEventListener("message", listener)
        onDispose { kotlinx.browser.window.removeEventListener("message", listener) }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Newsletter" to null)

        Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); property("margin-bottom", "10px") } }) {
            Span(attrs = {
                style { fontSize(13.px); fontWeight(600); color(Primary); cursor("pointer") }
                onClick { onNavigate(DrillDownView.NewsletterSubscribers) }
            }) { Text("View Subscribers →") }
        }

        CardSection("Campaigns", onRefresh = { fetchCampaigns() }) {
            when {
                loadingCampaigns -> P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading...") }
                campaigns.isEmpty() -> P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No campaigns sent yet.") }
                else -> campaigns.forEach { c ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                            padding(12.px, 0.px); property("border-bottom", "1px solid $Gray200")
                            cursor("pointer")
                        }
                        onClick { onNavigate(DrillDownView.NewsletterCampaignDetail(c.id)) }
                    }) {
                        Div {
                            Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(c.subject) }
                            Div(attrs = { style { fontSize(12.px); color(Gray400); property("margin-top", "2px") } }) { Text(c.createdAt.take(10)) }
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                            Span(attrs = {
                                style {
                                    fontSize(11.px); fontWeight(700); padding(3.px, 10.px); borderRadius(20.px)
                                    color(if (c.status == "sent") Color("#065f46") else if (c.status == "failed") Color("#991b1b") else Color("#92400e"))
                                    backgroundColor(if (c.status == "sent") Color("#ecfdf5") else if (c.status == "failed") Color("#fef2f2") else Color("#fffbeb"))
                                }
                            }) { Text(c.status) }
                            Span(attrs = { style { fontSize(13.px); color(Gray600) } }) {
                                Text("${c.sentCount} sent" + if (c.failedCount > 0) ", ${c.failedCount} failed" else "")
                            }
                        }
                    }
                }
            }
        }

        if (!showCompose) {
            Span(attrs = {
                style {
                    display(DisplayStyle.InlineBlock); padding(12.px, 24.px); borderRadius(10.px)
                    backgroundColor(Primary); color(Color.white); fontSize(14.px); fontWeight(600)
                    cursor("pointer"); property("margin-top", "16px")
                }
                onClick { showCompose = true; suggestions = null; fetchSuggestions(); fetchPlaceholders() }
            }) { Text("+ New Campaign") }
        } else {
            CardSection("New Campaign") {
                Span(attrs = {
                    style {
                        fontSize(12.px); fontWeight(600); color(Gray500)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                        display(DisplayStyle.Block); property("margin-bottom", "6px")
                    }
                }) { Text("Subject") }
                org.jetbrains.compose.web.dom.TextInput(subject) {
                    onInput { subject = it.value }
                    style {
                        width(100.percent); padding(10.px, 14.px); borderRadius(8.px)
                        property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                        property("box-sizing", "border-box"); property("margin-bottom", "16px")
                    }
                    attr("placeholder", "This week's picks from Fitrah Filter")
                }

                // Send-to mode: a real audience segment, or one specific address for testing
                Span(attrs = {
                    style {
                        fontSize(12.px); fontWeight(600); color(Gray500)
                        property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                        display(DisplayStyle.Block); property("margin-bottom", "6px")
                    }
                }) { Text("Send To") }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "12px") } }) {
                    listOf("audience" to "Audience segment", "test" to "Specific address (test)").forEach { (mode, label) ->
                        Span(attrs = {
                            style {
                                padding(6.px, 14.px); borderRadius(8.px); fontSize(12.px); fontWeight(600); cursor("pointer")
                                if (sendMode == mode) { backgroundColor(Primary); color(Color.white) }
                                else { backgroundColor(Gray100); color(Gray600) }
                            }
                            onClick { sendMode = mode }
                        }) { Text(label) }
                    }
                }

                if (sendMode == "test") {
                    org.jetbrains.compose.web.dom.TextInput(testEmail) {
                        onInput { testEmail = it.value }
                        style {
                            width(100.percent); padding(10.px, 14.px); borderRadius(8.px)
                            property("border", "1px solid $Gray200"); fontSize(14.px); property("outline", "none")
                            property("box-sizing", "border-box"); property("margin-bottom", "8px")
                        }
                        attr("type", "email")
                        attr("placeholder", "you@example.com")
                    }
                    P(attrs = { style { fontSize(13.px); color(Gray400); property("margin-bottom", "18px") } }) {
                        Text("Sends to exactly this address — skips audience filters and unsubscribe status, doesn't count against anyone's subscription.")
                    }
                } else {
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap"); property("margin-bottom", "8px") } }) {
                        AudiencePreset.entries.forEach { preset ->
                            Span(attrs = {
                                style {
                                    padding(6.px, 14.px); borderRadius(8.px); fontSize(12.px); fontWeight(600); cursor("pointer")
                                    if (audiencePreset == preset) { backgroundColor(Primary); color(Color.white) }
                                    else { backgroundColor(Gray100); color(Gray600) }
                                }
                                onClick { audiencePreset = preset }
                            }) { Text(preset.label) }
                        }
                    }
                    P(attrs = { style { fontSize(13.px); color(Gray400); property("margin-bottom", "18px") } }) {
                        Text(audienceCount?.let { "$it recipient(s) match this audience" } ?: "Loading recipient count...")
                    }
                }

                // Suggested titles
                if (loadingSuggestions) {
                    P(attrs = { style { fontSize(13.px); color(Gray400); property("margin-bottom", "10px") } }) { Text("Loading suggestions...") }
                } else suggestions?.let { s ->
                    val pick = s.newThisWeek.ifEmpty { s.evergreen }
                    if (pick.isNotEmpty()) {
                        Span(attrs = {
                            style {
                                display(DisplayStyle.InlineBlock); padding(8.px, 16.px); borderRadius(8.px)
                                backgroundColor(Color("#eff6ff")); color(Color("#1e40af")); fontSize(13.px); fontWeight(600)
                                cursor("pointer"); property("margin-bottom", "18px")
                            }
                            onClick { insertSuggestions(pick) }
                        }) {
                            Text(
                                if (s.newThisWeek.isNotEmpty()) "✨ Suggest this week's new titles"
                                else "✨ Suggest popular titles"
                            )
                        }
                    }
                }

                // No separate message textarea — the message is typed directly into the preview
                // below, so there is one place to look and what you edit is literally what sends.
                //
                // True preview: the server renders the SAME template the send loop uses and we drop
                // it into an iframe. An iframe (not inline HTML) because the email is a whole
                // document with its own <style>, gradient background and dark-mode rules — inlined,
                // those would leak into the admin page and the admin's CSS would leak into it,
                // making the preview lie in both directions.
                Div(attrs = {
                    style {
                        borderRadius(10.px); backgroundColor(Gray50)
                        property("border", "1px solid $Gray200"); property("margin-bottom", "16px")
                        property("overflow", "hidden")
                    }
                }) {
                    // Merge-tag chips. Catalog comes from the server so the UI can never offer a tag
                    // the send loop doesn't resolve.
                    if (placeholders.isNotEmpty()) {
                        Div(attrs = {
                            style {
                                padding(12.px, 16.px); backgroundColor(Color.white)
                                property("border-bottom", "1px solid $Gray200")
                            }
                        }) {
                            Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray400); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "8px") } }) {
                                Text("Insert personalisation")
                            }
                            // Grouped — 20 chips in one undifferentiated row is a wall, not a menu.
                            placeholders.groupBy { it.group }.forEach { (groupName, items) ->
                                Div(attrs = { style { property("margin-bottom", "8px") } }) {
                                    Div(attrs = { style { fontSize(10.px); fontWeight(700); color(Gray400); property("margin-bottom", "4px") } }) {
                                        Text(groupName)
                                    }
                                    Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); property("flex-wrap", "wrap") } }) {
                                        items.forEach { p ->
                                            Span(attrs = {
                                                style {
                                                    padding(5.px, 11.px); borderRadius(20.px); fontSize(12.px); fontWeight(600)
                                                    backgroundColor(PrimaryPale); color(Primary); cursor("pointer")
                                                    property("border", "1px solid ${Primary}33")
                                                    property("user-select", "none")
                                                }
                                                attr("title", "${p.description}  ·  Preview shows: ${p.sample}")
                                                onClick { insertPlaceholder("{{${p.key}}}") }
                                            }) { Text("+ ${p.label}") }
                                        }
                                    }
                                }
                            }
                            Div(attrs = { style { fontSize(11.px); color(Gray400); property("margin-top", "8px") } }) {
                                Text("Each has a fallback for recipients missing that detail — override with {{first_name|friend}}. Works in the subject too.")
                            }
                            // Formatting: **bold** is typed; an image is uploaded and lands at the caret.
                            Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); alignItems(AlignItems.Center); property("margin-top", "10px"); property("flex-wrap", "wrap") } }) {
                                Label(forId = "ff-image-input", attrs = {
                                    style {
                                        padding(5.px, 11.px); borderRadius(20.px); fontSize(12.px); fontWeight(600)
                                        backgroundColor(PrimaryPale); color(Primary); cursor("pointer")
                                        property("border", "1px solid ${Primary}33"); property("user-select", "none")
                                    }
                                    attr("title", "Upload a picture; it goes in where your cursor is, full width")
                                }) { Text("+ Add image") }
                                Input(type = org.jetbrains.compose.web.attributes.InputType.File, attrs = {
                                    id("ff-image-input")
                                    attr("accept", "image/png,image/jpeg,image/gif")
                                    style { display(DisplayStyle.None) }
                                    onChange {
                                        val input = kotlinx.browser.document.getElementById("ff-image-input") as? org.w3c.dom.HTMLInputElement
                                        input?.files?.item(0)?.let { uploadImage(it) }
                                        input?.value = ""
                                    }
                                })
                                Span(attrs = { style { fontSize(11.px); color(Gray400) } }) {
                                    Text(imageStatus ?: "Bold: wrap words in **double stars** (or select and press ⌘B).")
                                }
                            }
                        }
                    }

                    // Mock inbox header — the subject and sender live in the mail client's chrome,
                    // not in the email body, so they'd be invisible in the iframe alone.
                    Div(attrs = {
                        style {
                            padding(12.px, 16.px); property("border-bottom", "1px solid $Gray200")
                            backgroundColor(Color.white)
                        }
                    }) {
                        Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Primary); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "6px") } }) {
                            Text("✎ Click the message below to edit — this is exactly what recipients get")
                        }
                        Div(attrs = { style { fontSize(14.px); color(Gray900); fontWeight(600) } }) {
                            Text(subject.ifBlank { "(no subject)" })
                        }
                        Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "2px") } }) {
                            Text("Yusuf at Fitrah Filter · yusuf@fitrahfilter.com")
                        }
                    }
                    val html = previewHtml
                    if (html == null) {
                        P(attrs = { style { fontSize(13.px); color(Gray400); padding(20.px, 16.px); property("margin", "0") } }) {
                            Text("Rendering…")
                        }
                    } else {
                        TagElement<org.w3c.dom.HTMLIFrameElement>("iframe", {
                            // srcdoc via setAttribute — the DOM API escapes the value for us, so the
                            // email HTML needs no hand-rolled escaping to survive the round trip.
                            attr("srcdoc", html.replace("</body>", "<script>window.FF_RICH=true</script>$PREVIEW_EDITOR_SCRIPT</body>"))
                            attr("id", "ff-preview-frame")
                            attr("title", "Email preview — the message area is editable")
                            // allow-scripts ONLY. No allow-same-origin, so the frame keeps an opaque
                            // origin and cannot reach this page's DOM, cookies or storage — its one
                            // channel out is the postMessage the injected script sends.
                            attr("sandbox", "allow-scripts")
                            style {
                                width(100.percent); height(700.px)
                                property("border", "none"); property("display", "block")
                            }
                        }) {}
                    }
                }

                val canSend = subject.isNotBlank() && bodyPlain.isNotBlank() && !sending &&
                    (sendMode != "test" || testEmail.isNotBlank())
                Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px) } }) {
                    Span(attrs = {
                        style {
                            display(DisplayStyle.InlineBlock); padding(12.px, 24.px); borderRadius(10.px)
                            backgroundColor(if (canSend) Primary else Gray300); color(Color.white); fontSize(14.px); fontWeight(600)
                            if (canSend) cursor("pointer")
                        }
                        if (canSend) onClick { showConfirm = true }
                    }) { Text(if (sending) "Sending..." else "Send Campaign") }
                    Span(attrs = {
                        style {
                            display(DisplayStyle.InlineBlock); padding(12.px, 24.px); borderRadius(10.px)
                            backgroundColor(Gray100); color(Gray700); fontSize(14.px); fontWeight(600); cursor("pointer")
                        }
                        onClick { showCompose = false; result = null }
                    }) { Text("Cancel") }
                }

                result?.let {
                    Div(attrs = {
                        style {
                            padding(12.px, 16.px); borderRadius(10.px); property("margin-top", "16px")
                            backgroundColor(if (resultIsError) Color("#fef2f2") else Color("#ecfdf5"))
                            property("border", "1px solid ${if (resultIsError) Color("#fecaca") else Color("#bbf7d0")}")
                        }
                    }) {
                        Span(attrs = { style { fontSize(13.px); color(if (resultIsError) Color("#991b1b") else Color("#065f46")); fontWeight(500) } }) { Text(it) }
                    }
                }
            }
        }

        if (showConfirm) {
            Div(attrs = {
                style {
                    property("position", "fixed"); property("top", "0"); property("left", "0")
                    property("right", "0"); property("bottom", "0"); backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
                onClick { showConfirm = false }
            }) {
                Div(attrs = {
                    style {
                        backgroundColor(Color.white); borderRadius(16.px); padding(28.px)
                        property("max-width", "460px"); width(90.percent); property("box-shadow", "0 20px 50px rgba(0,0,0,0.3)")
                    }
                    onClick { event -> event.stopPropagation() }
                }) {
                    H2(attrs = { style { fontSize(18.px); fontWeight(700); color(Gray900); property("margin", "0 0 12px") } }) {
                        Text(if (sendMode == "test") "Send test to ${testEmail.trim()}?" else "Send to ${audienceCount ?: "?"} recipient(s)?")
                    }
                    P(attrs = { style { fontSize(14.px); color(Gray600); lineHeight(20.px.unsafeCast<CSSNumeric>()); property("margin", "0 0 16px") } }) {
                        Text(
                            if (sendMode == "test") "This sends real email immediately to this one address only — it won't touch your subscriber list."
                            else "This sends real email immediately and cannot be undone. Each recipient gets a working unsubscribe link."
                        )
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); justifyContent(JustifyContent.FlexEnd) } }) {
                        Span(attrs = {
                            style { padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Gray100); color(Gray700); fontSize(14.px); fontWeight(600); cursor("pointer") }
                            onClick { showConfirm = false }
                        }) { Text("Cancel") }
                        Span(attrs = {
                            style { padding(10.px, 20.px); borderRadius(8.px); backgroundColor(Primary); color(Color.white); fontSize(14.px); fontWeight(600); cursor("pointer") }
                            onClick { showConfirm = false; sendCampaign() }
                        }) { Text("Send Now") }
                    }
                }
            }
        }
    }
}

@Serializable
private data class NewsletterSubscriberRow(
    val email: String = "",
    val subscribed: Boolean = true,
    @SerialName("unsubscribed_at") val unsubscribedAt: String? = null,
    val source: String = "",
    @SerialName("created_at") val createdAt: String = ""
)

/**
 * Injected into the preview iframe to make the message region editable in place.
 *
 * Only `#ff-message` (the wrapper NewsletterEmail puts around the admin's own text) is touched —
 * the logo, basmallah, CTA, signature and footer stay fixed. That's deliberate: the footer carries
 * the legally-required unsubscribe link and postal address, and the shell is the shared brand
 * template, so neither should be editable per-campaign.
 *
 * Sends plain text back, not HTML: the server is the only thing that turns text into email markup,
 * so the round trip stays text → server → HTML and the preview can never drift from what is sent.
 */
private const val PREVIEW_EDITOR_SCRIPT = """
<script>
(function () {
  var el = document.getElementById('ff-message');
  if (!el) return;
  el.setAttribute('contenteditable', 'true');
  el.style.outline = 'none';
  el.style.cursor = 'text';
  // A quiet affordance so it's discoverable without shouting in what is meant to look like an email.
  el.addEventListener('focus', function () { el.style.boxShadow = '0 0 0 2px rgba(14,124,123,0.35)'; el.style.borderRadius = '6px'; });
  el.addEventListener('blur',  function () { el.style.boxShadow = 'none'; });
  // The newsletter frame sets window.FF_RICH: its template renders **bold** and image lines. The
  // drip frame doesn't (DripEmail renders neither), so there bold and images stay plain text.
  var RICH = !!window.FF_RICH;
  var read = function () {
    // Put back the source text of everything the server rendered from a mark, read innerText, then
    // undo it — the live DOM is only changed for the length of this call:
    //  - a merge tag shows its sample value ("Aisha") in a data-ff-tag span but must save as the tag,
    //    or one blur would quietly turn {{first_name}} into "Aisha" for every recipient;
    //  - (RICH) bold saves as **…** (Cmd+B's <b> too), an image as its own ![alt](url) paragraph.
    var undo = [];
    el.querySelectorAll('[data-ff-tag]').forEach(function (s) {
      var t = document.createTextNode(s.getAttribute('data-ff-tag'));
      s.replaceWith(t); undo.push(function () { t.replaceWith(s); });
    });
    if (RICH) {
      el.querySelectorAll('strong, b').forEach(function (b) {
        var outer = b.parentElement && b.parentElement.closest('strong, b');
        if (outer && el.contains(outer)) return;   // nested bold: the outer pair covers it
        var open = document.createTextNode('**'), close = document.createTextNode('**');
        b.insertBefore(open, b.firstChild); b.appendChild(close);
        undo.push(function () { open.remove(); close.remove(); });
      });
      el.querySelectorAll('img').forEach(function (img) {
        var src = img.getAttribute('src') || '';
        if (src.indexOf('https://') !== 0) return;
        // A block, not a text node: innerText then puts it on its own paragraph even when the
        // image sits inside a paragraph of text, which is what the server needs to render it.
        var line = document.createElement('p');
        line.textContent = '![' + (img.getAttribute('alt') || '') + '](' + src + ')';
        img.parentNode.insertBefore(line, img); undo.push(function () { line.remove(); });
      });
    }
    // innerText (not textContent) so paragraph breaks survive as newlines; collapse 3+ blank
    // lines to the double newline the server treats as a paragraph split.
    var text = el.innerText || '';
    for (var i = undo.length - 1; i >= 0; i--) undo[i]();
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };
  // Which paragraph the caret is in, sent on blur. Clicking "Add image" blurs the message and the
  // preview re-renders before the upload finishes, so the parent has to remember where it goes.
  var caretPara = function () {
    var sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) return -1;
    var n = sel.anchorNode;
    while (n && n.parentNode !== el) n = n.parentNode;
    return n ? Array.prototype.indexOf.call(el.children, n) : -1;
  };
  // While typing: 'ff-body:' — the parent stores the text but does NOT re-render, so the caret
  // survives. On blur: 'ff-body-final:' — the parent re-renders through the real server template,
  // so what's on screen goes back to being byte-for-byte the sent email rather than raw
  // contenteditable output.
  el.addEventListener('input', function () { parent.postMessage('ff-body:' + read(), '*'); });
  el.addEventListener('blur',  function () {
    parent.postMessage('ff-caret:' + caretPara(), '*');
    parent.postMessage('ff-body-final:' + read(), '*');
  });

  // Merge-tag insertion, driven from the chips in the admin page. Inserts at the caret if the
  // message has focus, otherwise appends — so clicking a chip always does something sensible
  // rather than silently going nowhere when the user hasn't clicked into the text yet.
  window.addEventListener('message', function (ev) {
    var d = ev.data;
    // An uploaded image ('ff-insert-image:<paragraph>|<url>'): a new paragraph after the one the
    // caret was last in (-1: at the end), then an immediate re-render through the real template.
    if (RICH && typeof d === 'string' && d.indexOf('ff-insert-image:') === 0) {
      var rest = d.slice('ff-insert-image:'.length), bar = rest.indexOf('|');
      var after = parseInt(rest.slice(0, bar), 10), url = rest.slice(bar + 1);
      if (url.indexOf('https://') !== 0 || /[\s"'<>()]/.test(url)) return;
      var p = document.createElement('p'), img = document.createElement('img');
      img.setAttribute('src', url); img.setAttribute('alt', ''); img.style.maxWidth = '100%';
      p.appendChild(img);
      var kids = el.children;
      if (after >= 0 && after < kids.length) kids[after].after(p); else el.appendChild(p);
      parent.postMessage('ff-body-final:' + read(), '*');
      return;
    }
    if (typeof d !== 'string' || d.indexOf('ff-insert:') !== 0) return;
    var tag = d.slice('ff-insert:'.length);
    el.focus();
    var sel = window.getSelection();
    var inside = sel && sel.rangeCount > 0 && el.contains(sel.anchorNode);
    if (inside && document.execCommand) {
      document.execCommand('insertText', false, tag);
    } else {
      // Append a text node rather than rewriting innerText, which would flatten the tag spans
      // (to their sample values), the bold and the images.
      el.appendChild(document.createTextNode(' ' + tag));
    }
    parent.postMessage('ff-body:' + read(), '*');
  });
})();
</script>
"""

@Composable
private fun NewsletterSubscribersView(onNavigate: (DrillDownView) -> Unit) {
    var subscribers by remember { mutableStateOf<List<NewsletterSubscriberRow>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var filter by remember { mutableStateOf("all") } // "all" | "subscribed" | "unsubscribed"

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchSubscribers() {
        scope.launch {
            loading = true
            error = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/subscribers",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    subscribers = adminJson.decodeFromString<List<NewsletterSubscriberRow>>(text)
                } else {
                    error = "Failed to fetch subscribers: ${response.status}"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            loading = false
        }
    }

    LaunchedEffect(Unit) { fetchSubscribers() }

    val subscribedCount = subscribers.count { it.subscribed }
    val unsubscribedCount = subscribers.count { !it.subscribed }
    val filtered = remember(subscribers, filter) {
        when (filter) {
            "subscribed" -> subscribers.filter { it.subscribed }
            "unsubscribed" -> subscribers.filter { !it.subscribed }
            else -> subscribers
        }
    }

    PageShell {
        BreadcrumbNav(
            "Dashboard" to { onNavigate(DrillDownView.Overview) },
            "Newsletter" to { onNavigate(DrillDownView.Newsletter) },
            "Subscribers" to null
        )

        if (loading) {
            CardSection("Loading...") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching subscribers...") }
            }
            return@PageShell
        }
        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
            return@PageShell
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(160px, 1fr))")
                gap(16.px)
                property("margin-bottom", "20px")
            }
        }) {
            SummaryCard("Total", subscribers.size.toString(), Primary)
            SummaryCard("Subscribed", subscribedCount.toString(), ScoreGreen)
            SummaryCard("Unsubscribed", unsubscribedCount.toString(), ScoreRed)
        }

        Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "20px"); property("flex-wrap", "wrap") } }) {
            listOf(
                "all" to "All (${subscribers.size})",
                "subscribed" to "Subscribed ($subscribedCount)",
                "unsubscribed" to "Unsubscribed ($unsubscribedCount)"
            ).forEach { (key, label) ->
                Button(attrs = {
                    onClick { filter = key }
                    style {
                        padding(6.px, 14.px); fontSize(12.px); fontWeight(600); borderRadius(8.px); cursor("pointer")
                        property("border", "none")
                        if (filter == key) { backgroundColor(Primary); color(Color.white) } else { backgroundColor(Gray50); color(Gray600) }
                    }
                }) { Text(label) }
            }
        }

        CardSection("Subscribers", onRefresh = { fetchSubscribers() }) {
            if (filtered.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No subscribers match this filter.") }
            } else {
                filtered.forEach { s ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                            padding(10.px, 0.px); property("border-bottom", "1px solid $Gray200")
                        }
                    }) {
                        Div {
                            Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(s.email) }
                            Div(attrs = { style { fontSize(12.px); color(Gray400); property("margin-top", "2px") } }) {
                                Text(
                                    if (!s.subscribed && s.unsubscribedAt != null) "Unsubscribed ${s.unsubscribedAt.take(10)}"
                                    else "Since ${s.createdAt.take(10)}" + if (s.source.isNotBlank()) " · ${s.source}" else ""
                                )
                            }
                        }
                        Span(attrs = {
                            style {
                                fontSize(11.px); fontWeight(700); padding(3.px, 10.px); borderRadius(20.px)
                                color(if (s.subscribed) Color("#065f46") else Color("#991b1b"))
                                backgroundColor(if (s.subscribed) Color("#ecfdf5") else Color("#fef2f2"))
                            }
                        }) { Text(if (s.subscribed) "subscribed" else "unsubscribed") }
                    }
                }
            }
        }
    }
}

@Serializable
private data class NewsletterSendDetail(
    val email: String = "",
    val success: Boolean = false,
    val error: String? = null,
    @SerialName("sent_at") val sentAt: String = "",
    @SerialName("clicked_at") val clickedAt: String? = null,
    @SerialName("unsubscribed_at") val unsubscribedAt: String? = null
)

@Serializable
private data class NewsletterCampaignDetailData(
    val id: String = "",
    val subject: String = "",
    @SerialName("body_plain") val bodyPlain: String = "",
    val status: String = "",
    @SerialName("sent_count") val sentCount: Int = 0,
    @SerialName("failed_count") val failedCount: Int = 0,
    @SerialName("clicked_count") val clickedCount: Int = 0,
    @SerialName("unsubscribed_count") val unsubscribedCount: Int = 0,
    @SerialName("created_at") val createdAt: String = "",
    @SerialName("sent_at") val sentAt: String? = null,
    val sends: List<NewsletterSendDetail> = emptyList()
)

@Composable
private fun NewsletterCampaignDetailView(campaignId: String, onNavigate: (DrillDownView) -> Unit) {
    var detail by remember { mutableStateOf<NewsletterCampaignDetailData?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchDetail() {
        scope.launch {
            loading = true
            error = null
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/newsletter/campaign/$campaignId",
                    kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                if (response.ok) {
                    detail = adminJson.decodeFromString<NewsletterCampaignDetailData>(text)
                } else {
                    error = "Failed to fetch campaign: ${response.status}"
                }
            } catch (e: Exception) {
                error = "Error: ${e.message}"
            }
            loading = false
        }
    }

    LaunchedEffect(campaignId) { fetchDetail() }

    PageShell {
        BreadcrumbNav(
            "Dashboard" to { onNavigate(DrillDownView.Overview) },
            "Newsletter" to { onNavigate(DrillDownView.Newsletter) },
            (detail?.subject?.takeIf { it.isNotBlank() } ?: "Campaign") to null
        )

        if (loading) {
            CardSection("Loading...") {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Fetching campaign...") }
            }
            return@PageShell
        }
        error?.let {
            CardSection("Error") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px) } }) { Text(it) }
            }
            return@PageShell
        }
        val d = detail ?: return@PageShell

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
                gap(16.px)
                property("margin-bottom", "20px")
            }
        }) {
            SummaryCard("Sent", d.sentCount.toString(), ScoreGreen)
            SummaryCard("Failed", d.failedCount.toString(), if (d.failedCount > 0) ScoreRed else Gray400)
            SummaryCard("Clicked", d.clickedCount.toString(), Primary)
            SummaryCard("Unsubscribed", d.unsubscribedCount.toString(), if (d.unsubscribedCount > 0) ScoreRed else Gray400)
        }

        CardSection("Message") {
            Div(attrs = { style { fontSize(15.px); fontWeight(700); color(Gray900); property("margin-bottom", "10px") } }) { Text(d.subject) }
            d.bodyPlain.split(Regex("\n\\s*\n")).map { it.trim() }.filter { it.isNotEmpty() }.forEach { para ->
                P(attrs = { style { fontSize(14.px); color(Gray600); lineHeight(20.px.unsafeCast<CSSNumeric>()); property("margin", "0 0 10px") } }) { Text(para) }
            }
            Div(attrs = { style { fontSize(12.px); color(Gray400); property("margin-top", "12px") } }) {
                Text("Status: ${d.status} · Created ${d.createdAt.take(10)}" + (d.sentAt?.let { " · Sent ${it.take(10)}" } ?: ""))
            }
        }

        CardSection("Recipients (${d.sends.size})", onRefresh = { fetchDetail() }) {
            if (d.sends.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("No recipient records for this campaign.") }
            } else {
                // Campaigns sent before click tracking shipped carry no tracked link, so their
                // rows will never show a click — worth saying so rather than looking broken.
                if (d.clickedCount == 0) {
                    P(attrs = { style { color(Gray400); fontSize(12.px); property("margin-bottom", "10px") } }) {
                        Text("No clicks recorded. Campaigns sent before click tracking shipped will always show zero.")
                    }
                }
                d.sends.forEach { s ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                            padding(10.px, 0.px); property("border-bottom", "1px solid $Gray200")
                        }
                    }) {
                        Div {
                            Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(s.email) }
                            Div(attrs = { style { fontSize(12.px); color(Gray400); property("margin-top", "2px") } }) {
                                Text(s.error ?: s.sentAt.take(16).replace("T", " "))
                            }
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px); property("flex-wrap", "wrap") } }) {
                            if (s.clickedAt != null) StatusPill("clicked", Color("#065f46"), Color("#ecfdf5"))
                            if (s.unsubscribedAt != null) StatusPill("unsubscribed", Color("#991b1b"), Color("#fef2f2"))
                            StatusPill(
                                if (s.success) "sent" else "failed",
                                if (s.success) Gray600 else Color("#991b1b"),
                                if (s.success) Gray100 else Color("#fef2f2")
                            )
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Drip campaigns — automated email sequences.
//   GET|PATCH /api/admin/drip/config
//   GET       /api/admin/drip/campaigns          POST /api/admin/drip/campaign
//   PATCH     /api/admin/drip/campaign/{id}
//   GET|POST  /api/admin/drip/campaign/{id}/steps | /step
//   PATCH|DEL /api/admin/drip/step/{stepId}      GET /step/{stepId}/impact
//   POST      /api/admin/drip/preview            GET /placeholders, /rule-catalog
//   POST      /api/admin/drip/test-send
//   backfill: /campaign/{id}/backfill{,/dry-run,/start}, /backfill/{runId}/{mode}
//   GET       /api/admin/drip/runs, /campaign/{id}/sends, /campaign/{id}/enrollments
//
// Three switches must ALL be on before anything sends, and they are deliberately
// separate: drip_config.enabled (global), campaign.active && !campaign.dry_run,
// and step.active. The UI shows all three because "why did nothing send" is
// otherwise unanswerable from this screen.
// ---------------------------------------------------------------------------

/** GET /api/admin/drip/config returns a PostgREST array of one row, not an object. */
@Serializable
private data class DripConfigRow(
    val enabled: Boolean = false,
    @SerialName("send_hour_utc") val sendHourUtc: Int = 13,
    @SerialName("max_sends_per_night") val maxSendsPerNight: Int = 500,
    @SerialName("updated_at") val updatedAt: String = ""
)

@Serializable
private data class DripCampaignRow(
    val id: String = "",
    val key: String = "",
    val name: String = "",
    val description: String? = null,
    @SerialName("trigger_type") val triggerType: String = "",
    val active: Boolean = false,
    @SerialName("dry_run") val dryRun: Boolean = true,
    @SerialName("from_name") val fromName: String = "",
    @SerialName("trigger_config") val triggerConfig: JsonObject = JsonObject(emptyMap()),
    @SerialName("stop_rules") val stopRules: JsonObject = JsonObject(emptyMap()),
    @SerialName("catchup_grace_days") val catchupGraceDays: Int = 2,
    @SerialName("max_enroll_per_run") val maxEnrollPerRun: Int = 500,
    @SerialName("max_sends_per_run") val maxSendsPerRun: Int = 500,
    @SerialName("created_at") val createdAt: String = ""
)

@Serializable
private data class DripStepRow(
    val id: String = "",
    @SerialName("campaign_id") val campaignId: String = "",
    @SerialName("day_offset") val dayOffset: Int = 0,
    val subject: String = "",
    @SerialName("body_plain") val bodyPlain: String = "",
    val preheader: String? = null,
    @SerialName("cta_label") val ctaLabel: String? = null,
    @SerialName("cta_url") val ctaUrl: String? = null,
    val active: Boolean = false,
    @SerialName("eligible_from") val eligibleFrom: String = ""
)

@Serializable
private data class DripRunRow(
    val id: String = "",
    @SerialName("run_date") val runDate: String = "",
    val trigger: String = "",
    @SerialName("machine_id") val machineId: String? = null,
    val region: String? = null,
    @SerialName("started_at") val startedAt: String = "",
    @SerialName("finished_at") val finishedAt: String? = null,
    val error: String? = null
)

@Serializable
private data class DripPlaceholderEntry(
    val key: String = "",
    val label: String = "",
    val group: String = "",
    val description: String = "",
    val sample: String = ""
)

/** GET /placeholders splits tags by where the value comes from: the enrolment's own
 *  context (only some triggers populate these) vs the recipient's user record. */
@Serializable
private data class DripPlaceholderGroups(
    val context: List<DripPlaceholderEntry> = emptyList(),
    val user: List<DripPlaceholderEntry> = emptyList()
)

@Serializable
private data class DripRuleEntry(
    val key: String = "",
    val label: String = "",
    val description: String = ""
)

@Serializable
private data class DripSendRow(
    val id: String = "",
    val email: String = "",
    @SerialName("day_offset") val dayOffset: Int = 0,
    val status: String = "",
    val error: String? = null,
    @SerialName("subject_snapshot") val subjectSnapshot: String = "",
    @SerialName("claimed_at") val claimedAt: String = "",
    @SerialName("sent_at") val sentAt: String? = null,
    @SerialName("clicked_at") val clickedAt: String? = null,
    @SerialName("unsubscribed_at") val unsubscribedAt: String? = null
)

@Serializable
private data class DripEnrollmentRow(
    val id: String = "",
    val email: String = "",
    @SerialName("enrolled_at") val enrolledAt: String = "",
    val source: String = "",
    val reason: String? = null,
    val status: String = "",
    @SerialName("stopped_reason") val stoppedReason: String? = null,
    @SerialName("last_sent_offset") val lastSentOffset: Int? = null,
    @SerialName("last_sent_at") val lastSentAt: String? = null
)

@Serializable
private data class DripBackfillRun(
    val id: String = "",
    val status: String = "",
    @SerialName("total_matched") val totalMatched: Int = 0,
    @SerialName("enrolled_count") val enrolledCount: Int = 0,
    @SerialName("pace_per_day") val pacePerDay: Int = 200,
    @SerialName("enrolled_today") val enrolledToday: Int = 0,
    @SerialName("abort_requested") val abortRequested: Boolean = false,
    @SerialName("started_at") val startedAt: String = "",
    @SerialName("finished_at") val finishedAt: String? = null
)

/** Colour a send/enrolment status so a table scans at a glance rather than being read word by word. */
private fun dripStatusPill(status: String): Triple<String, CSSColorValue, CSSColorValue> = when (status) {
    "sent" -> Triple("SENT", rgb(6, 95, 70), rgb(209, 250, 229))
    "failed" -> Triple("FAILED", rgb(153, 27, 27), rgb(254, 226, 226))
    "skipped" -> Triple("SKIPPED", Gray600, Gray200)
    "simulated" -> Triple("SIMULATED", rgb(146, 64, 14), rgb(254, 243, 199))
    "claimed", "sending" -> Triple(status.uppercase(), rgb(30, 64, 175), rgb(219, 234, 254))
    "active" -> Triple("ACTIVE", rgb(6, 95, 70), rgb(209, 250, 229))
    "completed" -> Triple("COMPLETED", rgb(30, 64, 175), rgb(219, 234, 254))
    "stopped" -> Triple("STOPPED", Gray600, Gray200)
    else -> Triple(status.uppercase(), Gray600, Gray200)
}

/** The five trigger types the server's CHECK constraint allows, with plain-English labels. */
private val DRIP_TRIGGER_TYPES = listOf(
    "post_registration" to "After registration",
    "post_analysis" to "After an analysis",
    "post_subscribe" to "After subscribing",
    "subscription_lapsing" to "Access about to lapse",
    "inactive_reengagement" to "Gone quiet"
)

private fun dripTriggerLabel(key: String): String =
    DRIP_TRIGGER_TYPES.firstOrNull { it.first == key }?.second ?: key

/**
 * A campaign's real state, as one phrase. Reading three booleans off a row and
 * working out whether mail can actually leave is the single most common thing
 * this screen has to answer, so it is computed once here rather than at each
 * call site.
 */
private fun dripCampaignState(c: DripCampaignRow, globalEnabled: Boolean): Triple<String, CSSColorValue, CSSColorValue> = when {
    !globalEnabled -> Triple("GLOBAL OFF", Gray600, Gray200)
    !c.active -> Triple("PAUSED", Gray600, Gray200)
    c.dryRun -> Triple("SIMULATING", rgb(146, 64, 14), rgb(254, 243, 199))
    else -> Triple("LIVE", rgb(6, 95, 70), rgb(209, 250, 229))
}

@Composable
private fun DripView(onNavigate: (DrillDownView) -> Unit) {
    var config by remember { mutableStateOf<DripConfigRow?>(null) }
    var campaigns by remember { mutableStateOf<List<DripCampaignRow>>(emptyList()) }
    var runs by remember { mutableStateOf<List<DripRunRow>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadError by remember { mutableStateOf<String?>(null) }

    var showCreate by remember { mutableStateOf(false) }
    var newKey by remember { mutableStateOf("") }
    var newName by remember { mutableStateOf("") }
    var newTrigger by remember { mutableStateOf("post_registration") }
    var creating by remember { mutableStateOf(false) }
    var result by remember { mutableStateOf<String?>(null) }
    var resultIsError by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(withBody: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (withBody) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun fetchAll() {
        scope.launch {
            loading = true
            loadError = null
            try {
                val h = authHeaders(false)
                val cfgResp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/config",
                    kotlin.js.json("method" to "GET", "headers" to h).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (cfgResp.ok) {
                    config = adminJson.decodeFromString<List<DripConfigRow>>(cfgResp.text().await().toString()).firstOrNull()
                } else {
                    // A 500 here almost always means the drip migrations were never applied to
                    // this environment's database — the server throws on a PostgREST 404. Say so
                    // rather than rendering an empty screen that looks like "no campaigns yet".
                    loadError = "Could not read drip config (HTTP ${cfgResp.status}). " +
                        "If this is a 500, the drip migrations may not be applied to this database."
                }
                val campResp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaigns",
                    kotlin.js.json("method" to "GET", "headers" to h).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (campResp.ok) {
                    campaigns = adminJson.decodeFromString<List<DripCampaignRow>>(campResp.text().await().toString())
                }
                val runsResp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/runs",
                    kotlin.js.json("method" to "GET", "headers" to h).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (runsResp.ok) {
                    runs = adminJson.decodeFromString<List<DripRunRow>>(runsResp.text().await().toString())
                }
            } catch (e: Exception) {
                loadError = e.message ?: "Failed to load"
            }
            loading = false
        }
    }

    fun patchConfig(body: String) {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/config",
                    kotlin.js.json("method" to "PATCH", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) fetchAll()
                else { resultIsError = true; result = "Could not update settings (HTTP ${resp.status})" }
            } catch (e: Exception) { resultIsError = true; result = e.message }
        }
    }

    fun createCampaign() {
        scope.launch {
            creating = true
            result = null
            try {
                val body = buildJsonObject {
                    put("key", newKey.trim())
                    put("name", newName.trim())
                    put("trigger_type", newTrigger)
                }.toString()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaign",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                if (resp.ok) {
                    resultIsError = false
                    result = "Created “${newName.trim()}”. It is paused and simulating until you arm it."
                    newKey = ""; newName = ""; showCreate = false
                    fetchAll()
                } else {
                    resultIsError = true
                    result = "Could not create: $text"
                }
            } catch (e: Exception) { resultIsError = true; result = e.message }
            creating = false
        }
    }

    LaunchedEffect(Unit) { fetchAll() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Drip Campaigns" to null)

        if (loadError != null) {
            Div(attrs = {
                style {
                    property("background", "#FEF2F2"); property("border", "1px solid #FECACA")
                    borderRadius(12.px); padding(16.px); property("margin-bottom", "20px")
                    color(rgb(153, 27, 27)); fontSize(14.px)
                }
            }) { Text(loadError!!) }
        }

        // ── Global switches ────────────────────────────────────────────────
        CardSection("Global settings", onRefresh = { fetchAll() }) {
            val cfg = config
            if (loading && cfg == null) {
                Div(attrs = { style { color(Gray500); fontSize(14.px) } }) { Text("Loading…") }
            } else if (cfg == null) {
                Div(attrs = { style { color(Gray500); fontSize(14.px) } }) { Text("No config row found.") }
            } else {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                        gap(24.px); property("flex-wrap", "wrap")
                    }
                }) {
                    // The master kill switch. Read inside drip_claim_due, so turning it off
                    // stops every campaign no matter how a run was triggered.
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                            padding(12.px, 16.px); borderRadius(12.px)
                            property("background", if (cfg.enabled) "#ECFDF5" else Gray100.toString())
                            property("border", "1px solid ${if (cfg.enabled) "#A7F3D0" else Gray200.toString()}")
                        }
                    }) {
                        Span(attrs = { style { fontSize(14.px); fontWeight(700); color(if (cfg.enabled) rgb(6, 95, 70) else Gray600) } }) {
                            Text(if (cfg.enabled) "Sending is ON" else "Sending is OFF")
                        }
                        Button(attrs = {
                            onClick { patchConfig("""{"enabled":${!cfg.enabled}}""") }
                            style {
                                padding(7.px, 14.px); fontSize(13.px); fontWeight(600)
                                borderRadius(8.px); property("border", "none"); property("cursor", "pointer")
                                property("background", if (cfg.enabled) Gray600.toString() else Primary.toString())
                                color(Color.white)
                            }
                        }) { Text(if (cfg.enabled) "Turn off" else "Turn on") }
                    }

                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                        Label(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-bottom", "4px") } }) {
                            Text("SEND HOUR (UTC)")
                        }
                        Span(attrs = { style { fontSize(14.px); color(Gray900) } }) {
                            Text("${cfg.sendHourUtc}:00 — set by the pg_cron schedule")
                        }
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                        Label(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-bottom", "4px") } }) {
                            Text("NIGHTLY CAP (ALL CAMPAIGNS)")
                        }
                        Span(attrs = { style { fontSize(14.px); color(Gray900) } }) { Text("${cfg.maxSendsPerNight}") }
                    }
                }
            }
        }

        // ── Campaigns ──────────────────────────────────────────────────────
        CardSection("Campaigns") {
            Div(attrs = { style { property("margin-bottom", "16px") } }) {
                Button(attrs = {
                    onClick { showCreate = !showCreate; result = null }
                    style {
                        padding(9.px, 18.px); fontSize(14.px); fontWeight(600)
                        borderRadius(9.px); property("border", "none"); property("cursor", "pointer")
                        property("background", Primary.toString()); color(Color.white)
                    }
                }) { Text(if (showCreate) "Cancel" else "+ New campaign") }
            }

            if (showCreate) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap")
                        alignItems(AlignItems.FlexEnd)
                        padding(16.px); borderRadius(12.px); property("background", Gray50.toString())
                        property("margin-bottom", "20px")
                    }
                }) {
                    AdminField("NAME", newName, "Welcome sequence", 240) { newName = it }
                    AdminField("KEY (used by the code)", newKey, "post_registration", 220) { newKey = it }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                        Label(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-bottom", "4px") } }) {
                            Text("TRIGGER")
                        }
                        Select(attrs = {
                            onChange { ev -> newTrigger = ev.value ?: "post_registration" }
                            style {
                                padding(9.px, 11.px); fontSize(14.px); borderRadius(9.px)
                                property("border", "1px solid $Gray200"); property("outline", "none")
                            }
                        }) {
                            DRIP_TRIGGER_TYPES.forEach { (k, label) ->
                                Option(k, attrs = { if (k == newTrigger) attr("selected", "") }) { Text(label) }
                            }
                        }
                    }
                    Button(attrs = {
                        if (creating || newKey.isBlank() || newName.isBlank()) disabled()
                        onClick { createCampaign() }
                        style {
                            padding(9.px, 18.px); fontSize(14.px); fontWeight(600)
                            borderRadius(9.px); property("border", "none")
                            property("cursor", if (creating || newKey.isBlank() || newName.isBlank()) "not-allowed" else "pointer")
                            property("background", if (creating || newKey.isBlank() || newName.isBlank()) Gray300.toString() else Primary.toString())
                            color(Color.white)
                        }
                    }) { Text(if (creating) "Creating…" else "Create") }

                    Div(attrs = { style { fontSize(12.px); color(Gray500); width(100.percent) } }) {
                        Text("The key is what the server looks up when it enrols someone — " +
                            "for a registration sequence it must be exactly “post_registration”. " +
                            "New campaigns start paused and simulating.")
                    }
                }
            }

            if (result != null) {
                Div(attrs = {
                    style {
                        padding(12.px, 16.px); borderRadius(10.px); property("margin-bottom", "16px")
                        fontSize(14.px)
                        property("background", if (resultIsError) "#FEF2F2" else "#ECFDF5")
                        color(if (resultIsError) rgb(153, 27, 27) else rgb(6, 95, 70))
                    }
                }) { Text(result!!) }
            }

            if (loading && campaigns.isEmpty()) {
                Div(attrs = { style { color(Gray500); fontSize(14.px) } }) { Text("Loading…") }
            } else if (campaigns.isEmpty()) {
                Div(attrs = { style { color(Gray500); fontSize(14.px); padding(20.px, 0.px) } }) {
                    Text("No campaigns yet. Nothing can send until you create one and arm it.")
                }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", Gray50.toString()) } }) {
                            AdminTh("Name"); AdminTh("Trigger"); AdminTh("Key")
                            AdminTh("State", center = true); AdminTh("Created")
                        }
                    }
                    Tbody {
                        campaigns.forEach { c ->
                            val (label, fg, bg) = dripCampaignState(c, config?.enabled == true)
                            Tr(attrs = {
                                onClick { onNavigate(DrillDownView.DripCampaignDetail(c.id)) }
                                style {
                                    property("cursor", "pointer")
                                    property("border-top", "1px solid $Gray200")
                                }
                            }) {
                                Td(attrs = { style { padding(12.px, 16.px); fontWeight(600); color(Primary) } }) { Text(c.name) }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray700) } }) { Text(dripTriggerLabel(c.triggerType)) }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray500); fontSize(13.px) } }) { Text(c.key) }
                                Td(attrs = { style { padding(12.px, 16.px); property("text-align", "center") } }) {
                                    StatusPill(label, fg, bg)
                                }
                                Td(attrs = { style { padding(12.px, 16.px); color(Gray500); fontSize(13.px) } }) {
                                    Text(formatIsoDate(c.createdAt))
                                }
                            }
                        }
                    }
                }
            }
        }

        // ── Nightly run history ────────────────────────────────────────────
        CardSection("Nightly runs") {
            if (runs.isEmpty()) {
                Div(attrs = { style { color(Gray500); fontSize(14.px) } }) {
                    Text("No runs recorded yet. The schedule fires once a day; a run appears here even when it sends nothing.")
                }
            } else {
                // Two runs on one UTC date from the real schedule would mean the job fired
                // twice — the condition the run lease exists to prevent. Manual and
                // boot-resume runs are excluded so they cannot cry wolf.
                val cronDates = runs.filter { it.trigger == "cron" }.map { it.runDate }
                val duplicateDates = cronDates.groupingBy { it }.eachCount().filter { it.value > 1 }.keys
                if (duplicateDates.isNotEmpty()) {
                    Div(attrs = {
                        style {
                            property("background", "#FEF2F2"); property("border", "1px solid #FECACA")
                            borderRadius(10.px); padding(12.px, 16.px); property("margin-bottom", "16px")
                            color(rgb(153, 27, 27)); fontSize(13.px); fontWeight(600)
                        }
                    }) {
                        Text("The nightly job ran more than once on: ${duplicateDates.joinToString(", ")}. " +
                            "PROD runs three machines — a duplicate run can mean someone received a step twice.")
                    }
                }
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", Gray50.toString()) } }) {
                            AdminTh("Date"); AdminTh("Trigger"); AdminTh("Machine")
                            AdminTh("Started"); AdminTh("Finished"); AdminTh("Error")
                        }
                    }
                    Tbody {
                        runs.take(20).forEach { r ->
                            Tr(attrs = { style { property("border-top", "1px solid $Gray200") } }) {
                                Td(attrs = { style { padding(10.px, 16.px); color(Gray900) } }) { Text(r.runDate) }
                                Td(attrs = { style { padding(10.px, 16.px); color(Gray700); fontSize(13.px) } }) { Text(r.trigger) }
                                Td(attrs = { style { padding(10.px, 16.px); color(Gray500); fontSize(12.px) } }) {
                                    Text(listOfNotNull(r.machineId?.take(8), r.region).joinToString(" / ").ifBlank { "-" })
                                }
                                Td(attrs = { style { padding(10.px, 16.px); color(Gray500); fontSize(13.px) } }) { Text(formatIsoDate(r.startedAt)) }
                                Td(attrs = { style { padding(10.px, 16.px); color(Gray500); fontSize(13.px) } }) {
                                    Text(r.finishedAt?.let { formatIsoDate(it) } ?: "running…")
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(if (r.error != null) rgb(153, 27, 27) else Gray400) } }) {
                                    Text(r.error ?: "—")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun DripCampaignDetailView(campaignId: String, onNavigate: (DrillDownView) -> Unit) {
    var campaign by remember { mutableStateOf<DripCampaignRow?>(null) }
    var globalEnabled by remember { mutableStateOf(false) }
    var steps by remember { mutableStateOf<List<DripStepRow>>(emptyList()) }
    var rules by remember { mutableStateOf<List<DripRuleEntry>>(emptyList()) }
    var tags by remember { mutableStateOf(DripPlaceholderGroups()) }
    var loading by remember { mutableStateOf(true) }
    var result by remember { mutableStateOf<String?>(null) }
    var resultIsError by remember { mutableStateOf(false) }

    // Step editor. editingStepId == null && composerOpen means "adding a new one".
    var composerOpen by remember { mutableStateOf(false) }
    var editingStepId by remember { mutableStateOf<String?>(null) }
    var fOffset by remember { mutableStateOf("") }
    var fSubject by remember { mutableStateOf("") }
    var fBody by remember { mutableStateOf("") }
    var fCtaLabel by remember { mutableStateOf("") }
    var fCtaUrl by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }

    // Live preview, rendered by the SERVER through the real template so it can never drift
    // from what actually gets sent.
    var previewHtml by remember { mutableStateOf<String?>(null) }
    var previewRefreshKey by remember { mutableStateOf(0) }
    var editFromPreview by remember { mutableStateOf(false) }

    var confirmStepId by remember { mutableStateOf<String?>(null) }
    var impactReachable by remember { mutableStateOf<Int?>(null) }
    var impactDueToday by remember { mutableStateOf<Int?>(null) }
    var impactError by remember { mutableStateOf<String?>(null) }
    var impactLoading by remember { mutableStateOf(false) }
    var confirmDeleteId by remember { mutableStateOf<String?>(null) }
    var testEmail by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(withBody: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (withBody) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun fetchAll() {
        scope.launch {
            loading = true
            try {
                val h = authHeaders(false)
                suspend fun get(path: String): String? {
                    val r = kotlinx.browser.window.fetch(
                        "$serverUrl$path",
                        kotlin.js.json("method" to "GET", "headers" to h).unsafeCast<org.w3c.fetch.RequestInit>()
                    ).await()
                    return if (r.ok) r.text().await().toString() else null
                }
                // No GET /campaign/{id} exists — the list is the only source.
                get("/api/admin/drip/campaigns")?.let {
                    campaign = adminJson.decodeFromString<List<DripCampaignRow>>(it).firstOrNull { c -> c.id == campaignId }
                }
                get("/api/admin/drip/config")?.let {
                    globalEnabled = adminJson.decodeFromString<List<DripConfigRow>>(it).firstOrNull()?.enabled == true
                }
                get("/api/admin/drip/campaign/$campaignId/steps")?.let {
                    steps = adminJson.decodeFromString<List<DripStepRow>>(it)
                }
                get("/api/admin/drip/rule-catalog")?.let {
                    rules = adminJson.decodeFromString<List<DripRuleEntry>>(it)
                }
                campaign?.triggerType?.let { tt ->
                    get("/api/admin/drip/placeholders?trigger_type=$tt")?.let {
                        tags = adminJson.decodeFromString<DripPlaceholderGroups>(it)
                    }
                }
            } catch (e: Exception) { resultIsError = true; result = e.message }
            loading = false
        }
    }

    fun patchCampaign(body: String, okMessage: String) {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaign/$campaignId",
                    kotlin.js.json("method" to "PATCH", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) { resultIsError = false; result = okMessage; fetchAll() }
                else { resultIsError = true; result = "Failed (HTTP ${resp.status})" }
            } catch (e: Exception) { resultIsError = true; result = e.message }
        }
    }

    fun fetchPreview() {
        scope.launch {
            try {
                val body = buildJsonObject {
                    put("body_plain", fBody)
                    if (fCtaLabel.isNotBlank()) put("cta_label", fCtaLabel.trim())
                    if (fCtaUrl.isNotBlank()) put("cta_url", fCtaUrl.trim())
                }.toString()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/preview",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                previewHtml = if (resp.ok) resp.text().await().toString() else null
            } catch (_: Exception) { previewHtml = null }
        }
    }

    fun openComposer(step: DripStepRow?) {
        editingStepId = step?.id
        fOffset = step?.dayOffset?.toString() ?: ""
        fSubject = step?.subject ?: ""
        fBody = step?.bodyPlain ?: ""
        fCtaLabel = step?.ctaLabel ?: ""
        fCtaUrl = step?.ctaUrl ?: ""
        composerOpen = true
        result = null
        // Tear the old iframe down. Without this the composer shows the PREVIOUS step's fully
        // rendered, still-editable email for the ~600ms until the debounced re-render lands —
        // and since the iframe is the only body input, that is a window in which typing edits
        // the wrong step. editFromPreview must reset too, or the LaunchedEffect consumes the
        // flag, skips the fetch, and the composer sits on "Rendering…" forever.
        previewHtml = null
        editFromPreview = false
        previewRefreshKey++
    }

    fun saveStep() {
        scope.launch {
            saving = true
            result = null
            try {
                val editing = editingStepId
                val body = buildJsonObject {
                    // day_offset is immutable server-side, so it is only sent on create.
                    if (editing == null) put("day_offset", fOffset.trim().toIntOrNull() ?: 0)
                    put("subject", fSubject.trim())
                    put("body_plain", fBody)
                    put("cta_label", fCtaLabel.trim())
                    put("cta_url", fCtaUrl.trim())
                }.toString()
                val url = if (editing == null) "$serverUrl/api/admin/drip/campaign/$campaignId/step"
                          else "$serverUrl/api/admin/drip/step/$editing"
                val resp = kotlinx.browser.window.fetch(
                    url,
                    kotlin.js.json("method" to if (editing == null) "POST" else "PATCH",
                                   "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                if (resp.ok) {
                    resultIsError = false
                    result = if (editing == null)
                        "Step added at day ${fOffset.trim()}. It is a DRAFT — activate it when the copy is ready."
                    else "Step saved."
                    composerOpen = false; editingStepId = null
                    fetchAll()
                } else {
                    resultIsError = true
                    result = when (resp.status.toInt()) {
                        409 -> "There is already a step at that day."
                        400 -> "Rejected: $text"
                        else -> "Could not save: $text"
                    }
                }
            } catch (e: Exception) { resultIsError = true; result = e.message }
            saving = false
        }
    }

    fun loadImpactThenConfirm(stepId: String) {
        scope.launch {
            impactReachable = null; impactDueToday = null; impactError = null
            confirmStepId = stepId; impactLoading = true
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/step/$stepId/impact",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                if (resp.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? JsonObject
                    impactReachable = (obj?.get("reachable") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    impactDueToday = (obj?.get("due_today") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    if (impactReachable == null) impactError = "The server did not return a number."
                } else {
                    // Never swallow this. The whole point of the confirm is the number; without
                    // it the dialog would sit on "Working out…" with the button live.
                    impactError = "Could not work out who this reaches (HTTP ${resp.status})."
                }
            } catch (e: Exception) {
                impactError = e.message ?: "Could not work out who this reaches."
            }
            impactLoading = false
        }
    }

    fun setStepActive(stepId: String, active: Boolean) {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/step/$stepId",
                    kotlin.js.json("method" to "PATCH", "headers" to authHeaders(true), "body" to """{"active":$active}""")
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) {
                    resultIsError = false
                    result = if (active) "Step activated." else "Step deactivated."
                    confirmStepId = null; fetchAll()
                } else { resultIsError = true; result = "Failed (HTTP ${resp.status})" }
            } catch (e: Exception) { resultIsError = true; result = e.message }
        }
    }

    fun deleteStep(stepId: String) {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/step/$stepId",
                    kotlin.js.json("method" to "DELETE", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) { resultIsError = false; result = "Step deleted."; fetchAll() }
                else { resultIsError = true; result = "Failed (HTTP ${resp.status})" }
            } catch (e: Exception) { resultIsError = true; result = e.message }
        }
    }

    fun sendTest(subject: String, bodyPlain: String, ctaLabel: String?, ctaUrl: String?) {
        scope.launch {
            result = null
            try {
                val body = buildJsonObject {
                    put("to_email", testEmail.trim())
                    put("subject", subject)
                    put("body_plain", bodyPlain)
                    ctaLabel?.takeIf { it.isNotBlank() }?.let { put("cta_label", it) }
                    ctaUrl?.takeIf { it.isNotBlank() }?.let { put("cta_url", it) }
                }.toString()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/test-send",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                val sent = ((adminJson.parseToJsonElement(text) as? JsonObject)
                    ?.get("ok") as? kotlinx.serialization.json.JsonPrimitive)?.content == "true"
                when {
                    resp.ok && sent -> { resultIsError = false; result = "Test sent to ${testEmail.trim()}." }
                    // 200 with ok:false means SMTP refused it or is not configured — the status
                    // code alone would have claimed success for a mail that never left.
                    resp.ok -> { resultIsError = true; result = "The mail server would not send it. Check the server log for [EMAIL]." }
                    else -> { resultIsError = true; result = "Test send failed (HTTP ${resp.status})" }
                }
            } catch (e: Exception) { resultIsError = true; result = e.message }
        }
    }

    /** Ask the preview iframe to drop a tag in at the caret. postMessage is its only channel. */
    fun insertTag(tag: String) {
        try {
            val frame = kotlinx.browser.document.getElementById("drip-preview-frame")
                .unsafeCast<org.w3c.dom.HTMLIFrameElement?>()
            frame?.contentWindow?.postMessage("ff-insert:$tag", "*")
        } catch (_: Exception) {}
    }

    LaunchedEffect(campaignId) { fetchAll() }

    // Debounced re-render. Skipped when the edit came FROM the iframe, or the caret dies mid-word.
    LaunchedEffect(composerOpen, fBody, fCtaLabel, fCtaUrl, previewRefreshKey) {
        if (!composerOpen) return@LaunchedEffect
        if (editFromPreview) { editFromPreview = false; return@LaunchedEffect }
        delay(500)
        fetchPreview()
    }

    DisposableEffect(Unit) {
        val listener: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val s = ev.unsafeCast<org.w3c.dom.MessageEvent>().data?.toString()
            when {
                s != null && s.startsWith("ff-body-final:") -> {
                    editFromPreview = false
                    fBody = s.removePrefix("ff-body-final:")
                    previewRefreshKey++
                }
                s != null && s.startsWith("ff-body:") -> {
                    editFromPreview = true
                    fBody = s.removePrefix("ff-body:")
                }
            }
        }
        kotlinx.browser.window.addEventListener("message", listener)
        onDispose { kotlinx.browser.window.removeEventListener("message", listener) }
    }

    PageShell {
        val c = campaign
        BreadcrumbNav(
            "Dashboard" to { onNavigate(DrillDownView.Overview) },
            "Drip Campaigns" to { onNavigate(DrillDownView.Drip) },
            (c?.name ?: "Campaign") to null
        )

        if (result != null) {
            Div(attrs = {
                style {
                    padding(12.px, 16.px); borderRadius(10.px); property("margin-bottom", "16px"); fontSize(14.px)
                    property("background", if (resultIsError) "#FEF2F2" else "#ECFDF5")
                    color(if (resultIsError) rgb(153, 27, 27) else rgb(6, 95, 70))
                }
            }) { Text(result!!) }
        }

        if (loading && c == null) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Loading…") }
            return@PageShell
        }
        if (c == null) {
            P(attrs = { style { color(Gray400); fontSize(14.px) } }) { Text("Campaign not found.") }
            return@PageShell
        }

        // ── Arming ─────────────────────────────────────────────────────────
        CardSection("Status", onRefresh = { fetchAll() }) {
            val (label, fg, bg) = dripCampaignState(c, globalEnabled)
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(16.px); property("flex-wrap", "wrap") } }) {
                StatusPill(label, fg, bg)
                Span(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                    Text(dripTriggerLabel(c.triggerType) + " · key: " + c.key)
                }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("margin-top", "16px"); property("flex-wrap", "wrap") } }) {
                Button(attrs = {
                    onClick { patchCampaign("""{"active":${!c.active}}""", if (c.active) "Campaign paused." else "Campaign resumed.") }
                    style {
                        padding(9.px, 18.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                        property("border", "none"); cursor("pointer")
                        property("background", if (c.active) Gray600.toString() else Primary.toString()); color(Color.white)
                    }
                }) { Text(if (c.active) "Pause campaign" else "Resume campaign") }

                Button(attrs = {
                    onClick { patchCampaign("""{"dry_run":${!c.dryRun}}""", if (c.dryRun) "Now sending for real." else "Back to simulation.") }
                    style {
                        padding(9.px, 18.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                        property("border", "1px solid ${if (c.dryRun) "#DC2626" else Gray300}")
                        cursor("pointer"); property("background", "white")
                        color(if (c.dryRun) rgb(153, 27, 27) else Gray700)
                    }
                }) { Text(if (c.dryRun) "Stop simulating — send for real" else "Switch back to simulation") }
            }
            if (!globalEnabled) {
                P(attrs = { style { fontSize(13.px); color(rgb(146, 64, 14)); property("margin", "12px 0 0") } }) {
                    Text("Global sending is off, so this campaign cannot send regardless of the switches above.")
                }
            }
        }


        // ── Timing & limits ────────────────────────────────────────────────
        // These were constants until now: the two predicate windows lived in trigger_config with
        // no editor, and the event cooldowns were literals in Kotlin. Both are here so the whole
        // cadence is data, which was the point of the feature.
        run {
            fun cfgInt(key: String): String? =
                (c.triggerConfig[key] as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it.isNotBlank() }

            var windowDays by remember(c.id, c.triggerConfig.toString()) {
                mutableStateOf(cfgInt(if (c.triggerType == "subscription_lapsing") "within_days" else "dormant_days") ?: "")
            }
            var cooldown by remember(c.id, c.triggerConfig.toString()) { mutableStateOf(cfgInt("reenroll_cooldown_days") ?: "") }
            val instantOn = (c.triggerConfig["instant_first_step"] as? kotlinx.serialization.json.JsonPrimitive)
                ?.content?.toBooleanStrictOrNull() ?: false
            var grace by remember(c.id) { mutableStateOf(c.catchupGraceDays.toString()) }
            var maxEnroll by remember(c.id) { mutableStateOf(c.maxEnrollPerRun.toString()) }
            var maxSends by remember(c.id) { mutableStateOf(c.maxSendsPerRun.toString()) }

            val windowKey = when (c.triggerType) {
                "subscription_lapsing" -> "within_days"
                "inactive_reengagement" -> "dormant_days"
                else -> null
            }
            val windowLabel = when (c.triggerType) {
                "subscription_lapsing" -> "SEND WHEN ACCESS ENDS WITHIN (DAYS)"
                "inactive_reengagement" -> "COUNT AS QUIET AFTER (DAYS)"
                else -> null
            }
            val windowDefault = if (c.triggerType == "subscription_lapsing") "5" else "21"
            val cooldownDefault = when (c.triggerType) {
                "post_analysis" -> "30"
                "post_subscribe" -> "3650"
                "post_registration" -> "0"
                else -> "90"
            }

            CardSection("Timing & limits") {
                P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0 0 16px") } }) {
                    Text("Leave a box empty to keep the built-in default, shown as the placeholder.")
                }
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart); gap(12.px)
                        padding(14.px); borderRadius(10.px); property("margin-bottom", "18px")
                        property("background", if (instantOn) PrimaryPale.toString() else Gray50.toString())
                        property("border", "1px solid ${if (instantOn) Primary.toString() else Gray200.toString()}")
                    }
                }) {
                    CheckboxInput(checked = instantOn, attrs = {
                        onChange { ev ->
                            val cfg = buildJsonObject {
                                c.triggerConfig.forEach { (k, v) -> if (k != "instant_first_step") put(k, v) }
                                put("instant_first_step", ev.value)
                            }
                            patchCampaign(
                                buildJsonObject { put("trigger_config", cfg) }.toString(),
                                if (ev.value) "Day 0 will now send as soon as someone is enrolled."
                                else "Day 0 will wait for the nightly run."
                            )
                        }
                        style { property("margin-top", "3px"); cursor("pointer") }
                    })
                    Div {
                        Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) {
                            Text("Send the first step immediately")
                        }
                        Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-top", "3px"); property("line-height", "1.6") } }) {
                            Text("Off by default, a day-0 step goes out on the next nightly run — so it " +
                                "lands anywhere from minutes to 23 hours after the person is enrolled, " +
                                "depending on the time of day. Turn this on and it goes out at once, which " +
                                "is usually what you want for a welcome email.")
                        }
                        if (instantOn) {
                            Div(attrs = { style { fontSize(12.px); color(rgb(146, 64, 14)); property("margin-top", "6px"); property("line-height", "1.6") } }) {
                                Text("Immediate sends are best-effort: if one fails it is not retried the " +
                                    "next night, and it does not count toward the nightly volume cap. " +
                                    "Every other guard still applies.")
                            }
                        }
                    }
                }

                Div(attrs = { style { display(DisplayStyle.Flex); gap(14.px); property("flex-wrap", "wrap"); alignItems(AlignItems.FlexEnd) } }) {
                    if (windowKey != null && windowLabel != null) {
                        AdminField(windowLabel, windowDays, windowDefault, 230) { windowDays = it }
                    }
                    AdminField("DON'T RE-ENROL THE SAME PERSON FOR (DAYS)", cooldown, cooldownDefault, 300) { cooldown = it }
                    AdminField("SEND LATE STEPS UP TO (DAYS)", grace, "2", 220) { grace = it }
                    AdminField("MAX ENROLLED PER NIGHT", maxEnroll, "500", 190) { maxEnroll = it }
                    AdminField("MAX EMAILS PER NIGHT", maxSends, "500", 185) { maxSends = it }

                    Button(attrs = {
                        onClick {
                            // trigger_config is replaced wholesale, so preserve any key this
                            // screen does not know about rather than silently dropping it.
                            val cfg = buildJsonObject {
                                c.triggerConfig.forEach { (k, v) ->
                                    if (k != "reenroll_cooldown_days" && k != windowKey && k != "instant_first_step") put(k, v)
                                }
                                // Preserved explicitly: the checkbox writes it, and Save rebuilds
                                // trigger_config wholesale, so without this a Save would silently
                                // switch instant back off.
                                put("instant_first_step", instantOn)
                                windowDays.trim().toIntOrNull()?.let { n -> windowKey?.let { put(it, n) } }
                                cooldown.trim().toIntOrNull()?.let { put("reenroll_cooldown_days", it) }
                            }
                            val body = buildJsonObject {
                                put("trigger_config", cfg)
                                grace.trim().toIntOrNull()?.let { put("catchup_grace_days", it) }
                                maxEnroll.trim().toIntOrNull()?.let { put("max_enroll_per_run", it) }
                                maxSends.trim().toIntOrNull()?.let { put("max_sends_per_run", it) }
                            }.toString()
                            patchCampaign(body, "Timing saved.")
                        }
                        style {
                            padding(9.px, 20.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                            property("border", "none"); cursor("pointer")
                            property("background", Primary.toString()); color(Color.white)
                        }
                    }) { Text("Save timing") }
                }

                Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "14px"); property("line-height", "1.6") } }) {
                    Text(
                        when (c.triggerType) {
                            "post_analysis" ->
                                "Someone who analyses a title re-enters this sequence at most once per " +
                                "cooldown period — not once per analysis. They also cannot re-enter while " +
                                "they are still partway through it."
                            "post_registration" ->
                                "A cooldown of 0 means someone can only be in this sequence once at a time. " +
                                "They re-enter only if a previous run finished or was stopped."
                            "post_subscribe" ->
                                "The default is effectively “once ever”. Only a FIRST payment enrols anyone — " +
                                "renewals never do — so this cooldown is a second line of defence."
                            "subscription_lapsing" ->
                                "Checked nightly. Someone is picked up once their access is inside the window " +
                                "and still in the future — an expiry that has already passed is not caught here."
                            "inactive_reengagement" ->
                                "“Quiet” means no sign-in, analysis or re-opened result for this many days, AND " +
                                "not currently subscribed. Note last-seen tracking only began on 25 Aug 2026, so " +
                                "for now this leans on sign-in dates and will sharpen over the coming weeks."
                            else -> ""
                        }
                    )
                }

                Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "10px") } }) {
                    Text("“Send late steps up to” is the catch-up window: if a run is missed or the campaign " +
                        "is paused, a step more than this many days overdue is recorded as skipped rather " +
                        "than sent, so nobody gets a stale burst on resume.")
                }
            }
        }

        // ── Stop rules ─────────────────────────────────────────────────────
        CardSection("Stop rules") {
            P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0 0 14px") } }) {
                Text("When to drop someone out of the sequence. Unsubscribes, deleted accounts and " +
                    "unconfirmed addresses are always handled and are not listed here.")
            }
            if (rules.isEmpty()) {
                P(attrs = { style { fontSize(13.px); color(Gray400); property("margin", "0") } }) { Text("Loading rules…") }
            } else {
                rules.forEach { rule ->
                    val on = (c.stopRules[rule.key] as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: false
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart); gap(12.px)
                            padding(12.px, 0.px); property("border-bottom", "1px solid $Gray200")
                        }
                    }) {
                        CheckboxInput(checked = on, attrs = {
                            onChange { ev ->
                                val next = buildJsonObject {
                                    rules.forEach { r ->
                                        val cur = (c.stopRules[r.key] as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() ?: false
                                        put(r.key, if (r.key == rule.key) ev.value else cur)
                                    }
                                }
                                patchCampaign(buildJsonObject { put("stop_rules", next) }.toString(), "Stop rules updated.")
                            }
                            style { property("margin-top", "3px"); property("cursor", "pointer") }
                        })
                        Div {
                            Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) { Text(rule.label) }
                            Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "2px") } }) { Text(rule.description) }
                        }
                    }
                }
                // The one combination that silently defeats its own campaign.
                if (c.triggerType == "inactive_reengagement" &&
                    ((c.stopRules["became_active"] as? kotlinx.serialization.json.JsonPrimitive)?.content?.toBooleanStrictOrNull() == true)) {
                    Div(attrs = {
                        style {
                            property("background", "#FEF2F2"); property("border", "1px solid #FECACA")
                            borderRadius(10.px); padding(12.px, 16.px); property("margin-top", "14px")
                            color(rgb(153, 27, 27)); fontSize(13.px)
                        }
                    }) {
                        Text("“They come back to the app” is on for a re-engagement campaign. " +
                            "That stop suppresses exactly the people this campaign exists to reach — turn it off.")
                    }
                }
            }
        }

        // ── Steps ──────────────────────────────────────────────────────────
        CardSection("Steps") {
            P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0 0 16px") } }) {
                Text("Each step fires a set number of days after someone is enrolled. Offsets are " +
                    "arbitrary — 1, 3, 7 or 2 then 15, whatever you want. A day offset cannot be " +
                    "changed after the step exists; delete it and add it at the new day instead.")
            }

            Button(attrs = {
                onClick { if (composerOpen && editingStepId == null) composerOpen = false else openComposer(null) }
                style {
                    padding(9.px, 18.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                    property("border", "none"); cursor("pointer")
                    property("background", Primary.toString()); color(Color.white)
                    property("margin-bottom", "16px")
                }
            }) { Text(if (composerOpen && editingStepId == null) "Cancel" else "+ Add step") }

            if (composerOpen) {
                Div(attrs = {
                    style {
                        padding(16.px); borderRadius(12.px); property("background", Gray50.toString())
                        property("margin-bottom", "20px"); property("border", "1px solid $Gray200")
                    }
                }) {
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "12px") } }) {
                        if (editingStepId == null) {
                            AdminField("DAY OFFSET", fOffset, "3", 110) { fOffset = it }
                        } else {
                            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                                Label(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-bottom", "4px") } }) { Text("DAY OFFSET") }
                                Span(attrs = { style { fontSize(14.px); color(Gray500); padding(9.px, 0.px) } }) {
                                    Text("Day $fOffset · fixed")
                                }
                            }
                        }
                        AdminField("SUBJECT", fSubject, "Still checking titles by hand?", 400) { fSubject = it }
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap"); property("margin-bottom", "14px") } }) {
                        AdminField("BUTTON LABEL (optional)", fCtaLabel, "Open Fitrah Filter", 220) { fCtaLabel = it }
                        AdminField("BUTTON LINK (optional)", fCtaUrl, "https://fitrahfilter.com", 300) { fCtaUrl = it }
                    }

                    // Only shown for a day-0 step, because that is the only case where it changes
                    // anything. It writes the same campaign-level trigger_config the Timing card
                    // does — surfaced twice on purpose, since this is where the question occurs to
                    // you and the other card is where you go looking for settings.
                    if (fOffset.trim() == "0") {
                        val instantHere = (c.triggerConfig["instant_first_step"] as? kotlinx.serialization.json.JsonPrimitive)
                            ?.content?.toBooleanStrictOrNull() ?: false
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.FlexStart); gap(12.px)
                                padding(14.px); borderRadius(10.px); property("margin-bottom", "14px")
                                property("background", if (instantHere) PrimaryPale.toString() else "white")
                                property("border", "1px solid ${if (instantHere) Primary.toString() else Gray200.toString()}")
                            }
                        }) {
                            CheckboxInput(checked = instantHere, attrs = {
                                onChange { ev ->
                                    val cfg = buildJsonObject {
                                        c.triggerConfig.forEach { (k, v) -> if (k != "instant_first_step") put(k, v) }
                                        put("instant_first_step", ev.value)
                                    }
                                    patchCampaign(
                                        buildJsonObject { put("trigger_config", cfg) }.toString(),
                                        if (ev.value) "Day 0 will now send the moment someone is enrolled."
                                        else "Day 0 will wait for the nightly run."
                                    )
                                }
                                style { property("margin-top", "3px"); cursor("pointer") }
                            })
                            Div {
                                Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) {
                                    Text("Send this the moment someone is enrolled")
                                }
                                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-top", "3px"); property("line-height", "1.6") } }) {
                                    Text("Day 0 normally goes out on the next nightly run, which is 13:00 UTC — " +
                                        "so depending on when someone signs up it can be anywhere from a few " +
                                        "minutes to 23 hours later. Tick this and it goes out straight away. " +
                                        "Applies to the whole campaign, not just this step.")
                                }
                            }
                        }
                    }

                    // Merge tags. Served by the server so the UI can never offer a tag the send
                    // path cannot resolve; context tags depend on the trigger type.
                    val allTags = tags.context + tags.user
                    if (allTags.isNotEmpty()) {
                        Div(attrs = { style { property("margin-bottom", "14px") } }) {
                            Label(attrs = { style { fontSize(11.px); fontWeight(600); color(Gray500) } }) { Text("INSERT A DETAIL") }
                            Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); property("flex-wrap", "wrap"); property("margin-top", "6px") } }) {
                                allTags.forEach { t ->
                                    Span(attrs = {
                                        attr("title", t.description + "  e.g. " + t.sample)
                                        onClick { insertTag("{{${t.key}}}") }
                                        style {
                                            fontSize(12.px); fontWeight(600); padding(4.px, 10.px)
                                            borderRadius(20.px); cursor("pointer")
                                            property("background", if (tags.context.contains(t)) AccentLight.toString() else PrimaryPale.toString())
                                            color(if (tags.context.contains(t)) rgb(146, 64, 14) else Primary)
                                            property("border", "1px solid ${if (tags.context.contains(t)) Accent.toString() else PrimaryPale.toString()}")
                                        }
                                    }) { Text(t.label) }
                                }
                            }
                            Div(attrs = { style { fontSize(11.px); color(Gray400); property("margin-top", "8px") } }) {
                                Text("Each falls back when a recipient is missing that detail — override with {{first_name|friend}}. Works in the subject too.")
                            }
                        }
                    }

                    // Mock inbox chrome — subject and sender live in the mail client, not the body,
                    // so they would be invisible inside the iframe alone.
                    Div(attrs = {
                        style {
                            property("border", "1px solid $Gray200"); borderRadius(12.px)
                            property("overflow", "hidden"); backgroundColor(Color.white)
                        }
                    }) {
                        Div(attrs = {
                            style { padding(12.px, 16.px); property("border-bottom", "1px solid $Gray200"); backgroundColor(Color.white) }
                        }) {
                            Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Primary); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "6px") } }) {
                                Text("✎ Click the message below to edit — this is exactly what recipients get")
                            }
                            Div(attrs = { style { fontSize(14.px); color(Gray900); fontWeight(600) } }) {
                                Text(fSubject.ifBlank { "(no subject)" })
                            }
                            Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "2px") } }) {
                                Text("${c.fromName.ifBlank { "Yusuf at Fitrah Filter" }} · yusuf@fitrahfilter.com")
                            }
                        }
                        val html = previewHtml
                        if (html == null) {
                            P(attrs = { style { fontSize(13.px); color(Gray400); padding(20.px, 16.px); property("margin", "0") } }) {
                                Text("Rendering…")
                            }
                        } else {
                            TagElement<org.w3c.dom.HTMLIFrameElement>("iframe", {
                                attr("srcdoc", html.replace("</body>", "$PREVIEW_EDITOR_SCRIPT</body>"))
                                attr("id", "drip-preview-frame")
                                attr("title", "Email preview — the message area is editable")
                                // allow-scripts ONLY: opaque origin, so postMessage is its one way out.
                                attr("sandbox", "allow-scripts")
                                style { width(100.percent); height(640.px); property("border", "none"); property("display", "block") }
                            }) {}
                        }
                    }

                    Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.Center); property("margin-top", "14px"); property("flex-wrap", "wrap") } }) {
                        Button(attrs = {
                            if (saving || fSubject.isBlank() || fBody.isBlank() ||
                                (editingStepId == null && fOffset.trim().toIntOrNull() == null)) disabled()
                            onClick { saveStep() }
                            style {
                                padding(9.px, 20.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                                property("border", "none"); cursor("pointer")
                                property("background", Primary.toString()); color(Color.white)
                            }
                        }) { Text(if (saving) "Saving…" else if (editingStepId == null) "Add step" else "Save changes") }

                        Button(attrs = {
                            onClick { composerOpen = false; editingStepId = null }
                            style {
                                padding(9.px, 18.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                                property("border", "1px solid $Gray300"); cursor("pointer")
                                property("background", "white"); color(Gray700)
                            }
                        }) { Text("Cancel") }

                        AdminField("SEND A TEST TO", testEmail, "you@example.com", 220) { testEmail = it }
                        Button(attrs = {
                            if (testEmail.isBlank()) disabled()
                            onClick { sendTest(fSubject, fBody, fCtaLabel, fCtaUrl) }
                            style {
                                padding(9.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                property("border", "1px solid $Gray300")
                                cursor(if (testEmail.isBlank()) "not-allowed" else "pointer")
                                property("background", "white"); color(Gray700)
                            }
                        }) { Text("Send test") }
                    }
                }
            }

            if (steps.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px); property("margin", "16px 0 0") } }) {
                    Text("No steps yet. A campaign with no active steps sends nothing.")
                }
            } else {
                steps.sortedBy { it.dayOffset }.forEach { s ->
                    Div(attrs = {
                        style {
                            padding(16.px); borderRadius(12.px); property("margin-bottom", "12px")
                            property("border", "1px solid ${if (s.active) "#A7F3D0" else Gray200.toString()}")
                            property("background", if (s.active) "#F6FFFB" else "white")
                        }
                    }) {
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); property("flex-wrap", "wrap") } }) {
                            Span(attrs = {
                                style {
                                    fontSize(13.px); fontWeight(700); color(Primary)
                                    padding(4.px, 10.px); borderRadius(8.px); property("background", PrimaryPale.toString())
                                }
                            }) { Text("Day ${s.dayOffset}") }
                            Span(attrs = { style { fontSize(15.px); fontWeight(600); color(Gray900) } }) { Text(s.subject) }
                            if (s.active) StatusPill("ACTIVE", rgb(6, 95, 70), rgb(209, 250, 229))
                            else StatusPill("DRAFT", Gray600, Gray200)
                        }
                        Div(attrs = { style { fontSize(13.px); color(Gray600); property("margin-top", "10px"); property("white-space", "pre-wrap") } }) {
                            Text(s.bodyPlain.take(240) + if (s.bodyPlain.length > 240) "…" else "")
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); property("margin-top", "14px"); property("flex-wrap", "wrap") } }) {
                            Button(attrs = {
                                onClick { openComposer(s) }
                                style {
                                    padding(7.px, 14.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                    property("border", "1px solid $Gray300"); cursor("pointer")
                                    property("background", "white"); color(Gray700)
                                }
                            }) { Text("Edit") }
                            Button(attrs = {
                                onClick { if (s.active) setStepActive(s.id, false) else loadImpactThenConfirm(s.id) }
                                style {
                                    padding(7.px, 14.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                    property("border", "1px solid $Gray300"); cursor("pointer")
                                    property("background", "white"); color(Gray700)
                                }
                            }) { Text(if (s.active) "Deactivate" else "Activate") }
                            Button(attrs = {
                                onClick { confirmDeleteId = if (confirmDeleteId == s.id) null else s.id }
                                style {
                                    padding(7.px, 14.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                    property("border", "1px solid #FECACA"); cursor("pointer")
                                    property("background", "white"); color(rgb(153, 27, 27))
                                }
                            }) { Text("Delete") }
                        }

                        if (confirmDeleteId == s.id) {
                            Div(attrs = {
                                style {
                                    property("margin-top", "14px"); padding(14.px); borderRadius(10.px)
                                    property("background", "#FEF2F2"); property("border", "1px solid #FECACA")
                                }
                            }) {
                                Div(attrs = { style { fontSize(14.px); fontWeight(700); color(rgb(153, 27, 27)); property("margin-bottom", "6px") } }) {
                                    Text("Delete day ${s.dayOffset} for good?")
                                }
                                Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "12px") } }) {
                                    Text("This also erases the record of every email already sent for this step, " +
                                        "including the unsubscribe links in mail people have already received. " +
                                        "If you only want it to stop going out, deactivate it instead.")
                                }
                                Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px) } }) {
                                    Button(attrs = {
                                        onClick { confirmDeleteId = null; deleteStep(s.id) }
                                        style {
                                            padding(8.px, 16.px); fontSize(13.px); fontWeight(700); borderRadius(8.px)
                                            property("border", "none"); cursor("pointer")
                                            property("background", "#B91C1C"); color(Color.white)
                                        }
                                    }) { Text("Yes, delete it") }
                                    Button(attrs = {
                                        onClick { confirmDeleteId = null }
                                        style {
                                            padding(8.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                            property("border", "1px solid $Gray300"); cursor("pointer")
                                            property("background", "white"); color(Gray700)
                                        }
                                    }) { Text("Keep it") }
                                }
                            }
                        }

                        if (confirmStepId == s.id) {
                            Div(attrs = {
                                style {
                                    property("margin-top", "14px"); padding(14.px); borderRadius(10.px)
                                    property("background", AccentLight.toString()); property("border", "1px solid ${Accent}")
                                }
                            }) {
                                Div(attrs = { style { fontSize(14.px); fontWeight(700); color(Gray900); property("margin-bottom", "6px") } }) {
                                    Text("Activate this step?")
                                }
                                Div(attrs = {
                                    style {
                                        fontSize(13.px); property("margin-bottom", "12px")
                                        color(if (impactError != null) rgb(153, 27, 27) else Gray700)
                                    }
                                }) {
                                    Text(
                                        when {
                                            impactError != null -> impactError!! + " Reload before activating — arming without knowing the reach is the thing this confirm exists to prevent."
                                            impactLoading || impactReachable == null -> "Working out how many people this reaches…"
                                            else -> "$impactReachable enrolment(s) can still receive this, " +
                                                "${impactDueToday ?: 0} of them on tonight's run. " +
                                                "It can never reach anyone already past day ${s.dayOffset}."
                                        }
                                    )
                                }
                                Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px) } }) {
                                    // Held closed until the number is actually in hand. Previously
                                    // this was live while the panel still read "Working out…", and a
                                    // failed fetch left it live indefinitely.
                                    val impactReady = impactReachable != null && impactError == null && !impactLoading
                                    Button(attrs = {
                                        if (!impactReady) disabled()
                                        onClick { setStepActive(s.id, true) }
                                        style {
                                            padding(8.px, 16.px); fontSize(13.px); fontWeight(700); borderRadius(8.px)
                                            property("border", "none")
                                            cursor(if (impactReady) "pointer" else "not-allowed")
                                            property("background", if (impactReady) Primary.toString() else Gray300.toString())
                                            color(Color.white)
                                        }
                                    }) { Text("Yes, activate") }
                                    Button(attrs = {
                                        onClick { confirmStepId = null }
                                        style {
                                            padding(8.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                            property("border", "1px solid $Gray300"); cursor("pointer")
                                            property("background", "white"); color(Gray700)
                                        }
                                    }) { Text("Cancel") }
                                }
                            }
                        }
                    }
                }
            }
        }

        DripBackfillPanel(campaignId, c) { msg, isErr -> result = msg; resultIsError = isErr }
        DripActivityPanel(campaignId)
    }
}

/**
 * The launch backfill: enrolling people who already exist, rather than waiting for new ones.
 *
 * This is the highest-consequence control in the feature — it is the one action that mails a
 * large number of real people at once, and nothing un-sends. So it is deliberately a staircase:
 * dry run (count + sample, changes nothing) → type the count → type the campaign key → start.
 * The server enforces the last two independently, so skipping the UI does not skip the gates.
 */
@Composable
private fun DripBackfillPanel(campaignId: String, campaign: DripCampaignRow, onResult: (String, Boolean) -> Unit) {
    var runs by remember { mutableStateOf<List<DripBackfillRun>>(emptyList()) }
    var dryTotal by remember { mutableStateOf<Int?>(null) }
    var drySample by remember { mutableStateOf<List<String>>(emptyList()) }
    var confirmCount by remember { mutableStateOf("") }
    var confirmKey by remember { mutableStateOf("") }
    var pace by remember { mutableStateOf("200") }
    var busy by remember { mutableStateOf(false) }
    var dryError by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(withBody: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (withBody) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun fetchRuns() {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaign/$campaignId/backfill",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) runs = adminJson.decodeFromString<List<DripBackfillRun>>(resp.text().await().toString())
            } catch (_: Exception) {}
        }
    }

    fun dryRun() {
        scope.launch {
            busy = true; dryError = null; dryTotal = null; drySample = emptyList()
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaign/$campaignId/backfill/dry-run",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to "{}")
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                if (resp.ok) {
                    val obj = adminJson.parseToJsonElement(text) as? JsonObject
                    dryTotal = (obj?.get("total") as? kotlinx.serialization.json.JsonPrimitive)?.content?.toIntOrNull()
                    drySample = (obj?.get("sample") as? kotlinx.serialization.json.JsonArray)
                        ?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: emptyList()
                } else {
                    // The server refuses a backfill for any trigger other than post_registration.
                    dryError = text
                }
            } catch (e: Exception) { dryError = e.message }
            busy = false
        }
    }

    fun start() {
        scope.launch {
            busy = true
            try {
                val body = buildJsonObject {
                    put("confirm_count", confirmCount.trim().toIntOrNull() ?: -1)
                    put("confirm_key", confirmKey.trim())
                    put("pace_per_day", pace.trim().toIntOrNull() ?: 200)
                }.toString()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/campaign/$campaignId/backfill/start",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(true), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                if (resp.ok) {
                    onResult("Backfill started. It enrols up to ${pace.trim()} people a day.", false)
                    confirmCount = ""; confirmKey = ""; dryTotal = null; fetchRuns()
                } else onResult("Could not start: $text", true)
            } catch (e: Exception) { onResult(e.message ?: "Failed", true) }
            busy = false
        }
    }

    fun setMode(runId: String, mode: String) {
        scope.launch {
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/drip/backfill/$runId/$mode",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) { onResult("Backfill ${mode}d.", false); fetchRuns() }
                else onResult("Failed (HTTP ${resp.status})", true)
            } catch (e: Exception) { onResult(e.message ?: "Failed", true) }
        }
    }

    LaunchedEffect(campaignId) { fetchRuns() }

    CardSection("Backfill existing users", onRefresh = { fetchRuns() }) {
        val live = runs.firstOrNull { it.status == "running" || it.status == "paused" }

        if (live != null) {
            Div(attrs = {
                style {
                    padding(16.px); borderRadius(12.px); property("margin-bottom", "16px")
                    property("background", PrimaryPale.toString()); property("border", "1px solid $Primary")
                }
            }) {
                Div(attrs = { style { fontSize(15.px); fontWeight(700); color(Primary); property("margin-bottom", "6px") } }) {
                    Text(if (live.status == "paused") "Backfill paused" else "Backfill running")
                }
                Div(attrs = { style { fontSize(13.px); color(Gray700); property("margin-bottom", "12px") } }) {
                    Text("${live.enrolledCount} of ${live.totalMatched} enrolled · " +
                        "${live.enrolledToday} today · pace ${live.pacePerDay}/day")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); property("flex-wrap", "wrap") } }) {
                    if (live.status == "running") {
                        Button(attrs = {
                            onClick { setMode(live.id, "pause") }
                            style {
                                padding(8.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                property("border", "1px solid $Gray300"); cursor("pointer")
                                property("background", "white"); color(Gray700)
                            }
                        }) { Text("Pause enrolling") }
                    } else {
                        Button(attrs = {
                            onClick { setMode(live.id, "resume") }
                            style {
                                padding(8.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                                property("border", "none"); cursor("pointer")
                                property("background", Primary.toString()); color(Color.white)
                            }
                        }) { Text("Resume") }
                    }
                    Button(attrs = {
                        onClick { setMode(live.id, "abort") }
                        style {
                            padding(8.px, 16.px); fontSize(13.px); fontWeight(600); borderRadius(8.px)
                            property("border", "1px solid #FECACA"); cursor("pointer")
                            property("background", "white"); color(rgb(153, 27, 27))
                        }
                    }) { Text("Stop enrolling more people") }
                }
                // Abort halts RECRUITMENT only. Saying so here matters: the obvious reading of a
                // red stop button is "stop everything", and it does not.
                Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-top", "10px") } }) {
                    Text("These only control who gets ADDED. People already enrolled keep receiving " +
                        "their sequence — to stop that too, pause the campaign at the top of this page.")
                }
            }
        }

        if (campaign.triggerType != "post_registration") {
            P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0") } }) {
                Text("Backfill only applies to registration sequences. For this trigger, people are " +
                    "enrolled automatically when the event happens.")
            }
        } else {
            P(attrs = { style { fontSize(13.px); color(Gray500); property("margin", "0 0 14px") } }) {
                Text("Enrols people who already have accounts. Everyone starts at step 1 — the clock " +
                    "runs from when they are enrolled, not from when they registered, so nobody gets " +
                    "a burst of back-dated emails.")
            }

            Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                Button(attrs = {
                    if (busy) disabled()
                    onClick { dryRun() }
                    style {
                        padding(9.px, 18.px); fontSize(14.px); fontWeight(600); borderRadius(9.px)
                        property("border", "1px solid $Primary"); cursor("pointer")
                        property("background", "white"); color(Primary)
                    }
                }) { Text(if (busy) "Checking…" else "1. Dry run — who would this reach?") }
            }

            if (dryError != null) {
                Div(attrs = {
                    style {
                        property("background", "#FEF2F2"); property("border", "1px solid #FECACA")
                        borderRadius(10.px); padding(12.px, 16.px); property("margin-top", "14px")
                        color(rgb(153, 27, 27)); fontSize(13.px)
                    }
                }) { Text(dryError!!) }
            }

            val total = dryTotal
            if (total != null) {
                Div(attrs = {
                    style {
                        property("background", AccentLight.toString()); property("border", "1px solid $Accent")
                        borderRadius(12.px); padding(16.px); property("margin-top", "14px")
                    }
                }) {
                    Div(attrs = { style { fontSize(20.px); fontWeight(800); color(Gray900) } }) {
                        Text("$total people would be enrolled")
                    }
                    if (drySample.isNotEmpty()) {
                        Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-top", "8px"); property("word-break", "break-all") } }) {
                            Text("For example: " + drySample.take(8).joinToString(", "))
                        }
                    }
                    Div(attrs = {
                        style {
                            fontSize(13.px); color(rgb(146, 64, 14)); fontWeight(600)
                            property("margin-top", "12px")
                        }
                    }) {
                        Text("This sends real email to real people and cannot be undone. " +
                            "Type the number and the campaign key to confirm.")
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.FlexEnd); property("flex-wrap", "wrap"); property("margin-top", "12px") } }) {
                        AdminField("TYPE $total", confirmCount, "$total", 120) { confirmCount = it }
                        AdminField("TYPE “${campaign.key}”", confirmKey, campaign.key, 220) { confirmKey = it }
                        AdminField("PEOPLE PER DAY", pace, "200", 130) { pace = it }
                        Button(attrs = {
                            // campaign.active is part of `ready` because drip_backfill_start
                            // refuses a paused campaign outright, and a campaign is created
                            // paused — so this is the first thing a new campaign hits.
                            val ready = confirmCount.trim() == total.toString() &&
                                confirmKey.trim() == campaign.key && campaign.active && !busy
                            if (!ready) disabled()
                            onClick { start() }
                            style {
                                padding(10.px, 20.px); fontSize(14.px); fontWeight(700); borderRadius(9.px)
                                property("border", "none")
                                cursor(if (ready) "pointer" else "not-allowed")
                                property("background", if (ready) "#B91C1C" else Gray300.toString())
                                color(Color.white)
                            }
                        }) { Text("Start backfill") }
                    }
                    if (!campaign.active) {
                        Div(attrs = { style { fontSize(13.px); color(rgb(146, 64, 14)); fontWeight(600); property("margin-top", "10px") } }) {
                            Text("This campaign is paused, so a backfill would be refused. " +
                                "Resume it at the top of this page first.")
                        }
                    }
                    if (campaign.dryRun) {
                        Div(attrs = { style { fontSize(12.px); color(Gray600); property("margin-top", "10px") } }) {
                            Text("This campaign is still simulating, so the backfill will enrol people " +
                                "and rehearse the sends without mailing anyone. That is a good way to start.")
                        }
                    }
                }
            }
        }

        if (runs.isNotEmpty()) {
            Div(attrs = { style { property("margin-top", "20px") } }) {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", Gray50.toString()) } }) {
                            AdminTh("Started"); AdminTh("Status"); AdminTh("Enrolled"); AdminTh("Pace"); AdminTh("Finished")
                        }
                    }
                    Tbody {
                        runs.forEach { r ->
                            Tr(attrs = { style { property("border-top", "1px solid $Gray200") } }) {
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray700) } }) { Text(formatIsoDate(r.startedAt)) }
                                Td(attrs = { style { padding(10.px, 16.px) } }) {
                                    val (l, fg, bg) = dripStatusPill(r.status)
                                    StatusPill(l, fg, bg)
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray700) } }) { Text("${r.enrolledCount} / ${r.totalMatched}") }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray500) } }) { Text("${r.pacePerDay}/day") }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray500) } }) {
                                    Text(r.finishedAt?.let { formatIsoDate(it) } ?: "—")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Who is in the sequence, and what has actually gone out. */
@Composable
private fun DripActivityPanel(campaignId: String) {
    var sends by remember { mutableStateOf<List<DripSendRow>>(emptyList()) }
    var enrollments by remember { mutableStateOf<List<DripEnrollmentRow>>(emptyList()) }
    var tab by remember { mutableStateOf("sends") }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun fetchAll() {
        scope.launch {
            try {
                val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
                val headers = kotlin.js.json("Accept" to "application/json")
                token?.let { headers["Authorization"] = "Bearer $it" }
                suspend fun get(p: String): String? {
                    val r = kotlinx.browser.window.fetch(
                        "$serverUrl$p", kotlin.js.json("method" to "GET", "headers" to headers).unsafeCast<org.w3c.fetch.RequestInit>()
                    ).await()
                    return if (r.ok) r.text().await().toString() else null
                }
                get("/api/admin/drip/campaign/$campaignId/sends")?.let { sends = adminJson.decodeFromString(it) }
                get("/api/admin/drip/campaign/$campaignId/enrollments")?.let { enrollments = adminJson.decodeFromString(it) }
            } catch (_: Exception) {}
        }
    }

    LaunchedEffect(campaignId) { fetchAll() }

    CardSection("Activity", onRefresh = { fetchAll() }) {
        Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); property("margin-bottom", "16px") } }) {
            listOf("sends" to "Emails (${sends.size})", "people" to "People (${enrollments.size})").forEach { (k, label) ->
                Span(attrs = {
                    onClick { tab = k }
                    style {
                        fontSize(13.px); fontWeight(600); padding(7.px, 14.px); borderRadius(8.px); cursor("pointer")
                        property("background", if (tab == k) PrimaryPale.toString() else "transparent")
                        color(if (tab == k) Primary else Gray500)
                    }
                }) { Text(label) }
            }
        }

        if (tab == "sends") {
            if (sends.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px); property("margin", "0") } }) { Text("Nothing sent yet.") }
            } else {
                // A skipped row is not a failure — it is the stop rules doing their job, and the
                // reason column is the only place that distinction is visible.
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", Gray50.toString()) } }) {
                            AdminTh("Who"); AdminTh("Day", center = true); AdminTh("Status", center = true)
                            AdminTh("Subject"); AdminTh("Sent"); AdminTh("Clicked"); AdminTh("Reason")
                        }
                    }
                    Tbody {
                        sends.forEach { s ->
                            Tr(attrs = { style { property("border-top", "1px solid $Gray200") } }) {
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray900) } }) { Text(s.email) }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray600); property("text-align", "center") } }) { Text("${s.dayOffset}") }
                                Td(attrs = { style { padding(10.px, 16.px); property("text-align", "center") } }) {
                                    val (l, fg, bg) = dripStatusPill(s.status)
                                    StatusPill(l, fg, bg)
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray600) } }) { Text(s.subjectSnapshot) }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray500) } }) { Text(s.sentAt?.let { formatIsoDate(it) } ?: "—") }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(if (s.clickedAt != null) Primary else Gray400) } }) {
                                    Text(if (s.clickedAt != null) "yes" else "—")
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray500) } }) { Text(s.error ?: "—") }
                            }
                        }
                    }
                }
            }
        } else {
            if (enrollments.isEmpty()) {
                P(attrs = { style { color(Gray400); fontSize(14.px); property("margin", "0") } }) { Text("Nobody enrolled yet.") }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", Gray50.toString()) } }) {
                            AdminTh("Who"); AdminTh("Enrolled"); AdminTh("How"); AdminTh("Status", center = true)
                            AdminTh("Last sent"); AdminTh("Stopped because")
                        }
                    }
                    Tbody {
                        enrollments.forEach { e ->
                            Tr(attrs = { style { property("border-top", "1px solid $Gray200") } }) {
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(13.px); color(Gray900) } }) { Text(e.email) }
                                // The offset origin — every step's due date counts from here.
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray600) } }) { Text(formatIsoDate(e.enrolledAt)) }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray500) } }) { Text(e.source) }
                                Td(attrs = { style { padding(10.px, 16.px); property("text-align", "center") } }) {
                                    val (l, fg, bg) = dripStatusPill(e.status)
                                    StatusPill(l, fg, bg)
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray500) } }) {
                                    Text(e.lastSentOffset?.let { "day $it" } ?: "—")
                                }
                                Td(attrs = { style { padding(10.px, 16.px); fontSize(12.px); color(Gray500) } }) { Text(e.stoppedReason ?: "—") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatusPill(label: String, fg: CSSColorValue, bg: CSSColorValue) {
    Span(attrs = {
        style {
            fontSize(11.px); fontWeight(700); padding(3.px, 10.px); borderRadius(20.px)
            color(fg); backgroundColor(bg)
        }
    }) { Text(label) }
}

// ---------------------------------------------------------------------------
// Registration Funnel (GET /api/admin/funnel)
// ---------------------------------------------------------------------------

/** Mirrors the server's FunnelStepStat. See Models.kt for what `people` vs `direct` mean. */
@Serializable
private data class FunnelStepStat(
    val step: Int = 0,
    val event: String = "",
    val label: String = "",
    val people: Int = 0,
    val direct: Int = 0,
    val events: Int = 0,
    @SerialName("pct_of_start") val pctOfStart: Double = 0.0,
    @SerialName("pct_of_prev") val pctOfPrev: Double = 0.0,
    @SerialName("dropped_off") val droppedOff: Int = 0
)

@Serializable
private data class FunnelExitStat(
    val event: String = "",
    val label: String = "",
    val people: Int = 0,
    val events: Int = 0
)

/** Mirror of the server's FunnelAcquisitionStat — see the note on FunnelData about hand-mirroring. */
@Serializable
private data class FunnelAcquisitionStat(
    val source: String = "",
    val medium: String = "",
    val campaign: String = "",
    val people: Int = 0,
    val subscribed: Int = 0,
    @SerialName("pct_of_people") val pctOfPeople: Double = 0.0,
    @SerialName("pct_converted") val pctConverted: Double = 0.0
)

@Serializable
private data class FunnelJourneyEvent(
    val event: String = "",
    val label: String = "",
    val step: Int? = null,
    @SerialName("params_json") val paramsJson: String? = null,
    @SerialName("created_at") val createdAt: String? = null
)

@Serializable
private data class FunnelJourney(
    @SerialName("anon_id") val anonId: String = "",
    @SerialName("user_email") val userEmail: String? = null,
    val platform: String? = null,
    @SerialName("app_version") val appVersion: String? = null,
    @SerialName("utm_source") val utmSource: String? = null,
    @SerialName("utm_medium") val utmMedium: String? = null,
    @SerialName("utm_campaign") val utmCampaign: String? = null,
    @SerialName("referrer_host") val referrerHost: String? = null,
    @SerialName("furthest_step") val furthestStep: Int = 0,
    @SerialName("furthest_label") val furthestLabel: String = "",
    val converted: Boolean = false,
    @SerialName("started_at") val startedAt: String? = null,
    @SerialName("last_seen_at") val lastSeenAt: String? = null,
    val events: List<FunnelJourneyEvent> = emptyList()
)

@Serializable
private data class FunnelData(
    val steps: List<FunnelStepStat> = emptyList(),
    val exits: List<FunnelExitStat> = emptyList(),
    val journeys: List<FunnelJourney> = emptyList(),
    @SerialName("total_people") val totalPeople: Int = 0,
    @SerialName("total_events") val totalEvents: Int = 0,
    val acquisition: List<FunnelAcquisitionStat> = emptyList(),
    val days: Int = 0
)

/**
 * Human label for an acquisition source.
 *
 * The server emits a parenthesised marker when a group of people has no recorded source, encoding
 * WHY rather than collapsing every reason into one row (see FunnelRoutes.kt's bucket constants).
 * Anything unparenthesised is a real channel and passes through untouched.
 */
private fun acquisitionSourceLabel(source: String): String = when (source) {
    "(pre-tracking)"     -> "Before tracking shipped"
    "(ios-no-referrer)"  -> "iOS — not attributable"
    "(android-untagged)" -> "Android — no install referrer"
    "(direct-inferred)"  -> "Direct / untagged"
    "(unknown)"          -> "Not recorded"
    // A real "(direct)" the client actually stored, as opposed to the inferred bucket above.
    "(direct)"           -> "Direct"
    else                 -> source
}

/** One-line explanation for a bucket, shown under the table only when that bucket is present. */
private fun acquisitionSourceNote(source: String): String? = when (source) {
    "(pre-tracking)" ->
        "\"Before tracking shipped\" — these people were on an app build, or visited before a web deploy, " +
            "that had no acquisition tracking in it. Nothing was recorded because nothing could be. " +
            "Mobile only starts being attributable once a release containing the capture code ships."
    "(ios-no-referrer)" ->
        "\"iOS — not attributable\" — Apple exposes no free install-referrer equivalent, so an iOS install " +
            "can never be traced to its source. This will not improve; the way to attribute an iPhone user " +
            "is to have them land on the website first."
    "(android-untagged)" ->
        "\"Android — no install referrer\" — a sideload, a device without Play Services, or a first launch " +
            "where the referrer had not arrived yet. It usually lands on their next launch."
    "(direct-inferred)" ->
        "\"Direct / untagged\" — we looked and found no campaign tag and no external referrer: typed the " +
            "address, a bookmark, or an app the browser hides. This is an answer, not a gap."
    "(unknown)" ->
        "\"Not recorded\" — the platform could not be identified from the request, so no claim is made."
    else -> null
}

/** Shorten an ISO timestamp to "Jul 31, 14:03" for the journey timeline. */
private fun funnelTimeLabel(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    return try {
        val d = kotlin.js.Date(iso)
        if (d.getTime().isNaN()) iso.take(16) else "${shortDateLabel(d)}, " +
            "${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}"
    } catch (_: Exception) { iso.take(16) }
}

/**
 * Registration + paywall drop-off, read from `funnel_events` (NOT analytics_events — that table is
 * analysis-shaped and filtered to completed analyses everywhere else in this dashboard).
 *
 * This is the only place the WEB funnel is visible at all: shared AnalyticsTracker.js is a no-op,
 * so Firebase has never recorded a single fitrahfilter.com user.
 */
@Composable
private fun RegistrationFunnelView(onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    var data by remember { mutableStateOf<FunnelData?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var days by remember { mutableStateOf(30) }
    var expanded by remember { mutableStateOf<String?>(null) }
    var onlyConverted by remember { mutableStateOf(false) }

    fun authHeaders(): dynamic {
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
            catch (_: Exception) { null }
            ?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    val load: () -> Unit = {
        scope.launch {
            loading = true; error = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/funnel?days=$days",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                when {
                    // PROD serves the exported Kobweb site, so an undeployed /api/* route falls
                    // through to index.html with a 200 — success until you read the body.
                    resp.ok && text.trimStart().startsWith("<") ->
                        error = "The server has no /api/admin/funnel endpoint yet — it returned the website instead of JSON. Deploy the server (fly deploy --config fly.toml)."
                    resp.ok -> data = adminJson.decodeFromString<FunnelData>(text)
                    else -> error = "Failed to load (${resp.status}). ${text.take(300)}"
                }
            } catch (e: Exception) {
                error = e.message ?: "Failed to load funnel data"
            }
            loading = false
        }
    }
    LaunchedEffect(days) { load() }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Registration Funnel" to null)

        Div(attrs = { style { property("margin-bottom", "20px") } }) {
            H1(attrs = {
                style {
                    color(Color.white); fontSize(28.px); fontWeight(700)
                    property("font-family", TitleFontFamily); property("margin", "0 0 6px")
                }
            }) { Text("Registration Funnel") }
            P(attrs = { style { color(Color("#d7ede9")); fontSize(14.px); property("margin", "0") } }) {
                Text("How far people get from opening the app to subscribing — web, Android and iOS together.")
            }
        }

        // Window selector
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); gap(8.px); property("flex-wrap", "wrap")
                property("margin-bottom", "20px"); alignItems(AlignItems.Center)
            }
        }) {
            listOf(7 to "Last 7 days", 30 to "Last 30 days", 90 to "Last 90 days", 0 to "All time")
                .forEach { (d, label) ->
                    val active = d == days
                    Button(attrs = {
                        onClick { if (!active) days = d }
                        style {
                            padding(8.px, 14.px); borderRadius(999.px); cursor("pointer")
                            fontSize(13.px); fontWeight(if (active) 700 else 500)
                            property("border", "1px solid rgba(255,255,255,0.35)")
                            backgroundColor(if (active) Color.white else Color("rgba(255,255,255,0.12)"))
                            color(if (active) Primary else Color.white)
                        }
                    }) { Text(label) }
                }
            if (loading) {
                Span(attrs = { style { color(Color("#d7ede9")); fontSize(13.px) } }) { Text("Loading…") }
            }
        }

        error?.let { msg ->
            Div(attrs = {
                style {
                    backgroundColor(Color("#fef2f2")); property("border", "1px solid #fca5a5")
                    borderRadius(12.px); padding(16.px); property("margin-bottom", "20px"); color(Color("#991b1b"))
                    fontSize(14.px)
                }
            }) { Text(msg) }
        }

        val d = data
        if (d != null) {
            val start = d.steps.firstOrNull()?.people ?: 0

            // ── Headline numbers ──────────────────────────────────────────────
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap")
                    property("margin-bottom", "24px")
                }
            }) {
                val subscribed = d.steps.lastOrNull()?.people ?: 0
                val convPct = if (start > 0) subscribed * 100.0 / start else 0.0
                FunnelHeadline("People", d.totalPeople.toString(), "everyone with any activity")
                FunnelHeadline("Events", d.totalEvents.toString(), "individual actions recorded")
                FunnelHeadline("Subscribed", subscribed.toString(), "reached the final step")
                FunnelHeadline(
                    "Conversion", "${((convPct * 10).let { v -> kotlin.math.round(v).toInt() } / 10.0)}%",
                    "of everyone who opened the app"
                )
            }

            // ── The funnel ────────────────────────────────────────────────────
            FunnelCard("Drop-off by step") {
                if (start == 0) {
                    P(attrs = { style { color(Gray500); fontSize(14.px); property("margin", "0") } }) {
                        Text("No funnel events in this window yet. Once the app build with funnel tracking ships, steps appear here within seconds of a user acting.")
                    }
                } else {
                    d.steps.forEachIndexed { i, s ->
                        val widthPct = if (start > 0) (s.people * 100.0 / start) else 0.0
                        Div(attrs = { style { property("margin-bottom", "14px") } }) {
                            // Label row
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center); gap(12.px); property("margin-bottom", "6px")
                                    property("flex-wrap", "wrap")
                                }
                            }) {
                                Span(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) {
                                    Text("${s.step}. ${s.label}")
                                }
                                Span(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                                    Text("${s.people} ${if (s.people == 1) "person" else "people"} · ${(widthPct * 10).let { v -> kotlin.math.round(v).toInt() } / 10.0}%")
                                }
                            }
                            // Bar
                            Div(attrs = {
                                style {
                                    width(100.percent); height(28.px); borderRadius(6.px)
                                    backgroundColor(Gray100); property("overflow", "hidden")
                                }
                            }) {
                                Div(attrs = {
                                    style {
                                        width(widthPct.coerceIn(0.0, 100.0).percent); height(100.percent)
                                        property("background", if (i == d.steps.lastIndex) "linear-gradient(90deg,#0E7C7B,#14A3A0)" else "linear-gradient(90deg,#14A3A0,#5eead4)")
                                        property("transition", "width .35s ease")
                                    }
                                }) {}
                            }
                            // Drop-off caption
                            if (i > 0 && s.droppedOff > 0) {
                                Div(attrs = { style { fontSize(12.px); color(Color("#b45309")); property("margin-top", "4px") } }) {
                                    Text("↓ ${s.droppedOff} dropped off here (${(s.pctOfPrev * 10).let { v -> kotlin.math.round(v).toInt() } / 10.0}% continued from \"${d.steps[i - 1].label}\")")
                                }
                            }
                            // Raw truth: the count of people who literally fired this event. Differs
                            // from the bar when someone skips a step (a returning user who is
                            // already signed in never fires sign_up but does reach the paywall).
                            if (s.direct != s.people) {
                                Div(attrs = { style { fontSize(11.px); color(Gray400); property("margin-top", "2px") } }) {
                                    Text("${s.direct} fired this exact event (${s.events} times) — the bar counts everyone who got at least this far.")
                                }
                            }
                        }
                    }
                }
            }

            // ── Acquisition ───────────────────────────────────────────────────
            if (d.acquisition.isNotEmpty()) {
                FunnelCard("Where people came from") {
                    TableWrapper {
                        Thead {
                            Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                                AdminTh("Source"); AdminTh("Medium"); AdminTh("Campaign")
                                AdminTh("People", center = true); AdminTh("Subscribed", center = true)
                                AdminTh("Conv.", center = true)
                            }
                        }
                        Tbody {
                            d.acquisition.forEachIndexed { index, a ->
                                Tr(attrs = {
                                    style {
                                        if (index % 2 == 1) backgroundColor(Gray50)
                                        property("border-bottom", "1px solid ${Gray200}")
                                    }
                                }) {
                                    Td(attrs = { style { padding(12.px, 16.px); fontWeight(600); color(Gray900) } }) {
                                        // The server no longer lumps every unattributed person into
                                        // one row — it says WHY each group has no source. Translate
                                        // its markers here; anything else is a real channel name.
                                        Text(acquisitionSourceLabel(a.source))
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); color(Gray500) } }) {
                                        Text(a.medium.ifBlank { "—" })
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); color(Gray500) } }) {
                                        Text(a.campaign.ifBlank { "—" })
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); property("text-align", "center"); fontWeight(700); color(Primary) } }) {
                                        Text(a.people.toString())
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); property("text-align", "center"); color(Gray900) } }) {
                                        Text(a.subscribed.toString())
                                    }
                                    Td(attrs = { style { padding(12.px, 16.px); property("text-align", "center"); fontSize(13.px); color(Gray500) } }) {
                                        Text("${(a.pctConverted * 10).let { v -> kotlin.math.round(v).toInt() } / 10.0}%")
                                    }
                                }
                            }
                        }
                    }
                    // Each unattributed bucket explains ITSELF — the old single footnote described
                    // only two of the four reasons and named the wrong one as the largest. Only the
                    // notes for buckets actually present are shown.
                    val notes = d.acquisition.map { it.source }.distinct()
                        .mapNotNull { acquisitionSourceNote(it) }
                    if (notes.isNotEmpty()) {
                        Div(attrs = { style { fontSize(12.px); color(Gray400); property("margin-top", "10px") } }) {
                            notes.forEach { note ->
                                Div(attrs = { style { property("margin-top", "4px") } }) { Text(note) }
                            }
                        }
                    }
                }
            }

            // ── Exits ─────────────────────────────────────────────────────────
            if (d.exits.any { it.events > 0 }) {
                FunnelCard("How people leave") {
                    d.exits.filter { it.events > 0 }.forEach { e ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center); padding(10.px, 0.px)
                                property("border-bottom", "1px solid #f1f5f9")
                            }
                        }) {
                            Span(attrs = { style { fontSize(14.px); color(Gray900) } }) { Text(e.label) }
                            Span(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                                Text("${e.people} ${if (e.people == 1) "person" else "people"} · ${e.events} times")
                            }
                        }
                    }
                }
            }

            // ── Individual journeys ───────────────────────────────────────────
            FunnelCard("Individual journeys") {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px)
                        property("margin-bottom", "12px"); property("flex-wrap", "wrap")
                    }
                }) {
                    Label(attrs = {
                        style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px); fontSize(13.px); color(Gray500); cursor("pointer") }
                    }) {
                        CheckboxInput(checked = onlyConverted, attrs = {
                            onInput { onlyConverted = it.value }
                        })
                        Text("Subscribers only")
                    }
                    Span(attrs = { style { fontSize(12.px); color(Gray400) } }) {
                        // The server caps the list; say so rather than letting a truncated page
                        // read as the whole picture.
                        Text(
                            if (d.totalPeople > d.journeys.size)
                                "Showing the ${d.journeys.size} most recent of ${d.totalPeople} people."
                            else "Showing all ${d.journeys.size}."
                        )
                    }
                }

                val shown = if (onlyConverted) d.journeys.filter { it.converted } else d.journeys
                if (shown.isEmpty()) {
                    P(attrs = { style { color(Gray500); fontSize(14.px); property("margin", "0") } }) {
                        Text("No journeys in this window.")
                    }
                }
                shown.forEach { j ->
                    val isOpen = expanded == j.anonId
                    Div(attrs = {
                        style {
                            property("border", "1px solid #e2e8f0"); borderRadius(10.px)
                            property("margin-bottom", "8px"); property("overflow", "hidden")
                        }
                    }) {
                        // Header row — click to expand the literal sequence of actions.
                        Div(attrs = {
                            onClick { expanded = if (isOpen) null else j.anonId }
                            style {
                                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center); gap(12.px); padding(12.px, 14.px)
                                cursor("pointer"); property("flex-wrap", "wrap")
                                backgroundColor(if (j.converted) Color("#f0fdf4") else Color.white)
                            }
                        }) {
                            Div {
                                Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray900) } }) {
                                    // Anonymous until they register — that's the point of the funnel.
                                    Text(j.userEmail ?: "anonymous · ${j.anonId.take(8)}…")
                                }
                                Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-top", "2px") } }) {
                                    // Acquisition is already on the wire for every journey — the
                                    // server computes first-touch per person (FunnelJourney) and it
                                    // was simply never rendered. Shown here rather than as its own
                                    // column so an untagged person costs no width.
                                    //
                                    // referrerHost only appears when it ADDS something: when a visit
                                    // was untagged, source IS the referring host, so printing both
                                    // would read "instagram.com · instagram.com".
                                    val referrer = j.referrerHost?.takeIf { it != j.utmSource }
                                    Text(
                                        listOfNotNull(
                                            j.platform,
                                            j.appVersion,
                                            j.utmSource?.let { src ->
                                                listOfNotNull(src, j.utmMedium).joinToString("/")
                                            },
                                            j.utmCampaign,
                                            referrer?.let { "via $it" },
                                            "${j.events.size} actions",
                                            funnelTimeLabel(j.lastSeenAt)
                                        ).joinToString(" · ")
                                    )
                                }
                            }
                            Span(attrs = {
                                style {
                                    fontSize(12.px); fontWeight(600); padding(5.px, 10.px); borderRadius(999.px)
                                    backgroundColor(if (j.converted) Color("#dcfce7") else Color("#fff7ed"))
                                    color(if (j.converted) Color("#166534") else Color("#b45309"))
                                }
                            }) { Text(if (j.converted) "Subscribed" else "Stopped at: ${j.furthestLabel}") }
                        }
                        if (isOpen) {
                            Div(attrs = {
                                style { padding(4.px, 14.px, 14.px); backgroundColor(Color("#fafafa")) }
                            }) {
                                j.events.forEach { ev ->
                                    Div(attrs = {
                                        style {
                                            display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                            gap(10.px); padding(6.px, 0.px); fontSize(13.px)
                                            property("border-bottom", "1px dashed #e2e8f0")
                                        }
                                    }) {
                                        Span(attrs = {
                                            style {
                                                property("min-width", "112px"); color(Gray400); fontSize(12.px)
                                            }
                                        }) { Text(funnelTimeLabel(ev.createdAt)) }
                                        Span(attrs = { style { color(Gray900); fontWeight(500) } }) { Text(ev.label) }
                                        ev.paramsJson?.takeIf { it.isNotBlank() && it != "{}" }?.let { p ->
                                            Span(attrs = { style { color(Gray500); fontSize(12.px) } }) { Text(p) }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FunnelHeadline(label: String, value: String, hint: String) {
    Div(attrs = {
        style {
            flex("1 1 180px"); backgroundColor(Color.white); borderRadius(14.px)
            padding(16.px, 18.px); property("box-shadow", CardElevatedShadow)
        }
    }) {
        Div(attrs = { style { fontSize(12.px); color(Gray500); fontWeight(600); property("text-transform", "uppercase"); property("letter-spacing", "0.4px") } }) { Text(label) }
        Div(attrs = { style { fontSize(28.px); fontWeight(700); color(Gray900); property("margin", "4px 0 2px") } }) { Text(value) }
        Div(attrs = { style { fontSize(12.px); color(Gray400) } }) { Text(hint) }
    }
}

@Composable
private fun FunnelCard(title: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(Color.white); borderRadius(16.px); padding(20.px, 22.px)
            property("box-shadow", CardElevatedShadow); property("margin-bottom", "20px")
        }
    }) {
        H2(attrs = {
            style {
                fontSize(17.px); fontWeight(700); color(Gray900)
                property("font-family", TitleFontFamily); property("margin", "0 0 16px")
            }
        }) { Text(title) }
        content()
    }
}

// ---------------------------------------------------------------------------
// Infrastructure Costs (GET/POST/DELETE /api/admin/infra-costs)
// ---------------------------------------------------------------------------

/**
 * Hand-entered Fly.io + Supabase invoices — the fixed-cost half of Total Profit.
 *
 * These are typed in because neither vendor exposes spend programmatically (verified 2026-08-03:
 * Fly's GraphQL Organization has billing STATUS but no amounts and no invoice query; the Supabase
 * Management API has no billing endpoint at all). Recording each real invoice rather than one
 * "average monthly cost" keeps every figure traceable to a document and stays correct as the bill
 * steps up — Fly has already gone $0 → $5.87 → $14.18 → $22.60 → $25.46 in five months.
 */
@Composable
private fun InfraCostsView(onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    var data by remember { mutableStateOf<InfraCostsData?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }

    // New-row form
    var provider by remember { mutableStateOf("fly") }
    var period by remember { mutableStateOf("") }       // YYYY-MM
    var amount by remember { mutableStateOf("") }
    var invoiceRef by remember { mutableStateOf("") }

    fun authHeaders(): dynamic {
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
            catch (_: Exception) { null }
            ?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    val load: () -> Unit = {
        scope.launch {
            loading = true; error = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/infra-costs",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                when {
                    resp.ok && text.trimStart().startsWith("<") ->
                        error = "The server has no /api/admin/infra-costs endpoint yet — it returned the website instead of JSON. Deploy the server (fly deploy --config fly.toml)."
                    resp.ok -> data = adminJson.decodeFromString<InfraCostsData>(text)
                    else -> error = "Failed to load (${resp.status}). ${text.take(300)}"
                }
            } catch (e: Exception) {
                error = e.message ?: "Failed to load infrastructure costs"
            }
            loading = false
        }
    }
    LaunchedEffect(Unit) { load() }

    fun save() {
        val amt = amount.trim().removePrefix("$").toDoubleOrNull()
        val p = period.trim()
        // YYYY-MM is what the user types; the table stores a real DATE, so pin it to the 1st.
        if (amt == null || !Regex("^\\d{4}-\\d{2}$").matches(p)) {
            error = "Enter a period as YYYY-MM (e.g. 2026-08) and an amount as a number."
            return
        }
        scope.launch {
            saving = true; error = null
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val body = kotlin.js.JSON.stringify(kotlin.js.json(
                    "provider" to provider,
                    "periodStart" to "$p-01",
                    "amountUsd" to amt,
                    "invoiceRef" to invoiceRef.trim(),
                ))
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/infra-costs",
                    kotlin.js.json("method" to "POST", "headers" to authHeaders(), "body" to body)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) { amount = ""; invoiceRef = ""; period = ""; load() }
                else error = "Save failed (${resp.status}). ${resp.text().await().toString().take(200)}"
            } catch (e: Exception) {
                error = "Save failed: ${e.message}"
            }
            saving = false
        }
    }

    fun remove(id: Long) {
        scope.launch {
            try {
                val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/infra-costs/$id",
                    kotlin.js.json("method" to "DELETE", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (resp.ok) load() else error = "Delete failed (${resp.status})"
            } catch (e: Exception) {
                error = "Delete failed: ${e.message}"
            }
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Infrastructure Costs" to null)

        Div(attrs = { style { property("margin-bottom", "20px") } }) {
            H1(attrs = {
                style {
                    color(Color.white); fontSize(24.px); fontWeight(700)
                    property("font-family", TitleFontFamily); property("margin", "0 0 6px")
                }
            }) { Text("Infrastructure Costs") }
            P(attrs = { style { color(Color("#d7ede9")); fontSize(13.px); property("margin", "0") } }) {
                Text("Fly.io and Supabase invoices. Neither vendor exposes billing through an API, so these are entered by hand — one row per invoice.")
            }
        }

        error?.let { msg ->
            Div(attrs = {
                style {
                    backgroundColor(Color("#fef2f2")); property("border", "1px solid #fca5a5")
                    borderRadius(12.px); padding(14.px); property("margin-bottom", "16px")
                    color(Color("#991b1b")); fontSize(13.px)
                }
            }) { Text(msg) }
        }

        val d = data
        if (d != null) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(10.px); property("flex-wrap", "wrap")
                    property("margin-bottom", "18px")
                }
            }) {
                FunnelHeadline("Total Recorded", formatMoney(d.total), "subtracted from Total Profit")
                d.byProvider.entries.sortedByDescending { it.value }.forEach { (name, total) ->
                    FunnelHeadline(name.replaceFirstChar { it.uppercase() }, formatMoney(total), "all recorded invoices")
                }
            }
        }

        // ── Add / correct an invoice ──────────────────────────────────────
        FunnelCard("Add or correct an invoice") {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(10.px); property("flex-wrap", "wrap"); alignItems(AlignItems.FlexEnd)
                }
            }) {
                Div {
                    Label(attrs = { style { fontSize(11.px); color(Gray500); display(DisplayStyle.Block); property("margin-bottom", "4px") } }) { Text("Provider") }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(4.px) } }) {
                        listOf("fly" to "Fly.io", "supabase" to "Supabase", "other" to "Other").forEach { (key, label) ->
                            val active = provider == key
                            Button(attrs = {
                                onClick { provider = key }
                                style {
                                    padding(8.px, 12.px); borderRadius(8.px); cursor("pointer"); fontSize(13.px)
                                    fontWeight(if (active) 700 else 500)
                                    property("border", "1px solid " + if (active) "#0E7C7B" else "#e2e8f0")
                                    backgroundColor(if (active) PrimaryPale else Color.white)
                                    color(if (active) Primary else Gray600)
                                }
                            }) { Text(label) }
                        }
                    }
                }
                InfraField("Period (YYYY-MM)", period, "2026-08") { period = it }
                InfraField("Amount (USD)", amount, "25.46") { amount = it }
                InfraField("Invoice ref", invoiceRef, "FQTZJPGH-0007") { invoiceRef = it }
                Button(attrs = {
                    if (!saving) onClick { save() }
                    style {
                        padding(9.px, 18.px); borderRadius(8.px); property("border", "none")
                        backgroundColor(if (saving) Gray400 else Primary); color(Color.white)
                        fontSize(13.px); fontWeight(600); cursor(if (saving) "default" else "pointer")
                    }
                }) { Text(if (saving) "Saving…" else "Save") }
            }
            P(attrs = { style { fontSize(11.px); color(Gray500); property("margin", "10px 0 0") } }) {
                Text("Re-entering an existing provider + period overwrites that row rather than adding a second one, so a correction can't double-count.")
            }
        }

        // ── Recorded invoices ─────────────────────────────────────────────
        FunnelCard("Recorded invoices") {
            if (loading) {
                P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
            } else if (d == null || d.rows.isEmpty()) {
                P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) {
                    Text("Nothing recorded yet. Run db/migrations/2026-08-03_infra_costs.sql to seed the real Fly + Supabase history.")
                }
            } else {
                d.rows.forEach { row ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center); gap(12.px); padding(9.px, 0.px)
                            property("border-bottom", "1px solid #f1f5f9"); property("flex-wrap", "wrap")
                        }
                    }) {
                        Div {
                            Span(attrs = { style { fontSize(13.px); fontWeight(600); color(Gray900) } }) {
                                Text("${row.provider.replaceFirstChar { it.uppercase() }} · ${row.periodStart.take(7)}")
                            }
                            Div(attrs = { style { fontSize(11.px); color(Gray500); property("margin-top", "2px") } }) {
                                Text(listOfNotNull(row.invoiceRef?.takeIf { it.isNotBlank() }, row.note).joinToString(" · ").ifBlank { "—" })
                            }
                        }
                        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) } }) {
                            Span(attrs = { style { fontSize(14.px); fontWeight(700); color(Gray900) } }) {
                                Text(formatMoney(row.amountUsd))
                            }
                            row.id?.let { id ->
                                Span(attrs = {
                                    onClick { remove(id) }
                                    style { fontSize(12.px); color(ScoreRed); cursor("pointer") }
                                }) { Text("Delete") }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Small labelled text input for the infra-cost form. */
@Composable
private fun InfraField(label: String, value: String, placeholder: String, onChange: (String) -> Unit) {
    Div {
        Label(attrs = { style { fontSize(11.px); color(Gray500); display(DisplayStyle.Block); property("margin-bottom", "4px") } }) { Text(label) }
        Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
            value(value)
            attr("placeholder", placeholder)
            onInput { onChange(it.value) }
            style {
                padding(8.px, 10.px); borderRadius(8.px); property("border", "1px solid #e2e8f0")
                fontSize(13.px); width(130.px)
            }
        })
    }
}

// ---------------------------------------------------------------------------
// Vouchers View — multi-use promo codes granting N days of unlimited access
// ---------------------------------------------------------------------------
// Deliberately separate from Invitations: different tables, different RPCs, no shared state.
// Invitations are single-use tokenised gifts; vouchers are shareable codes you email to a list.

/** One row of /api/admin/vouchers (the voucher_list view — includes the live redemption count). */
private data class VoucherRow(
    val id: String,
    val code: String,
    val durationDays: Int,
    val enabled: Boolean,
    val expiresAt: String?,
    val note: String?,
    val createdAt: String?,
    val redemptionCount: Int,
    val lastRedeemedAt: String?,
)

/** One row of /api/admin/vouchers/redemptions. */
private data class VoucherRedemptionRow(
    val email: String,
    val redeemedAt: String?,
    val grantedUntil: String?,
)

/**
 * Create, edit and disable promo codes.
 *
 * The code is stored and shown in PLAINTEXT, unlike invitation tokens which are hashed. That's
 * deliberate: a voucher is a marketing string you have to be able to re-read in order to email it
 * out, whereas an invite token is a single-use secret nobody needs back.
 *
 * Disabling stops NEW redemptions only — anyone already inside their window keeps the days they
 * were granted. There is no clawback here by design.
 */
@Composable
private fun VouchersView(onNavigate: (DrillDownView) -> Unit) {
    var rows by remember { mutableStateOf<List<VoucherRow>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var busyId by remember { mutableStateOf<String?>(null) }

    // Create form
    var newCode by remember { mutableStateOf("") }
    var newDays by remember { mutableStateOf("7") }
    var newExpiry by remember { mutableStateOf("") }     // yyyy-mm-dd, blank = never
    var newNote by remember { mutableStateOf("") }
    var creating by remember { mutableStateOf(false) }

    // Redemption drill-down
    var openRedemptionsFor by remember { mutableStateOf<String?>(null) }
    var redemptions by remember { mutableStateOf<List<VoucherRedemptionRow>>(emptyList()) }
    var loadingRedemptions by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    suspend fun authHeaders(withBody: Boolean): dynamic {
        val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
        val headers = kotlin.js.json("Accept" to "application/json")
        if (withBody) headers["Content-Type"] = "application/json"
        token?.let { headers["Authorization"] = "Bearer $it" }
        return headers
    }

    fun refresh() {
        scope.launch {
            loading = true
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/vouchers",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                rows = if (!response.ok) emptyList() else {
                    val arr = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonArray
                    arr?.mapNotNull { el ->
                        val o = el as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        fun s(k: String) = (o[k] as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                        VoucherRow(
                            id = s("id") ?: return@mapNotNull null,
                            code = s("code") ?: return@mapNotNull null,
                            durationDays = s("duration_days")?.toIntOrNull() ?: 0,
                            enabled = s("enabled")?.toBooleanStrictOrNull() ?: true,
                            expiresAt = s("expires_at"),
                            note = s("note"),
                            createdAt = s("created_at"),
                            redemptionCount = s("redemption_count")?.toIntOrNull() ?: 0,
                            lastRedeemedAt = s("last_redeemed_at"),
                        )
                    } ?: emptyList()
                }
            } catch (_: Exception) {
                rows = emptyList()
            }
            loading = false
        }
    }

    LaunchedEffect(Unit) { refresh() }

    fun create() {
        val code = newCode.trim().uppercase()
        val days = newDays.trim().toIntOrNull()
        if (!code.matches(Regex("^[A-Z0-9_-]{3,32}$"))) {
            error = "Code must be 3–32 characters: A–Z, 0–9, dash or underscore."
            return
        }
        if (days == null || days !in 1..365) {
            error = "Days must be a number from 1 to 365."
            return
        }
        scope.launch {
            creating = true
            error = null
            try {
                // A bare yyyy-mm-dd becomes end-of-day UTC, so an expiry of "the 5th" stays valid
                // through the 5th rather than dying at midnight as it starts.
                val expiryIso = newExpiry.trim().takeIf { it.isNotBlank() }?.let { "${it}T23:59:59Z" }
                val body = buildString {
                    append("{\"code\":\"").append(code).append("\",\"durationDays\":").append(days)
                    expiryIso?.let { append(",\"expiresAt\":\"").append(it).append("\"") }
                    newNote.trim().takeIf { it.isNotBlank() }?.let {
                        append(",\"note\":\"").append(it.replace("\"", "\\\"")).append("\"")
                    }
                    append("}")
                }
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/vouchers",
                    kotlin.js.json(
                        "method" to "POST", "headers" to authHeaders(true), "body" to body
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                if (!response.ok) {
                    val t = response.text().await().toString()
                    error = if (response.status.toInt() == 409) "That code already exists." else "Create failed: $t"
                } else {
                    newCode = ""; newDays = "7"; newExpiry = ""; newNote = ""
                    refresh()
                }
            } catch (e: Exception) {
                error = "Create failed: ${e.message}"
            }
            creating = false
        }
    }

    fun patch(id: String, bodyJson: String) {
        scope.launch {
            busyId = id
            try {
                kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/vouchers/update",
                    kotlin.js.json(
                        "method" to "POST", "headers" to authHeaders(true), "body" to bodyJson
                    ).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                refresh()
            } catch (_: Exception) { }
            busyId = null
        }
    }

    fun loadRedemptions(id: String) {
        scope.launch {
            loadingRedemptions = true
            openRedemptionsFor = id
            try {
                val response = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/vouchers/redemptions?id=$id",
                    kotlin.js.json("method" to "GET", "headers" to authHeaders(false)).unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = response.text().await().toString()
                redemptions = if (!response.ok) emptyList() else {
                    val arr = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonArray
                    arr?.mapNotNull { el ->
                        val o = el as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        fun s(k: String) = (o[k] as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }
                        VoucherRedemptionRow(
                            email = s("email") ?: return@mapNotNull null,
                            redeemedAt = s("redeemed_at"),
                            grantedUntil = s("granted_until"),
                        )
                    } ?: emptyList()
                }
            } catch (_: Exception) {
                redemptions = emptyList()
            }
            loadingRedemptions = false
        }
    }

    BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Vouchers" to null)

    H2(attrs = { style { fontSize(22.px); fontWeight(700); color(Gray900); margin(0.px, 0.px, 4.px) } }) {
        Text("Vouchers")
    }
    P(attrs = { style { color(Gray500); fontSize(13.px); margin(0.px, 0.px, 20.px) } }) {
        Text("Promo codes granting N days of unlimited access. Share by email; anyone registered can redeem once.")
    }

    // ── Create ──────────────────────────────────────────────────────────────
    Div(attrs = {
        style {
            backgroundColor(Color.white); borderRadius(14.px); padding(18.px)
            property("border", "1px solid $Gray200"); property("margin-bottom", "22px")
        }
    }) {
        H3(attrs = { style { fontSize(15.px); fontWeight(700); color(Gray900); margin(0.px, 0.px, 12.px) } }) {
            Text("Create a code")
        }
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); property("gap", "10px"); property("flex-wrap", "wrap")
                alignItems(AlignItems.FlexEnd)
            }
        }) {
            AdminField("Code", newCode, "7FREE26", 150) { newCode = it.uppercase() }
            AdminField("Days", newDays, "7", 80) { newDays = it }
            AdminField("Expires (optional)", newExpiry, "yyyy-mm-dd", 150) { newExpiry = it }
            AdminField("Note (optional)", newNote, "Ramadan mailout", 200) { newNote = it }
            Button(attrs = {
                onClick { if (!creating) create() }
                if (creating) disabled()
                style {
                    padding(10.px, 18.px); fontSize(14.px); fontWeight(700)
                    color(Color.white); backgroundColor(Color("#0E7C7B"))
                    borderRadius(10.px); property("border", "none"); cursor("pointer")
                    if (creating) property("opacity", "0.6")
                }
            }) { Text(if (creating) "Creating…" else "Create") }
        }
        error?.let {
            P(attrs = { style { color(Color("#dc2626")); fontSize(13.px); margin(10.px, 0.px, 0.px) } }) { Text(it) }
        }
    }

    // ── List ────────────────────────────────────────────────────────────────
    if (loading) {
        P(attrs = { style { color(Gray500); fontSize(14.px) } }) { Text("Loading…") }
    } else if (rows.isEmpty()) {
        P(attrs = { style { color(Gray500); fontSize(14.px) } }) { Text("No voucher codes yet.") }
    } else {
        rows.forEach { v ->
            val expired = v.expiresAt?.let { it < nowIsoUtc() } == true
            Div(attrs = {
                style {
                    backgroundColor(Color.white); borderRadius(14.px); padding(16.px)
                    property("border", "1px solid $Gray200"); property("margin-bottom", "12px")
                    if (!v.enabled || expired) property("opacity", "0.62")
                }
            }) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                        property("gap", "12px"); property("flex-wrap", "wrap")
                    }
                }) {
                    Span(attrs = {
                        style {
                            fontSize(17.px); fontWeight(700); color(Gray900)
                            property("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
                            property("letter-spacing", "1px")
                        }
                    }) { Text(v.code) }

                    // Status chip — disabled beats expired, since disabling is the deliberate act.
                    val (chipText, chipColor) = when {
                        !v.enabled -> "Disabled" to "#6b7280"
                        expired -> "Expired" to "#b45309"
                        else -> "Active" to "#0E7C7B"
                    }
                    Span(attrs = {
                        style {
                            fontSize(11.px); fontWeight(700); color(Color.white)
                            backgroundColor(Color(chipColor)); borderRadius(999.px)
                            padding(3.px, 10.px)
                        }
                    }) { Text(chipText) }

                    Span(attrs = { style { fontSize(13.px); color(Gray600) } }) {
                        Text("${v.durationDays} days")
                    }
                    Span(attrs = { style { fontSize(13.px); color(Gray600) } }) {
                        Text("· ${v.redemptionCount} redeemed")
                    }
                    v.expiresAt?.let {
                        Span(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                            Text("· expires ${it.substringBefore('T')}")
                        }
                    }
                    v.note?.let {
                        Span(attrs = { style { fontSize(13.px); color(Gray500); property("font-style", "italic") } }) {
                            Text("· $it")
                        }
                    }
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); property("gap", "8px")
                        property("margin-top", "12px"); property("flex-wrap", "wrap")
                    }
                }) {
                    // The kill switch. Stops NEW redemptions; existing windows are untouched.
                    Button(attrs = {
                        onClick { patch(v.id, """{"id":"${v.id}","enabled":${!v.enabled}}""") }
                        if (busyId == v.id) disabled()
                        style {
                            padding(7.px, 14.px); fontSize(13.px); fontWeight(600)
                            borderRadius(8.px); cursor("pointer")
                            if (v.enabled) {
                                color(Color("#b91c1c")); backgroundColor(Color("#fef2f2"))
                                property("border", "1px solid #fecaca")
                            } else {
                                color(Color("#0E7C7B")); backgroundColor(Color("#ecfdf5"))
                                property("border", "1px solid #a7f3d0")
                            }
                        }
                    }) { Text(if (v.enabled) "Disable" else "Enable") }

                    Button(attrs = {
                        onClick { if (openRedemptionsFor == v.id) openRedemptionsFor = null else loadRedemptions(v.id) }
                        style {
                            padding(7.px, 14.px); fontSize(13.px); fontWeight(600)
                            color(Gray700); backgroundColor(Color.white)
                            property("border", "1px solid $Gray200"); borderRadius(8.px); cursor("pointer")
                        }
                    }) { Text(if (openRedemptionsFor == v.id) "Hide redemptions" else "View redemptions") }
                }

                if (openRedemptionsFor == v.id) {
                    Div(attrs = { style { property("margin-top", "12px") } }) {
                        when {
                            loadingRedemptions -> P(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Loading…") }
                            redemptions.isEmpty() -> P(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Nobody has redeemed this yet.") }
                            else -> redemptions.forEach { r ->
                                Div(attrs = {
                                    style {
                                        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                                        fontSize(13.px); color(Gray600)
                                        padding(6.px, 0.px)
                                        property("border-bottom", "1px solid $Gray100")
                                    }
                                }) {
                                    Span { Text(r.email) }
                                    Span(attrs = { style { color(Gray500) } }) {
                                        Text("${r.redeemedAt?.substringBefore('T') ?: "?"} → ${r.grantedUntil?.substringBefore('T') ?: "?"}")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Current UTC instant as an ISO string, for comparing against stored `expires_at` values. */
private fun nowIsoUtc(): String = kotlin.js.Date().toISOString()

/** Labelled admin text input. */
@Composable
private fun AdminField(
    label: String,
    value: String,
    placeholderText: String,
    widthPx: Int,
    onChange: (String) -> Unit
) {
    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        Label(attrs = {
            style { fontSize(11.px); fontWeight(600); color(Gray500); property("margin-bottom", "4px") }
        }) { Text(label) }
        TextInput(value) {
            onInput { onChange(it.value) }
            placeholder(placeholderText)
            style {
                width(widthPx.px); padding(9.px, 11.px); fontSize(14.px)
                borderRadius(9.px); property("border", "1px solid $Gray200"); property("outline", "none")
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Referrals View — the refer-a-friend program (/api/admin/referrals*)
// ---------------------------------------------------------------------------
// One row per referred friend, newest first. Every free month is given (and, in the friend's
// first month, taken back) by the server: a subscription's next bill pushed back a month, a
// founding member's access end moved; nothing on this page writes to Stripe. The buttons only
// record what a person did, or move a row through the database's state machine
// (2026-09-23_referrals.sql).

/**
 * One row of GET /api/admin/referrals (the database's referral_json). Every field has a default so
 * a column the server adds later, or leaves null, can never blank the whole page.
 */
@Serializable
private data class AdminReferralRow(
    val id: String = "",
    val code: String = "",
    @SerialName("referrer_email") val referrerEmail: String = "",
    @SerialName("referee_email") val refereeEmail: String = "",
    val source: String? = null,
    val status: String = "",
    @SerialName("paid_at") val paidAt: String? = null,
    /** What Stripe collected for their first month. Shown only: any amount counts. */
    @SerialName("amount_paid_cents") val amountPaidCents: Int? = null,
    @SerialName("referee_interval") val refereeInterval: String? = null,
    val currency: String? = null,
    @SerialName("referee_subscription_id") val refereeSubscriptionId: String? = null,
    /**
     * The end of the friend's first month (a month from paying, or their first paid period if
     * shorter): until then a cancel, refund or dispute takes both months back. A window, not an
     * access date: for a yearly member it is a month in, while their paid year runs on.
     */
    @SerialName("month1_end") val month1End: String? = null,
    /** The end of what their first payment paid for: where their next bill goes back to if the free month is taken back. */
    @SerialName("paid_period_end") val paidPeriodEnd: String? = null,
    @SerialName("locked_at") val lockedAt: String? = null,
    @SerialName("revoked_at") val revokedAt: String? = null,
    @SerialName("revoke_reason") val revokeReason: String? = null,
    @SerialName("friend_push_status") val friendPushStatus: String = "none",
    /** The friend's next bill after their free month. */
    @SerialName("friend_push_to") val friendPushTo: String? = null,
    @SerialName("friend_attempts") val friendAttempts: Int = 0,
    @SerialName("reward_status") val rewardStatus: String = "none",
    @SerialName("reward_cohort") val rewardCohort: String? = null,
    @SerialName("reward_kind") val rewardKind: String? = null,
    /** The subscription pushed, a founding member's new access end, or your note from Mark applied. */
    @SerialName("reward_ref") val rewardRef: String? = null,
    /** The referrer's next bill (or access end) after their free month. */
    @SerialName("reward_push_to") val rewardPushTo: String? = null,
    @SerialName("rewarded_at") val rewardedAt: String? = null,
    @SerialName("reward_attempts") val rewardAttempts: Int = 0,
    @SerialName("reward_next_attempt_at") val rewardNextAttemptAt: String? = null,
    @SerialName("checked_at") val checkedAt: String? = null,
    @SerialName("last_error") val lastError: String? = null,
    @SerialName("void_reason") val voidReason: String? = null,
    @SerialName("voided_at") val voidedAt: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("code_disabled") val codeDisabled: Boolean = false,
    /**
     * Which Stripe account the friend paid on: "main" (the app, /register) or "thrivecart" (the
     * ThriveCart checkout). A manual task on a ThriveCart row is done on the ThriveCart Stripe account.
     */
    @SerialName("referee_account") val refereeAccount: String = "main",
)

/** The friend paid on the ThriveCart checkout, so their subscription is on the ThriveCart Stripe account. */
private val AdminReferralRow.friendOnThriveCart: Boolean get() = refereeAccount == "thrivecart"

@Serializable
private data class AdminReferralList(val referrals: List<AdminReferralRow> = emptyList())

/** Rows per load. The server allows up to 1000; this is years of referrals at today's size. */
private const val REFERRAL_LIST_LIMIT = 500

/** The filter chips, in the order a row moves through them. */
private val REFERRAL_FILTERS = listOf(
    "all" to "All",
    "pending" to "Pending",
    "granted" to "In first month",
    "capped" to "Capped",
    "needs_manual" to "Needs you",
    "locked" to "Locked in",
    "revoked" to "Revoked",
    "voided" to "Voided",
)

/** Colour a referral status so the table scans at a glance. Red is kept for the rows waiting on you. */
private fun referralStatusPill(status: String): Triple<String, CSSColorValue, CSSColorValue> = when (status) {
    "pending" -> Triple("PENDING", Gray600, Gray200)
    "granted" -> Triple("IN FIRST MONTH", rgb(146, 64, 14), rgb(254, 243, 199))
    "capped" -> Triple("CAPPED", rgb(91, 33, 182), rgb(237, 233, 254))
    "needs_manual" -> Triple("NEEDS YOU", rgb(153, 27, 27), rgb(254, 226, 226))
    "locked" -> Triple("LOCKED IN", rgb(6, 95, 70), rgb(209, 250, 229))
    "revoked" -> Triple("REVOKED", rgb(55, 65, 81), rgb(243, 244, 246))
    "voided" -> Triple("VOIDED", Gray600, Gray200)
    else -> Triple(status.uppercase(), Gray600, Gray200)
}

private fun referralSourceLabel(source: String?): String? = when (source) {
    "link" -> "via share link"
    "typed_paywall" -> "code typed in the app"
    "public_checkout" -> "code on the offer page"
    "checkout_metadata" -> "code at checkout"
    // Typed on the Free Gift screen after subscribing (in the app, or on the ThriveCart page).
    "redeem" -> "code entered after subscribing"
    null, "" -> null
    else -> source
}

private fun referralCohortLabel(cohort: String?): String? = when (cohort) {
    "main_monthly" -> "Stripe monthly"
    "main_annual" -> "Stripe annual"
    "thrivecart" -> "ThriveCart"
    "founding" -> "Founding member"
    null, "" -> null
    else -> cohort
}

/** Why a paid referral was stopped in its first month (referrals.revoke_reason), in words. */
private fun referralRevokeReasonLabel(reason: String?): String? = when (reason) {
    "canceled" -> "cancelled in their first month"
    "ended" -> "subscription ended in their first month"
    "refund" -> "refunded in their first month"
    "dispute" -> "disputed in their first month"
    "admin" -> "voided by you"
    "not_new" -> "not a new member (found after a claimed purchase)"
    "self_email" -> "the referrer's own email (found after a claimed purchase)"
    null, "" -> null
    else -> reason
}

/** Why a referral was voided (referrals.void_reason), in words. An admin void carries its typed reason. */
private fun referralVoidReasonLabel(reason: String): String = when {
    reason.startsWith("admin:") -> "by admin: " + reason.removePrefix("admin:")
    reason == "no_payment" -> "nothing was paid"
    reason == "self_email" -> "the referrer's own email"
    reason == "self_card" -> "the referrer's own card"
    reason == "circular" -> "a loop: they had referred the referrer"
    reason == "card_reused" -> "a card used by another member"
    reason == "not_new" -> "not a new member"
    reason == "merged" -> "merged into another account"
    reason == "superseded" -> "from an earlier test version of the program"
    else -> reason
}

/** Cents in the row's own currency. Stripe sends currencies in lower case. */
private fun referralMoney(cents: Int?, currency: String?): String? =
    cents?.let { formatMoneyShort(it / 100.0, currency?.trim()?.uppercase()?.ifBlank { null } ?: "USD") }

/** "in 5d" / "in 3h" / "ended", so a date reads at a glance without date arithmetic. */
private fun referralTimeLeft(iso: String?): String? {
    if (iso.isNullOrBlank()) return null
    val at = kotlin.js.Date.parse(iso)
    if (at.isNaN()) return null
    val ms = at - kotlin.js.Date.now()
    return when {
        ms <= 0 -> "ended"
        ms < 86_400_000 -> "in ${kotlin.math.ceil(ms / 3_600_000).toInt()}h"
        else -> "in ${kotlin.math.ceil(ms / 86_400_000).toInt()}d"
    }
}

/**
 * Whether the FRIEND's month waits on you, whatever the row status (mirrors
 * referral_friend_needs_person): a push Stripe refused on a row still running, or a take-back only
 * a person can do. The server's "needs_manual" list includes these rows.
 */
private fun referralFriendNeedsYou(row: AdminReferralRow): Boolean =
    row.friendPushStatus == "revert_failed" ||
        (row.friendPushStatus == "failed" && row.revokedAt == null && row.status !in setOf("revoked", "voided"))

/** The friend's free month (friend_push_status), in words, with the colour it's shown in. */
private fun referralFriendMonthLabel(row: AdminReferralRow): Pair<String, CSSColorValue> = when (row.friendPushStatus) {
    "applied" -> "Given" to rgb(6, 95, 70)
    "applied_by_hand" -> "Given by hand" to rgb(6, 95, 70)
    "owed" -> (if (row.friendAttempts > 1) "Retrying" else "Being given") to rgb(146, 64, 14)
    // Stopped since: nothing is owed any more (the admin push at the stop said to undo a hand push).
    "failed" -> if (referralFriendNeedsYou(row)) "Push it by hand" to rgb(153, 27, 27) else "Not given (push failed)" to Gray600
    "skipped" -> "Not given" to Gray600
    "reverted" -> "Taken back" to Gray600
    "revert_failed" -> "Take it back by hand" to rgb(153, 27, 27)
    else -> (if (row.status == "pending") "When they pay" else "None") to Gray400
}

/** The referrer's free month (reward_status), in words, with the colour it's shown in. */
private fun referralRewardLabel(row: AdminReferralRow): Pair<String, CSSColorValue> = when (row.rewardStatus) {
    "applied" -> (if (row.rewardKind == "manual") "Given by hand" else "Given") to rgb(6, 95, 70)
    "owed" -> {
        val retry = row.rewardNextAttemptAt?.takeIf { referralTimeLeft(it) != "ended" }
        (if (retry != null) "Waiting (no paid access)" else "Being given") to rgb(146, 64, 14)
    }
    "capped" -> "Capped: 12 this year" to rgb(91, 33, 182)
    "manual" -> "Give it by hand" to rgb(153, 27, 27)
    "withdrawn" -> "Not given" to Gray600
    "reverted" -> "Taken back" to Gray600
    "revert_manual" -> "Take it back by hand" to rgb(153, 27, 27)
    else -> (if (row.status == "pending") "When they pay" else "None") to Gray400
}

/** What an admin Void does to this row right now. */
private enum class ReferralVoidEffect { PENDING, TAKE_BOTH_BACK, WITHDRAW_REFERRER, NONE }

/**
 * Whether the friend's first month is still running by its window (month1_end), whatever the lock:
 * the nightly lock lands only when that night's Stripe check succeeds, so it can be days late.
 */
private fun referralMonthOneRunning(row: AdminReferralRow): Boolean =
    row.month1End?.let { kotlin.js.Date.parse(it) }?.takeUnless { it.isNaN() }?.let { it > kotlin.js.Date.now() } == true

/** Mirrors referral_void in the migration: which rows a void can still stop, and how. */
private fun referralVoidEffect(row: AdminReferralRow): ReferralVoidEffect = when {
    row.status == "pending" -> ReferralVoidEffect.PENDING
    row.status in setOf("granted", "capped", "needs_manual") && row.revokedAt == null && row.lockedAt == null &&
        referralMonthOneRunning(row) -> ReferralVoidEffect.TAKE_BOTH_BACK
    row.status == "needs_manual" && row.revokedAt == null && row.rewardStatus == "manual" -> ReferralVoidEffect.WITHDRAW_REFERRER
    else -> ReferralVoidEffect.NONE
}

/**
 * The referral program's admin page.
 *
 * Read-mostly: the server gives and takes back every month it can. The four buttons are the calls
 * only a person can make:
 *  - Void: stops a referral. One still pending just stops; a paid one still in the friend's first
 *    month has both free months taken back, as a cancel would; a referrer month waiting on you
 *    whose friend's first month is over is simply not given. Nothing is taken back after the
 *    friend's first month (its end date, even before the nightly check has locked the row).
 *  - Mark applied: a month waiting on you (a ThriveCart member's or ThriveCart friend's the server
 *    couldn't push on the ThriveCart Stripe account, or a friend's that Stripe refused) once you
 *    have pushed the bill back, or moved it forward again, by hand. One job per press, the friend's
 *    first. It records; it doesn't change any bill.
 *  - Disable code: stops NEW friends attaching. Friends already attached still get their month.
 *  - Run nightly check now: the nightly job, early. Nothing is ever given twice.
 *
 * Localhost-only and pointed at PROD like the rest of /admin, so it shows nothing until the referral
 * server build is live on PROD and the migration has been run there.
 */
@Composable
private fun ReferralsView(onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }
    var filter by remember { mutableStateOf("all") }
    var rows by remember { mutableStateOf<List<AdminReferralRow>>(emptyList()) }
    var needsManualCount by remember { mutableStateOf<Int?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var notice by remember { mutableStateOf<String?>(null) }

    // The one open inline form: row id to "void" | "applied" | "code". One at a time, so a
    // half-typed reason can never be sent against a different row.
    var openAction by remember { mutableStateOf<Pair<String, String>?>(null) }
    var actionText by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var sweepRunning by remember { mutableStateOf(false) }

    fun authHeaders(): dynamic {
        val h = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
        try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
            catch (_: Exception) { null }
            ?.let { h["Authorization"] = "Bearer $it" }
        return h
    }

    fun JsonObject.str(key: String): String? =
        (this[key] as? kotlinx.serialization.json.JsonPrimitive)?.content?.takeIf { it != "null" }

    /** One page of the list. Throws with a message meant for the page on anything but a JSON list. */
    suspend fun fetchReferrals(status: String, limit: Int): List<AdminReferralRow> {
        val statusParam = if (status == "all") "" else "status=${encodeURIComponent(status)}&"
        val resp = kotlinx.browser.window.fetch(
            "$serverUrl/api/admin/referrals?${statusParam}limit=$limit",
            kotlin.js.json("method" to "GET", "headers" to authHeaders()).unsafeCast<org.w3c.fetch.RequestInit>()
        ).await()
        val text = resp.text().await()
        return when {
            // The site's catch-all answers any unknown GET with index.html and a 200.
            resp.ok && text.trimStart().startsWith("<") -> throw IllegalStateException(
                "The server has no /api/admin/referrals endpoint yet — it returned the website instead of JSON. " +
                    "The referral server build isn't live on PROD."
            )
            resp.ok -> adminJson.decodeFromString<AdminReferralList>(text).referrals
            // list_failed: the RPC is missing or broke, which on PROD almost always means the migration.
            resp.status.toInt() == 500 -> throw IllegalStateException(
                "The server couldn't read the referrals. Has db/migrations/2026-09-23_referrals.sql been run on PROD?"
            )
            // An older server build that doesn't know this filter's status yet.
            resp.status.toInt() == 400 -> throw IllegalStateException(
                "The server doesn't know that status. The referral server build on PROD is older than this page."
            )
            else -> throw IllegalStateException("Failed to load (${resp.status}). ${text.take(300)}")
        }
    }

    fun load() {
        val wanted = filter
        scope.launch {
            loading = true; error = null
            try {
                val list = fetchReferrals(wanted, REFERRAL_LIST_LIMIT)
                // A slower answer for a chip you've already clicked away from must not overwrite the new one.
                if (filter != wanted) return@launch
                rows = list
                loading = false
                // The needs_manual count rides on its chip from every filter: the server's needs_manual
                // list is every row waiting on a person (a friend's month too, whatever the row status).
                needsManualCount = if (wanted == "needs_manual") list.size
                    else try { fetchReferrals("needs_manual", 1000).size } catch (_: Exception) { null }
            } catch (e: Exception) {
                if (filter != wanted) return@launch
                rows = emptyList()
                error = e.message ?: "Failed to load referrals"
                loading = false
            }
        }
    }
    LaunchedEffect(filter) { load() }

    /** POST a JSON body to an admin endpoint and return the JSON object it answers with. */
    suspend fun postJson(path: String, body: JsonObject): JsonObject {
        val resp = kotlinx.browser.window.fetch(
            "$serverUrl$path",
            kotlin.js.json("method" to "POST", "headers" to authHeaders(), "body" to body.toString())
                .unsafeCast<org.w3c.fetch.RequestInit>()
        ).await()
        val text = resp.text().await()
        if (!resp.ok) throw IllegalStateException("${resp.status} ${text.take(200)}")
        val parsed = try { adminJson.parseToJsonElement(text) as? JsonObject } catch (_: Exception) { null }
        return parsed
            ?: throw IllegalStateException("the server answered with something that isn't JSON. Is the referral build live on PROD?")
    }

    fun toggleAction(rowId: String, kind: String) {
        openAction = if (openAction == (rowId to kind)) null else rowId to kind
        actionText = ""
        error = null
    }

    fun runAction(row: AdminReferralRow, kind: String) {
        if (busy) return
        val text = actionText.trim()
        if (kind == "void" && text.isBlank()) {
            error = "Give a reason for the void. It goes in the audit trail."
            return
        }
        scope.launch {
            busy = true; error = null; notice = null
            try {
                notice = when (kind) {
                    "void" -> {
                        val r = postJson("/api/admin/referrals/void", buildJsonObject {
                            put("id", row.id); put("reason", text)
                        })
                        when (r.str("status")) {
                            "voided" -> if (row.status == "pending") {
                                "Voided. ${row.refereeEmail} hadn't paid, so nothing had been given, and now nothing will be."
                            } else {
                                "Voided. Both free months were taken back: ${row.refereeEmail}'s next bill is back on its " +
                                    "original date, and ${row.referrerEmail}'s free month is gone. If a month was given by " +
                                    "hand (a ThriveCart referrer), the row now asks you to take it back by hand."
                            }
                            "withdrawn" -> "Done. ${row.referrerEmail} won't be given this free month. " +
                                "${row.refereeEmail}'s first month is over, so they keep theirs."
                            "already_locked" -> "Locked in: ${row.refereeEmail}'s first month is over, so nothing can be " +
                                "taken back. Undo it by hand in Stripe if you really must."
                            "already_final" -> "It was already revoked or voided, so nothing changed."
                            "not_found" -> "That referral no longer exists."
                            else -> "The server answered: ${r.str("status") ?: "nothing"}"
                        }
                    }
                    "applied" -> {
                        val r = postJson("/api/admin/referrals/mark-applied", buildJsonObject {
                            put("id", row.id); put("note", text)
                        })
                        when (r.str("status")) {
                            "friend_applied" -> "Recorded: ${row.refereeEmail}'s free month was given by hand."
                            "friend_reverted" -> "Recorded: ${row.refereeEmail}'s free month was taken back by hand."
                            "applied" -> "Recorded: ${row.referrerEmail}'s free month was given by hand."
                            "reverted" -> "Recorded: ${row.referrerEmail}'s free month was taken back by hand."
                            "bad_state" -> "It isn't waiting on you any more, so nothing was changed. The list is refreshed below."
                            "not_found" -> "That referral no longer exists."
                            else -> "The server answered: ${r.str("status") ?: "nothing"}"
                        }
                    }
                    else -> {
                        val disable = !row.codeDisabled
                        val shown = com.islamicmedia.shared.util.ReferralCodeFormat.display(row.code)
                        val r = postJson("/api/admin/referral-codes/disable", buildJsonObject {
                            put("code", row.code); put("disabled", disable)
                            if (disable && text.isNotBlank()) put("reason", text)
                        })
                        when (r.str("status")) {
                            "ok" -> if (disable) "Code $shown is disabled. New friends can't use it." else "Code $shown works again."
                            "not_found" -> "Code $shown doesn't exist."
                            else -> "The server answered: ${r.str("status") ?: "nothing"}"
                        }
                    }
                }
                openAction = null; actionText = ""
                load()
            } catch (e: Exception) {
                error = "That didn't go through: ${e.message}"
            }
            busy = false
        }
    }

    fun runSweep() {
        if (sweepRunning) return
        val ok = kotlinx.browser.window.confirm(
            "Run the nightly referral check now?\n\nIt checks every friend still in their first month against Stripe " +
                "(a cancel, refund or dispute the webhook missed takes both free months back; a first month that is " +
                "over locks them in), then retries every free month still to give or take back. Nothing is ever given twice."
        )
        if (!ok) return
        scope.launch {
            sweepRunning = true; error = null; notice = null
            try {
                val r = postJson("/api/cron/referrals/payout", buildJsonObject { put("trigger", "admin_console") })
                notice = when {
                    r.str("started") == "true" ->
                        "Started. It runs in the background on the server; refresh in a minute to see the results."
                    r.str("reason") == "already running" -> "It is already running on the server. Refresh in a minute."
                    r.str("reason") == "disabled" ->
                        "Referrals are switched off on the server (REFERRALS_ENABLED isn't \"true\"), so nothing ran."
                    else -> "It didn't start: ${r.str("reason") ?: "no reason given"}"
                }
            } catch (e: Exception) {
                error = "It didn't start: ${e.message}"
            }
            sweepRunning = false
        }
    }

    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Referrals" to null)

        Div(attrs = { style { property("margin-bottom", "20px") } }) {
            H1(attrs = {
                style {
                    color(Color.white); fontSize(24.px); fontWeight(700)
                    property("font-family", TitleFontFamily); property("margin", "0 0 6px")
                }
            }) { Text("Referrals") }
            P(attrs = { style { color(Color("#d7ede9")); fontSize(13.px); property("margin", "0"); property("max-width", "820px") } }) {
                Text(
                    "Refer a friend: when a friend subscribes with a member's code and pays their first month (any " +
                        "amount), both get a free month at once. Each of their next bills moves back a month (a founding " +
                        "member's access end moves instead; a ThriveCart member's bill is yours to push by hand). If the " +
                        "friend cancels, is refunded or disputes within their first month (a month from paying, even on " +
                        "a yearly plan), both months are taken back; after that, nothing is. No limit per referrer. The server does all of it; this " +
                        "page is for checking, and for what only a person can do."
                )
            }
        }

        error?.let { msg ->
            Div(attrs = {
                style {
                    backgroundColor(Color("#fef2f2")); property("border", "1px solid #fca5a5")
                    borderRadius(12.px); padding(14.px); property("margin-bottom", "16px")
                    color(Color("#991b1b")); fontSize(13.px)
                }
            }) { Text(msg) }
        }
        notice?.let { msg ->
            Div(attrs = {
                style {
                    backgroundColor(PrimaryPale); property("border", "1px solid #99d5d1")
                    borderRadius(12.px); padding(14.px); property("margin-bottom", "16px")
                    color(Gray900); fontSize(13.px)
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); gap(12.px)
                }
            }) {
                Span { Text(msg) }
                Span(attrs = {
                    onClick { notice = null }
                    attr("title", "Dismiss")
                    style { cursor("pointer"); color(Gray500); fontSize(15.px) }
                }) { Text("×") }
            }
        }

        FunnelCard(if (loading) "Referred friends" else "Referred friends (${rows.size}${if (rows.size >= REFERRAL_LIST_LIMIT) "+" else ""})") {
            // ── Filters + page actions ────────────────────────────────────────
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center); gap(12.px); property("flex-wrap", "wrap")
                    property("margin-bottom", "14px")
                }
            }) {
                Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); property("flex-wrap", "wrap") } }) {
                    REFERRAL_FILTERS.forEach { (key, label) ->
                        val active = filter == key
                        Button(attrs = {
                            onClick { if (filter != key) { filter = key; openAction = null; notice = null } }
                            style {
                                padding(7.px, 12.px); borderRadius(999.px); cursor("pointer"); fontSize(13.px)
                                fontWeight(if (active) 700 else 500)
                                property("border", "1px solid " + if (active) "#0E7C7B" else "#e2e8f0")
                                backgroundColor(if (active) PrimaryPale else Color.white)
                                color(if (active) Primary else Gray600)
                            }
                        }) {
                            Text(label)
                            val waiting = needsManualCount
                            if (key == "needs_manual" && waiting != null && waiting > 0) {
                                Span(attrs = {
                                    style {
                                        marginLeft(6.px); fontSize(11.px); fontWeight(700); color(Color.white)
                                        backgroundColor(Color("#dc2626")); borderRadius(999.px); padding(1.px, 7.px)
                                    }
                                }) { Text(waiting.toString()) }
                            }
                        }
                    }
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px) } }) {
                    Button(attrs = {
                        onClick { notice = null; load() }
                        style {
                            padding(8.px, 14.px); borderRadius(8.px); cursor("pointer"); fontSize(13.px); fontWeight(600)
                            property("border", "1px solid $Gray200"); backgroundColor(Color.white); color(Gray700)
                        }
                    }) { Text("Refresh") }
                    Button(attrs = {
                        if (!sweepRunning) onClick { runSweep() }
                        attr("title", "The nightly job, now: checks every friend still in their first month, then retries every free month still owed")
                        style {
                            padding(8.px, 14.px); borderRadius(8.px); property("border", "none")
                            backgroundColor(if (sweepRunning) Gray400 else Primary); color(Color.white)
                            fontSize(13.px); fontWeight(600); cursor(if (sweepRunning) "default" else "pointer")
                        }
                    }) { Text(if (sweepRunning) "Starting…" else "Run nightly check now") }
                }
            }

            // ── The list ──────────────────────────────────────────────────────
            if (loading && rows.isEmpty()) {
                P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) { Text("Loading…") }
            } else if (rows.isEmpty()) {
                P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0") } }) {
                    Text(if (filter == "all") "No referrals yet." else "No referrals with this status.")
                }
            } else {
                TableWrapper {
                    Thead {
                        Tr(attrs = { style { property("background", PrimaryPale.toString()) } }) {
                            AdminTh("Created")
                            AdminTh("Referrer")
                            AdminTh("Friend")
                            AdminTh("Status")
                            AdminTh("Paid")
                            AdminTh("First month")
                            AdminTh("Friend's month")
                            AdminTh("Referrer's month")
                            AdminTh("Last error")
                            AdminTh("")
                        }
                    }
                    Tbody {
                        rows.forEachIndexed { index, row ->
                            ReferralTableRow(
                                row = row,
                                striped = index % 2 == 1,
                                openKind = openAction?.takeIf { it.first == row.id }?.second,
                                onNavigate = onNavigate,
                                onToggle = { kind -> toggleAction(row.id, kind) },
                            )
                            val open = openAction
                            if (open != null && open.first == row.id) {
                                ReferralActionForm(
                                    row = row,
                                    kind = open.second,
                                    text = actionText,
                                    busy = busy,
                                    onText = { actionText = it },
                                    onConfirm = { runAction(row, open.second) },
                                    onCancel = { openAction = null; actionText = "" },
                                )
                            }
                        }
                    }
                }
            }

            P(attrs = { style { fontSize(11.px); color(Gray500); property("margin", "12px 0 0"); property("line-height", "1.6") } }) {
                Text(
                    "Pending: a code is attached, no payment yet (if the friend then buys on the ThriveCart page, the " +
                        "nightly check finishes it while they are in their first month; they can also enter it on their " +
                        "Free Gift screen). In first month: the friend has paid, and both free " +
                        "months were given at that moment (each next bill moved back a month); until their first month " +
                        "ends (a month from paying, even on a yearly plan: \"paid to\" shows where their paid year ends), " +
                        "a cancel, refund or dispute takes both back (un-cancelling doesn't restore them). " +
                        "Locked in: their first month ended with none of that, so both months are theirs for good. " +
                        "Revoked: they left, or were refunded, in their first month; both months were taken back. Capped: " +
                        "the referrer was over the optional yearly limit (none by default), so only the friend got one. Needs you: " +
                        "a free month the server can't give or take back itself (usually a ThriveCart referrer's, or a " +
                        "friend's that Stripe refused): do it by hand, then press Mark applied. \"Being given\" or \"Retrying\": the server is still writing it, " +
                        "and tries again every night. The nightly check also catches any cancel or refund the webhook missed."
                )
            }
        }
    }
}

/** One referral's line in the table. Pure display: the parent owns every action. */
@Composable
private fun ReferralTableRow(
    row: AdminReferralRow,
    striped: Boolean,
    openKind: String?,
    onNavigate: (DrillDownView) -> Unit,
    onToggle: (String) -> Unit,
) {
    Tr(attrs = {
        style {
            when {
                // The rows waiting on a person stand out without a filter.
                row.status == "needs_manual" || referralFriendNeedsYou(row) -> backgroundColor(Color("#fff5f5"))
                striped -> backgroundColor(Gray50)
            }
            property("border-bottom", if (openKind != null) "none" else "1px solid $Gray200")
        }
    }) {
        ReferralCell {
            Span(attrs = { style { property("white-space", "nowrap"); color(Gray600) } }) { Text(formatIsoDate(row.createdAt)) }
        }
        ReferralCell {
            ClickableText(row.referrerEmail) { onNavigate(DrillDownView.UserDetail(row.referrerEmail, null)) }
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px); property("margin-top", "3px") } }) {
                Span(attrs = {
                    style {
                        fontSize(12.px); color(Gray700); property("letter-spacing", "0.5px")
                        property("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
                    }
                }) { Text(com.islamicmedia.shared.util.ReferralCodeFormat.display(row.code)) }
                if (row.codeDisabled) StatusPill("CODE OFF", Gray600, Gray200)
            }
        }
        ReferralCell {
            ClickableText(row.refereeEmail) { onNavigate(DrillDownView.UserDetail(row.refereeEmail, null)) }
            // Paid on the ThriveCart checkout: any job by hand for this friend is on the ThriveCart Stripe account.
            if (row.friendOnThriveCart) {
                Div(attrs = { style { property("margin-top", "3px") } }) {
                    StatusPill("THRIVECART", rgb(146, 64, 14), rgb(255, 237, 213))
                }
            }
            referralSourceLabel(row.source)?.let { ReferralSubLine(it) }
        }
        ReferralCell {
            val (label, fg, bg) = referralStatusPill(row.status)
            Span(attrs = { style { property("white-space", "nowrap") } }) { StatusPill(label, fg, bg) }
            // A row waiting on you can be one of four jobs: say which (the friend's comes first).
            if (referralFriendNeedsYou(row)) {
                ReferralSubLine(
                    if (row.friendPushStatus == "revert_failed") "take the friend's month back" else "give the friend's month",
                    rgb(153, 27, 27),
                )
            }
            if (row.status == "needs_manual") {
                ReferralSubLine(
                    if (row.rewardStatus == "revert_manual") "take the referrer's month back" else "give the referrer's month",
                    rgb(153, 27, 27),
                )
            }
            referralRevokeReasonLabel(row.revokeReason)?.takeIf { row.revokedAt != null }?.let { ReferralSubLine(it) }
            row.voidReason?.takeIf { row.status == "voided" && row.revokedAt == null }?.let { ReferralSubLine(referralVoidReasonLabel(it)) }
        }
        ReferralCell {
            if (row.paidAt == null) {
                Span(attrs = { style { color(Gray400) } }) { Text("-") }
            } else {
                Span(attrs = { style { property("white-space", "nowrap") } }) { Text(formatIsoDate(row.paidAt)) }
                listOfNotNull(referralMoney(row.amountPaidCents, row.currency), row.refereeInterval?.let { "per $it" })
                    .joinToString(" ").takeIf { it.isNotBlank() }?.let { ReferralSubLine(it) }
            }
        }
        ReferralCell {
            val end = row.month1End
            if (end == null || row.paidAt == null) {
                Span(attrs = { style { color(Gray400) } }) { Text("-") }
            } else {
                Span(attrs = { style { property("white-space", "nowrap") } }) { Text("ends ${formatIsoDate(end)}") }
                // A yearly (or longer) member: their first month ends long before what they paid for.
                row.paidPeriodEnd?.takeIf { formatIsoDate(it) != formatIsoDate(end) }
                    ?.let { ReferralSubLine("paid to ${formatIsoDate(it)}") }
                when {
                    row.lockedAt != null -> ReferralSubLine("locked in ${formatIsoDate(row.lockedAt)}", rgb(6, 95, 70))
                    row.revokedAt != null -> ReferralSubLine("stopped ${formatIsoDate(row.revokedAt)}")
                    else -> referralTimeLeft(end)?.let { ReferralSubLine(if (it == "ended") "ended, check pending" else it) }
                }
                row.checkedAt?.takeIf { row.lockedAt == null && row.revokedAt == null }
                    ?.let { ReferralSubLine("checked ${formatIsoDate(it)}") }
            }
        }
        ReferralCell {
            val (label, tint) = referralFriendMonthLabel(row)
            Span(attrs = { style { color(tint); fontWeight(600) } }) { Text(label) }
            row.friendPushTo?.takeIf { row.friendPushStatus == "applied" }?.let { ReferralSubLine("next bill ${formatIsoDate(it)}") }
        }
        ReferralCell {
            val (label, tint) = referralRewardLabel(row)
            Span(attrs = { style { color(tint); fontWeight(600) } }) { Text(label) }
            referralCohortLabel(row.rewardCohort)?.let { ReferralSubLine(it) }
            when {
                row.rewardStatus == "applied" && row.rewardKind == "founding_extend" ->
                    row.rewardPushTo?.let { ReferralSubLine("access to ${formatIsoDate(it)}") }
                row.rewardStatus == "applied" && row.rewardKind == "founding_no_expiry" -> ReferralSubLine("no expiry, nothing to add")
                row.rewardStatus == "applied" -> row.rewardPushTo?.let { ReferralSubLine("next bill ${formatIsoDate(it)}") }
                row.rewardStatus == "owed" -> row.rewardNextAttemptAt?.let { ReferralSubLine("retry ${referralTimeLeft(it) ?: formatIsoDate(it)}") }
            }
            // The subscription pushed, or your note from Mark applied.
            row.rewardRef?.takeIf { it.isNotBlank() && row.rewardKind != "founding_extend" }?.let { ref ->
                Div(attrs = {
                    attr("title", ref)
                    style {
                        fontSize(11.px); color(Gray500); property("margin-top", "2px"); property("max-width", "200px")
                        property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                    }
                }) { Text(ref) }
            }
        }
        ReferralCell {
            val err = row.lastError?.takeIf { it.isNotBlank() }
            if (err == null) {
                Span(attrs = { style { color(Gray400) } }) { Text("-") }
            } else {
                Div(attrs = {
                    attr("title", err)
                    style {
                        fontSize(12.px); color(rgb(153, 27, 27)); property("max-width", "220px")
                        property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap")
                    }
                }) { Text(err) }
                val tries = maxOf(row.friendAttempts, row.rewardAttempts)
                if (tries > 1) ReferralSubLine("after $tries tries")
            }
        }
        ReferralCell {
            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px); alignItems(AlignItems.FlexStart) } }) {
                if (row.status == "needs_manual" || referralFriendNeedsYou(row)) {
                    ReferralActionButton("Mark applied", openKind == "applied", Primary, PrimaryPale) { onToggle("applied") }
                }
                // Locked in, revoked and voided rows are final: nothing is taken back after month 1.
                if (referralVoidEffect(row) != ReferralVoidEffect.NONE) {
                    ReferralActionButton("Void", openKind == "void", rgb(185, 28, 28), rgb(254, 242, 242)) { onToggle("void") }
                }
                ReferralActionButton(
                    if (row.codeDisabled) "Enable code" else "Disable code",
                    openKind == "code", Gray700, Color.white,
                ) { onToggle("code") }
            }
        }
    }
}

/** One table cell, top-aligned so a row with a second line under one column still reads straight. */
@Composable
private fun ReferralCell(content: @Composable () -> Unit) {
    Td(attrs = { style { padding(10.px, 12.px); property("vertical-align", "top"); fontSize(13.px) } }) { content() }
}

/** The small grey second line under a cell's main value. */
@Composable
private fun ReferralSubLine(text: String, tint: CSSColorValue = Gray500) {
    Div(attrs = { style { fontSize(11.px); color(tint); property("margin-top", "2px") } }) { Text(text) }
}

@Composable
private fun ReferralActionButton(
    label: String,
    open: Boolean,
    fg: CSSColorValue,
    bg: CSSColorValue,
    onClick: () -> Unit,
) {
    Button(attrs = {
        this.onClick { onClick() }
        style {
            padding(5.px, 10.px); fontSize(12.px); fontWeight(600); borderRadius(7.px); cursor("pointer")
            property("white-space", "nowrap"); color(fg); backgroundColor(bg)
            property("border", "1px solid " + if (open) "#0E7C7B" else "#e2e8f0")
        }
    }) { Text(label) }
}

/**
 * The inline confirm for one row's action, drawn as a full-width line under it: what the action
 * does to THIS referral in plain words, the reason or note, and the confirm button.
 */
@Composable
private fun ReferralActionForm(
    row: AdminReferralRow,
    kind: String,
    text: String,
    busy: Boolean,
    onText: (String) -> Unit,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    val code = com.islamicmedia.shared.util.ReferralCodeFormat.display(row.code)
    val enabling = kind == "code" && row.codeDisabled
    val thriveCart = row.rewardCohort == "thrivecart"
    // Mark applied does one job per press, the friend's first (as referral_admin_mark_applied does).
    val friendJob = referralFriendNeedsYou(row)
    val takeBack = if (friendJob) row.friendPushStatus == "revert_failed" else row.rewardStatus == "revert_manual"
    val explanation = when (kind) {
        "void" -> when (referralVoidEffect(row)) {
            ReferralVoidEffect.PENDING ->
                "Void this referral: ${row.refereeEmail} hasn't paid yet, so nothing has been given, and after this " +
                    "paying won't give either of them a free month. Use it for self-referrals and abuse. It can't be undone."
            ReferralVoidEffect.TAKE_BOTH_BACK ->
                "Void this referral: both free months are taken back now. ${row.refereeEmail}'s next bill goes back to " +
                    "${(row.paidPeriodEnd ?: row.month1End)?.let { formatIsoDate(it) } ?: "the end of what they paid for"}, and " +
                    "${row.referrerEmail}'s free month is removed (one given by hand is left for you to take back by " +
                    "hand). Use it for self-referrals and abuse. It can't be undone."
            ReferralVoidEffect.WITHDRAW_REFERRER ->
                "Don't give ${row.referrerEmail} this free month: it is marked not given. ${row.refereeEmail}'s first " +
                    "month is over, so they keep theirs. It can't be undone."
            ReferralVoidEffect.NONE -> "This referral can't be voided any more."
        }
        "applied" -> if (friendJob && takeBack) {
            "${row.refereeEmail} left in their first month, so their free month is lost and only you can take it " +
                "back: move their next bill forward one month in Stripe" +
                (if (row.friendOnThriveCart) " (on the ThriveCart Stripe account)" else "") +
                (row.refereeSubscriptionId?.let { " (subscription $it)" } ?: "") +
                ". Then record what you did here. This only records it; it doesn't change any bill."
        } else if (friendJob) {
            (if (row.friendOnThriveCart) {
                "The server couldn't give ${row.refereeEmail} their free month on the ThriveCart Stripe account (no " +
                    "write key, not on the test list, or Stripe refused it: see the last error). Push their next bill " +
                    "back one month by hand there"
            } else {
                "Stripe refused ${row.refereeEmail}'s free month. Push their next bill back one month by hand in Stripe"
            }) +
                (row.refereeSubscriptionId?.let { " (subscription $it)" } ?: "") +
                ": trial end one calendar month past their renewal date, proration off. Then record what you did " +
                "here. This only records it; it doesn't change any bill."
        } else if (takeBack && row.rewardCohort == "founding") {
            "${row.refereeEmail} left in their first month, so take back the free month ${row.referrerEmail} was " +
                "given as a founding member. They have since subscribed, so their access date wasn't changed: take a " +
                "month off by hand, then record what you did here. This only records it; it doesn't change any date."
        } else if (takeBack) {
            "${row.refereeEmail} left in their first month, so take back the free month ${row.referrerEmail} was " +
                "given by hand: move their next bill forward one month" +
                (if (thriveCart) " on the ThriveCart Stripe account" else "") +
                ". Then record what you did here. This only records it; it doesn't change any bill."
        } else if (thriveCart) {
            "The server couldn't push this ThriveCart member's month (no ThriveCart write key, not on the test list, " +
                "or Stripe refused it: see the last error). Push ${row.referrerEmail}'s next ThriveCart bill back one " +
                "month by hand (on the ThriveCart Stripe account), then record what you did here. This only records " +
                "it; it doesn't change any bill."
        } else {
            "Give ${row.referrerEmail} their free month by hand (push their next bill back one month in Stripe), then " +
                "record what you did here. This only records it; it doesn't change any bill."
        }
        else -> if (enabling) {
            "Let new friends use $code again."
        } else {
            "Stop new friends using $code. Friends already attached still get their month when they pay, and " +
                "referrals already under way stay as they are; void those rows to stop them."
        }
    }
    val placeholderText = when (kind) {
        "void" -> "Reason (required, kept in the audit trail)"
        "applied" -> when {
            friendJob && takeBack -> "What you did, e.g. moved their bill forward on 2 Oct"
            friendJob -> "What you did, e.g. pushed their bill back one month on 2 Oct"
            takeBack -> "What you did, e.g. moved ThriveCart bill forward on 2 Oct"
            else -> "What you did, e.g. pushed ThriveCart bill back one month on 2 Oct"
        }
        else -> "Reason (optional)"
    }
    val confirmLabel = when (kind) {
        "void" -> if (referralVoidEffect(row) == ReferralVoidEffect.WITHDRAW_REFERRER) "Don't give it" else "Void referral"
        "applied" -> if (takeBack) "Mark taken back" else "Mark given"
        else -> if (enabling) "Enable $code" else "Disable $code"
    }
    val danger = kind == "void" || (kind == "code" && !enabling)
    // A void needs its reason (it's the audit trail); the button stays grey until there is one.
    val canConfirm = !busy && (kind != "void" || text.isNotBlank())

    Tr(attrs = { style { property("border-bottom", "1px solid $Gray200") } }) {
        Td(attrs = {
            attr("colspan", "10")
            style { padding(12.px, 16.px); backgroundColor(if (danger) Color("#fffbeb") else PrimaryPale) }
        }) {
            P(attrs = { style { fontSize(13.px); color(Gray700); property("margin", "0 0 10px"); property("max-width", "860px") } }) {
                Text(explanation)
            }
            row.lastError?.takeIf { kind == "applied" && it.isNotBlank() }?.let {
                P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "0 0 10px") } }) {
                    Text("Server's note: $it")
                }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center); property("flex-wrap", "wrap") } }) {
                if (!enabling) {
                    TextInput(text) {
                        onInput { onText(it.value) }
                        onKeyDown { if (it.key == "Enter" && canConfirm) onConfirm() }
                        placeholder(placeholderText)
                        style {
                            width(360.px); padding(8.px, 10.px); fontSize(13.px)
                            borderRadius(8.px); property("border", "1px solid $Gray200"); property("outline", "none")
                        }
                    }
                }
                Button(attrs = {
                    if (canConfirm) onClick { onConfirm() }
                    style {
                        padding(8.px, 14.px); borderRadius(8.px); property("border", "none")
                        fontSize(13.px); fontWeight(600); color(Color.white); cursor(if (canConfirm) "pointer" else "default")
                        backgroundColor(if (!canConfirm) Gray400 else if (danger) Color("#b91c1c") else Primary)
                    }
                }) { Text(if (busy) "Working…" else confirmLabel) }
                Button(attrs = {
                    onClick { onCancel() }
                    style {
                        padding(8.px, 14.px); borderRadius(8.px); cursor("pointer"); fontSize(13.px); fontWeight(600)
                        property("border", "1px solid $Gray200"); backgroundColor(Color.white); color(Gray700)
                    }
                }) { Text("Cancel") }
            }
        }
    }
}

/**
 * Natural-language questions over the admin data (POST /api/admin/ask).
 *
 * Built for someone who has never used this dashboard: the numbers live behind a dozen drill-downs
 * and a lot of implicit vocabulary, so asking in words is often faster than knowing where to look.
 *
 * Every answer is shown WITH a plain-English restatement of what was actually counted. That is the
 * one thing standing between a wrong reading of the question and a confidently wrong number — the
 * reader can't check SQL, but they can check "analyses completed between 1–7 Sep" against what
 * they meant.
 */
@Composable
private fun AskView(onNavigate: (DrillDownView) -> Unit) {
    var question by remember { mutableStateOf("") }
    var asking by remember { mutableStateOf(false) }
    var answer by remember { mutableStateOf<String?>(null) }
    var restatement by remember { mutableStateOf("") }
    var assumptions by remember { mutableStateOf("") }
    var rowCount by remember { mutableStateOf(0) }
    var kind by remember { mutableStateOf("data") }
    var errorText by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }

    fun ask() {
        val q = question.trim()
        if (q.isBlank() || asking) return
        scope.launch {
            asking = true; errorText = null; answer = null; restatement = ""; assumptions = ""
            val r = performAsk(serverUrl, q, "")
            errorText = r.error
            if (r.error == null) {
                answer = r.answer
                restatement = r.restatement
                assumptions = r.assumptions
                rowCount = r.rowCount
                kind = r.kind
            }
            asking = false
        }
    }

    AskBodyLayout(
        onNavigate = onNavigate,
        question = question, onQuestionChange = { question = it },
        asking = asking, ask = ::ask,
        answer = answer, restatement = restatement, assumptions = assumptions,
        rowCount = rowCount, kind = kind, errorText = errorText,
    )
}

/** Kept as a routed page too (/admin#ask) so an answer can be bookmarked or shared. */
@Composable
private fun AskBodyLayout(
    onNavigate: (DrillDownView) -> Unit,
    question: String, onQuestionChange: (String) -> Unit,
    asking: Boolean, ask: () -> Unit,
    answer: String?, restatement: String, assumptions: String,
    rowCount: Int, kind: String, errorText: String?,
) {
    PageShell {
        BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "Ask a Question" to null)

        CardSection("Ask about your data") {
            P(attrs = { style { color(Gray500); fontSize(13.px); property("margin", "0 0 12px") } }) {
                Text(
                    "Ask anything about users, analyses, revenue or campaigns — in plain English. " +
                        "Each answer says what was counted, so you can check it matched what you meant."
                )
            }
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px) } }) {
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(question)
                    onInput { onQuestionChange(it.value) }
                    onKeyDown { if (it.key == "Enter") ask() }
                    attr("placeholder", "e.g. How many analyses last week had minor concerns?")
                    style {
                        property("flex", "1"); padding(12.px, 14.px); fontSize(14.px)
                        borderRadius(10.px); property("border", "1px solid $Gray200")
                        property("box-sizing", "border-box")
                    }
                }
                Div(attrs = {
                    onClick { ask() }
                    style {
                        property("background", if (asking) Gray400.toString() else GradientPrimary)
                        color(Color.white); padding(12.px, 22.px); borderRadius(10.px)
                        fontWeight(700); fontSize(14.px)
                        cursor(if (asking) "default" else "pointer")
                        property("white-space", "nowrap")
                    }
                }) { Text(if (asking) "Thinking…" else "Ask") }
            }

            // Starter questions: someone new to the dashboard often doesn't know what it can answer,
            // and a blank box is the least discoverable UI there is.
            Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(6.px); property("margin-top", "10px") } }) {
                listOf(
                    "How many people signed up this week?",
                    "How much revenue did we make this month?",
                    "Which titles were analysed most this month?",
                    "How many subscribers do we have right now?",
                ).forEach { suggestion ->
                    Span(attrs = {
                        onClick { onQuestionChange(suggestion); ask() }
                        style {
                            fontSize(12.px); color(Gray600); backgroundColor(Gray100)
                            padding(6.px, 10.px); borderRadius(999.px); cursor("pointer")
                        }
                    }) { Text(suggestion) }
                }
            }
        }

        errorText?.let { msg ->
            CardSection("Couldn't answer") {
                P(attrs = { style { color(ScoreRed); fontSize(14.px); property("margin", "0") } }) { Text(msg) }
            }
        }

        answer?.let { ans ->
            CardSection("Answer") {
                // The restatement leads, deliberately: check the question was understood BEFORE
                // reading the number, not after acting on it.
                if (restatement.isNotBlank()) {
                    Div(attrs = {
                        style {
                            backgroundColor(Gray100); borderRadius(10.px); padding(10.px, 12.px)
                            property("margin-bottom", "12px")
                            property("border-left", "3px solid $Primary")
                        }
                    }) {
                        Div(attrs = { style { fontSize(11.px); fontWeight(700); color(Gray500); property("margin-bottom", "3px") } }) {
                            Text("WHAT WAS COUNTED")
                        }
                        Div(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text(restatement) }
                    }
                }
                P(attrs = { style { fontSize(16.px); color(Gray900); property("line-height", "1.6"); property("margin", "0") } }) {
                    Text(ans)
                }
                if (assumptions.isNotBlank()) {
                    P(attrs = { style { fontSize(12.px); color(Gray500); property("margin", "10px 0 0") } }) {
                        Text("Assumed: $assumptions")
                    }
                }
                P(attrs = { style { fontSize(11.px); color(Gray400); property("margin", "12px 0 0") } }) {
                    Text(
                        if (kind == "explain") "An explanation of how this works — not a figure from the data."
                        else "Based on $rowCount row(s) read live from the database."
                    )
                }
            }
        }
    }
}

/**
 * The current screen, described the way a person would say it out loud.
 *
 * This is what makes "how many analyses does this user have?" answerable while standing on a user
 * detail page — the pronoun has nothing to bind to otherwise, and the model would either guess a
 * user or ask a clarifying question, both of which are worse than useless to someone who just
 * wants the number for the person on their screen.
 *
 * Parameterised views name their subject explicitly; list views describe what they list. Ask
 * returns empty — "this page" on the ask page itself refers to nothing.
 */
private fun describeCurrentPage(v: DrillDownView): String = when (v) {
    is DrillDownView.Ask -> ""
    is DrillDownView.Overview -> "the admin dashboard overview (headline totals for users, analyses, revenue)"
    is DrillDownView.UserDetail ->
        "the user detail page for the user " + (v.userEmail ?: v.userId ?: "(unidentified)") +
            " — questions about \"this user\"/\"they\"/\"her\" mean that person"
    is DrillDownView.TitleDetail ->
        "the title detail page for the title \"" + v.title + "\"" +
            listOfNotNull(v.mediaType, v.year).joinToString(", ").let { if (it.isBlank()) "" else " ($it)" } +
            " — questions about \"this title\"/\"this show\"/\"it\" mean that work"
    is DrillDownView.MediaTypeDetail ->
        "the media type page for \"" + v.mediaType + "\" — \"this type\"/\"these\" mean " + v.mediaType
    is DrillDownView.EditAnalyses ->
        "the edit analyses page" + (v.title?.let { " for the title \"" + it + "\"" } ?: "")
    is DrillDownView.NewsletterCampaignDetail -> "the newsletter campaign detail page for campaign " + v.campaignId
    is DrillDownView.DripCampaignDetail -> "the drip campaign detail page for campaign " + v.campaignId
    is DrillDownView.ResolveFlag -> "the page for resolving flagged analysis " + v.flagId
    is DrillDownView.AllAnalyses -> "the list of all analyses"
    is DrillDownView.AllMedia -> "the list of all media titles"
    is DrillDownView.AllUsers -> "the list of all users"
    is DrillDownView.FoundingMembers -> "the founding members list"
    is DrillDownView.RegularUsers -> "the regular (non-founding) users list"
    is DrillDownView.CostAnalysis -> "the cost analysis page (what analyses cost to run)"
    is DrillDownView.UserJourneys -> "the user journeys page"
    is DrillDownView.FlaggedAnalyses -> "the flagged analyses list"
    is DrillDownView.Engagement -> "the engagement page (comments and reactions)"
    is DrillDownView.RegistrationFunnel -> "the registration funnel page"
    is DrillDownView.InfraCosts -> "the infrastructure costs page (Fly and Supabase invoices)"
    is DrillDownView.MrrDetail -> "the MRR breakdown page (active subscribers across both Stripe accounts)"
    is DrillDownView.AdminEmails -> "the admin emails page"
    is DrillDownView.Broadcast -> "the broadcast push page"
    is DrillDownView.Newsletter -> "the newsletter page"
    is DrillDownView.NewsletterSubscribers -> "the newsletter subscribers list"
    is DrillDownView.GrantCredits -> "the grant credits page"
    is DrillDownView.Invitations -> "the invitations page"
    is DrillDownView.Vouchers -> "the vouchers page"
    is DrillDownView.Referrals ->
        "the referrals page (the refer-a-friend program: every referred friend, its status, whether " +
            "the friend has paid a full month yet (neither side gets a free month before), the end of its hold, " +
            "the friend's free month and the referrer's reward)"
    is DrillDownView.Pricing -> "the pricing and paywall settings page"
    is DrillDownView.Limits -> "the limits page: weekly analysis limit, free analyses, who hit a limit, usage"
    is DrillDownView.CancelFlow -> "the cancel flow editor page (survey reasons and save offers for the in-app cancellation flow)"
    is DrillDownView.ManageCovers -> "the manage covers page"
    is DrillDownView.ReconcileDuplicates -> "the reconcile duplicates page"
    is DrillDownView.Drip -> "the drip campaigns page"
}

/** One answer from the assistant, plus the plain-English statement of what it actually counted. */
/**
 * One line of the conversation.
 *
 * [result] is kept only for assistant turns so an earlier answer keeps its basis, confidence and
 * row count when re-rendered — an answer that loses its provenance on scroll-back is exactly the
 * kind of bare number this feature is built to avoid.
 */
private data class ChatTurn(
    val role: String,
    val text: String,
    val result: AskResult? = null,
)

private data class AskResult(
    val answer: String = "",
    val restatement: String = "",
    val assumptions: String = "",
    val rowCount: Int = 0,
    /** "data" = counted from the database. "explain" = a definition, no query was run. */
    val kind: String = "data",
    /** How many of [rowCount] rows the answer was actually written from. */
    val shownRows: Int = 0,
    val truncated: Boolean = false,
    /** "high" | "medium" | "low" — the model's own certainty, surfaced rather than hidden. */
    val confidence: String = "high",
    /** How many queries it ran to get here. More than one usually means it checked itself. */
    val queryCount: Int = 0,
    val error: String? = null,
)

/** Shared by the floating panel and the routed page so the two can never drift apart. */
private suspend fun performAsk(
    serverUrl: String,
    question: String,
    context: String,
    history: List<ChatTurn> = emptyList(),
): AskResult = try {
    val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
    val token = try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken } catch (_: Exception) { null }
    token?.let { headers["Authorization"] = "Bearer $it" }
    val body = adminJson.encodeToString(
        kotlinx.serialization.json.JsonObject.serializer(),
        kotlinx.serialization.json.buildJsonObject {
            put("question", kotlinx.serialization.json.JsonPrimitive(question))
            if (context.isNotBlank()) put("context", kotlinx.serialization.json.JsonPrimitive(context))
            if (history.isNotEmpty()) {
                put("history", kotlinx.serialization.json.buildJsonArray {
                    history.forEach { t ->
                        add(kotlinx.serialization.json.buildJsonObject {
                            put("role", kotlinx.serialization.json.JsonPrimitive(t.role))
                            put("text", kotlinx.serialization.json.JsonPrimitive(t.text))
                        })
                    }
                })
            }
        }
    )
    val resp = kotlinx.browser.window.fetch(
        "$serverUrl/api/admin/ask",
        kotlin.js.json("method" to "POST", "headers" to headers, "body" to body)
            .unsafeCast<org.w3c.fetch.RequestInit>()
    ).await()
    val text = resp.text().await().toString()
    if (resp.ok) {
        val o = adminJson.parseToJsonElement(text) as? kotlinx.serialization.json.JsonObject
        fun str(k: String) = (o?.get(k) as? kotlinx.serialization.json.JsonPrimitive)?.content.orEmpty()
        AskResult(
            str("answer"), str("restatement"), str("assumptions"),
            str("rowCount").toIntOrNull() ?: 0,
            str("kind").ifBlank { "data" },
            str("shownRows").toIntOrNull() ?: 0,
            str("truncated") == "true",
            str("confidence").ifBlank { "high" },
            str("queryCount").toIntOrNull() ?: 0,
        )
    } else {
        AskResult(error = "Couldn't get an answer (${resp.status}).")
    }
} catch (e: Exception) {
    AskResult(error = "Something went wrong: ${e.message}")
}

/**
 * Floating "ask" button, present on every admin screen.
 *
 * A FAB rather than a dashboard card because the question usually occurs to you while you are
 * looking at something else — three drill-downs deep in Users, say — and having to navigate home
 * first is exactly the friction that stops someone new from using it at all.
 */
/** localStorage key for the conversation. Per-browser convenience only — never authoritative. */
private const val ASK_HISTORY_KEY = "admin-ask-history"

private fun loadAskHistory(): List<ChatTurn> = try {
    val raw = kotlinx.browser.window.localStorage.getItem(ASK_HISTORY_KEY).orEmpty()
    if (raw.isBlank()) emptyList() else {
        (adminJson.parseToJsonElement(raw) as? kotlinx.serialization.json.JsonArray)
            .orEmpty()
            .filterIsInstance<kotlinx.serialization.json.JsonObject>()
            .mapNotNull { o ->
                fun str(k: String) = (o[k] as? kotlinx.serialization.json.JsonPrimitive)?.content.orEmpty()
                fun int(k: String) = str(k).toIntOrNull() ?: 0
                val role = str("role"); val text = str("text")
                if (role.isBlank() || text.isBlank()) null
                else ChatTurn(
                    role, text,
                    if (role == "assistant") AskResult(
                        answer = text, restatement = str("basis"),
                        rowCount = int("rowCount"), kind = str("kind").ifBlank { "data" },
                        confidence = str("confidence").ifBlank { "high" }, queryCount = int("queryCount"),
                    ) else null,
                )
            }
    }
} catch (_: Exception) {
    // A private window, cleared site data, or a storage-blocking browser. An empty conversation is
    // the correct outcome — never let a storage failure take the panel down with it.
    emptyList()
}

private fun saveAskHistory(turns: List<ChatTurn>) {
    try {
        val arr = kotlinx.serialization.json.buildJsonArray {
            turns.takeLast(30).forEach { t ->
                add(kotlinx.serialization.json.buildJsonObject {
                    put("role", kotlinx.serialization.json.JsonPrimitive(t.role))
                    put("text", kotlinx.serialization.json.JsonPrimitive(t.text))
                    t.result?.let { r ->
                        put("basis", kotlinx.serialization.json.JsonPrimitive(r.restatement))
                        put("kind", kotlinx.serialization.json.JsonPrimitive(r.kind))
                        put("confidence", kotlinx.serialization.json.JsonPrimitive(r.confidence))
                        put("rowCount", kotlinx.serialization.json.JsonPrimitive(r.rowCount))
                        put("queryCount", kotlinx.serialization.json.JsonPrimitive(r.queryCount))
                    }
                })
            }
        }
        kotlinx.browser.window.localStorage.setItem(ASK_HISTORY_KEY, arr.toString())
    } catch (_: Exception) { /* storage unavailable — the conversation still works in-memory */ }
}

/**
 * The assistant, as a conversation rather than a series of unrelated questions.
 *
 * History matters here for a specific reason: the natural way to interrogate data is in
 * follow-ups — "how many signed up this week?", then "how many of those are paying?", then "why
 * the drop?". Forcing every question to restate its own context is how people give up on a tool
 * like this. Prior turns are replayed to the model, so pronouns and "those" resolve.
 *
 * The transcript survives a page reload via localStorage, and each answer keeps its basis,
 * confidence and row count when scrolled back to — a number that loses its provenance on
 * scroll-back is the thing this whole feature exists to prevent.
 */
@Composable
private fun AskFab(currentView: DrillDownView) {
    var open by remember { mutableStateOf(false) }
    var turns by remember { mutableStateOf(loadAskHistory()) }
    var question by remember { mutableStateOf("") }
    var asking by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val serverUrl = remember { com.islamicmedia.shared.data.api.serverBaseUrl() }
    val pageContext = describeCurrentPage(currentView)
    val transcriptId = "ask-transcript"

    val suggestions = when (currentView) {
        is DrillDownView.UserDetail -> listOf(
            "How many analyses has this user run?",
            "Is this user still paying?",
            "What does this user's status mean?",
        )
        is DrillDownView.TitleDetail -> listOf(
            "How many people analysed this title?",
            "What score did this title get?",
            "How is the score worked out?",
        )
        is DrillDownView.MrrDetail -> listOf(
            "Which subscribers joined this month?",
            "What is MRR exactly?",
        )
        else -> listOf(
            "How many signed up this week?",
            "Revenue this month?",
            "What does this page show?",
        )
    }

    // Keep the newest exchange in view as it arrives.
    LaunchedEffect(turns.size, asking) {
        kotlinx.browser.document.getElementById(transcriptId)?.let { el ->
            el.scrollTop = el.scrollHeight.toDouble()
        }
    }

    fun submit(q: String) {
        val trimmed = q.trim()
        if (trimmed.isBlank() || asking) return
        val priorForModel = turns
        turns = turns + ChatTurn("user", trimmed)
        question = ""
        scope.launch {
            asking = true
            val r = performAsk(serverUrl, trimmed, pageContext, priorForModel)
            turns = turns + ChatTurn("assistant", r.error ?: r.answer, if (r.error == null) r else null)
            saveAskHistory(turns)
            asking = false
        }
    }

    if (open) {
        Div(attrs = {
            style {
                property("position", "fixed"); property("right", "24px"); property("bottom", "96px")
                width(440.px); property("max-width", "calc(100vw - 32px)")
                property("max-height", "78vh")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                backgroundColor(Color.white); borderRadius(16.px)
                property("box-shadow", "0 18px 50px rgba(0,0,0,0.22)")
                property("z-index", "2450")
            }
        }) {
            // ── header ──
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    padding(16.px, 18.px, 10.px, 18.px)
                    property("border-bottom", "1px solid $Gray100")
                }
            }) {
                Div(attrs = { style { property("flex", "1") } }) {
                    Div(attrs = { style { fontWeight(700); fontSize(15.px); color(Gray900) } }) {
                        Text("Ask about your data")
                    }
                    if (pageContext.isNotBlank()) {
                        Div(attrs = { style { fontSize(11.px); color(Gray500); property("margin-top", "2px") } }) {
                            Text("On this page: " + pageContext.substringBefore(" — "))
                        }
                    }
                }
                if (turns.isNotEmpty()) {
                    Span(attrs = {
                        onClick {
                            turns = emptyList()
                            try { kotlinx.browser.window.localStorage.removeItem(ASK_HISTORY_KEY) } catch (_: Exception) {}
                        }
                        style { fontSize(11.px); color(Gray500); cursor("pointer"); property("margin-right", "12px") }
                    }) { Text("Clear") }
                }
                Span(attrs = {
                    onClick { open = false }
                    style { fontSize(18.px); color(Gray400); cursor("pointer") }
                }) { Text("✕") }
            }

            // ── transcript ──
            Div(attrs = {
                id(transcriptId)
                style {
                    property("flex", "1"); property("overflow-y", "auto")
                    property("min-height", "120px"); padding(14.px, 18.px)
                }
            }) {
                if (turns.isEmpty() && !asking) {
                    Div(attrs = { style { fontSize(12.px); color(Gray500); property("margin-bottom", "10px") } }) {
                        Text("Ask anything about your users, revenue or analyses. Follow-up questions work — you can just say “what about last month?”")
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); gap(5.px) } }) {
                        suggestions.forEach { sug ->
                            Span(attrs = {
                                onClick { submit(sug) }
                                style {
                                    fontSize(11.px); color(Gray600); backgroundColor(Gray100)
                                    padding(5.px, 9.px); borderRadius(999.px); cursor("pointer")
                                }
                            }) { Text(sug) }
                        }
                    }
                }

                turns.forEach { turn ->
                    if (turn.role == "user") {
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd)
                                property("margin-bottom", "10px")
                            }
                        }) {
                            Div(attrs = {
                                style {
                                    property("background", GradientPrimary); color(Color.white)
                                    padding(8.px, 12.px); borderRadius(14.px)
                                    fontSize(13.px); property("max-width", "82%")
                                }
                            }) { Text(turn.text) }
                        }
                    } else {
                        Div(attrs = { style { property("margin-bottom", "16px") } }) {
                            val r = turn.result
                            if (r != null && r.restatement.isNotBlank()) {
                                Div(attrs = {
                                    style {
                                        backgroundColor(Gray100); borderRadius(8.px); padding(7.px, 10.px)
                                        property("margin-bottom", "7px"); property("border-left", "3px solid $Primary")
                                    }
                                }) {
                                    Div(attrs = { style { fontSize(9.px); fontWeight(700); color(Gray500) } }) { Text("WHAT WAS COUNTED") }
                                    Div(attrs = { style { fontSize(11.px); color(Gray700) } }) { Text(r.restatement) }
                                }
                            }
                            Div(attrs = { style { fontSize(13.px); color(Gray900); property("line-height", "1.55") } }) {
                                Text(turn.text)
                            }
                            if (r != null && (r.confidence == "medium" || r.confidence == "low")) {
                                Div(attrs = {
                                    style {
                                        property("margin-top", "7px"); padding(6.px, 9.px)
                                        backgroundColor(Color("#fef3c7")); borderRadius(7.px)
                                        fontSize(11.px); color(Color("#92400e"))
                                    }
                                }) {
                                    Text(
                                        if (r.confidence == "low") "Low confidence — treat this as a rough indication, not a fact."
                                        else "Moderate confidence — worth double-checking before you rely on it."
                                    )
                                }
                            }
                            if (r != null) {
                                Div(attrs = { style { fontSize(9.px); color(Gray400); property("margin-top", "6px") } }) {
                                    Text(
                                        if (r.kind == "explain") "An explanation — not a figure from the data."
                                        else "From ${r.rowCount} row(s) read live" +
                                            (if (r.queryCount > 1) " across ${r.queryCount} queries" else "") + "."
                                    )
                                }
                            }
                        }
                    }
                }

                if (asking) {
                    Div(attrs = { style { fontSize(12.px); color(Gray500) } }) {
                        Text("Looking it up…")
                    }
                }
            }

            // ── composer ──
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); gap(6.px)
                    padding(10.px, 14.px, 14.px, 14.px)
                    property("border-top", "1px solid $Gray100")
                }
            }) {
                Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                    value(question)
                    onInput { question = it.value }
                    onKeyDown { if (it.key == "Enter") submit(question) }
                    attr("placeholder", if (turns.isEmpty()) "e.g. How many signed up this week?" else "Ask a follow-up…")
                    style {
                        property("flex", "1"); padding(10.px, 12.px); fontSize(13.px)
                        borderRadius(9.px); property("border", "1px solid $Gray200")
                        property("box-sizing", "border-box")
                    }
                }
                Div(attrs = {
                    onClick { submit(question) }
                    style {
                        property("background", if (asking) Gray400.toString() else GradientPrimary)
                        color(Color.white); padding(10.px, 16.px); borderRadius(9.px)
                        fontWeight(700); fontSize(13.px); cursor(if (asking) "default" else "pointer")
                        property("white-space", "nowrap")
                    }
                }) { Text(if (asking) "…" else "Ask") }
            }
        }
    }

    Div(attrs = {
        onClick { open = !open }
        style {
            property("position", "fixed"); property("right", "24px"); property("bottom", "24px")
            width(56.px); height(56.px); borderRadius(50.percent)
            property("background", GradientPrimary); color(Color.white)
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            fontSize(32.px); fontWeight(700); property("line-height", "1")
            property("padding-bottom", "4px")
            cursor("pointer"); property("z-index", "2440")
            property("box-shadow", "0 8px 24px rgba(0,0,0,0.25)")
        }
        attr("title", "Ask a question about your data")
    }) { Text(if (open) "✕" else "?") }
}


/** Label, text color, background for the MRR subscriber STATUS badge. Cancelling reuses the
 *  amber this view already used for that tag; cancelled gets the file's standard muted-gray
 *  "nothing to see here" treatment rather than a red that would overstate it as a problem. */
private fun mrrStatusBadge(status: String): Triple<String, CSSColorValue, CSSColorValue> = when (status) {
    "cancelling" -> Triple("Cancelling", Color("#b45309"), Color("#fef3c7"))
    "canceled" -> Triple("Cancelled", Gray600, Gray100)
    else -> Triple("Active", ScoreGreen, ScoreGreenBg)
}

// Explicit per-column pixel widths shared by BOTH account tables (and their own header/body
// cells), so the in-app and ThriveCart tables read as one grid split by heading instead of two
// tables that happen to drift by a few pixels once STATUS/COUPON were added.
private data class MrrCol(val header: String, val width: Int, val alignRight: Boolean = false)
private val MRR_COLUMNS = listOf(
    MrrCol("SUBSCRIBER", 210),
    MrrCol("STATUS", 90),
    MrrCol("PLAN", 130),
    MrrCol("COUPON", 150),
    MrrCol("CHARGED", 100, alignRight = true),
    MrrCol("PER MONTH", 100, alignRight = true),
    MrrCol("SINCE", 90),
    MrrCol("RENEWS", 90),
)

/**
 * MRR broken down to the individual subscriptions behind it.
 *
 * The headline card is one number spanning two Stripe accounts, which is exactly the kind of
 * figure nobody should have to take on trust — this page shows every active subscription that
 * contributes to it, which account bills them, and what each one is worth per month.
 *
 * Both accounts get a section even when one is empty: a ThriveCart section reading "no active
 * subscriptions" is the answer to a real question (does ThriveCart bill through Stripe
 * subscriptions at all?), whereas omitting it would look like the data simply wasn't fetched.
 */
@Composable
private fun MrrDetailView(onNavigate: (DrillDownView) -> Unit) {
    val scope = rememberCoroutineScope()
    var mrr by remember { mutableStateOf<MrrData?>(null) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        scope.launch {
            val serverUrl = com.islamicmedia.shared.data.api.serverBaseUrl()
            val headers = kotlin.js.json("Content-Type" to "application/json", "Accept" to "application/json")
            try { com.islamicmedia.shared.data.auth.SupabaseAuthService.instance.accessToken }
                catch (_: Exception) { null }
                ?.let { headers["Authorization"] = "Bearer $it" }
            try {
                val resp = kotlinx.browser.window.fetch(
                    "$serverUrl/api/admin/mrr",
                    kotlin.js.json("method" to "GET", "headers" to headers)
                        .unsafeCast<org.w3c.fetch.RequestInit>()
                ).await()
                val text = resp.text().await().toString()
                mrr = if (resp.ok && !text.trimStart().startsWith("<")) adminJson.decodeFromString<MrrData>(text)
                else MrrData(error = "MRR lookup failed (${resp.status})")
            } catch (e: Exception) {
                mrr = MrrData(error = e.message ?: "MRR lookup failed")
            }
            loading = false
        }
    }

    BreadcrumbNav("Dashboard" to { onNavigate(DrillDownView.Overview) }, "MRR" to null)

    val m = mrr
    Div(attrs = { style { property("margin-bottom", "18px") } }) {
        Div(attrs = { style { fontSize(13.px); color(Gray500) } }) { Text("Monthly recurring revenue") }
        Div(attrs = { style { fontSize(34.px); fontWeight(800); color(Gray900) } }) {
            Text(
                when {
                    loading -> "…"
                    m != null && m.complete -> formatMoneyShort(m.total)
                    m != null && (m.mainAvailable || m.thriveCartAvailable) -> formatMoneyShort(m.total) + "*"
                    else -> "n/a"
                }
            )
        }
        // Same figure in dirhams. Headline totals only: converting every row of the subscriber
        // table below would double its width to say nothing new.
        if (m != null && !loading && (m.mainAvailable || m.thriveCartAvailable)) {
            Div(attrs = { style { fontSize(14.px); fontWeight(600); color(Gray500) } }) {
                Text(formatAed(m.total))
            }
        }
        Div(attrs = { style { fontSize(12.px); color(Gray500) } }) {
            Text(
                if (m == null || loading) ""
                else "${m.totalSubs} active subscription${if (m.totalSubs == 1) "" else "s"} across 2 Stripe accounts · " +
                    "annual plans normalised to a month"
            )
        }
        if (m != null && !loading && m.cancelingSubs > 0) {
            // Stripe still reports a cancel-scheduled subscription as active, so it must be called
            // out explicitly here or a reader assumes the total above already includes it.
            Div(attrs = { style { color(Color("#b45309")); fontSize(12.px); property("margin-top", "4px") } }) {
                Text(
                    "${m.cancelingSubs} more subscription${if (m.cancelingSubs == 1) "" else "s"} " +
                        "(${formatMoneyShort(m.canceling)}/mo) ${if (m.cancelingSubs == 1) "is" else "are"} scheduled to cancel " +
                        "and excluded from the total above."
                )
            }
        }
        if (m != null && !loading && !m.complete) {
            Div(attrs = { style { color(Color("#b45309")); fontSize(12.px); property("margin-top", "6px") } }) {
                Text(
                    if (m.mainAvailable || m.thriveCartAvailable)
                        "* PARTIAL — ${m.error ?: "one Stripe account did not answer"}. The real figure is higher than shown."
                    else "Unavailable — ${m.error ?: "neither Stripe account answered"}."
                )
            }
        }
    }

    if (loading) {
        Div(attrs = { style { color(Gray500); fontSize(13.px) } }) { Text("Loading subscriptions from both Stripe accounts…") }
        return
    }

    listOf(
        "in-app" to ("In-app product" to (m?.mainAvailable ?: false)),
        "thrivecart" to ("ThriveCart (Good Child)" to (m?.thriveCartAvailable ?: false)),
    ).forEach { (key, meta) ->
        val (label, available) = meta
        // All active-status subscriptions for this account, cancel-scheduled and fully-cancelled
        // ones included — the table below shows every one of them, tagged, so the reader can see
        // who is leaving and who has already left.
        val rows = m?.subscribers.orEmpty().filter { it.account == key }
        val cancelingRows = rows.filter { it.canceling }
        val canceledRows = rows.filter { it.status == "canceled" }
        // The subtotal and "active" count must match the headline MRR, which excludes both
        // cancelling subs and fully-cancelled ones, or this line would silently disagree with the
        // number it is meant to explain.
        val activeRows = rows.filterNot { it.canceling || it.status == "canceled" }
        val subtotal = activeRows.sumOf { it.monthly }
        // Cancelled rows sink to the bottom of their account's table instead of interleaving with
        // the still-paying ones. sortedBy is stable, so everything else keeps the server's order.
        val sortedRows = rows.sortedBy { it.status == "canceled" }

        Div(attrs = { style { property("margin-bottom", "22px") } }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    property("gap", "10px"); property("margin-bottom", "8px")
                }
            }) {
                Span(attrs = { style { fontWeight(700); fontSize(15.px); color(Gray900) } }) { Text(label) }
                Span(attrs = { style { fontSize(13.px); color(Gray600) } }) {
                    Text(
                        if (!available) "unavailable"
                        else "${formatMoney(subtotal)}/mo · ${activeRows.size} active" +
                            (if (cancelingRows.isNotEmpty())
                                " (+ ${cancelingRows.size} cancelling, excluded)" else "") +
                            (if (canceledRows.isNotEmpty())
                                " (+ ${canceledRows.size} cancelled)" else "")
                    )
                }
            }

            when {
                !available -> Div(attrs = { style { fontSize(13.px); color(Color("#b45309")) } }) {
                    Text("This Stripe account did not answer, so its subscriptions are missing from the total above.")
                }
                rows.isEmpty() -> Div(attrs = { style { fontSize(13.px); color(Gray500) } }) {
                    // Stated explicitly rather than left blank: for ThriveCart this is the answer
                    // to "does it bill through Stripe subscriptions at all?", not a loading gap.
                    Text("No active Stripe subscriptions on this account. It answered — there are genuinely none.")
                }
                else -> Div(attrs = { style { property("overflow-x", "auto") } }) {
                    // Fixed table-layout plus an explicit width on every header AND body cell
                    // (from the shared MRR_COLUMNS grid) is what keeps the two account tables
                    // lined up as one grid instead of each auto-sizing to its own content.
                    Table(attrs = {
                        style {
                            width(MRR_COLUMNS.sumOf { it.width }.px)
                            property("table-layout", "fixed")
                            property("border-collapse", "collapse"); fontSize(13.px)
                        }
                    }) {
                        Thead {
                            Tr {
                                MRR_COLUMNS.forEach { col ->
                                    Th(attrs = {
                                        style {
                                            width(col.width.px)
                                            textAlign(if (col.alignRight) "right" else "left")
                                            padding(8.px, 10.px); fontSize(10.px)
                                            color(Gray500); property("border-bottom", "1px solid $Gray200")
                                            property("white-space", "nowrap")
                                        }
                                    }) { Text(col.header) }
                                }
                            }
                        }
                        Tbody {
                            sortedRows.forEach { sub ->
                                // Cancelling AND cancelled rows stay in the table (the reader should
                                // see who is leaving and who has already left) but are visibly muted;
                                // the STATUS badge below says which is which.
                                val muted = sub.canceling || sub.status == "canceled"
                                Tr(attrs = { style { if (muted) property("opacity", "0.65") } }) {
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[0].width.px); padding(8.px, 10.px)
                                            property("border-bottom", "1px solid $Gray100")
                                            property("overflow", "hidden"); property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(sub.email ?: "(no email on the Stripe customer)")
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[1].width.px); padding(8.px, 10.px)
                                            property("border-bottom", "1px solid $Gray100")
                                        }
                                    }) {
                                        val (badgeText, badgeFg, badgeBg) = mrrStatusBadge(sub.status)
                                        Span(attrs = {
                                            style {
                                                fontSize(10.px); fontWeight(700)
                                                padding(2.px, 8.px); borderRadius(6.px)
                                                display(DisplayStyle.InlineBlock); property("white-space", "nowrap")
                                                backgroundColor(badgeBg); color(badgeFg)
                                            }
                                        }) { Text(badgeText) }
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[2].width.px); padding(8.px, 10.px)
                                            color(Gray600); property("border-bottom", "1px solid $Gray100")
                                            property("overflow", "hidden"); property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(sub.plan ?: "—")
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[3].width.px); padding(8.px, 10.px)
                                            // A dash for "no coupon" reads as absence rather than as data,
                                            // so it gets a lighter gray than an actual discount would.
                                            color(if (sub.discount != null) Gray600 else Gray400)
                                            property("border-bottom", "1px solid $Gray100")
                                            property("overflow", "hidden"); property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(sub.discount ?: "—")
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[4].width.px); padding(8.px, 10.px)
                                            textAlign("right"); color(Gray600)
                                            property("border-bottom", "1px solid $Gray100"); property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text("${formatMoney(sub.amount)} / ${sub.interval}")
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[5].width.px); padding(8.px, 10.px)
                                            textAlign("right"); fontWeight(600)
                                            property("border-bottom", "1px solid $Gray100"); property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(formatMoney(sub.monthly) + (if (sub.canceling) " (excluded)" else ""))
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[6].width.px); padding(8.px, 10.px)
                                            color(Gray500); property("border-bottom", "1px solid $Gray100"); property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(sub.since ?: "—")
                                    }
                                    Td(attrs = {
                                        style {
                                            width(MRR_COLUMNS[7].width.px); padding(8.px, 10.px)
                                            color(Gray500); property("border-bottom", "1px solid $Gray100"); property("white-space", "nowrap")
                                        }
                                    }) {
                                        Text(sub.renews ?: "—")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    Div(attrs = { style { fontSize(11.px); color(Gray500); property("line-height", "1.6") } }) {
        Div { Text("Counts subscriptions Stripe reports as active, minus those already scheduled to cancel. Trialing subscribers are not revenue yet, and past_due ones have already failed to pay, so neither is included.") }
        Div { Text("Ongoing discounts are deducted. A one-off coupon is not, since it only affects a single invoice rather than the steady state.") }
    }
}
